import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import React__default, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ReadOnlyService, YardiCheckbox, YardiControlTextBox, YardiControlCurrency, YardiControlDatePicker, YardiControlDropdown, YardiControlButton } from "@yardi/yardi-react-framework";
import { MultiSelectComponent } from "@syncfusion/ej2-react-dropdowns";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, parsePhoneNumber } from "libphonenumber-js";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "yardi-react-translation/translationService";
import { BehaviorSubject } from "rxjs";
import { take, filter, switchMap } from "rxjs/operators";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faFileArrowUp, faFileLines, faDownload, faTrashCan, faCircleInfo } from "@fortawesome/pro-light-svg-icons";
import { faMapMarkerAlt, faLock } from "@fortawesome/pro-solid-svg-icons";
import * as LabelPrimitive from "@radix-ui/react-label";
import { X, ChevronRight, Check, Circle, ChevronDown } from "lucide-react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import "react-dom";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
let config = null;
function setConfigurableFormsConfig(c) {
  config = c;
}
function getConfigurableFormsConfig() {
  return config;
}
function getConfigurableFormsConfigOrThrow() {
  if (!config) {
    throw new Error(
      "Configurable forms config is not set. Call setConfigurableFormsConfig() from your app before using configurable-forms components or API."
    );
  }
  return config;
}
function getApiClient() {
  return getConfigurableFormsConfigOrThrow().apiClient;
}
function normalizeIdentifier(value) {
  return String(value ?? "").trim();
}
function normalizeNumericIdentifier(value) {
  const normalizedValue = normalizeIdentifier(value);
  return /^\d+$/.test(normalizedValue) && normalizedValue !== "0" ? normalizedValue : "";
}
function throwIfApiOperationFailed(responseData, fallbackMessage) {
  if (!responseData || typeof responseData !== "object") {
    return;
  }
  if (responseData.Status === false || responseData.Success === false) {
    throw new Error(responseData.Message || responseData.ErrorMessage || fallbackMessage);
  }
}
function resolveFileIdentifiers(file) {
  if (typeof file === "string") {
    return {
      uploadId: normalizeIdentifier(file),
      pmdocsid: normalizeNumericIdentifier(file),
      setupId: ""
    };
  }
  return {
    uploadId: normalizeIdentifier(file.uploadId),
    pmdocsid: normalizeNumericIdentifier(
      file.pmdocsid ?? file.VoyagerAttachmentId ?? file.DocumentId ?? file.uploadId
    ),
    setupId: normalizeNumericIdentifier(file.SetupId)
  };
}
const getStateFieldLabelByCountryCode = (countryCode) => {
  if (!countryCode) return "State";
  const code = countryCode.toUpperCase().trim();
  const countryCodeToLabelMap = {
    "US": "State",
    "USA": "State",
    "MX": "State",
    "MEXICO": "State",
    "AU": "State",
    "AUSTRALIA": "State",
    "CA": "Province",
    "CANADA": "Province",
    "GB": "County",
    "UK": "County",
    "UNITED KINGDOM": "County",
    "IE": "County",
    "IRELAND": "County",
    "IN": "State",
    "INDIA": "State",
    "BR": "State",
    "BRAZIL": "State",
    "DE": "State",
    "GERMANY": "State",
    "FR": "Region",
    "FRANCE": "Region",
    "IT": "Region",
    "ITALY": "Region",
    "ES": "Region",
    "SPAIN": "Region",
    "PT": "Region",
    "PORTUGAL": "Region",
    "JP": "Prefecture",
    "JAPAN": "Prefecture",
    "CN": "Province",
    "CHINA": "Province",
    "KR": "Province",
    "SOUTH KOREA": "Province",
    "KOREA": "Province",
    "TH": "Province",
    "THAILAND": "Province",
    "VN": "Province",
    "VIETNAM": "Province",
    "PH": "Province",
    "PHILIPPINES": "Province",
    "ID": "Province",
    "INDONESIA": "Province",
    "MY": "State",
    "MALAYSIA": "State",
    "ZA": "Province",
    "SOUTH AFRICA": "Province",
    "AR": "Province",
    "ARGENTINA": "Province",
    "CL": "Region",
    "CHILE": "Region",
    "CO": "Department",
    "COLOMBIA": "Department",
    "PE": "Region",
    "PERU": "Region",
    "RU": "Region",
    "RUSSIA": "Region",
    "PL": "Voivodeship",
    "POLAND": "Voivodeship",
    "UA": "Oblast",
    "UKRAINE": "Oblast",
    "TR": "Province",
    "TURKEY": "Province",
    "EG": "Governorate",
    "EGYPT": "Governorate",
    "SA": "Province",
    "SAUDI ARABIA": "Province",
    "AE": "Emirate",
    "UAE": "Emirate",
    "UNITED ARAB EMIRATES": "Emirate",
    "QA": "Municipality",
    "QATAR": "Municipality",
    "KW": "Governorate",
    "KUWAIT": "Governorate",
    "IL": "District",
    "ISRAEL": "District",
    "PK": "Province",
    "PAKISTAN": "Province",
    "BD": "Division",
    "BANGLADESH": "Division",
    "AF": "Province",
    "AFGHANISTAN": "Province",
    "IR": "Province",
    "IRAN": "Province",
    "IQ": "Governorate",
    "IRAQ": "Governorate",
    "NZ": "Region",
    "NEW ZEALAND": "Region",
    "CH": "Canton",
    "SWITZERLAND": "Canton",
    "BE": "Province",
    "BELGIUM": "Province",
    "NL": "Province",
    "NETHERLANDS": "Province",
    "AT": "State",
    "AUSTRIA": "State",
    "SE": "County",
    "SWEDEN": "County",
    "NO": "County",
    "NORWAY": "County",
    "DK": "Region",
    "DENMARK": "Region",
    "FI": "Region",
    "FINLAND": "Region",
    "GR": "Region",
    "GREECE": "Region",
    "SG": "Region",
    "SINGAPORE": "Region",
    "HK": "Region",
    "HONG KONG": "Region",
    "TW": "County",
    "TAIWAN": "County"
  };
  const countryLabel = countryCodeToLabelMap[code] || "State";
  return countryLabel;
};
const handleApiError = (error) => {
  var _a, _b;
  console.error("API Error:", error);
  if ((_b = (_a = error.response) == null ? void 0 : _a.data) == null ? void 0 : _b.Message) {
    throw new Error(error.response.data.Message);
  }
  throw error;
};
const configurableFormsApi = {
  loadFieldsForEndUser: async (sectionId, configureData) => {
    var _a, _b, _c, _d, _e, _f;
    try {
      console.log("Loading fields for end user with:", { sectionId, configureData });
      const response = await getApiClient().get(`cafeformfields/loadfieldsforenduser`, {
        params: {
          sectionId,
          // Use sectionId directly as in Angular
          configurableFormAppData: configureData ? JSON.stringify(configureData) : void 0,
          checkCountry: true
        }
      });
      if ((_c = (_b = (_a = response.data) == null ? void 0 : _a.Response) == null ? void 0 : _b[0]) == null ? void 0 : _c.fields) {
        const fields = response.data.Response[0].fields;
        for (const field of fields) {
          if (((_d = field.VoyColName) == null ? void 0 : _d.toLowerCase().includes("state")) || ((_e = field.VoyColName) == null ? void 0 : _e.toLowerCase().includes("province"))) {
            try {
              const typeResponse = await getApiClient().get(`cafeformfields/getStateFieldType`, {
                params: {
                  fieldId: field.hmy,
                  loginId: (configureData == null ? void 0 : configureData.Id) || ""
                }
              });
              if ((_f = typeResponse.data) == null ? void 0 : _f.CountryCode) {
                const countryCode = typeResponse.data.CountryCode.Name.toUpperCase();
                const stateLabel = getStateFieldLabelByCountryCode(countryCode);
                let updatedFieldName = stateLabel;
                if (field.sFieldName) {
                  const stateRegex = /\bstate\b/gi;
                  if (stateRegex.test(field.sFieldName)) {
                    updatedFieldName = field.sFieldName.replace(stateRegex, stateLabel);
                  } else {
                    updatedFieldName = stateLabel;
                  }
                }
                if (["US", "MX", "AU", "CA"].includes(countryCode)) {
                  field.sControlType = "drop down";
                  field.sLabel = updatedFieldName;
                  field.sFieldName = updatedFieldName;
                  const countryId = typeResponse.data.CountryCode.id;
                  if (countryId) {
                    const statesResponse = await getApiClient().get(`cafeformfields/getAutoCompleteData`, {
                      params: {
                        countryName: countryId,
                        isStatesInfo: countryCode !== "CA",
                        isProvinceInfo: countryCode === "CA"
                      }
                    });
                    if (statesResponse.data) {
                      field.dropdownlist = statesResponse.data;
                    }
                  }
                } else {
                  field.sControlType = "text box";
                  field.sLabel = updatedFieldName;
                  field.sFieldName = updatedFieldName;
                }
                field.countryCode = countryCode;
              }
            } catch (error) {
              console.error("Error getting state field type:", error);
            }
          }
        }
      }
      console.log("Fields for end user response:", response.data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getCustomFormData: async (Id, sectionId) => {
    try {
      const response = await getApiClient().get(`cafeformfields/getcustomformdata`, {
        params: { Id, sectionId }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getParentFieldSetup: async (sectionId) => {
    try {
      const response = await getApiClient().get(`customscreenparentFieldSetup/custompage`, {
        params: { sectionId }
      });
      return response.data.Response;
    } catch (error) {
      handleApiError(error);
    }
  },
  saveCustomFormData: async (data) => {
    try {
      const response = await getApiClient().post(`cafeformfields/savecustomformdata`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  saveSectionProgress: async (data) => {
    try {
      const response = await getApiClient().post(`cafeformsections/savesectionprogress`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getSectionProgress: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformsections/getsectionprogress`, {
        params
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  // Renamed from getCustomTablesData to match usage
  getCustomTablesFieldsData: async (params) => {
    try {
      const queryParams = {
        Name: params.Name,
        DId: params.DId ?? 0,
        pointer: params.pointer ?? 0,
        prospectId: params.prospectId
      };
      const response = await getApiClient().get(`cafeFormTemplates/getcustomtablesfieldsdata`, {
        params: queryParams
      });
      const body = response.data;
      if (Array.isArray(body)) return body;
      if ((body == null ? void 0 : body.Data) && Array.isArray(body.Data)) return body.Data;
      if ((body == null ? void 0 : body.data) && Array.isArray(body.data)) return body.data;
      return body ?? void 0;
    } catch (error) {
      handleApiError(error);
    }
  },
  getCustomTablesData: async (params) => {
    try {
      const response = await getApiClient().get(`cafeFormTemplates/getcustomtablesdata`, {
        params: {
          templateId: params.templateId,
          ObjectType: params.ObjectType,
          ObjectPointer: params.ObjectPointer
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  displayUploadDocument: async (data) => {
    try {
      const response = await getApiClient().post(`cafeformfields/displayuploaddocument`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  // Add file upload methods
  getUploadSetup: async (request) => {
    try {
      const response = await getApiClient().post(`cafeformfields/displayuploaddocument`, request);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  uploadConfigurableFormFile: async (formData) => {
    try {
      const response = await getApiClient().post(`api/configurableforms/fileupload`, formData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  listConfigurableFormFiles: async (params) => {
    try {
      const response = await getApiClient().get(`api/configurableforms/fileupload`, {
        params
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getConfigurableFormFilePreview: async (file) => {
    const client = getApiClient();
    const { uploadId, pmdocsid } = resolveFileIdentifiers(file);
    try {
      if (pmdocsid) {
        const response = await client.get(`cafeformfields/reviewdocument`, {
          params: { pmdocsid },
          timeout: 18e4
        });
        throwIfApiOperationFailed(response.data, "Failed to load document preview.");
        return response.data;
      }
    } catch (error) {
      if (!uploadId || uploadId === pmdocsid) {
        handleApiError(error);
      }
    }
    try {
      if (!uploadId) {
        throw new Error("Missing upload identifier.");
      }
      const fallbackResponse = await client.get(`api/configurableforms/fileupload/${uploadId}/content`, {
        timeout: 18e4
      });
      throwIfApiOperationFailed(fallbackResponse.data, "Failed to load document preview.");
      return fallbackResponse.data;
    } catch (fallbackError) {
      handleApiError(fallbackError);
    }
  },
  deleteConfigurableFormFile: async (file) => {
    const client = getApiClient();
    const { uploadId, pmdocsid, setupId } = resolveFileIdentifiers(file);
    try {
      if (pmdocsid && setupId) {
        const response = await client.delete(`cafeformfields/deleteuploaddocument`, {
          params: {
            pmdocsid,
            MySetupId: setupId
          }
        });
        throwIfApiOperationFailed(response.data, "Failed to delete document.");
        return response.data;
      }
    } catch (error) {
      if (!uploadId || uploadId === pmdocsid) {
        handleApiError(error);
      }
    }
    try {
      if (!uploadId) {
        throw new Error("Missing upload identifier.");
      }
      const response = await client.delete(`api/configurableforms/fileupload/${uploadId}`);
      throwIfApiOperationFailed(response.data, "Failed to delete document.");
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  uploadDocument: async (formData) => {
    try {
      const response = await getApiClient().post(`cafeformfields/uploaddocument`, formData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  reviewDocument: async (pmdocsid) => {
    try {
      const response = await getApiClient().get(`cafeformfields/reviewdocument?pmdocsid=${pmdocsid}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  deleteUploadedDocument: async (pmdocsid, setupId) => {
    try {
      const response = await getApiClient().delete(`cafeformfields/deleteuploaddocument?pmdocsid=${pmdocsid}&MySetupId=${setupId}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getDocumentsByField: async (prospectId, fieldHmy) => {
    try {
      const response = await getApiClient().get(`cafeformfields/getdocumentsbyfield?prospectId=${prospectId}&fieldHmy=${fieldHmy}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getStatesProvinces: async (countryId, isStatesInfo = true, isProvinceInfo = false) => {
    try {
      console.log("?? API Call - getStatesProvinces:", { countryId, isStatesInfo, isProvinceInfo });
      const response = await getApiClient().get(`cafeformfields/getAutoCompleteData`, {
        params: {
          countryName: countryId.toString(),
          // Ensure it's a string for the API
          isStatesInfo: isStatesInfo.toString(),
          isProvinceInfo: isProvinceInfo.toString()
        }
      });
      console.log("?? API Response - getStatesProvinces:", response.data);
      return response.data;
    } catch (error) {
      console.error("?? API Error - getStatesProvinces:", error);
      handleApiError(error);
    }
  },
  // Added back saveCustomTableData
  saveCustomTableData: async (data) => {
    try {
      const response = await getApiClient().post(`cafeformfields/SaveCustomTableData`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  // Additional methods from Angular service
  loadActiveTemplates: async (configurableFormAppData) => {
    try {
      const response = await getApiClient().get(`cafeformtemplates/loadactivetemplates`, {
        params: {
          configurableFormAppData: JSON.stringify(configurableFormAppData),
          checkCountry: true
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  loadSections: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformsections/loadsections`, {
        params: {
          ...params,
          configurableFormAppData: JSON.stringify(params.configurableFormAppData)
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  loadMatchingSectionsFromActiveTemplate: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformsections/loadmatchingsectionsfromactivetemplate`, {
        params: {
          ...params,
          configurableFormAppData: params.configurableFormAppData ? JSON.stringify(params.configurableFormAppData) : void 0
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  loadSectionsBySectionIds: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformsections/loadsectionsbysectionids`, {
        params: {
          ...params,
          configurableFormAppData: params.configurableFormAppData ? JSON.stringify(params.configurableFormAppData) : void 0
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getSectionIdsFromType0Templates: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformtemplates/getsectionidsfromtype0templates`, {
        params
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  processSectionMatchingByNames: async (params) => {
    try {
      const response = await getApiClient().get(`cafeformtemplates/processsectionmatchingbynames`, {
        params: {
          ...params,
          configurableFormAppData: params.configurableFormAppData ? JSON.stringify(params.configurableFormAppData) : void 0
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  // Step 1: Get section IDs from type 0 templates (OPTIMIZED: Use existing method to avoid duplicate calls)
  getStep1SectionIdsFromType0Templates: async (applicant, sectionNames, formType = "Standard Application") => {
    try {
      const configurableFormAppData = {
        Id: applicant.hmy || applicant.Id,
        CountryId: applicant.hcountry || applicant.CountryId,
        StateId: applicant.hstate || applicant.StateId,
        StateName: applicant.stateName || applicant.StateName,
        ProvinceId: applicant.hprovince || applicant.ProvinceId,
        ProvinceName: applicant.provinceName || applicant.ProvinceName,
        PropertyId: applicant.propertyid || applicant.PropertyId,
        CompanyId: applicant.companyId || applicant.CompanyId,
        TemplateType: formType,
        CafeType: applicant.CafeType || 2,
        RenterType: applicant.RenterType || 103,
        foreigndb: applicant.hforeigndb || applicant.foreigndb
      };
      const params = {
        formType,
        sectionNames,
        configurableFormAppData: JSON.stringify(configurableFormAppData)
      };
      if (applicant) {
        if (applicant.CompanyId || applicant.companyId) params.companyId = applicant.CompanyId || applicant.companyId;
        if (applicant.PropertyId || applicant.propertyid) params.propertyId = applicant.PropertyId || applicant.propertyid;
        if (applicant.CountryId || applicant.hcountry) params.countryId = applicant.CountryId || applicant.hcountry;
        if (applicant.StateId || applicant.hstate) params.stateId = applicant.StateId || applicant.hstate;
        if (applicant.StateName || applicant.stateName) params.stateName = applicant.StateName || applicant.stateName;
        if (applicant.ProvinceId || applicant.hprovince) params.provinceId = applicant.ProvinceId || applicant.hprovince;
        if (applicant.ProvinceName || applicant.provinceName) params.provinceName = applicant.ProvinceName || applicant.provinceName;
        if (applicant.CafeType) params.cafeType = applicant.CafeType;
        if (applicant.RenterType) params.renterType = applicant.RenterType;
        if (applicant.hforeigndb || applicant.foreigndb) params.foreigndb = applicant.hforeigndb || applicant.foreigndb;
        if (applicant.Id || applicant.hmy) params.prospectId = applicant.Id || applicant.hmy;
      }
      return await configurableFormsApi.getSectionIdsFromType0Templates(params);
    } catch (error) {
      handleApiError(error);
    }
  },
  // Step 2: Get active template matching sections
  getStep2ActiveTemplateMatchingSections: async (sectionIds, sectionNames, applicant, formType = "Standard Application") => {
    try {
      const configurableFormAppData = {
        Id: applicant.hmy || applicant.Id,
        CountryId: applicant.hcountry || applicant.CountryId,
        StateId: applicant.hstate || applicant.StateId,
        StateName: applicant.stateName || applicant.StateName,
        ProvinceId: applicant.hprovince || applicant.ProvinceId,
        ProvinceName: applicant.provinceName || applicant.ProvinceName,
        PropertyId: applicant.propertyid || applicant.PropertyId,
        CompanyId: applicant.companyId || applicant.CompanyId,
        TemplateType: formType,
        CafeType: applicant.CafeType || 2,
        RenterType: applicant.RenterType || 103,
        foreigndb: applicant.hforeigndb || applicant.foreigndb
      };
      const parameters = {
        formType,
        sectionIds,
        sectionNames,
        configurableFormAppData: JSON.stringify(configurableFormAppData)
      };
      if (applicant) {
        if (applicant.CompanyId || applicant.companyId) parameters.companyId = applicant.CompanyId || applicant.companyId;
        if (applicant.PropertyId || applicant.propertyid) parameters.propertyId = applicant.PropertyId || applicant.propertyid;
        if (applicant.CountryId || applicant.hcountry) parameters.countryId = applicant.CountryId || applicant.hcountry;
        if (applicant.StateId || applicant.hstate) parameters.stateId = applicant.StateId || applicant.hstate;
        if (applicant.StateName || applicant.stateName) parameters.stateName = applicant.StateName || applicant.stateName;
        if (applicant.ProvinceId || applicant.hprovince) parameters.provinceId = applicant.ProvinceId || applicant.hprovince;
        if (applicant.ProvinceName || applicant.provinceName) parameters.provinceName = applicant.ProvinceName || applicant.provinceName;
        if (applicant.CafeType) parameters.cafeType = applicant.CafeType;
        if (applicant.RenterType) parameters.renterType = applicant.RenterType;
        if (applicant.hforeigndb || applicant.foreigndb) parameters.foreigndb = applicant.hforeigndb || applicant.foreigndb;
        if (applicant.Id || applicant.hmy) parameters.prospectId = applicant.Id || applicant.hmy;
      }
      const response = await getApiClient().get(`cafeformsections/loadmatchingsectionsfromactivetemplate`, { params: parameters });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  // Main initialization method that combines both steps
  initializeCustomFormSectionsWithClean2Step: async (applicant, propertyName, loginApplicantId, sectionNames, formType = "Standard Application") => {
    try {
      const step1Response = await configurableFormsApi.getStep1SectionIdsFromType0Templates(applicant, sectionNames, formType);
      if ((step1Response == null ? void 0 : step1Response.Status) === "Success") {
        const step2Response = await configurableFormsApi.getStep2ActiveTemplateMatchingSections(
          step1Response.CaretSeparatedIds,
          step1Response.CaretSeparatedNames,
          applicant,
          formType
        );
        if ((step2Response == null ? void 0 : step2Response.Status) === true) {
          if (step2Response.ActiveTemplateId) {
            const customTablesData = await configurableFormsApi.getCustomTablesData({
              templateId: step2Response.ActiveTemplateId,
              ObjectType: step2Response.ActiveTemplateIObjType || 0,
              ObjectPointer: step2Response.ActiveTemplateObjPointer || 0
            });
            const processedSections = step2Response.MatchingSections.map((section) => ({
              ...section,
              name: section.Label,
              title: section.Label,
              url: "",
              sectionId: section.UUID,
              sectionOrder: section._Order || 0,
              iorder: section.iorder || section.iOrder,
              // Preserve iorder if available
              isExpanded: false,
              sTableName: section.TableName,
              completed: false,
              id: section.id,
              isCustomTable: false,
              ObjType: step2Response.ActiveTemplateIObjType || 0,
              ObjPointer: step2Response.ActiveTemplateObjPointer || 0
            }));
            const processedCustomTables = (customTablesData || []).map((item) => ({
              ...item,
              name: item.sDisplayName,
              title: item.sDisplayName,
              url: "",
              sectionId: item.UUID,
              sectionOrder: item._Order || 0,
              iorder: item.iorder || item.iOrder,
              // Preserve iorder if available
              isExpanded: false,
              sTableName: item.sCustomTableName,
              completed: false,
              id: item.hmy,
              isCustomTable: true,
              sCustomTableName: item.sCustomTableName,
              ObjType: step2Response.ActiveTemplateIObjType || 0,
              ObjPointer: step2Response.ActiveTemplateObjPointer || 0,
              TableName: item.sCustomTableName
              // Add this to match Angular's check
            }));
            const allSections = [...processedSections, ...processedCustomTables].sort((a, b) => {
              const aTable = !!a.isCustomTable;
              const bTable = !!b.isCustomTable;
              if (!aTable && bTable) return -1;
              if (aTable && !bTable) return 1;
              const orderA = Number(a.sectionOrder) || 0;
              const orderB = Number(b.sectionOrder) || 0;
              if (orderA === 0 && orderB !== 0) return 1;
              if (orderB === 0 && orderA !== 0) return -1;
              return orderA - orderB;
            });
            await configurableFormsApi.getSectionProgress({
              templateId: step2Response.ActiveTemplateId,
              propertyId: applicant.PropertyId || applicant.propertyid,
              companyId: applicant.CompanyId ?? applicant.companyId,
              hObjPointer: loginApplicantId
            });
            return {
              Status: "Success",
              Message: "Workflow completed successfully",
              Sections: allSections,
              TemplateId: step2Response.ActiveTemplateId
            };
          }
        }
        throw new Error((step2Response == null ? void 0 : step2Response.Message) || "Failed to get active template matching sections");
      }
      throw new Error((step1Response == null ? void 0 : step1Response.Message) || "Failed to get section IDs from type 0 templates");
    } catch (error) {
      handleApiError(error);
    }
  },
  getCustomTableData: async (params) => {
    try {
      const response = await getApiClient().post(`cafeformfields/GetCustomTableData`, params);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  getCustomTableDataComplete: async (params) => {
    var _a;
    try {
      const response = await getApiClient().post(`configurableforms/GetCustomTableDataComplete`, params);
      return (_a = response.data) == null ? void 0 : _a.Response;
    } catch (error) {
      handleApiError(error);
    }
  },
  /**
   * Get field validation settings for all fields in a section
   */
  getSectionFieldValidationSettings: async (sectionId) => {
    try {
      const response = await getApiClient().get(`cafeformfields/getsectionfieldvalidationsettings`, {
        params: { sectionId }
      });
      return response.data;
    } catch (error) {
      console.error("Error loading validation settings:", error);
      handleApiError(error);
    }
  },
  /**
   * Fetch template-level terms and conditions from database
   * @param templateId The form template ID
   * @returns Array of terms and conditions for the template
   */
  getTemplateTermsAndConditions: async (templateId) => {
    try {
      const response = await getApiClient().get(`cafeformsections/loadtemplatetermsandconditions`, {
        params: { templateId: templateId.toString() }
      });
      if (response.data.Status && response.data.Response && response.data.Response.length > 0) {
        const data = response.data.Response[0];
        if (data.sectionExists && data.fields && data.fields.length > 0) {
          return data.fields.map((field) => ({
            hmy: field.hmy,
            fieldName: field.sFieldName || field.fieldName || "",
            termsText: field.sTermsandConditions || field.termsText || "",
            isRequired: field.bRequired === -1 || field.bRequired === 1,
            isVisible: field.bVisible === -1 || field.bVisible === 1,
            order: field.iOrder || field.order || 0
          })).filter((tc) => tc.isVisible).sort((a, b) => a.order - b.order);
        }
      }
      return [];
    } catch (error) {
      console.error("Error fetching template terms and conditions:", error);
      return [];
    }
  },
  /**
   * Fetch section hide query settings (general hide/show based on field values and conditions)
   * Uses existing RentCafe framework endpoint: /connect/cafeformsections/getsectionquerydata
   * @param sectionIds Comma-separated section IDs to get settings for
   * @returns Section hide query configuration
   */
  getSectionHideQuerySettings: async (sectionIds) => {
    try {
      const response = await getApiClient().get(`cafeformsections/getsectionquerydata`, {
        params: { sectionIds }
      });
      if (response.data.Status && response.data.Response) {
        return response.data.Response.map((setting) => {
          var _a, _b, _c;
          return {
            sectionId: ((_a = setting.hCafeFormSections) == null ? void 0 : _a.toString()) || ((_b = setting.hCafeFormSectionsReference) == null ? void 0 : _b.toString()),
            sectionName: "",
            // Not provided by backend
            fieldId: (_c = setting.hCafeFormFields) == null ? void 0 : _c.toString(),
            fieldName: "",
            // Not provided by backend
            expectedValue: setting.sValue,
            condition: setting.sCondition,
            // 1=<, 2=>, 3==
            calendarCondition: setting.sCalendarCondition,
            // 1=Years, 2=Months, 3=Days
            action: setting.iAction,
            // 0=Hide when met, 1=Show when met
            hideIfNotMatch: setting.iAction === 0
            // Derived: 0 means hide if condition met
          };
        });
      }
      return [];
    } catch (error) {
      console.error("Error fetching section hide query settings:", error);
      return [];
    }
  },
  /**
   * Fetch section country settings (hide/show based on country selection)
   * Uses existing RentCafe framework endpoint: /connect/cafeformsections/getsectioncountrydata
   * @param objectType Object type (2 for sections)
   * @param objectPointer Section ID
   * @returns Section country visibility configuration
   */
  getSectionCountrySettings: async (objectType, objectPointer) => {
    try {
      const response = await getApiClient().get(`cafeformsections/getsectioncountrydata`, {
        params: { ObjectType: objectType, ObjectPointer: objectPointer }
      });
      if (response.data.Status && response.data.Response) {
        const groupedBySection = response.data.Response.reduce((acc, setting) => {
          var _a;
          const sectionId = (_a = setting.ObjectPointer) == null ? void 0 : _a.toString();
          if (!acc[sectionId]) {
            acc[sectionId] = {
              sectionId,
              sectionName: "",
              sectionCountrySettings: []
            };
          }
          acc[sectionId].sectionCountrySettings.push({
            countryId: setting.Country,
            countryName: "",
            // Will need to be resolved via country lookup
            isVisible: true
            // Presence in table means visible for that country
          });
          return acc;
        }, {});
        return Object.values(groupedBySection);
      }
      return [];
    } catch (error) {
      console.error("Error fetching section country settings:", error);
      return [];
    }
  },
  /**
   * Get country settings for multiple sections at template level
   * @param templateId Form template ID
   * @param sectionIds Comma-separated section IDs
   * @param fieldIds Comma-separated field IDs
   * @returns Combined country settings
   */
  getCountrySettingsData: async (templateId, sectionIds, fieldIds) => {
    try {
      const response = await getApiClient().get(`cafeformsections/getcountrysettingsdata`, {
        params: { templateId, sectionIds, fieldIds }
      });
      if (response.data.Status && response.data.Response) {
        return response.data.Response;
      }
      return [];
    } catch (error) {
      console.error("Error fetching country settings data:", error);
      return [];
    }
  }
};
const VALIDATION_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  SSN: /^\d{3}-?\d{2}-?\d{4}$/,
  ZIP_CODE: /^\d{5}(-\d{4})?$/,
  ALPHABETS_ONLY: /^[a-zA-Z\s]+$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
  NUMBERS_ONLY: /^\d+$/
};
const ZIP_CODE_PATTERNS = {
  // ── Americas ──────────────────────────────────────────────────────────────
  "united states": { pattern: /^\d{5}(-?\d{4})?$/, format: "12345 or 12345-6789", name: "ZIP Code" },
  "united states of america": { pattern: /^\d{5}(-?\d{4})?$/, format: "12345 or 12345-6789", name: "ZIP Code" },
  "usa": { pattern: /^\d{5}(-?\d{4})?$/, format: "12345 or 12345-6789", name: "ZIP Code" },
  "canada": { pattern: /^[A-Za-z]\d[A-Za-z][\s\-]?\d[A-Za-z]\d$/, format: "A1A 1A1", name: "Postal Code" },
  "mexico": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "brazil": { pattern: /^\d{5}-?\d{3}$/, format: "12345-678", name: "CEP" },
  "argentina": { pattern: /^[A-Za-z]?\d{4}[A-Za-z]{0,3}$/, format: "1234 or A1234BCD", name: "Código Postal" },
  "chile": { pattern: /^\d{7}$/, format: "1234567", name: "Código Postal" },
  "colombia": { pattern: /^\d{6}$/, format: "123456", name: "Código Postal" },
  "peru": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "venezuela": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "ecuador": { pattern: /^\d{6}$/, format: "123456", name: "Código Postal" },
  "uruguay": { pattern: /^\d{5}$/, format: "11300", name: "Código Postal" },
  "paraguay": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "bolivia": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "suriname": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "guyana": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "costa rica": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "panama": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "guatemala": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "honduras": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "el salvador": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "nicaragua": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "belize": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "dominican republic": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "cuba": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "haiti": { pattern: /^HT\d{4}$/i, format: "HT6110", name: "Code Postal" },
  "jamaica": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "trinidad and tobago": { pattern: /^\d{6}$/, format: "123456", name: "Postal Code" },
  "barbados": { pattern: /^BB\d{5}$/i, format: "BB24000", name: "Postal Code" },
  "grenada": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "saint lucia": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "st lucia": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "st. lucia": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "saint vincent and the grenadines": { pattern: /^VC\d{4}$/i, format: "VC0100", name: "Postal Code" },
  "st vincent and the grenadines": { pattern: /^VC\d{4}$/i, format: "VC0100", name: "Postal Code" },
  "saint kitts and nevis": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "dominica": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "antigua and barbuda": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "bahamas": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "puerto rico": { pattern: /^\d{5}$/, format: "00601", name: "ZIP Code" },
  "us virgin islands": { pattern: /^\d{5}$/, format: "00801", name: "ZIP Code" },
  "virgin islands, u.s.": { pattern: /^\d{5}$/, format: "00801", name: "ZIP Code" },
  "virgin islands": { pattern: /^\d{5}$/, format: "00801", name: "ZIP Code" },
  "guam": { pattern: /^\d{5}$/, format: "96910", name: "ZIP Code" },
  "american samoa": { pattern: /^\d{5}$/, format: "96799", name: "ZIP Code" },
  "northern mariana islands": { pattern: /^\d{5}$/, format: "96950", name: "ZIP Code" },
  "greenland": { pattern: /^\d{4}$/, format: "3900", name: "Postnummer" },
  "bermuda": { pattern: /^[A-Za-z]{2}\s?\d{2}$/, format: "AA 12", name: "Postal Code" },
  "cayman islands": { pattern: /^KY\d-?\d{4}$/i, format: "KY1-1100", name: "Postal Code" },
  "turks and caicos islands": { pattern: /^TKCA\s?1ZZ$/i, format: "TKCA 1ZZ", name: "Postcode" },
  "british virgin islands": { pattern: /^VG\d{4}$/i, format: "VG1110", name: "Postal Code" },
  "anguilla": { pattern: /^AI-?\d{4}$/i, format: "AI-2640", name: "Postal Code" },
  "montserrat": { pattern: /^MSR-?\d{4}$/i, format: "MSR-1110", name: "Postal Code" },
  "falkland islands": { pattern: /^FIQQ\s?1ZZ$/i, format: "FIQQ 1ZZ", name: "Postcode" },
  "falkland islands (malvinas)": { pattern: /^FIQQ\s?1ZZ$/i, format: "FIQQ 1ZZ", name: "Postcode" },
  "french guiana": { pattern: /^973\d{2}$/, format: "97300", name: "Code Postal" },
  "guadeloupe": { pattern: /^971\d{2}$/, format: "97100", name: "Code Postal" },
  "martinique": { pattern: /^972\d{2}$/, format: "97200", name: "Code Postal" },
  "saint pierre and miquelon": { pattern: /^975\d{2}$/, format: "97500", name: "Code Postal" },
  "saint barthelemy": { pattern: /^97133$/, format: "97133", name: "Code Postal" },
  "saint barthélemy": { pattern: /^97133$/, format: "97133", name: "Code Postal" },
  "saint martin": { pattern: /^97150$/, format: "97150", name: "Code Postal" },
  "sint maarten": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "aruba": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "curacao": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "bonaire": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "netherlands antilles": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "south georgia": { pattern: /^SIQQ\s?1ZZ$/i, format: "SIQQ 1ZZ", name: "Postcode" },
  "south georgia and the south sandwich islands": { pattern: /^SIQQ\s?1ZZ$/i, format: "SIQQ 1ZZ", name: "Postcode" },
  // ── Europe ────────────────────────────────────────────────────────────────
  "united kingdom": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "great britain": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "uk": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "england": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "scotland": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "wales": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "northern ireland": { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s?\d[A-Za-z]{2}$/, format: "SW1A 1AA", name: "Postcode" },
  "germany": { pattern: /^\d{5}$/, format: "12345", name: "Postleitzahl" },
  "france": { pattern: /^\d{5}$/, format: "12345", name: "Code Postal" },
  "italy": { pattern: /^\d{5}$/, format: "12345", name: "CAP" },
  "spain": { pattern: /^\d{5}$/, format: "12345", name: "Código Postal" },
  "netherlands": { pattern: /^\d{4}\s?[A-Za-z]{2}$/, format: "1234 AB", name: "Postcode" },
  "belgium": { pattern: /^\d{4}$/, format: "1234", name: "Postcode" },
  "switzerland": { pattern: /^\d{4}$/, format: "1234", name: "PLZ" },
  "austria": { pattern: /^\d{4}$/, format: "1234", name: "Postleitzahl" },
  "liechtenstein": { pattern: /^\d{4}$/, format: "9490", name: "Postleitzahl" },
  "portugal": { pattern: /^\d{4}-?\d{3}$/, format: "1234-567", name: "Código Postal" },
  "ireland": { pattern: /^[A-Za-z]\d{1,2}\s?[A-Za-z0-9]{4}$/, format: "A65 F4E2", name: "Eircode" },
  "luxembourg": { pattern: /^(L-)?\d{4}$/i, format: "1234 or L-1234", name: "Code Postal" },
  "sweden": { pattern: /^\d{3}\s?\d{2}$/, format: "123 45", name: "Postnummer" },
  "norway": { pattern: /^\d{4}$/, format: "1234", name: "Postnummer" },
  "denmark": { pattern: /^\d{4}$/, format: "1234", name: "Postnummer" },
  "finland": { pattern: /^\d{5}$/, format: "12345", name: "Postinumero" },
  "iceland": { pattern: /^\d{3}$/, format: "123", name: "Póstnúmer" },
  "faroe islands": { pattern: /^FO-?\d{3}$/i, format: "FO-100", name: "Postal Code" },
  "åland islands": { pattern: /^\d{5}$/, format: "22150", name: "Postnummer" },
  "aland islands": { pattern: /^\d{5}$/, format: "22150", name: "Postnummer" },
  "svalbard and jan mayen": { pattern: /^\d{4}$/, format: "9170", name: "Postnummer" },
  "svalbard": { pattern: /^\d{4}$/, format: "9170", name: "Postnummer" },
  "jan mayen": { pattern: /^\d{4}$/, format: "9170", name: "Postnummer" },
  "poland": { pattern: /^\d{2}-?\d{3}$/, format: "12-345", name: "Kod Pocztowy" },
  "czech republic": { pattern: /^\d{3}\s?\d{2}$/, format: "123 45", name: "PSČ" },
  "czechia": { pattern: /^\d{3}\s?\d{2}$/, format: "123 45", name: "PSČ" },
  "slovakia": { pattern: /^\d{3}\s?\d{2}$/, format: "123 45", name: "PSČ" },
  "hungary": { pattern: /^\d{4}$/, format: "1234", name: "Irányítószám" },
  "romania": { pattern: /^\d{6}$/, format: "123456", name: "Cod Poștal" },
  "bulgaria": { pattern: /^\d{4}$/, format: "1234", name: "Пощенски код" },
  "slovenia": { pattern: /^(SI-)?\d{4}$/i, format: "1234 or SI-1234", name: "Poštna številka" },
  "croatia": { pattern: /^(HR-)?\d{5}$/i, format: "12345 or HR-12345", name: "Poštanski broj" },
  "serbia": { pattern: /^\d{5}$/, format: "12345", name: "Poštanski broj" },
  "montenegro": { pattern: /^\d{5}$/, format: "12345", name: "Poštanski broj" },
  "north macedonia": { pattern: /^\d{4}$/, format: "1234", name: "Поштенски број" },
  "macedonia": { pattern: /^\d{4}$/, format: "1234", name: "Поштенски број" },
  "bosnia and herzegovina": { pattern: /^\d{5}$/, format: "12345", name: "Poštanski broj" },
  "albania": { pattern: /^\d{4}$/, format: "1234", name: "Kodi Postar" },
  "kosovo": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "estonia": { pattern: /^\d{5}$/, format: "12345", name: "Sihtnumber" },
  "latvia": { pattern: /^(LV-)?\d{4}$/i, format: "1234 or LV-1234", name: "Pasta indekss" },
  "lithuania": { pattern: /^(LT-)?\d{5}$/i, format: "12345 or LT-12345", name: "Pašto kodas" },
  "russia": { pattern: /^\d{6}$/, format: "123456", name: "Почтовый индекс" },
  "russian federation": { pattern: /^\d{6}$/, format: "123456", name: "Почтовый индекс" },
  "ukraine": { pattern: /^\d{5}$/, format: "12345", name: "Поштовий індекс" },
  "belarus": { pattern: /^\d{6}$/, format: "123456", name: "Паштовы індэкс" },
  "moldova": { pattern: /^(MD-)?\d{4}$/i, format: "1234 or MD-1234", name: "Cod poștal" },
  "moldova, republic of": { pattern: /^(MD-)?\d{4}$/i, format: "1234 or MD-1234", name: "Cod poștal" },
  "greece": { pattern: /^\d{5}$/, format: "12345", name: "Ταχυδρομικός κώδικας" },
  "cyprus": { pattern: /^\d{4}$/, format: "1234", name: "Ταχυδρομικός Κώδικας" },
  "malta": { pattern: /^[A-Za-z]{3}\s?\d{4}$/, format: "ABC 1234", name: "Kodiċi Postali" },
  "turkey": { pattern: /^\d{5}$/, format: "12345", name: "Posta kodu" },
  "andorra": { pattern: /^AD\d{3}$/i, format: "AD500", name: "Codi postal" },
  "monaco": { pattern: /^980\d{2}$/, format: "98000", name: "Code Postal" },
  "san marino": { pattern: /^4789\d$/, format: "47890", name: "Codice Avviamento Postale" },
  "vatican city": { pattern: /^00120$/, format: "00120", name: "Codice Postale" },
  "holy see": { pattern: /^00120$/, format: "00120", name: "Codice Postale" },
  "gibraltar": { pattern: /^GX11\s?1AA$/i, format: "GX11 1AA", name: "Postcode" },
  "isle of man": { pattern: /^IM\d\s?\d[A-Za-z]{2}$/i, format: "IM9 1AA", name: "Postcode" },
  "jersey": { pattern: /^JE\d\s?\d[A-Za-z]{2}$/i, format: "JE2 3AA", name: "Postcode" },
  "guernsey": { pattern: /^GY\d\s?\d[A-Za-z]{2}$/i, format: "GY9 9AA", name: "Postcode" },
  // ── Asia ──────────────────────────────────────────────────────────────────
  "india": { pattern: /^\d{6}$/, format: "123456", name: "PIN Code" },
  "china": { pattern: /^\d{6}$/, format: "123456", name: "邮政编码" },
  "people's republic of china": { pattern: /^\d{6}$/, format: "123456", name: "邮政编码" },
  "japan": { pattern: /^\d{3}-?\d{4}$/, format: "123-4567", name: "郵便番号" },
  "south korea": { pattern: /^\d{5}$/, format: "12345", name: "우편번호" },
  "korea, republic of": { pattern: /^\d{5}$/, format: "12345", name: "우편번호" },
  "republic of korea": { pattern: /^\d{5}$/, format: "12345", name: "우편번호" },
  "korea": { pattern: /^\d{5}$/, format: "12345", name: "우편번호" },
  "north korea": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "taiwan": { pattern: /^\d{3}\s?\d{3}$|^\d{5}$/, format: "123 456 or 12345", name: "郵遞區號" },
  "taiwan, province of china": { pattern: /^\d{3}\s?\d{3}$|^\d{5}$/, format: "123 456 or 12345", name: "郵遞區號" },
  "hong kong": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "No standard format", name: "Postal Code" },
  "hong kong sar": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "No standard format", name: "Postal Code" },
  "macao": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "No standard format", name: "Postal Code" },
  "macau": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "No standard format", name: "Postal Code" },
  "macao sar": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "No standard format", name: "Postal Code" },
  "singapore": { pattern: /^\d{6}$/, format: "123456", name: "Postal Code" },
  "malaysia": { pattern: /^\d{5}$/, format: "12345", name: "Poskod" },
  "indonesia": { pattern: /^\d{5}$/, format: "12345", name: "Kode Pos" },
  "philippines": { pattern: /^\d{4}$/, format: "1234", name: "ZIP Code" },
  "thailand": { pattern: /^\d{5}$/, format: "12345", name: "รหัสไปรษณีย์" },
  "vietnam": { pattern: /^\d{5,6}$/, format: "12345 or 123456", name: "Mã bưu chính" },
  "laos": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "cambodia": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "myanmar": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "brunei": { pattern: /^[A-Za-z]{2}\d{4}$/, format: "AA1234", name: "Postal Code" },
  "brunei darussalam": { pattern: /^[A-Za-z]{2}\d{4}$/, format: "AA1234", name: "Postal Code" },
  "timor-leste": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "east timor": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "pakistan": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "bangladesh": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "sri lanka": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "nepal": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "bhutan": { pattern: /^\d{5}$/, format: "11001", name: "Postal Code" },
  "maldives": { pattern: /^\d{5}$/, format: "20026", name: "Postal Code" },
  "afghanistan": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "mongolia": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "kazakhstan": { pattern: /^\d{6}$/, format: "123456", name: "Postal Code" },
  "uzbekistan": { pattern: /^\d{6}$/, format: "123456", name: "Postal Code" },
  "tajikistan": { pattern: /^\d{6}$/, format: "735700", name: "Postal Code" },
  "turkmenistan": { pattern: /^\d{6}$/, format: "744000", name: "Postal Code" },
  "kyrgyzstan": { pattern: /^\d{6}$/, format: "720000", name: "Postal Code" },
  "georgia": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "armenia": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "azerbaijan": { pattern: /^(AZ)?\d{4}$/i, format: "1234 or AZ1234", name: "Postal Code" },
  "christmas island": { pattern: /^6798$/, format: "6798", name: "Postal Code" },
  "cocos (keeling) islands": { pattern: /^6799$/, format: "6799", name: "Postal Code" },
  "cocos islands": { pattern: /^6799$/, format: "6799", name: "Postal Code" },
  "british indian ocean territory": { pattern: /^BBND\s?1ZZ$/i, format: "BBND 1ZZ", name: "Postcode" },
  // ── Middle East ───────────────────────────────────────────────────────────
  "saudi arabia": { pattern: /^\d{5}(-?\d{4})?$/, format: "12345 or 12345-6789", name: "Postal Code" },
  "united arab emirates": { pattern: /^\d{5,6}$/, format: "12345 or 123456", name: "Postal Code" },
  "uae": { pattern: /^\d{5,6}$/, format: "12345 or 123456", name: "Postal Code" },
  "kuwait": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "qatar": { pattern: /^\d{4,6}$/, format: "1234", name: "Postal Code" },
  "bahrain": { pattern: /^\d{3,4}$/, format: "123 or 1234", name: "Postal Code" },
  "oman": { pattern: /^\d{3}$/, format: "123", name: "Postal Code" },
  "iran": { pattern: /^\d{10}$/, format: "1234567890", name: "Postal Code" },
  "iran, islamic republic of": { pattern: /^\d{10}$/, format: "1234567890", name: "Postal Code" },
  "iraq": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "israel": { pattern: /^\d{5}(\d{2})?$/, format: "12345 or 1234567", name: "Postal Code" },
  "jordan": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "lebanon": { pattern: /^\d{4,5}$/, format: "1234 or 12345", name: "Postal Code" },
  "syria": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "yemen": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "palestine": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "palestinian territory, occupied": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  // ── Africa ────────────────────────────────────────────────────────────────
  "south africa": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "kenya": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "ethiopia": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "egypt": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "ghana": { pattern: /^[A-Z]{2}\d{3,4}[A-Z0-9]{0,2}$/i, format: "GA-123-4567 or GA1234", name: "Ghana Post Digital Address" },
  "tanzania": { pattern: /^\d{4,5}$/, format: "1234 or 12345", name: "Postal Code" },
  "uganda": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "rwanda": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "burundi": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "malawi": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "zambia": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "zimbabwe": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "botswana": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "namibia": { pattern: /^\d{5}$/, format: "10005", name: "Postal Code" },
  "eswatini": { pattern: /^[A-Za-z]\d{3}$/, format: "H100", name: "Postal Code" },
  "swaziland": { pattern: /^[A-Za-z]\d{3}$/, format: "H100", name: "Postal Code" },
  "lesotho": { pattern: /^\d{3}$/, format: "100", name: "Postal Code" },
  "south sudan": { pattern: /^\d{5}$/, format: "11111", name: "Postal Code" },
  "sudan": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "somalia": { pattern: /^[A-Za-z]{2}\s?\d{5}$/, format: "AB 12345", name: "Postal Code" },
  "djibouti": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "eritrea": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "morocco": { pattern: /^\d{5}$/, format: "12345", name: "Code Postal" },
  "algeria": { pattern: /^\d{5}$/, format: "12345", name: "Code Postal" },
  "tunisia": { pattern: /^\d{4}$/, format: "1234", name: "Code Postal" },
  "libya": { pattern: /^\d{5}$/, format: "10001", name: "Postal Code" },
  "senegal": { pattern: /^\d{5}$/, format: "12345", name: "Code Postal" },
  "mali": { pattern: /^\d{5}$/, format: "10001", name: "Code Postal" },
  "burkina faso": { pattern: /^\d{5}$/, format: "12345", name: "Code Postal" },
  "niger": { pattern: /^\d{4}$/, format: "1234", name: "Code Postal" },
  "chad": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Code Postal" },
  "cameroon": { pattern: /^\d{5}$/, format: "10001", name: "Code Postal" },
  "nigeria": { pattern: /^\d{6}$/, format: "123456", name: "Postal Code" },
  "guinea": { pattern: /^\d{3}$/, format: "001", name: "Code Postal" },
  "guinea-bissau": { pattern: /^\d{4}$/, format: "1000", name: "Code Postal" },
  "sierra leone": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "liberia": { pattern: /^\d{4}$/, format: "1000", name: "Postal Code" },
  "ivory coast": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "cote d'ivoire": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "côte d'ivoire": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "togo": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "benin": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "mauritania": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "gambia": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "cape verde": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "gabon": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "equatorial guinea": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "sao tome and principe": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "central african republic": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "congo, the democratic republic of the": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "congo (drc)": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "congo (republic)": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "congo": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "angola": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "mozambique": { pattern: /^\d{4}$/, format: "1234", name: "Código Postal" },
  "madagascar": { pattern: /^\d{3}$/, format: "101", name: "Code Postal" },
  "mauritius": { pattern: /^\d{5}$/, format: "12345", name: "Postal Code" },
  "seychelles": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "comoros": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "mayotte": { pattern: /^976\d{2}$/, format: "97600", name: "Code Postal" },
  "reunion": { pattern: /^974\d{2}$/, format: "97400", name: "Code Postal" },
  "réunion": { pattern: /^974\d{2}$/, format: "97400", name: "Code Postal" },
  "saint helena": { pattern: /^STHL\s?1ZZ$/i, format: "STHL 1ZZ", name: "Postcode" },
  "western sahara": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  // ── Oceania ───────────────────────────────────────────────────────────────
  "australia": { pattern: /^(?:[A-Za-z]{2,3}\s+)?\d{4}$/, format: "1234 or NSW 1234", name: "Postcode" },
  "new zealand": { pattern: /^\d{4}$/, format: "1234", name: "Postcode" },
  "papua new guinea": { pattern: /^\d{3}$/, format: "123", name: "Postal Code" },
  "fiji": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "solomon islands": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "vanuatu": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "samoa": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "tonga": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "kiribati": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "tuvalu": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "nauru": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "micronesia": { pattern: /^\d{5}$/, format: "96941", name: "ZIP Code" },
  "micronesia, federated states of": { pattern: /^\d{5}$/, format: "96941", name: "ZIP Code" },
  "marshall islands": { pattern: /^\d{5}$/, format: "96960", name: "ZIP Code" },
  "palau": { pattern: /^\d{5}$/, format: "96940", name: "ZIP Code" },
  "cook islands": { pattern: /^\d{4}$/, format: "1234", name: "Postal Code" },
  "niue": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "tokelau": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "french polynesia": { pattern: /^987\d{2}$/, format: "98700", name: "Code Postal" },
  "new caledonia": { pattern: /^\d{5}$/, format: "98800", name: "Code Postal" },
  "wallis and futuna": { pattern: /^986\d{2}$/, format: "98600", name: "Code Postal" },
  "norfolk island": { pattern: /^2899$/, format: "2899", name: "Postal Code" },
  "pitcairn": { pattern: /^PCRN\s?1ZZ$/i, format: "PCRN 1ZZ", name: "Postcode" },
  "pitcairn islands": { pattern: /^PCRN\s?1ZZ$/i, format: "PCRN 1ZZ", name: "Postcode" },
  "heard island and mcdonald islands": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" },
  "antarctica": { pattern: /^[A-Za-z0-9\s\-]{3,12}$/, format: "3-12 alphanumeric", name: "Postal Code" }
};
const ISO_TO_ZIP_KEY = {
  // Americas
  us: "united states",
  usa: "united states",
  ca: "canada",
  can: "canada",
  mx: "mexico",
  mex: "mexico",
  br: "brazil",
  bra: "brazil",
  ar: "argentina",
  arg: "argentina",
  cl: "chile",
  chl: "chile",
  co: "colombia",
  col: "colombia",
  pe: "peru",
  per: "peru",
  ve: "ve",
  ven: "venezuela",
  ec: "ecuador",
  ecu: "ecuador",
  uy: "uruguay",
  ury: "uruguay",
  py: "paraguay",
  pry: "paraguay",
  bo: "bolivia",
  bol: "bolivia",
  sr: "suriname",
  sur: "suriname",
  gy: "guyana",
  guy: "guyana",
  cr: "costa rica",
  cri: "costa rica",
  pa: "panama",
  pan: "panama",
  gt: "guatemala",
  gtm: "guatemala",
  hn: "honduras",
  hnd: "honduras",
  sv: "el salvador",
  slv: "el salvador",
  ni: "nicaragua",
  nic: "nicaragua",
  bz: "belize",
  blz: "belize",
  do: "dominican republic",
  dom: "dominican republic",
  cu: "cuba",
  cub: "cuba",
  ht: "haiti",
  hti: "haiti",
  jm: "jamaica",
  jam: "jamaica",
  tt: "trinidad and tobago",
  tto: "trinidad and tobago",
  bb: "barbados",
  brb: "barbados",
  gd: "grenada",
  grd: "grenada",
  lc: "saint lucia",
  lca: "saint lucia",
  vc: "saint vincent and the grenadines",
  vct: "saint vincent and the grenadines",
  kn: "saint kitts and nevis",
  kna: "saint kitts and nevis",
  dm: "dominica",
  dma: "dominica",
  ag: "antigua and barbuda",
  atg: "antigua and barbuda",
  bs: "bahamas",
  bhs: "bahamas",
  pr: "puerto rico",
  pri: "puerto rico",
  vi: "us virgin islands",
  vir: "us virgin islands",
  gu: "guam",
  gum: "guam",
  as: "american samoa",
  asm: "american samoa",
  mp: "northern mariana islands",
  mnp: "northern mariana islands",
  gl: "greenland",
  grl: "greenland",
  bm: "bermuda",
  bmu: "bermuda",
  ky: "cayman islands",
  cym: "cayman islands",
  tc: "turks and caicos islands",
  tca: "turks and caicos islands",
  vg: "british virgin islands",
  vgb: "british virgin islands",
  ai: "anguilla",
  aia: "anguilla",
  ms: "montserrat",
  msr: "montserrat",
  fk: "falkland islands",
  flk: "falkland islands",
  gf: "french guiana",
  guf: "french guiana",
  gp: "guadeloupe",
  glp: "guadeloupe",
  mq: "martinique",
  mtq: "martinique",
  pm: "saint pierre and miquelon",
  spm: "saint pierre and miquelon",
  bl: "saint barthelemy",
  blm: "saint barthelemy",
  mf: "saint martin",
  maf: "saint martin",
  sx: "sint maarten",
  sxm: "sint maarten",
  aw: "aruba",
  abw: "aruba",
  cw: "curacao",
  cuw: "curacao",
  bq: "bonaire",
  bes: "bonaire",
  gs: "south georgia",
  sgs: "south georgia",
  // Europe
  gb: "united kingdom",
  gbr: "united kingdom",
  de: "germany",
  deu: "germany",
  fr: "france",
  fra: "france",
  it: "italy",
  ita: "italy",
  es: "spain",
  esp: "spain",
  nl: "netherlands",
  nld: "netherlands",
  be: "belgium",
  bel: "belgium",
  ch: "switzerland",
  che: "switzerland",
  at: "austria",
  aut: "austria",
  li: "liechtenstein",
  lie: "liechtenstein",
  pt: "portugal",
  prt: "portugal",
  ie: "ireland",
  irl: "ireland",
  lu: "luxembourg",
  lux: "luxembourg",
  se: "sweden",
  swe: "sweden",
  no: "norway",
  nor: "norway",
  dk: "denmark",
  dnk: "denmark",
  fi: "finland",
  fin: "finland",
  is: "iceland",
  isl: "iceland",
  fo: "faroe islands",
  fro: "faroe islands",
  ax: "åland islands",
  ala: "åland islands",
  sj: "svalbard and jan mayen",
  sjm: "svalbard and jan mayen",
  pl: "poland",
  pol: "poland",
  cz: "czech republic",
  cze: "czech republic",
  sk: "slovakia",
  svk: "slovakia",
  hu: "hungary",
  hun: "hungary",
  ro: "romania",
  rou: "romania",
  bg: "bulgaria",
  bgr: "bulgaria",
  si: "slovenia",
  svn: "slovenia",
  hr: "croatia",
  hrv: "croatia",
  rs: "serbia",
  srb: "serbia",
  me: "montenegro",
  mne: "montenegro",
  mk: "north macedonia",
  mkd: "north macedonia",
  ba: "bosnia and herzegovina",
  bih: "bosnia and herzegovina",
  al: "albania",
  alb: "albania",
  xk: "kosovo",
  ee: "estonia",
  est: "estonia",
  lv: "latvia",
  lva: "latvia",
  lt: "lithuania",
  ltu: "lithuania",
  ru: "russia",
  rus: "russia",
  ua: "ukraine",
  ukr: "ukraine",
  by: "belarus",
  blr: "belarus",
  md: "moldova",
  mda: "moldova",
  gr: "greece",
  grc: "greece",
  cy: "cyprus",
  cyp: "cyprus",
  mt: "malta",
  mlt: "malta",
  tr: "turkey",
  tur: "turkey",
  ad: "andorra",
  and: "andorra",
  mc: "monaco",
  mco: "monaco",
  sm: "san marino",
  smr: "san marino",
  va: "vatican city",
  vat: "vatican city",
  gi: "gibraltar",
  gib: "gibraltar",
  im: "isle of man",
  imn: "isle of man",
  je: "jersey",
  jey: "jersey",
  gg: "guernsey",
  ggy: "guernsey",
  // Asia
  in: "india",
  ind: "india",
  cn: "china",
  chn: "china",
  jp: "japan",
  jpn: "japan",
  kr: "south korea",
  kor: "south korea",
  kp: "north korea",
  prk: "north korea",
  tw: "taiwan",
  twn: "taiwan",
  hk: "hong kong",
  hkg: "hong kong",
  mo: "macao",
  mac: "macao",
  sg: "singapore",
  sgp: "singapore",
  my: "malaysia",
  mys: "malaysia",
  id: "indonesia",
  idn: "indonesia",
  ph: "philippines",
  phl: "philippines",
  th: "thailand",
  tha: "thailand",
  vn: "vietnam",
  vnm: "vietnam",
  la: "laos",
  lao: "laos",
  kh: "cambodia",
  khm: "cambodia",
  mm: "myanmar",
  mmr: "myanmar",
  bn: "brunei",
  brn: "brunei",
  tl: "timor-leste",
  tls: "timor-leste",
  pk: "pakistan",
  pak: "pakistan",
  bd: "bangladesh",
  bgd: "bangladesh",
  lk: "sri lanka",
  lka: "sri lanka",
  np: "nepal",
  npl: "nepal",
  bt: "bhutan",
  btn: "bhutan",
  mv: "maldives",
  mdv: "maldives",
  af: "afghanistan",
  afg: "afghanistan",
  mn: "mongolia",
  mng: "mongolia",
  kz: "kazakhstan",
  kaz: "kazakhstan",
  uz: "uzbekistan",
  uzb: "uzbekistan",
  tj: "tajikistan",
  tjk: "tajikistan",
  tm: "turkmenistan",
  tkm: "turkmenistan",
  kg: "kyrgyzstan",
  kgz: "kyrgyzstan",
  ge: "georgia",
  geo: "georgia",
  am: "armenia",
  arm: "armenia",
  az: "azerbaijan",
  aze: "azerbaijan",
  cx: "christmas island",
  cxr: "christmas island",
  cc: "cocos (keeling) islands",
  cck: "cocos (keeling) islands",
  io: "british indian ocean territory",
  iot: "british indian ocean territory",
  // Middle East
  sa: "saudi arabia",
  sau: "saudi arabia",
  ae: "united arab emirates",
  are: "united arab emirates",
  kw: "kuwait",
  kwt: "kuwait",
  qa: "qatar",
  qat: "qatar",
  bh: "bahrain",
  bhr: "bahrain",
  om: "oman",
  omn: "oman",
  ir: "iran",
  irn: "iran",
  iq: "iraq",
  irq: "iraq",
  il: "israel",
  isr: "israel",
  jo: "jordan",
  jor: "jordan",
  lb: "lebanon",
  lbn: "lebanon",
  sy: "syria",
  syr: "syria",
  ye: "yemen",
  yem: "yemen",
  ps: "palestine",
  pse: "palestine",
  // Africa
  za: "south africa",
  zaf: "south africa",
  ng: "nigeria",
  nga: "nigeria",
  ke: "kenya",
  ken: "kenya",
  et: "ethiopia",
  eth: "ethiopia",
  eg: "egypt",
  egy: "egypt",
  gh: "ghana",
  gha: "ghana",
  tz: "tanzania",
  tza: "tanzania",
  ug: "uganda",
  uga: "uganda",
  rw: "rwanda",
  rwa: "rwanda",
  bi: "burundi",
  bdi: "burundi",
  mw: "malawi",
  mwi: "malawi",
  zm: "zambia",
  zmb: "zambia",
  zw: "zimbabwe",
  zwe: "zimbabwe",
  bw: "botswana",
  bwa: "botswana",
  na: "namibia",
  nam: "namibia",
  sz: "eswatini",
  swz: "eswatini",
  ls: "lesotho",
  lso: "lesotho",
  ss: "south sudan",
  ssd: "south sudan",
  sd: "sudan",
  sdn: "sudan",
  so: "somalia",
  som: "somalia",
  dj: "djibouti",
  dji: "djibouti",
  er: "eritrea",
  eri: "eritrea",
  ma: "morocco",
  mar: "morocco",
  dz: "algeria",
  dza: "algeria",
  tn: "tunisia",
  tun: "tunisia",
  ly: "libya",
  lby: "libya",
  sn: "senegal",
  sen: "senegal",
  ml: "mali",
  mli: "mali",
  bf: "burkina faso",
  bfa: "burkina faso",
  ne: "niger",
  ner: "niger",
  td: "chad",
  tcd: "chad",
  cm: "cameroon",
  cmr: "cameroon",
  gn: "guinea",
  gin: "guinea",
  gw: "guinea-bissau",
  gnb: "guinea-bissau",
  sl: "sierra leone",
  sle: "sierra leone",
  lr: "liberia",
  lbr: "liberia",
  ci: "ivory coast",
  civ: "ivory coast",
  tg: "togo",
  tgo: "togo",
  bj: "benin",
  ben: "benin",
  mr: "mauritania",
  mrt: "mauritania",
  gm: "gambia",
  gmb: "gambia",
  cv: "cape verde",
  cpv: "cape verde",
  ga: "gabon",
  gab: "gabon",
  gq: "equatorial guinea",
  gnq: "equatorial guinea",
  st: "sao tome and principe",
  stp: "sao tome and principe",
  cf: "central african republic",
  caf: "central african republic",
  cd: "congo (drc)",
  cod: "congo (drc)",
  cg: "congo (republic)",
  cog: "congo (republic)",
  ao: "angola",
  ago: "angola",
  mz: "mozambique",
  moz: "mozambique",
  mg: "madagascar",
  mdg: "madagascar",
  mu: "mauritius",
  mus: "mauritius",
  sc: "seychelles",
  syc: "seychelles",
  km: "comoros",
  com: "comoros",
  yt: "mayotte",
  myt: "mayotte",
  re: "reunion",
  reu: "reunion",
  sh: "saint helena",
  shn: "saint helena",
  eh: "western sahara",
  esh: "western sahara",
  // Oceania
  au: "australia",
  aus: "australia",
  nz: "new zealand",
  nzl: "new zealand",
  pg: "papua new guinea",
  png: "papua new guinea",
  fj: "fiji",
  fji: "fiji",
  sb: "solomon islands",
  slb: "solomon islands",
  vu: "vanuatu",
  vut: "vanuatu",
  ws: "samoa",
  wsm: "samoa",
  to: "tonga",
  ton: "tonga",
  ki: "kiribati",
  kir: "kiribati",
  tv: "tuvalu",
  tuv: "tuvalu",
  nr: "nauru",
  nru: "nauru",
  fm: "micronesia",
  fsm: "micronesia",
  mh: "marshall islands",
  mhl: "marshall islands",
  pw: "palau",
  plw: "palau",
  ck: "cook islands",
  cok: "cook islands",
  nu: "niue",
  niu: "niue",
  tk: "tokelau",
  tkl: "tokelau",
  pf: "french polynesia",
  pyf: "french polynesia",
  nc: "new caledonia",
  ncl: "new caledonia",
  wf: "wallis and futuna",
  wlf: "wallis and futuna",
  nf: "norfolk island",
  nfk: "norfolk island",
  pn: "pitcairn",
  pcn: "pitcairn",
  hm: "heard island and mcdonald islands",
  hmd: "heard island and mcdonald islands",
  aq: "antarctica",
  ata: "antarctica"
};
const COUNTRY_DATE_FORMATS = {
  // MDY (MM/DD/YYYY)
  us: "MDY",
  ag: "MDY",
  as: "MDY",
  bb: "MDY",
  bs: "MDY",
  bz: "MDY",
  dm: "MDY",
  do: "MDY",
  fm: "MDY",
  gd: "MDY",
  gu: "MDY",
  hn: "MDY",
  jm: "MDY",
  kn: "MDY",
  lc: "MDY",
  lr: "MDY",
  mh: "MDY",
  mp: "MDY",
  ni: "MDY",
  pa: "MDY",
  ph: "MDY",
  pr: "MDY",
  pw: "MDY",
  sv: "MDY",
  um: "MDY",
  vc: "MDY",
  vi: "MDY",
  // YMD (YYYY-MM-DD / YYYY/MM/DD / YYYY.MM.DD)
  bt: "YMD",
  ca: "YMD",
  cn: "YMD",
  hu: "YMD",
  ir: "YMD",
  jp: "YMD",
  kp: "YMD",
  kr: "YMD",
  lt: "YMD",
  lk: "YMD",
  mn: "YMD",
  na: "YMD",
  np: "YMD",
  se: "YMD",
  tw: "YMD",
  za: "YMD",
  // DMY (DD/MM/YYYY / DD.MM.YYYY / DD-MM-YYYY) - all others
  af: "DMY",
  al: "DMY",
  ad: "DMY",
  ao: "DMY",
  ar: "DMY",
  am: "DMY",
  au: "DMY",
  at: "DMY",
  az: "DMY",
  bh: "DMY",
  bd: "DMY",
  bo: "DMY",
  ba: "DMY",
  bw: "DMY",
  br: "DMY",
  bn: "DMY",
  mx: "DMY",
  bg: "DMY",
  bf: "DMY",
  bi: "DMY",
  kh: "DMY",
  cm: "DMY",
  cv: "DMY",
  cf: "DMY",
  td: "DMY",
  cl: "DMY",
  co: "DMY",
  km: "DMY",
  cd: "DMY",
  cg: "DMY",
  cr: "DMY",
  cu: "DMY",
  hr: "DMY",
  cy: "DMY",
  cz: "DMY",
  dk: "DMY",
  dj: "DMY",
  ec: "DMY",
  eg: "DMY",
  gq: "DMY",
  er: "DMY",
  ee: "DMY",
  sz: "DMY",
  et: "DMY",
  fj: "DMY",
  fi: "DMY",
  fr: "DMY",
  ga: "DMY",
  gm: "DMY",
  ge: "DMY",
  de: "DMY",
  gh: "DMY",
  gr: "DMY",
  gt: "DMY",
  gw: "DMY",
  gy: "DMY",
  ht: "DMY",
  is: "DMY",
  in: "DMY",
  id: "DMY",
  iq: "DMY",
  ie: "DMY",
  il: "DMY",
  it: "DMY",
  ci: "DMY",
  jo: "DMY",
  kz: "DMY",
  ke: "DMY",
  ki: "DMY",
  kw: "DMY",
  kg: "DMY",
  la: "DMY",
  lv: "DMY",
  lb: "DMY",
  ls: "DMY",
  ly: "DMY",
  li: "DMY",
  lu: "DMY",
  my: "DMY",
  mv: "DMY",
  ml: "DMY",
  mt: "DMY",
  mr: "DMY",
  mu: "DMY",
  md: "DMY",
  mc: "DMY",
  me: "DMY",
  ma: "DMY",
  mz: "DMY",
  mm: "DMY",
  nr: "DMY",
  nl: "DMY",
  nz: "DMY",
  ne: "DMY",
  ng: "DMY",
  mk: "DMY",
  no: "DMY",
  om: "DMY",
  pk: "DMY",
  pg: "DMY",
  py: "DMY",
  pe: "DMY",
  pl: "DMY",
  pt: "DMY",
  qa: "DMY",
  ro: "DMY",
  ru: "DMY",
  rw: "DMY",
  ws: "DMY",
  sm: "DMY",
  st: "DMY",
  sa: "DMY",
  sn: "DMY",
  rs: "DMY",
  sc: "DMY",
  sl: "DMY",
  sg: "DMY",
  sk: "DMY",
  si: "DMY",
  sb: "DMY",
  so: "DMY",
  ss: "DMY",
  es: "DMY",
  sr: "DMY",
  ch: "DMY",
  sy: "DMY",
  tj: "DMY",
  tz: "DMY",
  th: "DMY",
  tl: "DMY",
  tg: "DMY",
  to: "DMY",
  tt: "DMY",
  tn: "DMY",
  tr: "DMY",
  tm: "DMY",
  tv: "DMY",
  ug: "DMY",
  ua: "DMY",
  ae: "DMY",
  gb: "DMY",
  uk: "DMY",
  uy: "DMY",
  uz: "DMY",
  vu: "DMY",
  va: "DMY",
  ve: "DMY",
  vn: "DMY",
  ye: "DMY",
  zm: "DMY",
  zw: "DMY",
  // Territories
  ax: "DMY",
  ai: "DMY",
  aq: "DMY",
  aw: "DMY",
  ac: "DMY",
  bm: "DMY",
  bq: "DMY",
  bv: "DMY",
  io: "DMY",
  vg: "DMY",
  ic: "DMY",
  ky: "DMY",
  cx: "DMY",
  cc: "DMY",
  ck: "DMY",
  cw: "DMY",
  fk: "DMY",
  fo: "DMY",
  gf: "DMY",
  pf: "DMY",
  tf: "DMY",
  gi: "DMY",
  gl: "DMY",
  gp: "DMY",
  gg: "DMY",
  hm: "DMY",
  hk: "DMY",
  im: "DMY",
  sj: "DMY",
  je: "DMY",
  xk: "DMY",
  mo: "DMY",
  mq: "DMY",
  yt: "DMY",
  nc: "DMY",
  nf: "DMY",
  nu: "DMY",
  pn: "DMY",
  ps: "DMY",
  re: "DMY",
  gs: "DMY",
  sh: "DMY",
  sx: "DMY",
  ta: "DMY",
  tc: "DMY",
  tk: "DMY",
  wf: "DMY",
  eh: "DMY"
};
const STATE_REGION_CODES = {
  australia: ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"],
  "united states": [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "DC",
    "AA",
    "AE",
    "AP"
  ],
  canada: ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"],
  "new zealand": ["AUK", "BOP", "CAN", "GIS", "HKB", "MWT", "NSN", "NTL", "OTA", "STL", "TAS", "TKI", "WGN", "WKO", "WTC"]
};
const TERRITORY_ISO_TO_RPI2_ISO = {
  aq: "us",
  // Antarctica — US stations / default
  ax: "fi",
  // Åland — +358
  cc: "au",
  // Cocos — +61
  cx: "au",
  // Christmas Island — +61
  eh: "ma",
  // Western Sahara — +212 (same as Morocco)
  gg: "gb",
  // Guernsey — +44
  im: "gb",
  // Isle of Man — +44
  yt: "re",
  // Mayotte — +262 (same numbering as Réunion)
  sj: "no",
  // Svalbard and Jan Mayen — +47
  tf: "re",
  // French Southern Territories — +262
  um: "us"
  // U.S. Minor Outlying Islands — +1
};
function toRpi2SelectableIso(iso2) {
  const c = (iso2 ?? "").toString().trim().toLowerCase();
  if (c.length !== 2) return "us";
  return TERRITORY_ISO_TO_RPI2_ISO[c] ?? c;
}
function isBridgedTerritory(iso2) {
  const c = (iso2 ?? "").toString().trim().toLowerCase();
  return c.length === 2 && Object.prototype.hasOwnProperty.call(TERRITORY_ISO_TO_RPI2_ISO, c);
}
function cn$2(...inputs) {
  return twMerge(clsx(inputs));
}
const cleanPhoneNumber$1 = (input) => {
  return input.replace(/[^\d+]/g, "");
};
const normalizeIso2 = (code, fallback = "us") => {
  const v = (code ?? "").toString().trim().toLowerCase();
  return v.length === 2 ? v : fallback;
};
const ISO2_PHONE_INPUT_FALLBACK = {
  uk: "gb",
  // common alias; ISO 3166 uses GB
  an: "nl",
  // Netherlands Antilles (dissolved)
  bv: "no",
  // Bouvet Island — not a rpi2 country entry
  hm: "au",
  // Heard & McDonald — not a rpi2 country entry
  pn: "nz"
  // Pitcairn — not a rpi2 country entry; +64 same as NZ
};
const getDialingIso2 = (iso2) => {
  const c = normalizeIso2(iso2);
  return ISO2_PHONE_INPUT_FALLBACK[c] ?? c;
};
const ISO2_VALIDATION_FALLBACK = {
  ...ISO2_PHONE_INPUT_FALLBACK,
  ...TERRITORY_ISO_TO_RPI2_ISO,
  aq: "us",
  // libphonenumber has no AQ
  um: "us"
  // libphonenumber has no UM
};
const getValidationIso2 = (iso2) => {
  const c = normalizeIso2(iso2);
  return ISO2_VALIDATION_FALLBACK[c] ?? c;
};
const RPI2_SHARED_DIAL_CODE_PRIORITY = { gb: -1 };
const PhoneInputComponent = React.forwardRef(
  ({
    value = "",
    onChange,
    onBlur,
    onFocus,
    placeholder,
    disabled = false,
    className,
    country = "us",
    inputProps = {},
    preferredCountries = ["us", "ca", "gb", "au", "in", "de", "fr"],
    countryList,
    ...props
  }, ref) => {
    const { t: translate } = useTranslation();
    const inputRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [validationMessage, setValidationMessage] = React.useState("");
    const previousCountryRef = React.useRef(
      toRpi2SelectableIso(getDialingIso2(country))
    );
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const handleClick = (e) => {
        const flagDropdown = container.querySelector(".flag-dropdown");
        if (!flagDropdown || !flagDropdown.contains(e.target)) return;
        setTimeout(() => {
          const rect = flagDropdown.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          if (spaceBelow < 250) {
            flagDropdown.classList.add("open-top");
          } else {
            flagDropdown.classList.remove("open-top");
          }
        }, 10);
      };
      container.addEventListener("click", handleClick);
      return () => container.removeEventListener("click", handleClick);
    }, []);
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      let cleanupFns = [];
      const timer = setTimeout(() => {
        const flagDropdown = container.querySelector(".flag-dropdown");
        const selectedFlag = container.querySelector(".selected-flag");
        const input = container.querySelector("input.form-control");
        if (flagDropdown && input) {
          input.setAttribute("tabindex", "-1");
          if (selectedFlag) {
            selectedFlag.setAttribute("tabindex", "-1");
          }
          flagDropdown.setAttribute("tabindex", "0");
          const handleFlagKeyDown = (e) => {
            if (e.key === "Tab" && !e.shiftKey) {
              e.preventDefault();
              input.focus();
            }
          };
          const handleInputKeyDown = (e) => {
            if (e.key === "Tab") {
              if (e.shiftKey) {
                e.preventDefault();
                flagDropdown.focus();
              } else {
                e.preventDefault();
                const allFocusable = document.querySelectorAll(
                  'button:not([disabled]), [href], input:not([tabindex="-1"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
                );
                const focusableArray = Array.from(allFocusable);
                const currentIndex = focusableArray.indexOf(flagDropdown);
                for (let i = currentIndex + 1; i < focusableArray.length; i++) {
                  const el = focusableArray[i];
                  if (!container.contains(el)) {
                    el.focus();
                    break;
                  }
                }
              }
            }
          };
          flagDropdown.addEventListener("keydown", handleFlagKeyDown);
          input.addEventListener("keydown", handleInputKeyDown);
          cleanupFns.push(() => {
            flagDropdown.removeEventListener("keydown", handleFlagKeyDown);
            input.removeEventListener("keydown", handleInputKeyDown);
          });
        }
      }, 100);
      return () => {
        clearTimeout(timer);
        cleanupFns.forEach((fn) => fn());
      };
    }, []);
    const normalizedValue = React.useMemo(() => {
      if (!value) return "";
      if (value.startsWith("+0")) return "";
      if (value && !value.startsWith("+")) {
        return "+" + value;
      }
      return value;
    }, [value]);
    React.useEffect(() => {
      if (value && !value.startsWith("+") && !value.startsWith("+0") && onChange) {
        const valueWithPlus = "+" + value;
        onChange(valueWithPlus);
      }
    }, [value, onChange]);
    const rpiCountry = toRpi2SelectableIso(getDialingIso2(country));
    const originalIso = normalizeIso2(country);
    const dataFlagIso = isBridgedTerritory(originalIso) ? originalIso : void 0;
    React.useEffect(() => {
      previousCountryRef.current = rpiCountry;
    }, [rpiCountry]);
    const localizationFromCountryList = React.useMemo(() => {
      if (!(countryList == null ? void 0 : countryList.length)) return void 0;
      const map = {};
      for (const c of countryList) {
        if (!(c == null ? void 0 : c.code) || typeof c.code !== "string" || c.code.length !== 2) continue;
        const rpiIso = toRpi2SelectableIso(normalizeIso2(getDialingIso2(c.code)));
        const label = (c.name ?? "").trim();
        if (label) map[rpiIso] = label;
      }
      return Object.keys(map).length ? map : void 0;
    }, [countryList]);
    const getAvailableCountries = React.useMemo(() => {
      if (countryList == null ? void 0 : countryList.length) {
        try {
          return countryList.filter((c) => (c == null ? void 0 : c.code) && typeof c.code === "string" && c.code.length === 2).map((c) => toRpi2SelectableIso(normalizeIso2(getDialingIso2(c.code)))).filter((code) => code.length === 2).filter((code, idx, arr) => arr.indexOf(code) === idx);
        } catch {
          return [];
        }
      }
      return [];
    }, [countryList]);
    const getPreferredCountries = React.useMemo(() => {
      if (countryList == null ? void 0 : countryList.length) {
        return preferredCountries.map((c) => toRpi2SelectableIso(normalizeIso2(getDialingIso2(c)))).filter((code) => getAvailableCountries.includes(code));
      }
      return preferredCountries;
    }, [preferredCountries, countryList, getAvailableCountries]);
    const defaultCountryForValidation = (getValidationIso2(country) || "us").toUpperCase();
    React.useImperativeHandle(ref, () => ({
      validate: () => {
        const cleaned = cleanPhoneNumber$1(normalizedValue);
        const parsed = parsePhoneNumberFromString(cleaned, defaultCountryForValidation);
        const isValid = (parsed == null ? void 0 : parsed.isPossible()) ?? false;
        setValidationMessage(isValid ? "" : translate("Please enter a valid phone number"));
        return isValid;
      },
      getInputElement: () => inputRef.current,
      checkValidity: () => {
        const parsed = parsePhoneNumberFromString(cleanPhoneNumber$1(normalizedValue), defaultCountryForValidation);
        return (parsed == null ? void 0 : parsed.isPossible()) ?? false;
      },
      reportValidity: () => {
        if (!inputRef.current) return false;
        const parsed = parsePhoneNumberFromString(cleanPhoneNumber$1(normalizedValue), defaultCountryForValidation);
        const isValid = (parsed == null ? void 0 : parsed.isPossible()) ?? false;
        inputRef.current.setCustomValidity(isValid ? "" : translate("Please enter a valid phone number"));
        setValidationMessage(isValid ? "" : translate("Please enter a valid phone number"));
        return inputRef.current.reportValidity();
      }
    }), [normalizedValue, defaultCountryForValidation, translate]);
    const handleChange = (value2, country2) => {
      var _a, _b, _c, _d;
      setValidationMessage("");
      const newCountryCode = (_a = country2 == null ? void 0 : country2.countryCode) == null ? void 0 : _a.toLowerCase();
      if (newCountryCode && newCountryCode !== previousCountryRef.current && value2) {
        previousCountryRef.current = newCountryCode;
        const countryPrefix = `+${country2.dialCode}`;
        onChange == null ? void 0 : onChange(countryPrefix, (_b = country2 == null ? void 0 : country2.countryCode) == null ? void 0 : _b.toUpperCase());
        return;
      }
      const valueWithPlus = value2 && !value2.startsWith("+") ? `+${value2}` : value2;
      onChange == null ? void 0 : onChange(valueWithPlus, (_c = country2 == null ? void 0 : country2.countryCode) == null ? void 0 : _c.toUpperCase());
      (_d = inputRef.current) == null ? void 0 : _d.setCustomValidity("");
    };
    const handleBlur = (e) => {
      const isRequired = (inputProps == null ? void 0 : inputProps.required) || false;
      const isOnlyCountryCode = () => {
        if (!normalizedValue || normalizedValue.trim() === "" || normalizedValue === "+") {
          return true;
        }
        const digitsOnly = normalizedValue.replace(/[^\d]/g, "");
        return digitsOnly.length <= 3;
      };
      const isEmptyOrCountryCodeOnly = isOnlyCountryCode();
      if (!isRequired && isEmptyOrCountryCodeOnly && inputRef.current) {
        inputRef.current.setCustomValidity("");
        setValidationMessage("");
        onBlur == null ? void 0 : onBlur(e);
        return;
      }
      if (normalizedValue && inputRef.current) {
        const defaultCountry = (getValidationIso2(country) || "us").toUpperCase();
        try {
          let parsed = parsePhoneNumberFromString(normalizedValue, defaultCountry);
          if (!parsed) {
            const cleaned = cleanPhoneNumber$1(normalizedValue);
            parsed = parsePhoneNumberFromString(cleaned, defaultCountry);
          }
          if (!parsed) {
            if (isEmptyOrCountryCodeOnly) {
              inputRef.current.setCustomValidity("");
              setValidationMessage("");
            } else {
              const message = translate("Please enter a valid phone number");
              inputRef.current.setCustomValidity(message);
              setValidationMessage(message);
            }
            return;
          }
          const isValid = parsed.isPossible();
          if (isValid) {
            inputRef.current.setCustomValidity("");
            setValidationMessage("");
          } else {
            if (isEmptyOrCountryCodeOnly) {
              inputRef.current.setCustomValidity("");
              setValidationMessage("");
            } else {
              const message = translate("Please enter a valid phone number");
              inputRef.current.setCustomValidity(message);
              setValidationMessage(message);
            }
          }
        } catch (err) {
          if (isEmptyOrCountryCodeOnly) {
            inputRef.current.setCustomValidity("");
            setValidationMessage("");
          } else {
            const message = translate("Please enter a valid phone number");
            inputRef.current.setCustomValidity(message);
            setValidationMessage(message);
          }
        }
      } else if (inputRef.current) {
        inputRef.current.setCustomValidity("");
        setValidationMessage("");
      }
      onBlur == null ? void 0 : onBlur(e);
    };
    const handleFocus = (e) => {
      var _a;
      (_a = inputRef.current) == null ? void 0 : _a.setCustomValidity("");
      setValidationMessage("");
      onFocus == null ? void 0 : onFocus(e);
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: containerRef,
        className: cn$2("configurable-phone-input react-tel-input", "relative w-full overflow-visible", className),
        "data-flag-iso": dataFlagIso,
        children: [
          /* @__PURE__ */ jsx(
            PhoneInput,
            {
              value: normalizedValue,
              onChange: handleChange,
              onBlur: handleBlur,
              onFocus: handleFocus,
              placeholder: placeholder ? translate(placeholder) : translate("Enter phone number"),
              disabled,
              country: rpiCountry,
              preferredCountries: getPreferredCountries,
              onlyCountries: getAvailableCountries.length > 0 ? getAvailableCountries : void 0,
              enableTerritories: true,
              enableSearch: true,
              disableSearchIcon: true,
              searchPlaceholder: translate("Search countries..."),
              searchNotFound: translate("No entries to show"),
              localization: localizationFromCountryList,
              priority: RPI2_SHARED_DIAL_CODE_PRIORITY,
              autoFormat: true,
              disableCountryCode: false,
              disableDropdown: disabled,
              enableAreaCodes: false,
              countryCodeEditable: true,
              isValid: () => true,
              defaultErrorMessage: "",
              enableLongNumbers: true,
              copyNumbersOnly: false,
              containerClass: cn$2(
                "react-tel-input",
                "flex w-full !h-12 !border !border-gray-300 !rounded-lg !bg-white !overflow-visible",
                validationMessage && "!border-red-500 ring-2 ring-red-500"
              ),
              inputClass: cn$2(
                "form-control",
                "!w-full !h-full !border-0 !py-2 !pl-14 !pr-3 !text-base !text-gray-900 !placeholder-gray-400 !bg-transparent !rounded-r-lg",
                "focus:!outline-none focus:!border-0 focus:!shadow-none focus:!ring-0",
                "disabled:!cursor-not-allowed disabled:!opacity-50"
              ),
              buttonClass: cn$2(
                "flag-dropdown",
                "!h-full !w-12 !min-w-[48px] !max-w-[48px] !border-0 !border-r !border-gray-300 !bg-transparent !p-0",
                "hover:!bg-gray-50",
                "focus:!outline-none focus:!border-2 focus:!border-[var(--primary)] focus:!shadow-[0_0_0_3px_var(--primary-focus-outer)]",
                "focus-within:!outline-none focus-within:!border-2 focus-within:!border-[var(--primary)] focus-within:!shadow-[0_0_0_3px_var(--primary-focus-outer)]",
                "[&.open]:!outline-none [&.open]:!border-2 [&.open]:!border-[var(--primary)] [&.open]:!shadow-[0_0_0_3px_var(--primary-focus-outer)]",
                "!flex !items-center !justify-center !rounded-l-lg"
              ),
              dropdownClass: cn$2(
                "country-list",
                "!z-[9999] !mt-1 !max-h-60 !w-64 !max-w-[90vw] !overflow-auto !rounded-none !bg-white !py-1 !text-sm !shadow-lg !border !border-gray-300 !left-0"
              ),
              searchClass: cn$2(
                "search-box",
                "!sticky !top-0 !z-10 !w-full !p-3 !m-0 !border-0 !border-b !border-gray-200 !rounded-none !bg-white !shadow-sm",
                "[&>input]:!w-full [&>input]:!p-2 [&>input]:!border [&>input]:!border-gray-300 [&>input]:!rounded-none [&>input]:!text-sm [&>input]:!bg-white",
                "[&>input]:focus:!outline-none [&>input]:focus:!border-2 [&>input]:focus:!border-[var(--primary)] [&>input]:focus:!shadow-[0_0_0_3px_var(--primary-focus-outer)]",
                "[&>input]:!placeholder-gray-400"
              ),
              inputProps: {
                ...inputProps,
                ref: inputRef,
                style: {
                  width: "100%",
                  height: "100%",
                  fontSize: "16px",
                  paddingLeft: "60px",
                  paddingRight: "12px",
                  border: "none",
                  borderRadius: "0 0.5rem 0.5rem 0",
                  background: "transparent",
                  ...inputProps.style
                }
              },
              buttonStyle: {
                width: "48px",
                height: "100%",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "0.5rem 0 0 0.5rem",
                borderRight: "1px solid #d1d5db",
                position: "absolute",
                left: "0",
                top: "0"
              },
              ...props
            }
          ),
          validationMessage && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-red-600", children: validationMessage })
        ]
      }
    );
  }
);
PhoneInputComponent.displayName = "PhoneInput";
const TOAST_LIMIT$1 = 1;
const TOAST_REMOVE_DELAY$1 = 5e3;
let count$1 = 0;
function genId$1() {
  count$1 = (count$1 + 1) % Number.MAX_SAFE_INTEGER;
  return count$1.toString();
}
const toastTimeouts$1 = /* @__PURE__ */ new Map();
const addToRemoveQueue$1 = (toastId, duration = TOAST_REMOVE_DELAY$1) => {
  if (toastTimeouts$1.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts$1.delete(toastId);
    dispatch$1({
      type: "REMOVE_TOAST",
      toastId
    });
  }, duration);
  toastTimeouts$1.set(toastId, timeout);
};
const reducer$1 = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT$1)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue$1(toastId, 0);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue$1(toast2.id, 0);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
const listeners$1 = [];
let memoryState$1 = { toasts: [] };
function dispatch$1(action) {
  memoryState$1 = reducer$1(memoryState$1, action);
  listeners$1.forEach((listener) => {
    listener(memoryState$1);
  });
}
function toast$1({ duration = TOAST_REMOVE_DELAY$1, ...props }) {
  const id = genId$1();
  const update = (props2) => dispatch$1({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch$1({ type: "DISMISS_TOAST", toastId: id });
  dispatch$1({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  if (duration > 0) {
    addToRemoveQueue$1(id, duration);
  }
  const variant = props.variant || "default";
  const prefix = variant === "destructive" ? "❌" : "✅";
  console.log(`${prefix} Toast: ${props.title || ""} ${props.description || ""}`);
  return {
    id,
    dismiss,
    update
  };
}
const currentSectionSubject = new BehaviorSubject(null);
const sectionResponseSubject = new BehaviorSubject(null);
const templateIdSubject = new BehaviorSubject(null);
const sectionSavedDataSubject = new BehaviorSubject(null);
const sectionsSubject = new BehaviorSubject([]);
const objTypeSubject = new BehaviorSubject(null);
const objPointerSubject = new BehaviorSubject(null);
const foreigndbSubject = new BehaviorSubject(null);
const completedSectionListSubject = new BehaviorSubject([]);
const incompleteSectionListSubject = new BehaviorSubject([]);
const sectionProgressIdSubject = new BehaviorSubject(null);
const navigationPathSubject = new BehaviorSubject([]);
const useCustomFormService = () => {
  const [sections, setSections] = useState([]);
  const [templateHMY, setTemplateHMY] = useState(null);
  const completedSectionList = completedSectionListSubject.value;
  const incompleteSectionList = incompleteSectionListSubject.value;
  const sectionProgressId = sectionProgressIdSubject.value;
  const updateCurrentSection = useCallback((section) => {
    currentSectionSubject.next(section);
    if (section) {
      const sectionIdStr = String(section.sectionId || section.UUID || section.id || "");
      if (sectionIdStr) {
        const currentPath = navigationPathSubject.value || [];
        if (!currentPath.includes(sectionIdStr)) {
          const newPath = [...currentPath, sectionIdStr];
          navigationPathSubject.next(newPath);
          console.log("Added current section to navigation path:", sectionIdStr, "Path:", newPath);
        }
      }
    }
  }, []);
  const setSectionResponse = useCallback((response) => {
    sectionResponseSubject.next(response);
  }, []);
  const setTemplateId = useCallback((id) => {
    templateIdSubject.next(id);
    if (id) {
      localStorage.setItem("TemplateId", id.toString());
      setTemplateHMY(id);
    }
  }, []);
  const getTemplateId = useCallback(() => {
    const currentId = templateIdSubject.value;
    if (currentId) {
      return currentId;
    }
    const storedId = localStorage.getItem("TemplateId");
    if (storedId) {
      templateIdSubject.next(storedId);
      setTemplateHMY(storedId);
      return storedId;
    }
    return null;
  }, []);
  const updateSavedFieldData = useCallback((data) => {
    const currentData = sectionSavedDataSubject.value || [];
    const updatedData = [...currentData, data];
    sectionSavedDataSubject.next(updatedData);
  }, []);
  const saveSectionProgress = useCallback(async (sectionId, sectionData, stepID) => {
    var _a, _b, _c;
    let sectionIdStr = String(sectionId || "").trim();
    if (sectionIdStr && sectionIdStr.includes("-")) {
      const allSections2 = sectionsSubject.value;
      const foundSection = allSections2.find(
        (s) => String(s.sectionId) === sectionIdStr || String(s.UUID) === sectionIdStr || String(s.id) === sectionIdStr
      );
      if (foundSection && foundSection.id) {
        sectionIdStr = String(foundSection.id);
        console.log("Converted UUID to numeric ID:", sectionId, "->", sectionIdStr);
      }
    } else if (sectionIdStr && !isNaN(Number(sectionIdStr))) {
      sectionIdStr = String(Number(sectionIdStr));
    }
    if (!sectionIdStr || sectionIdStr === "" || sectionIdStr === "null" || sectionIdStr === "undefined") {
      console.error("Invalid sectionId provided to saveSectionProgress:", sectionId, "converted to:", sectionIdStr);
      return { allSectionsCompleted: false };
    }
    const templateId = getTemplateId();
    if (!templateId) {
      return { allSectionsCompleted: false };
    }
    let currentCompleted = [...completedSectionList];
    let currentIncomplete = [...incompleteSectionList];
    if (currentCompleted.length === 0 && currentIncomplete.length === 0) {
      const allSections2 = sectionsSubject.value;
      currentIncomplete = allSections2.map((section) => {
        return String(section.id || "");
      }).filter((id) => id && id !== "null" && id !== "undefined" && !isNaN(Number(id)));
      console.log("Initializing incomplete sections list (using numeric IDs):", currentIncomplete);
    }
    if (currentIncomplete.includes(sectionIdStr)) {
      currentIncomplete = currentIncomplete.filter((id) => id !== sectionIdStr);
    }
    if (!currentCompleted.includes(sectionIdStr)) {
      currentCompleted.push(sectionIdStr);
    }
    completedSectionListSubject.next(currentCompleted);
    incompleteSectionListSubject.next(currentIncomplete);
    console.log("Section progress updated:", {
      sectionId: sectionIdStr,
      completed: currentCompleted.join(","),
      incomplete: currentIncomplete.join(","),
      completedCount: currentCompleted.length,
      incompleteCount: currentIncomplete.length
    });
    const allSections = sectionsSubject.value;
    allSections.forEach((section) => {
      const sectionIdToCheck = String(section.id || "");
      section.completed = currentCompleted.includes(sectionIdToCheck);
    });
    sectionsSubject.next([...allSections]);
    const allSectionsCompleted = currentIncomplete.length === 0;
    const userId = localStorage.getItem("LoginApplicantId");
    const propertyId = getConfigurableFormsConfigOrThrow().getPropertyId();
    if (!userId) {
      console.error("LoginApplicantId not found in localStorage");
      return { allSectionsCompleted: false };
    }
    if (!propertyId) {
      console.error("PropertyId not found in localStorage");
      return { allSectionsCompleted: false };
    }
    const currentStepID = stepID || parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
    const data = {
      UserId: 0,
      PropertyId: propertyId,
      TemplateId: templateId,
      CompletedSectionList: currentCompleted.join(","),
      IncompleteSectionList: currentIncomplete.join(","),
      hmy: sectionProgressId || 0,
      StepID: currentStepID
    };
    if (userId) data.hObjPointer = userId;
    console.log("Saving section progress for current section only:", {
      completed: currentCompleted.join(","),
      incomplete: currentIncomplete.join(","),
      completedCount: currentCompleted.length,
      incompleteCount: currentIncomplete.length
    });
    try {
      const response = await configurableFormsApi.saveSectionProgress(data);
      if (response && response.hmy) {
        sectionProgressIdSubject.next(response.hmy);
        if (allSectionsCompleted) {
          const completedStepPageName = getConfigurableFormsConfigOrThrow().completedStepPageName ?? "customformsections";
          localStorage.setItem("LastCompletedStepPageName", completedStepPageName);
        }
      } else {
        console.warn("saveSectionProgress response missing hmy field:", response);
      }
      const returnData = {
        allSectionsCompleted,
        completedSections: currentCompleted,
        allSectionData: allSections,
        templateId,
        finalSectionData: sectionData
      };
      return returnData;
    } catch (error) {
      console.error("Error saving section progress:", error);
      console.error("Error details:", {
        message: error == null ? void 0 : error.message,
        status: (_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status,
        statusText: (_b = error == null ? void 0 : error.response) == null ? void 0 : _b.statusText,
        data: (_c = error == null ? void 0 : error.response) == null ? void 0 : _c.data
      });
      return { allSectionsCompleted: false };
    }
  }, [completedSectionList, incompleteSectionList, sectionProgressId]);
  const nextButtonClick = useCallback(async (currentScreen, currentSectionId, allowCompletedSections) => {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const sections2 = await new Promise((resolve) => {
      sectionsSubject.pipe(take(1)).subscribe((sections3) => {
        resolve(sections3);
      });
    });
    let tabIdx = -1;
    if (currentSectionId) {
      const currentSectionIdStr = String(currentSectionId);
      tabIdx = sections2.findIndex(
        (t) => String(t.id) === currentSectionIdStr || String(t.sectionId) === currentSectionIdStr || String(t.UUID) === currentSectionIdStr
      );
    }
    if (tabIdx === -1) {
      tabIdx = sections2.findIndex((t) => t.name.toLowerCase() === currentScreen.toLowerCase());
    }
    if (tabIdx === -1) {
      tabIdx = sections2.findIndex((t) => t.ActiveSection === true);
    }
    if (tabIdx === -1 && currentSectionId) {
      console.warn(`?? Current section (ID: ${currentSectionId}) not found in sections list. This may happen during visibility recalculation. Waiting for sections to update...`);
      for (let retry = 0; retry < 3; retry++) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        const updatedSections = sectionsSubject.value || sections2;
        const retryTabIdx = updatedSections.findIndex(
          (t) => String(t.id) === String(currentSectionId) || String(t.sectionId) === String(currentSectionId) || String(t.UUID) === String(currentSectionId)
        );
        if (retryTabIdx !== -1) {
          tabIdx = retryTabIdx;
          console.log(`? Found current section after retry ${retry + 1} at index ${tabIdx}`);
          break;
        }
      }
      if (tabIdx === -1) {
        console.error(`? Current section (ID: ${currentSectionId}) still not found after retries. Preventing navigation to avoid showing "Get started".`);
        return null;
      }
    }
    if (tabIdx !== -1 && sections2[tabIdx]) {
      sections2[tabIdx].completed = true;
      sections2[tabIdx].ActiveSection = false;
    }
    let nextSectionIdx = tabIdx + 1;
    while (nextSectionIdx < sections2.length) {
      const candidateSection = sections2[nextSectionIdx];
      const isVisibleByQuery = candidateSection.hideQuerySettings ? candidateSection.isVisibleByQuery === true : candidateSection.isVisibleByQuery !== false;
      const isVisibleForCountry = candidateSection.isVisibleForCountry !== false;
      if (!isVisibleByQuery || !isVisibleForCountry) {
        candidateSection.completed = true;
      }
      if (isVisibleByQuery && isVisibleForCountry && (allowCompletedSections || !candidateSection.completed)) {
        break;
      }
      nextSectionIdx++;
    }
    if (tabIdx !== -1 && nextSectionIdx < sections2.length && nextSectionIdx > tabIdx && sections2[nextSectionIdx]) {
      const nextSection = sections2[nextSectionIdx];
      const completedSectionsData = sections2.filter((section) => section.completed).map((section) => ({
        sectionId: section.sectionId,
        sectionName: section.name,
        data: section.data || {},
        countryField: Object.entries(section.data || {}).find(
          ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
        ),
        stateField: Object.entries(section.data || {}).find(
          ([key, value]) => key.toLowerCase().includes("state") && value !== void 0
        )
      })).filter((section) => section.countryField || section.stateField);
      console.log("Navigation with completed sections data:", completedSectionsData);
      let navigationState = {
        sectionData: {
          id: localStorage.getItem("LoginApplicantId") || "0",
          sectionId: nextSection.sectionId,
          sectionName: nextSection.name,
          sectionNumber: nextSection.sectionNumber || nextSectionIdx + 1,
          // nextSectionIdx is 0-based, add 1 for display
          isCustomTable: nextSection.isCustomTable,
          sCustomTableName: nextSection.sCustomTableName,
          ObjType: objTypeSubject.value,
          ObjPointer: objPointerSubject.value,
          foreigndb: foreigndbSubject.value,
          // Add state-country relationships
          completedSectionsData
        }
      };
      const savedFieldData = await new Promise((resolve) => {
        sectionSavedDataSubject.pipe(take(1)).subscribe((data) => {
          resolve(data || []);
        });
      });
      if (savedFieldData && savedFieldData.length > 0) {
        savedFieldData.forEach((data) => {
          if (data.Label === nextSection.name) {
            navigationState.sectionData = {
              ...navigationState.sectionData,
              [data.ColumnName]: data.Value
            };
          }
        });
      }
      nextSection.ActiveSection = true;
      sectionsSubject.next([...sections2]);
      const currentPath = navigationPathSubject.value || [];
      const nextSectionIdStr = String(nextSection.sectionId || nextSection.UUID || nextSection.id || "");
      if (!currentPath.includes(nextSectionIdStr)) {
        const newPath = [...currentPath, nextSectionIdStr];
        navigationPathSubject.next(newPath);
        console.log("Next button: Added to navigation path:", newPath);
      }
      return { url: nextSection.url, state: navigationState };
    }
    return null;
  }, []);
  const previousButtonClick = useCallback(async (currentScreen, currentSectionId) => {
    var _a;
    const sections2 = await new Promise((resolve) => {
      sectionsSubject.pipe(take(1)).subscribe((sections3) => {
        resolve(sections3);
      });
    });
    console.log(`[Previous Button] Looking for previous section from "${currentScreen}"`);
    console.log(`[Previous Button] Sections visibility status:`, sections2.map((s) => ({
      name: s.name,
      isVisibleByQuery: s.isVisibleByQuery,
      isVisibleForCountry: s.isVisibleForCountry,
      hasHideQuerySettings: !!s.hideQuerySettings,
      completed: s.completed
    })));
    let tabIdx = -1;
    if (currentSectionId) {
      const currentSectionIdStr = String(currentSectionId);
      tabIdx = sections2.findIndex(
        (t) => String(t.id) === currentSectionIdStr || String(t.sectionId) === currentSectionIdStr || String(t.UUID) === currentSectionIdStr
      );
    }
    if (tabIdx === -1) {
      tabIdx = sections2.findIndex((t) => t.name.toLowerCase() === currentScreen.toLowerCase());
    }
    if (tabIdx === -1) {
      tabIdx = sections2.findIndex((t) => t.ActiveSection === true);
    }
    if (tabIdx !== -1) {
      console.log(`[Previous Button] Current section index: ${tabIdx}, section: "${(_a = sections2[tabIdx]) == null ? void 0 : _a.name}"`);
      const allFieldData = {};
      sections2.forEach((section, idx) => {
        if (idx < tabIdx && section.data) {
          Object.assign(allFieldData, section.data);
        }
      });
      sections2.forEach((section, idx) => {
        if (section.hideQuerySettings && idx < tabIdx) {
          console.log(`[Previous Button] Section "${section.name}" visibility: ${section.isVisibleByQuery}`);
        }
      });
      let prevSection = null;
      for (let i = tabIdx - 1; i >= 0; i--) {
        const candidateSection = sections2[i];
        const isVisibleByQuery = candidateSection.hideQuerySettings ? candidateSection.isVisibleByQuery === true : candidateSection.isVisibleByQuery !== false;
        const isVisibleForCountry = candidateSection.isVisibleForCountry !== false;
        console.log(`[Previous Button] Checking section "${candidateSection.name}" (index ${i}):`, {
          isVisibleByQuery,
          isVisibleForCountry,
          hasHideQuerySettings: !!candidateSection.hideQuerySettings,
          isCustomTable: candidateSection.isCustomTable,
          completed: candidateSection.completed
        });
        if (!isVisibleByQuery || !isVisibleForCountry) {
          console.log(`?? Skipping hidden section "${candidateSection.name}":`, {
            reason: !isVisibleByQuery ? "hidden by query" : "hidden by country",
            isVisibleByQuery,
            isVisibleForCountry
          });
          continue;
        }
        if ((!candidateSection.isCustomTable || i === 0) && isVisibleByQuery && isVisibleForCountry) {
          prevSection = candidateSection;
          console.log(`? Previous visible section found: "${candidateSection.name}" (index ${i})`);
          break;
        } else {
          console.log(`?? Skipping section "${candidateSection.name}":`, {
            reason: "custom table",
            isCustomTable: candidateSection.isCustomTable
          });
        }
      }
      if (prevSection) {
        const completedSectionsData = sections2.filter((section) => section.completed).map((section) => ({
          sectionId: section.sectionId,
          sectionName: section.name,
          data: section.data || {},
          countryField: Object.entries(section.data || {}).find(
            ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
          ),
          stateField: Object.entries(section.data || {}).find(
            ([key, value]) => key.toLowerCase().includes("state") && value !== void 0
          )
        })).filter((section) => section.countryField || section.stateField);
        console.log("Navigation with completed sections data:", completedSectionsData);
        let navigationState = {
          sectionData: {
            id: localStorage.getItem("LoginApplicantId") || "0",
            sectionId: prevSection.sectionId,
            sectionName: prevSection.name,
            sectionNumber: prevSection.sectionNumber || tabIdx,
            // Current index becomes previous section number
            isCustomTable: prevSection.isCustomTable,
            sCustomTableName: prevSection.sCustomTableName,
            ObjType: objTypeSubject.value,
            ObjPointer: objPointerSubject.value,
            foreigndb: foreigndbSubject.value,
            // Add state-country relationships
            completedSectionsData
          }
        };
        const savedFieldData = await new Promise((resolve) => {
          sectionSavedDataSubject.pipe(take(1)).subscribe((data) => {
            resolve(data || []);
          });
        });
        if (savedFieldData && savedFieldData.length > 0) {
          savedFieldData.forEach((data) => {
            if (data.Label === prevSection.name) {
              navigationState.sectionData = {
                ...navigationState.sectionData,
                [data.ColumnName]: data.Value
              };
            }
          });
        }
        sections2[tabIdx].ActiveSection = false;
        prevSection.ActiveSection = true;
        sections2[tabIdx].completed = true;
        sectionsSubject.next([...sections2]);
        return { url: prevSection.url, state: navigationState };
      }
    }
    return null;
  }, [sectionsSubject, sectionSavedDataSubject, objPointerSubject, objTypeSubject, foreigndbSubject]);
  const navigateToSection = useCallback(async (targetSectionId, isBreadcrumbClick = false) => {
    const sections2 = await new Promise((resolve) => {
      sectionsSubject.pipe(take(1)).subscribe((sections3) => {
        resolve(sections3);
      });
    });
    const targetSection = sections2.find(
      (section) => section.sectionId === targetSectionId || section.id === targetSectionId || String(section.sectionId) === String(targetSectionId) || String(section.id) === String(targetSectionId)
    );
    if (!targetSection) {
      console.error("Target section not found:", targetSectionId);
      return null;
    }
    const isVisibleByQuery = targetSection.isVisibleByQuery !== false;
    const isVisibleForCountry = targetSection.isVisibleForCountry !== false;
    if (!isVisibleByQuery || !isVisibleForCountry) {
      console.warn("Target section is not visible:", targetSection.name);
      return null;
    }
    const currentPath = navigationPathSubject.value || [];
    const targetSectionIdStr = String(targetSection.sectionId || targetSection.UUID || targetSection.id || "");
    if (isBreadcrumbClick) {
      const targetIndex = currentPath.findIndex((id) => id === targetSectionIdStr);
      if (targetIndex >= 0) {
        const newPath = currentPath.slice(0, targetIndex + 1);
        navigationPathSubject.next(newPath);
        console.log("Breadcrumb click: Truncated path to:", newPath);
      }
    } else {
      if (!currentPath.includes(targetSectionIdStr)) {
        const newPath = [...currentPath, targetSectionIdStr];
        navigationPathSubject.next(newPath);
        console.log("Forward navigation: Added to path:", newPath);
      } else {
        const currentSection = currentSectionSubject.value;
        const currentSectionIdStr = currentSection ? String(currentSection.sectionId || currentSection.UUID || currentSection.id || "") : "";
        const currentIndex = currentPath.findIndex((id) => id === currentSectionIdStr);
        const targetIndex = currentPath.findIndex((id) => id === targetSectionIdStr);
        if (targetIndex > currentIndex) {
          console.log("Forward navigation within existing path");
        } else {
          console.log("Navigation to section already in path at earlier position");
        }
      }
    }
    const completedSectionsData = sections2.filter((section) => section.completed).map((section) => ({
      sectionId: section.sectionId,
      sectionName: section.name,
      data: section.data || {},
      countryField: Object.entries(section.data || {}).find(
        ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
      ),
      stateField: Object.entries(section.data || {}).find(
        ([key, value]) => key.toLowerCase().includes("state") && value !== void 0
      )
    })).filter((section) => section.countryField || section.stateField);
    let navigationState = {
      sectionData: {
        id: localStorage.getItem("LoginApplicantId") || "0",
        sectionId: targetSection.sectionId,
        sectionName: targetSection.name,
        sectionNumber: targetSection.sectionNumber || sections2.indexOf(targetSection) + 1,
        isCustomTable: targetSection.isCustomTable,
        sCustomTableName: targetSection.sCustomTableName,
        ObjType: objTypeSubject.value,
        ObjPointer: objPointerSubject.value,
        foreigndb: foreigndbSubject.value,
        // Add state-country relationships
        completedSectionsData
      }
    };
    const savedFieldData = await new Promise((resolve) => {
      sectionSavedDataSubject.pipe(take(1)).subscribe((data) => {
        resolve(data || []);
      });
    });
    if (savedFieldData && savedFieldData.length > 0) {
      savedFieldData.forEach((data) => {
        if (data.Label === targetSection.name) {
          navigationState.sectionData = {
            ...navigationState.sectionData,
            [data.ColumnName]: data.Value
          };
        }
      });
    }
    sections2.forEach((s) => s.ActiveSection = false);
    targetSection.ActiveSection = true;
    sectionsSubject.next([...sections2]);
    return { url: targetSection.url, state: navigationState };
  }, [sectionsSubject, sectionSavedDataSubject, objPointerSubject, objTypeSubject, foreigndbSubject]);
  const getFormSections = useCallback(async (configurableFormAppData) => {
    var _a;
    try {
      const templatesResponse = await configurableFormsApi.loadActiveTemplates(configurableFormAppData);
      if ((templatesResponse == null ? void 0 : templatesResponse.id) !== 0) {
        objTypeSubject.next(templatesResponse.ObjType);
        objPointerSubject.next(templatesResponse.ObjPointer);
        foreigndbSubject.next(configurableFormAppData.foreigndb);
        setTemplateId(templatesResponse.id);
        const response = await configurableFormsApi.loadFieldsForEndUser("", configurableFormAppData);
        if ((_a = response == null ? void 0 : response.Response) == null ? void 0 : _a[0]) {
          const sections2 = response.Response.map((item) => ({
            name: item.Label,
            title: item.Label,
            url: "",
            sectionId: item.UUID,
            sectionOrder: item._Order,
            iorder: item.iorder || item.iOrder,
            // Preserve iorder if available
            isExpanded: false,
            sTableName: item.TableName,
            completed: completedSectionList.includes(item.id.toString()),
            isCustomTable: item.isCustomTable,
            sCustomTableName: item.sCustomTableName,
            ObjType: templatesResponse.ObjType,
            ObjPointer: templatesResponse.ObjPointer
          }));
          sections2.sort((a, b) => {
            const orderA = Number(a.sectionOrder);
            const orderB = Number(b.sectionOrder);
            if (orderA === 0 && orderB !== 0) return 1;
            if (orderB === 0 && orderA !== 0) return -1;
            return orderA - orderB;
          });
          sectionsSubject.next(sections2);
          setSections(sections2);
        }
      }
    } catch (error) {
      console.error("Error loading form sections:", error);
    }
  }, [completedSectionList, setTemplateId]);
  const initializeCustomFormSectionsWithClean2Step = useCallback(async (applicant, propertyName, loginApplicantId, sectionNames, formType = "Standard Application") => {
    var _a, _b, _c;
    try {
      const step1Response = await configurableFormsApi.getStep1SectionIdsFromType0Templates(
        applicant,
        sectionNames,
        formType
      );
      if ((step1Response == null ? void 0 : step1Response.Status) === "Success") {
        const step2Response = await configurableFormsApi.getStep2ActiveTemplateMatchingSections(
          step1Response.CaretSeparatedIds,
          step1Response.CaretSeparatedNames,
          applicant,
          formType
        );
        if ((step2Response == null ? void 0 : step2Response.Status) === true) {
          const templateId = step2Response.ActiveTemplateId;
          if (templateId) {
            setTemplateId(templateId);
            objTypeSubject.next(step2Response.ActiveTemplateIObjType);
            objPointerSubject.next(step2Response.ActiveTemplateObjPointer || 0);
            foreigndbSubject.next(applicant.hforeigndb || applicant.foreigndb);
          }
          const customTablesData = await configurableFormsApi.getCustomTablesData({
            templateId: templateId || 0,
            ObjectType: Number(step2Response.ActiveTemplateIObjType) || 0,
            ObjectPointer: Number(step2Response.ActiveTemplateObjPointer) || 0
          });
          const processedSections = step2Response.MatchingSections.map((section) => {
            var _a2;
            return {
              name: section.Label,
              title: section.Label,
              url: "",
              sectionId: section.UUID,
              sectionOrder: section._Order || 0,
              isExpanded: false,
              sTableName: section.TableName,
              completed: completedSectionList.includes((_a2 = section.id) == null ? void 0 : _a2.toString()),
              id: section.id,
              isCustomTable: false,
              ObjType: step2Response.ActiveTemplateIObjType,
              ObjPointer: step2Response.ActiveTemplateObjPointer || 0
            };
          });
          const processedCustomTables = (customTablesData || []).map((item) => ({
            name: item.sDisplayName,
            title: item.sDisplayName,
            url: "",
            sectionId: item.UUID,
            sectionOrder: item._Order || 0,
            isExpanded: false,
            sTableName: item.sCustomTableName,
            completed: false,
            id: item.hmy,
            isCustomTable: true,
            sCustomTableName: item.sCustomTableName,
            ObjType: step2Response.ActiveTemplateIObjType,
            ObjPointer: step2Response.ActiveTemplateObjPointer || 0,
            TableName: item.sCustomTableName
          }));
          const allSections = [...processedSections, ...processedCustomTables].sort((a, b) => {
            const aTable = !!a.isCustomTable;
            const bTable = !!b.isCustomTable;
            if (!aTable && bTable) return -1;
            if (aTable && !bTable) return 1;
            const orderA = Number(a.sectionOrder) || 0;
            const orderB = Number(b.sectionOrder) || 0;
            if (orderA === 0 && orderB !== 0) return 1;
            if (orderB === 0 && orderA !== 0) return -1;
            return orderA - orderB;
          });
          const stepID = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
          let progressResponse = null;
          try {
            progressResponse = await configurableFormsApi.getSectionProgress({
              templateId,
              propertyId: applicant.PropertyId || applicant.propertyid,
              companyId: applicant.CompanyId,
              stepID,
              hObjPointer: loginApplicantId
            });
          } catch (error) {
            console.error("Error getting section progress:", error);
            console.error("Error details:", {
              message: error == null ? void 0 : error.message,
              status: (_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status,
              statusText: (_b = error == null ? void 0 : error.response) == null ? void 0 : _b.statusText,
              data: (_c = error == null ? void 0 : error.response) == null ? void 0 : _c.data
            });
          }
          const progressData = (progressResponse == null ? void 0 : progressResponse.Data) || progressResponse;
          if (progressData && typeof progressData === "object") {
            if (progressData.hmy !== void 0) {
              sectionProgressIdSubject.next(progressData.hmy);
            }
            let completedList = [];
            let incompleteList = [];
            if (progressData.CompletedSectionList && typeof progressData.CompletedSectionList === "string") {
              completedList = progressData.CompletedSectionList.split(",").filter((id) => id.trim() !== "");
            }
            if (progressData.IncompleteSectionList && typeof progressData.IncompleteSectionList === "string") {
              incompleteList = progressData.IncompleteSectionList.split(",").filter((id) => id.trim() !== "");
            }
            const validSectionIds = new Set(allSections.map((s) => String(s.id || "")).filter((id) => id));
            const staleCompleted = completedList.filter((id) => !validSectionIds.has(id));
            const staleIncomplete = incompleteList.filter((id) => !validSectionIds.has(id));
            if (staleCompleted.length > 0 || staleIncomplete.length > 0) {
              console.log("[Progress Cleanup] Removing stale section IDs:", {
                staleCompleted,
                staleIncomplete
              });
              completedList = completedList.filter((id) => validSectionIds.has(id));
              incompleteList = incompleteList.filter((id) => validSectionIds.has(id));
              try {
                const stepID2 = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
                await configurableFormsApi.saveSectionProgress({
                  UserId: 0,
                  PropertyId: getConfigurableFormsConfigOrThrow().getPropertyId(),
                  TemplateId: templateId || templateId,
                  // use activeTemplateId in loadSectionsBySectionIds, templateId in initializeCustomFormSectionsWithClean2Step
                  CompletedSectionList: completedList.join(","),
                  IncompleteSectionList: incompleteList.join(","),
                  hmy: progressData.hmy || 0,
                  StepID: stepID2,
                  hObjPointer: loginApplicantId
                });
                console.log("[Progress Cleanup] Persisted cleaned lists to backend");
              } catch (err) {
                console.warn("[Progress Cleanup] Failed to persist cleaned lists:", err);
              }
            }
            if (completedList.length === 0 && incompleteList.length === 0 && allSections.length > 0) {
              incompleteList = allSections.map((section) => {
                var _a2;
                return (_a2 = section.id) == null ? void 0 : _a2.toString();
              }).filter((id) => id);
            }
            completedSectionListSubject.next(completedList);
            incompleteSectionListSubject.next(incompleteList);
            allSections.forEach((section) => {
              var _a2;
              const isCompleted = completedList.includes((_a2 = section.id) == null ? void 0 : _a2.toString());
              section.completed = isCompleted;
            });
          } else {
            const defaultIncompleteList = allSections.map((section) => {
              var _a2;
              return (_a2 = section.id) == null ? void 0 : _a2.toString();
            }).filter((id) => id);
            completedSectionListSubject.next([]);
            incompleteSectionListSubject.next(defaultIncompleteList);
            allSections.forEach((section) => {
              section.completed = false;
            });
            setTimeout(() => {
              initializeProgressForNewUser(templateId.toString(), loginApplicantId, applicant.PropertyId || applicant.propertyid);
            }, 1e3);
          }
          sectionsSubject.next(allSections);
          setSections(allSections);
          if (allSections.length > 0 && navigationPathSubject.value.length === 0) {
            const firstSection = allSections[0];
            const firstSectionIdStr = String(firstSection.sectionId || firstSection.UUID || firstSection.id || "");
            if (firstSectionIdStr) {
              navigationPathSubject.next([firstSectionIdStr]);
              console.log("Initialized navigation path with first section:", firstSectionIdStr);
            }
          }
          return {
            Status: "Success",
            Message: "Workflow completed successfully",
            Sections: allSections,
            TemplateId: templateId
          };
        }
        throw new Error((step2Response == null ? void 0 : step2Response.Message) || "Failed to get active template matching sections");
      }
      throw new Error((step1Response == null ? void 0 : step1Response.Message) || "Failed to get section IDs from type 0 templates");
    } catch (error) {
      console.error("Error initializing sections:", error);
      throw error;
    }
  }, [completedSectionList, setTemplateId]);
  const getNextIncompleteSection = useCallback(() => {
    const currentSections = sectionsSubject.value;
    const incompleteList = incompleteSectionListSubject.value;
    const isSectionVisible = (section) => {
      const isVisibleByQuery = section.hideQuerySettings ? section.isVisibleByQuery === true : section.isVisibleByQuery !== false;
      const isVisibleForCountry = section.isVisibleForCountry !== false;
      return isVisibleByQuery && isVisibleForCountry;
    };
    if (incompleteList && incompleteList.length > 0) {
      for (const section of currentSections) {
        const sectionIdStr = String(section.id || "");
        if (incompleteList.includes(sectionIdStr)) {
          if (isSectionVisible(section)) {
            console.log("Found next incomplete and visible section from progress:", section.name, "ID:", sectionIdStr);
            return section;
          } else {
            console.log("Skipping incomplete but hidden section:", section.name, "ID:", sectionIdStr, {
              isVisibleByQuery: section.isVisibleByQuery,
              isVisibleForCountry: section.isVisibleForCountry,
              hasHideQuerySettings: !!section.hideQuerySettings
            });
          }
        }
      }
    }
    const incompleteSection = currentSections.find((section) => !section.completed && isSectionVisible(section));
    if (incompleteSection) {
      console.log("Found next incomplete and visible section from completed flag:", incompleteSection.name);
      return incompleteSection;
    }
    const firstVisibleSection = currentSections.find((section) => isSectionVisible(section));
    if (firstVisibleSection) {
      console.log("No incomplete sections found, returning first visible section:", firstVisibleSection.name);
      return firstVisibleSection;
    }
    if (currentSections.length > 0) {
      console.warn("No visible sections found, returning first section as fallback:", currentSections[0].name);
      return currentSections[0];
    }
    return null;
  }, []);
  const initializeProgressForNewUser = useCallback(async (templateId, applicantId, propertyId) => {
    const sections2 = sectionsSubject.value;
    if (sections2.length === 0) return;
    const allSectionIds = sections2.map((section) => {
      var _a;
      return (_a = section.id) == null ? void 0 : _a.toString();
    }).filter((id) => id);
    try {
      const stepID = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
      const response = await configurableFormsApi.saveSectionProgress({
        UserId: 0,
        PropertyId: propertyId == null ? null : String(propertyId),
        TemplateId: templateId,
        CompletedSectionList: "",
        IncompleteSectionList: allSectionIds.join(","),
        hmy: 0,
        StepID: stepID,
        ...applicantId && { hObjPointer: applicantId }
      });
      if (response) {
        sectionProgressIdSubject.next(response.hmy);
        completedSectionListSubject.next([]);
        incompleteSectionListSubject.next(allSectionIds);
      }
    } catch (error) {
      console.error("Error creating initial progress:", error);
    }
  }, []);
  const resetService = useCallback(() => {
    currentSectionSubject.next(null);
    sectionResponseSubject.next(null);
    templateIdSubject.next(null);
    sectionSavedDataSubject.next(null);
    sectionsSubject.next([]);
    objTypeSubject.next(null);
    objPointerSubject.next(null);
    foreigndbSubject.next(null);
    completedSectionListSubject.next([]);
    incompleteSectionListSubject.next([]);
    sectionProgressIdSubject.next(null);
    navigationPathSubject.next([]);
    setSections([]);
    setTemplateHMY(null);
  }, []);
  const loadSectionsBySectionIds = useCallback(async (formId, sectionIds, configurableFormAppData, companyId, propertyId, templateId, objType, objPointer, loginApplicantId) => {
    var _a;
    try {
      const params = {
        formId,
        sectionIds,
        configurableFormAppData,
        companyId: companyId || "0",
        propertyId: propertyId || "0"
      };
      const response = await configurableFormsApi.loadSectionsBySectionIds(params);
      if (response.Success && response.Data && response.Data.MatchingSections) {
        const processedSections = response.Data.MatchingSections.map((section) => ({
          ...section,
          name: section.Label,
          title: section.Label,
          url: "",
          sectionId: section.UUID || section.id,
          // Use UUID if available, fallback to id
          sectionOrder: section._Order || 0,
          iorder: section.iorder || section.iOrder,
          // Preserve iorder if available
          isExpanded: false,
          completed: false,
          id: section.id,
          isCustomTable: section._CustomTablename == -1 ? true : false,
          ObjType: response.Data.ActiveTemplateIObjType || objType || 0,
          ObjPointer: response.Data.ActiveTemplateObjPointer || objPointer || 0,
          sTableName: section._CustomTablename == -1 ? section.CustomTableName : section.TableName,
          sCustomTableName: section.CustomTableName,
          TableName: section.CustomTableName
        }));
        let processedCustomTables = [];
        const activeTemplateId = response.Data.ActiveTemplateId ?? response.Data.activeTemplateId ?? templateId ?? formId;
        const objTypeNum = Number(response.Data.ActiveTemplateIObjType ?? response.Data.activeTemplateIObjType ?? objType ?? 0);
        const objPointerNum = Number(response.Data.ActiveTemplateObjPointer ?? response.Data.activeTemplateObjPointer ?? objPointer ?? 0);
        const allSections = [...processedSections, ...processedCustomTables].sort((a, b) => {
          const aTable = !!a.isCustomTable;
          const bTable = !!b.isCustomTable;
          if (!aTable && bTable) return -1;
          if (aTable && !bTable) return 1;
          const orderA = Number(a.sectionOrder) || 0;
          const orderB = Number(b.sectionOrder) || 0;
          if (orderA === 0 && orderB !== 0) return 1;
          if (orderB === 0 && orderA !== 0) return -1;
          return orderA - orderB;
        });
        if (activeTemplateId && loginApplicantId) {
          try {
            const stepID = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
            const progressResponse = await configurableFormsApi.getSectionProgress({
              templateId: activeTemplateId,
              propertyId: propertyId || ((_a = configurableFormAppData == null ? void 0 : configurableFormAppData.PropertyId) == null ? void 0 : _a.toString()) || "0",
              companyId: configurableFormAppData == null ? void 0 : configurableFormAppData.CompanyId,
              stepID,
              hObjPointer: loginApplicantId
            });
            const progressData = (progressResponse == null ? void 0 : progressResponse.Data) || progressResponse;
            if (progressData && typeof progressData === "object") {
              if (progressData.hmy !== void 0) {
                sectionProgressIdSubject.next(progressData.hmy);
              }
              let completedList = [];
              let incompleteList = [];
              if (progressData.CompletedSectionList && typeof progressData.CompletedSectionList === "string") {
                completedList = progressData.CompletedSectionList.split(",").filter((id) => id.trim() !== "");
              }
              if (progressData.IncompleteSectionList && typeof progressData.IncompleteSectionList === "string") {
                incompleteList = progressData.IncompleteSectionList.split(",").filter((id) => id.trim() !== "");
              }
              const validSectionIds = new Set(allSections.map((s) => String(s.id || "")).filter((id) => id));
              const staleCompleted = completedList.filter((id) => !validSectionIds.has(id));
              const staleIncomplete = incompleteList.filter((id) => !validSectionIds.has(id));
              if (staleCompleted.length > 0 || staleIncomplete.length > 0) {
                console.log("[Progress Cleanup] Removing stale section IDs:", {
                  staleCompleted,
                  staleIncomplete
                });
                completedList = completedList.filter((id) => validSectionIds.has(id));
                incompleteList = incompleteList.filter((id) => validSectionIds.has(id));
                try {
                  const stepID2 = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
                  await configurableFormsApi.saveSectionProgress({
                    UserId: 0,
                    PropertyId: getConfigurableFormsConfigOrThrow().getPropertyId(),
                    TemplateId: activeTemplateId || templateId,
                    // use activeTemplateId in loadSectionsBySectionIds, templateId in initializeCustomFormSectionsWithClean2Step
                    CompletedSectionList: completedList.join(","),
                    IncompleteSectionList: incompleteList.join(","),
                    hmy: progressData.hmy || 0,
                    StepID: stepID2,
                    hObjPointer: loginApplicantId
                  });
                  console.log("[Progress Cleanup] Persisted cleaned lists to backend");
                } catch (err) {
                  console.warn("[Progress Cleanup] Failed to persist cleaned lists:", err);
                }
              }
              if (completedList.length === 0 && incompleteList.length === 0 && allSections.length > 0) {
                incompleteList = allSections.map((section) => String(section.id || "")).filter((id) => id);
              }
              completedSectionListSubject.next(completedList);
              incompleteSectionListSubject.next(incompleteList);
              const currentSections = sectionsSubject.value || allSections;
              currentSections.forEach((section) => {
                const sectionIdStr = String(section.id || "");
                const isVisibleByQuery = section.hideQuerySettings ? section.isVisibleByQuery === true : section.isVisibleByQuery !== false;
                const isVisibleForCountry = section.isVisibleForCountry !== false;
                const isHidden = !isVisibleByQuery || !isVisibleForCountry;
                if (isHidden && sectionIdStr) {
                  section.completed = true;
                  if (incompleteList.includes(sectionIdStr)) {
                    incompleteList.splice(incompleteList.indexOf(sectionIdStr), 1);
                    console.log(`[Section Progress] Removing hidden section "${section.name || section.sectionName}" (ID: ${sectionIdStr}) from incomplete list`);
                  }
                  if (!completedList.includes(sectionIdStr)) {
                    completedList.push(sectionIdStr);
                    console.log(`[Section Progress] Adding hidden section "${section.name || section.sectionName}" (ID: ${sectionIdStr}) to completed list`);
                  }
                } else {
                  const wasInCompletedList = completedList.includes(sectionIdStr);
                  section.completed = wasInCompletedList;
                }
              });
              completedSectionListSubject.next(completedList);
              incompleteSectionListSubject.next(incompleteList);
              sectionsSubject.next([...currentSections]);
              console.log("Loaded section progress:", {
                completed: completedList.join(","),
                incomplete: incompleteList.join(","),
                completedCount: completedList.length,
                incompleteCount: incompleteList.length
              });
            }
          } catch (error) {
            console.warn("Failed to load section progress:", error);
            const defaultIncompleteList = allSections.map((section) => String(section.id || "")).filter((id) => id);
            incompleteSectionListSubject.next(defaultIncompleteList);
            completedSectionListSubject.next([]);
          }
        }
        sectionsSubject.next(allSections);
        setSections(allSections);
        const finalTemplateId = activeTemplateId || formId;
        templateIdSubject.next(finalTemplateId);
        return {
          Status: "Success",
          Message: "Sections loaded successfully by IDs",
          Sections: allSections,
          TemplateId: finalTemplateId
        };
      } else {
        throw new Error(response.Message || response.ErrorMessage || "Failed to load sections by section IDs");
      }
    } catch (error) {
      console.error("Error loading sections by section IDs:", error);
      throw error;
    }
  }, []);
  return {
    sections,
    completedSectionList,
    incompleteSectionList,
    updateCurrentSection,
    setSectionResponse,
    setTemplateId,
    getTemplateId,
    updateSavedFieldData,
    saveSectionProgress,
    nextButtonClick,
    previousButtonClick,
    navigateToSection,
    getFormSections,
    initializeCustomFormSectionsWithClean2Step,
    getNextIncompleteSection,
    initializeProgressForNewUser,
    resetService,
    // Add the reset method to the return object
    loadSectionsBySectionIds,
    // Add the new method
    // Observables
    currentSection$: currentSectionSubject,
    sectionResponseData$: sectionResponseSubject,
    templateId$: templateIdSubject,
    sectionSavedFieldData$: sectionSavedDataSubject,
    sections$: sectionsSubject,
    objType$: objTypeSubject,
    objPointer$: objPointerSubject,
    foreigndb$: foreigndbSubject,
    completedSectionList$: completedSectionListSubject,
    incompleteSectionList$: incompleteSectionListSubject,
    sectionProgressId$: sectionProgressIdSubject,
    navigationPath$: navigationPathSubject
  };
};
const ApplicationLoadingScreen$1 = ({
  message = "Loading your application..."
}) => {
  const { t: translate } = useTranslation();
  return /* @__PURE__ */ jsx("div", { className: "min-h-screen flex items-center justify-center bg-white", children: /* @__PURE__ */ jsxs("div", { className: "text-center", children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx("div", { className: "animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-primary mx-auto" }),
      /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "w-8 h-8 bg-primary rounded-full opacity-20 animate-pulse" }) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-lg font-medium text-gray-900", children: translate(message) }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-600", children: translate("Please wait while we prepare your application...") })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex justify-center mt-4 space-x-1", children: [
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce" }),
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce", style: { animationDelay: "0.1s" } }),
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce", style: { animationDelay: "0.2s" } })
    ] })
  ] }) });
};
function cn$1(...classes) {
  return classes.filter(Boolean).join(" ");
}
const Card = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn$1(
      "rounded-lg border border-gray-200 bg-white text-gray-900 shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
const CardHeader = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn$1("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
const CardTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn$1(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
const CardDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn$1("text-sm text-gray-600", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
const CardContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn$1("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
const CardFooter = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn$1("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}
const badgeVariants = {
  default: "border-transparent bg-blue-500 text-white hover:bg-blue-600",
  secondary: "border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200",
  gray: "border-transparent bg-gray-100 text-gray-600 hover:bg-gray-100",
  destructive: "border-transparent bg-red-500 text-white hover:bg-red-600",
  outline: "text-gray-900 border-gray-300"
};
function Badge({ className, variant = "default", ...props }) {
  const variantClasses = badgeVariants[variant] || badgeVariants.default;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        variantClasses,
        className
      ),
      ...props
    }
  );
}
const falsyToString = (value) => typeof value === "boolean" ? `${value}` : value === 0 ? "0" : value;
const cx = clsx;
const cva = (base, config2) => (props) => {
  var _config_compoundVariants;
  if ((config2 === null || config2 === void 0 ? void 0 : config2.variants) == null) return cx(base, props === null || props === void 0 ? void 0 : props.class, props === null || props === void 0 ? void 0 : props.className);
  const { variants, defaultVariants } = config2;
  const getVariantClassNames = Object.keys(variants).map((variant) => {
    const variantProp = props === null || props === void 0 ? void 0 : props[variant];
    const defaultVariantProp = defaultVariants === null || defaultVariants === void 0 ? void 0 : defaultVariants[variant];
    if (variantProp === null) return null;
    const variantKey = falsyToString(variantProp) || falsyToString(defaultVariantProp);
    return variants[variant][variantKey];
  });
  const propsWithoutUndefined = props && Object.entries(props).reduce((acc, param) => {
    let [key, value] = param;
    if (value === void 0) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
  const getCompoundVariantClassNames = config2 === null || config2 === void 0 ? void 0 : (_config_compoundVariants = config2.compoundVariants) === null || _config_compoundVariants === void 0 ? void 0 : _config_compoundVariants.reduce((acc, param) => {
    let { class: cvClass, className: cvClassName, ...compoundVariantOptions } = param;
    return Object.entries(compoundVariantOptions).every((param2) => {
      let [key, value] = param2;
      return Array.isArray(value) ? value.includes({
        ...defaultVariants,
        ...propsWithoutUndefined
      }[key]) : {
        ...defaultVariants,
        ...propsWithoutUndefined
      }[key] === value;
    }) ? [
      ...acc,
      cvClass,
      cvClassName
    ] : acc;
  }, []);
  return cx(base, getVariantClassNames, getCompoundVariantClassNames, props === null || props === void 0 ? void 0 : props.class, props === null || props === void 0 ? void 0 : props.className);
};
const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
const Label = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  LabelPrimitive.Root,
  {
    ref,
    className: cn$2(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;
const styles = {
  input: {
    base: "e-outline !rounded-lg !border-gray-300 !text-base !h-12 !p-0 overflow-hidden !font-semibold focus:!border-2 focus:!border-primary focus:!outline-none focus:!shadow-[0_0_0_3px_var(--primary-focus-outer)]",
    valid: "e-outline border-gray-300"
  }
};
function calculateAge(birthDate) {
  const today = /* @__PURE__ */ new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
    age--;
  }
  return age;
}
function getMonthsDifference(date1, date2) {
  const yearDiff = date2.getFullYear() - date1.getFullYear();
  const monthDiff = date2.getMonth() - date1.getMonth();
  return yearDiff * 12 + monthDiff;
}
function formatDateForDisplay(date) {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}
function validateDate(value, settings, customMessages) {
  if (!value) {
    return { isValid: true };
  }
  const date = new Date(value);
  const now = /* @__PURE__ */ new Date();
  const errors = [];
  if (isNaN(date.getTime())) {
    return { isValid: false, message: "Invalid date format" };
  }
  if (settings.disableFutureDate && date > now) {
    errors.push((customMessages == null ? void 0 : customMessages.disableFutureDate) || "Future dates are not allowed");
  }
  if (settings.disablePastDate && date < now) {
    errors.push((customMessages == null ? void 0 : customMessages.disablePastDate) || "Past dates are not allowed");
  }
  if (settings.minAge || settings.maxAge) {
    const age = calculateAge(date);
    if (settings.minAge && age < settings.minAge) {
      errors.push((customMessages == null ? void 0 : customMessages.minAge) || `Minimum age required is ${settings.minAge} years`);
    }
    if (settings.maxAge && age > settings.maxAge) {
      errors.push((customMessages == null ? void 0 : customMessages.maxAge) || `Maximum age allowed is ${settings.maxAge} years`);
    }
  }
  const year = date.getFullYear();
  if (settings.minYear && year < settings.minYear) {
    errors.push((customMessages == null ? void 0 : customMessages.minYear) || `Minimum year is ${settings.minYear}`);
  }
  if (settings.maxYear && year > settings.maxYear) {
    errors.push((customMessages == null ? void 0 : customMessages.maxYear) || `Maximum year is ${settings.maxYear}`);
  }
  const months = getMonthsDifference(date, now);
  if (settings.minMonths && months < settings.minMonths) {
    errors.push((customMessages == null ? void 0 : customMessages.minMonths) || `Minimum months is ${settings.minMonths}`);
  }
  if (settings.maxMonths && months > settings.maxMonths) {
    errors.push((customMessages == null ? void 0 : customMessages.maxMonths) || `Maximum months is ${settings.maxMonths}`);
  }
  if (settings.customDateRange) {
    const { startDate, endDate } = settings.customDateRange;
    const normalizeDate = (d) => {
      const normalized = new Date(d);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    const normalizedDate = normalizeDate(date);
    if (customMessages == null ? void 0 : customMessages.customDateRange) {
      if (startDate) {
        const normalizedStartDate = normalizeDate(new Date(startDate));
        if (normalizedDate < normalizedStartDate) {
          errors.push(customMessages.customDateRange);
        }
      }
      if (endDate) {
        const normalizedEndDate = normalizeDate(new Date(endDate));
        if (normalizedDate > normalizedEndDate) {
          errors.push(customMessages.customDateRange);
        }
      }
      if (errors.length > 0) {
        return {
          isValid: false,
          errors,
          message: errors.join(", ")
        };
      }
    }
    if (startDate) {
      const normalizedStartDate = normalizeDate(new Date(startDate));
      if (normalizedDate < normalizedStartDate) {
        const formattedStartDate = formatDateForDisplay(normalizedStartDate);
        errors.push(`Date must be on or after ${formattedStartDate}`);
      }
    }
    if (endDate) {
      const normalizedEndDate = normalizeDate(new Date(endDate));
      if (normalizedDate > normalizedEndDate) {
        const formattedEndDate = formatDateForDisplay(normalizedEndDate);
        errors.push(`Date must be on or before ${formattedEndDate}`);
      }
    }
  }
  return {
    isValid: errors.length === 0,
    errors,
    message: errors.length > 0 ? errors.join(", ") : void 0
  };
}
function validateText(value, settings, customMessages) {
  if (!value) {
    return { isValid: true };
  }
  const errors = [];
  const hasCharacterTypeValidations = settings.allowAlphabetsOnly || settings.allowAlphanumeric || settings.numbersOnly || settings.allowSpecialChars;
  if (hasCharacterTypeValidations) {
    let allowedCharsPattern = "";
    let errorMessage = "";
    if (settings.allowAlphabetsOnly) {
      allowedCharsPattern += "a-zA-Z";
      if (!errorMessage) errorMessage = (customMessages == null ? void 0 : customMessages.allowAlphabetsOnly) || "Only alphabets are allowed";
    }
    if (settings.allowAlphanumeric) {
      allowedCharsPattern += "a-zA-Z0-9";
      if (!errorMessage) errorMessage = (customMessages == null ? void 0 : customMessages.allowAlphanumeric) || "Only alphanumeric characters are allowed";
    }
    if (settings.numbersOnly) {
      allowedCharsPattern += "0-9";
      if (!errorMessage) errorMessage = (customMessages == null ? void 0 : customMessages.numbersOnly) || "Only numbers are allowed";
    }
    if (settings.allowSpecialChars) {
      allowedCharsPattern += "!@#$%^&*()_+\\-=\\[\\]{};':\"|,.<>?/~`";
    }
    if (!settings.noSpaces) {
      allowedCharsPattern += "\\s";
    }
    const combinedRegex = new RegExp(`^[${allowedCharsPattern}]+$`);
    if (!combinedRegex.test(value)) {
      errors.push(settings.customMessage || errorMessage || "Invalid characters entered");
    }
  }
  if (settings.disableSpecialChars && /[^a-zA-Z0-9\s]/.test(value)) {
    errors.push((customMessages == null ? void 0 : customMessages.disableSpecialChars) || "Special characters are not allowed");
  }
  if (settings.noSpaces && /\s/.test(value)) {
    errors.push((customMessages == null ? void 0 : customMessages.noSpaces) || "Spaces are not allowed");
  }
  const length = value.length;
  if (settings.minLength && length < settings.minLength) {
    errors.push((customMessages == null ? void 0 : customMessages.minLength) || `Minimum length is ${settings.minLength} characters`);
  }
  if (settings.maxLength && length > settings.maxLength) {
    errors.push((customMessages == null ? void 0 : customMessages.maxLength) || `Maximum length is ${settings.maxLength} characters`);
  }
  if (settings.exactLength && length !== settings.exactLength) {
    errors.push((customMessages == null ? void 0 : customMessages.exactLength) || `Length must be exactly ${settings.exactLength} characters`);
  }
  if (settings.customPattern) {
    try {
      const pattern = new RegExp(settings.customPattern);
      if (!pattern.test(value)) {
        errors.push((customMessages == null ? void 0 : customMessages.customPattern) || settings.customMessage || "Value does not match the required pattern");
      }
    } catch (e) {
      console.error("Invalid regex pattern:", settings.customPattern);
    }
  }
  return {
    isValid: errors.length === 0,
    errors,
    message: errors.length > 0 ? errors.join(", ") : void 0
  };
}
function parseBackendDateSettings(settings) {
  const dateSettings = {
    disableFutureDate: false,
    disablePastDate: false
  };
  const messages = {};
  settings.forEach((setting) => {
    const subtype = (setting.validationSubtype || setting.ValidationSubtype || "").toLowerCase();
    const value = setting.validationValue || setting.ValidationValue;
    const message = setting.validationMessage || setting.ValidationMessage || "";
    switch (subtype) {
      case "disablefuturedate":
        dateSettings.disableFutureDate = value === "true" || value === "1";
        if (message) messages.disableFutureDate = message;
        break;
      case "disablepastdate":
        dateSettings.disablePastDate = value === "true" || value === "1";
        if (message) messages.disablePastDate = message;
        break;
      case "minage":
        dateSettings.minAge = parseInt(value);
        if (message) messages.minAge = message;
        break;
      case "maxage":
        dateSettings.maxAge = parseInt(value);
        if (message) messages.maxAge = message;
        break;
      case "minyear":
        dateSettings.minYear = parseInt(value);
        if (message) messages.minYear = message;
        break;
      case "maxyear":
        dateSettings.maxYear = parseInt(value);
        if (message) messages.maxYear = message;
        break;
      case "minmonths":
        dateSettings.minMonths = parseInt(value);
        if (message) messages.minMonths = message;
        break;
      case "maxmonths":
        dateSettings.maxMonths = parseInt(value);
        if (message) messages.maxMonths = message;
        break;
      case "customdaterange":
        try {
          dateSettings.customDateRange = JSON.parse(value);
          if (message) messages.customDateRange = message;
        } catch (e) {
          console.error("Failed to parse custom date range:", value);
        }
        break;
    }
  });
  return { settings: dateSettings, messages };
}
function parseBackendTextSettings(settings) {
  const textSettings = {
    allowAlphabetsOnly: false,
    allowAlphanumeric: false,
    numbersOnly: false,
    allowSpecialChars: false,
    disableSpecialChars: false
  };
  const messages = {};
  settings.forEach((setting) => {
    const subtype = (setting.validationSubtype || setting.ValidationSubtype || "").toLowerCase();
    const value = setting.validationValue || setting.ValidationValue;
    const message = setting.validationMessage || setting.ValidationMessage || "";
    switch (subtype) {
      case "allowalphabetsonly":
        textSettings.allowAlphabetsOnly = value === "true" || value === "1";
        if (message) messages.allowAlphabetsOnly = message;
        break;
      case "allowalphanumeric":
        textSettings.allowAlphanumeric = value === "true" || value === "1";
        if (message) messages.allowAlphanumeric = message;
        break;
      case "numbersonly":
        textSettings.numbersOnly = value === "true" || value === "1";
        if (message) messages.numbersOnly = message;
        break;
      case "allowspecialchars":
        textSettings.allowSpecialChars = value === "true" || value === "1";
        if (message) messages.allowSpecialChars = message;
        break;
      case "disablespecialchars":
        textSettings.disableSpecialChars = value === "true" || value === "1";
        if (message) messages.disableSpecialChars = message;
        break;
      case "nospaces":
        textSettings.noSpaces = value === "true" || value === "1";
        if (message) messages.noSpaces = message;
        break;
      case "minlength":
        textSettings.minLength = parseInt(value);
        if (message) messages.minLength = message;
        break;
      case "maxlength":
        textSettings.maxLength = parseInt(value);
        if (message) messages.maxLength = message;
        break;
      case "exactlength":
        textSettings.exactLength = parseInt(value);
        if (message) messages.exactLength = message;
        break;
      case "custompattern":
        textSettings.customPattern = value;
        if (message) messages.customPattern = message;
        break;
      case "custommessage":
        textSettings.customMessage = value;
        break;
    }
  });
  return { settings: textSettings, messages };
}
function normalizeCountryName(countryName) {
  if (!countryName) return "";
  const normalized = countryName.toLowerCase().trim();
  const countryAliases = {
    "united states of america": "united states",
    "usa": "united states",
    "us": "united states",
    "u.s.": "united states",
    "u.s.a.": "united states",
    "united states": "united states",
    "uk": "united kingdom",
    "u.k.": "united kingdom",
    "great britain": "united kingdom",
    "gb": "united kingdom",
    "england": "united kingdom",
    "scotland": "united kingdom",
    "wales": "united kingdom",
    "northern ireland": "united kingdom",
    "united kingdom": "united kingdom",
    "iran, islamic republic of": "iran",
    "islamic republic of iran": "iran",
    "iran": "iran",
    "uae": "united arab emirates",
    "united arab emirates": "united arab emirates",
    "korea, republic of": "south korea",
    "korea, democratic people's republic of": "north korea",
    "korea": "south korea",
    "south korea": "south korea",
    "north korea": "north korea",
    "burma": "myanmar",
    "myanmar": "myanmar",
    "macedonia": "north macedonia",
    "macedonia, the former yugoslav republic of": "north macedonia",
    "north macedonia": "north macedonia",
    "congo, the democratic republic of the": "congo",
    "congo": "congo",
    "tanzania, united republic of": "tanzania",
    "tanzania": "tanzania",
    "taiwan, province of china": "taiwan",
    "taiwan": "taiwan",
    "micronesia, federated states of": "micronesia",
    "micronesia": "micronesia",
    "palestinian territory, occupied": "palestine",
    "palestine": "palestine",
    "libyan arab jamahiriya": "libya",
    "libya": "libya",
    "swaziland": "eswatini",
    "eswatini": "eswatini"
  };
  if (countryAliases[normalized]) {
    return countryAliases[normalized];
  }
  for (const [alias, canonical] of Object.entries(countryAliases)) {
    if (alias.length <= 3) {
      const escapedAlias = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(^|\\s)${escapedAlias}(\\s|$)`, "i");
      if (regex.test(normalized)) {
        return canonical;
      }
    } else {
      if (normalized.includes(alias)) {
        return canonical;
      }
    }
  }
  const normalizedClean = normalized.replace(/,.*$/, "").replace(/\s+republic\s+of\s+/i, " ").replace(/\s+the\s+/i, " ").replace(/\s+democratic\s+republic\s+of\s+/i, " ").replace(/\s+federative\s+republic\s+of\s+/i, " ").replace(/\s+federated\s+states\s+of\s+/i, " ").replace(/\s+kingdom\s+of\s+/i, " ").replace(/\s+state\s+of\s+/i, " ").replace(/\s+united\s+republic\s+of\s+/i, " ").replace(/\s+province\s+of\s+/i, " ").replace(/\s+territory\s*,\s*occupied/i, "").replace(/\s+arab\s+jamahiriya/i, "").trim();
  for (const patternKey of Object.keys(ZIP_CODE_PATTERNS)) {
    if (normalized === patternKey || normalizedClean === patternKey) {
      return patternKey;
    }
    if (normalized.includes(patternKey)) {
      if (patternKey.length >= 3 || normalized === patternKey || normalized.startsWith(patternKey + " ") || normalized.endsWith(" " + patternKey)) {
        return patternKey;
      }
    }
    if (normalizedClean.includes(patternKey)) {
      if (patternKey.length >= 3 || normalizedClean === patternKey || normalizedClean.startsWith(patternKey + " ") || normalizedClean.endsWith(" " + patternKey)) {
        return patternKey;
      }
    }
  }
  return normalized;
}
function validateZipCode(value, countryName) {
  if (!value || !value.trim()) {
    return { isValid: true };
  }
  const normalizedValue = value.trim();
  const normalizedCountryName = normalizeCountryName(countryName);
  if (process.env.NODE_ENV === "development") {
    console.log("?? Zip validation:", { countryName, normalizedCountryName, value: normalizedValue });
  }
  const zipConfig = ZIP_CODE_PATTERNS[normalizedCountryName];
  if (process.env.NODE_ENV === "development") {
    console.log("?? Zip config found:", !!zipConfig, zipConfig ? `format: ${zipConfig.format}` : "not found");
  }
  if (!zipConfig) {
    if (normalizedCountryName) {
      const length = normalizedValue.length;
      const cleanedValue = normalizedValue.replace(/[\s\-]/g, "");
      const getFormatExample = (value2) => {
        const cleaned = value2.replace(/[\s\-]/g, "");
        const isNumeric = /^\d+$/.test(cleaned);
        if (isNumeric) {
          if (cleaned.length === 4) return " (format: 1234)";
          if (cleaned.length === 5) return " (format: 12345)";
          if (cleaned.length === 6) return " (format: 123456)";
          if (cleaned.length >= 7) return " (format: 1234567...)";
          return " (format: 3-12 digits)";
        } else {
          return " (format: alphanumeric)";
        }
      };
      const formatExample = getFormatExample(normalizedValue);
      if (length < 3 || length > 12) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      if (!/^[A-Za-z0-9]/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      if (!/[A-Za-z0-9]$/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      if (!/^[A-Za-z0-9\s\-]+$/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      if (cleanedValue.length < 3) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      if (!/\d/.test(cleanedValue)) {
        if (cleanedValue.length < 3 || !/^[A-Za-z]{3,}$/.test(cleanedValue)) {
          return {
            isValid: false,
            message: `Invalid postal code for ${countryName}${formatExample}`
          };
        }
      }
      const uniqueChars = new Set(cleanedValue.toLowerCase().split(""));
      if (uniqueChars.size === 1 && cleanedValue.length >= 3) {
        return {
          isValid: false,
          message: `Invalid postal code for ${countryName}${formatExample}`
        };
      }
      return { isValid: true };
    } else {
      const length = normalizedValue.length;
      const cleanedValue = normalizedValue.replace(/[\s\-]/g, "");
      const getFormatExample = (value2) => {
        const cleaned = value2.replace(/[\s\-]/g, "");
        const isNumeric = /^\d+$/.test(cleaned);
        if (isNumeric) {
          if (cleaned.length === 4) return " (format: 1234)";
          if (cleaned.length === 5) return " (format: 12345)";
          if (cleaned.length === 6) return " (format: 123456)";
          if (cleaned.length >= 7) return " (format: 1234567...)";
          return " (format: 3-12 digits)";
        } else {
          return " (format: alphanumeric)";
        }
      };
      const formatExample = getFormatExample(normalizedValue);
      if (length < 3 || length > 12) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      if (!/^[A-Za-z0-9]/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      if (!/[A-Za-z0-9]$/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      if (!/^[A-Za-z0-9\s\-]+$/.test(normalizedValue)) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      if (cleanedValue.length < 3) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      if (!/\d/.test(cleanedValue) && (!/^[A-Za-z]{3,}$/.test(cleanedValue) || cleanedValue.length < 3)) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      const uniqueChars = new Set(cleanedValue.toLowerCase().split(""));
      if (uniqueChars.size === 1 && cleanedValue.length >= 3) {
        return {
          isValid: false,
          message: `Invalid postal code${formatExample}`
        };
      }
      return { isValid: true };
    }
  }
  if (!zipConfig.pattern.test(normalizedValue)) {
    return {
      isValid: false,
      message: `Invalid postal code (format: ${zipConfig.format})`
    };
  }
  return { isValid: true };
}
function getZipConfigForCountry(countryCodeOrName) {
  if (!countryCodeOrName || !String(countryCodeOrName).trim()) return null;
  const raw = String(countryCodeOrName).trim();
  const code = raw.toLowerCase();
  if (raw.length <= 3 && /^[a-z]{2,3}$/.test(code)) {
    const zipKey = ISO_TO_ZIP_KEY[code];
    if (zipKey && ZIP_CODE_PATTERNS[zipKey]) return ZIP_CODE_PATTERNS[zipKey];
  }
  const normalized = normalizeCountryName(raw);
  const zipConfig = ZIP_CODE_PATTERNS[normalized];
  return zipConfig ?? null;
}
function isValidPostcodeForCountry(value, countryCodeOrName) {
  const trimmed = (value == null ? void 0 : value.trim()) ?? "";
  if (trimmed === "") return true;
  const config2 = getZipConfigForCountry(countryCodeOrName);
  if (!config2) return false;
  return config2.pattern.test(trimmed);
}
function getStateRegionKey(countryCodeOrName) {
  if (!countryCodeOrName || !String(countryCodeOrName).trim()) return null;
  const raw = String(countryCodeOrName).trim();
  const code = raw.toLowerCase();
  if (raw.length <= 3 && /^[a-z]{2,3}$/.test(code)) {
    const zipKey = ISO_TO_ZIP_KEY[code];
    if (zipKey) return zipKey;
  }
  return normalizeCountryName(raw);
}
function isStateValidForCountry(stateValue, countryCodeOrName) {
  const trimmed = (stateValue == null ? void 0 : stateValue.trim()) ?? "";
  if (trimmed === "") return false;
  const key = getStateRegionKey(countryCodeOrName);
  if (!key || !STATE_REGION_CODES[key]) return true;
  const codes = STATE_REGION_CODES[key];
  const upper = trimmed.toUpperCase();
  if (codes.includes(upper)) return true;
  if (trimmed.length > 4) return true;
  return false;
}
function isCityReasonableForCountry(cityValue) {
  const trimmed = (cityValue == null ? void 0 : cityValue.trim()) ?? "";
  if (trimmed === "") return false;
  if (/^\d+$/.test(trimmed)) return false;
  if (trimmed.length > 100) return false;
  return true;
}
const validateField = (fieldId, value, controlType, allValidationSettings) => {
  var _a, _b;
  const validationSettings = allValidationSettings[fieldId];
  if (!validationSettings || validationSettings.length === 0) {
    return null;
  }
  if (!value || typeof value === "string" && value.trim() === "") {
    return null;
  }
  const isDateField = controlType && controlType.toLowerCase() === "date";
  const hasDateValidation = validationSettings.some((s) => {
    var _a2;
    return ((_a2 = s.validationType || s.ValidationType) == null ? void 0 : _a2.toLowerCase()) === "date";
  });
  const hasTextValidation = validationSettings.some((s) => {
    var _a2;
    return ((_a2 = s.validationType || s.ValidationType) == null ? void 0 : _a2.toLowerCase()) === "text";
  });
  if (hasDateValidation && isDateField) {
    const { settings: dateSettings, messages } = parseBackendDateSettings(validationSettings);
    const result = validateDate(value, dateSettings, messages);
    if (!result.isValid) {
      return result.message || ((_a = result.errors) == null ? void 0 : _a.join(", ")) || "Validation failed";
    }
  } else if (hasTextValidation && !isDateField) {
    const { settings: textSettings, messages } = parseBackendTextSettings(validationSettings);
    const result = validateText(value, textSettings, messages);
    if (!result.isValid) {
      return result.message || ((_b = result.errors) == null ? void 0 : _b.join(", ")) || "Validation failed";
    }
  }
  return null;
};
const decodeHtmlEntities = (text) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};
const TermsAndConditionsField = ({
  field,
  isRequired = false,
  isReadOnly = false,
  value,
  onChange,
  error,
  fieldName
}) => {
  const { t: translate } = useTranslation();
  const rawTermsText = field.sTermsandConditions || field.termsText || "";
  const termsText = useMemo(() => decodeHtmlEntities(translate(rawTermsText || "")), [rawTermsText, translate]);
  const isChecked = value === true || value === "true" || value === 1 || value === "1";
  const effectiveReadOnly = isReadOnly || ReadOnlyService.isReadOnly();
  const handleCheckboxChange = (e) => {
    if (!effectiveReadOnly) {
      const checked = e.checked || false;
      onChange(checked);
    }
  };
  const handleLabelClick = () => {
    if (!effectiveReadOnly) {
      onChange(!isChecked);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
      /* @__PURE__ */ jsx(
        YardiCheckbox,
        {
          checked: isChecked,
          onChange: handleCheckboxChange,
          label: "",
          disabled: effectiveReadOnly,
          "aria-required": isRequired,
          "aria-describedby": error ? `error_${field.hmy}` : void 0
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: `flex-1 text-gray-900 text-sm ${!effectiveReadOnly ? "cursor-pointer" : ""}`,
          onClick: handleLabelClick,
          children: [
            /* @__PURE__ */ jsx("style", { children: `
            .terms-content a {
              color: #1d4ed8;
              text-decoration: underline;
              font-weight: 500;
              cursor: pointer;
            }
            .terms-content a:hover {
              color: #1e40af;
            }
            .terms-content b,
            .terms-content strong {
              font-weight: 600;
              color: inherit;
            }
            .terms-content span {
              display: inline;
            }
            .terms-content p {
              margin-bottom: 0.5rem;
            }
            .terms-content p:last-child {
              margin-bottom: 0;
            }
          ` }),
            /* @__PURE__ */ jsx(
              "span",
              {
                dangerouslySetInnerHTML: { __html: termsText },
                className: "terms-content"
              }
            ),
            isRequired && /* @__PURE__ */ jsx("span", { className: "text-red-500 ml-1", children: "*" })
          ]
        }
      )
    ] }),
    error && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: translate(error) })
  ] });
};
const Progress = React.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn$2(
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      ProgressPrimitive.Indicator,
      {
        className: "h-full w-full flex-1 bg-primary transition-all",
        style: { transform: `translateX(-${100 - (value || 0)}%)` }
      }
    )
  }
));
Progress.displayName = ProgressPrimitive.Root.displayName;
const Sheet = SheetPrimitive.Root;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SheetPrimitive.Overlay,
  {
    className: cn$2(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
const SheetContent = React.forwardRef(({ side = "right", className, children, hideCloseButton = false, ...props }, ref) => /* @__PURE__ */ jsxs(SheetPortal, { children: [
  /* @__PURE__ */ jsx(SheetOverlay, {}),
  /* @__PURE__ */ jsxs(
    SheetPrimitive.Content,
    {
      ref,
      className: cn$2(sheetVariants({ side }), className),
      ...props,
      children: [
        children,
        !hideCloseButton && /* @__PURE__ */ jsxs(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary", children: [
          /* @__PURE__ */ jsx(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
SheetContent.displayName = SheetPrimitive.Content.displayName;
const SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn$2(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
const SheetTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SheetPrimitive.Title,
  {
    ref,
    className: cn$2("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SheetPrimitive.Description,
  {
    ref,
    className: cn$2("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn$2(
      "flex cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm outline-none focus:bg-[color-mix(in_srgb,var(--primary)_15%,white)] data-[state=open]:bg-accent cursor-pointer",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronRight, { className: "ml-auto h-4 w-4" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn$2(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, onKeyDown, ...props }, ref) => {
  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      event.currentTarget.dispatchEvent(
        new KeyboardEvent("keydown", {
          key: "Escape",
          code: "Escape",
          bubbles: true,
          cancelable: true
        })
      );
    }
    onKeyDown == null ? void 0 : onKeyDown(event);
  };
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Content,
    {
      ref,
      sideOffset,
      className: cn$2(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      onKeyDown: handleKeyDown,
      ...props
    }
  ) });
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn$2(
      "relative flex cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-[color-mix(in_srgb,var(--primary)_15%,white)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn$2(
      "relative flex cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-[color-mix(in_srgb,var(--primary)_15%,white)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", "aria-hidden": true, children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn$2(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-[color-mix(in_srgb,var(--primary)_15%,white)] focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", "aria-hidden": true, children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Circle, { className: "h-2 w-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn$2(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn$2("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const ConfigurableFormBreadcrumb = ({
  currentSectionId,
  sectionTitle = "",
  categoryLabel,
  Stepname,
  showRequired = true,
  isReadOnly = false,
  className = ""
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const customFormService = useCustomFormService();
  const [sections, setSections] = useState(customFormService.sections$.value || []);
  const completedSectionList = customFormService.completedSectionList || [];
  const [navigationPath, setNavigationPath] = useState(
    customFormService.navigationPath$.value || []
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const resolvedCategoryLabel = categoryLabel ?? Stepname ?? "Student Info";
  useEffect(() => {
    setSections(customFormService.sections$.value || []);
    const sub = customFormService.sections$.subscribe((next) => setSections(next || []));
    return () => sub.unsubscribe();
  }, [customFormService.sections$]);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  useEffect(() => {
    const subscription = customFormService.navigationPath$.subscribe((path) => {
      setNavigationPath(path || []);
    });
    return () => subscription.unsubscribe();
  }, [customFormService]);
  const sectionsToUse = sections.length > 0 ? sections : customFormService.sections$.value || [];
  const visibleSections = useMemo(() => {
    return sectionsToUse.filter((section) => {
      const isCustomTable = !!section.isCustomTable;
      if (isCustomTable) return true;
      const isVisibleByQuery = section.isVisibleByQuery !== false;
      const isVisibleForCountry = section.isVisibleForCountry !== false;
      return isVisibleByQuery && isVisibleForCountry;
    });
  }, [sectionsToUse]);
  const { steps, currentStepIndex, totalSteps, progressPercentage } = useMemo(() => {
    const currentSectionIdStr = String(currentSectionId || "");
    const sortedSections = [...visibleSections].sort((a, b) => {
      const aTable = !!a.isCustomTable;
      const bTable = !!b.isCustomTable;
      if (!aTable && bTable) return -1;
      if (aTable && !bTable) return 1;
      const orderA = Number(a.iorder ?? a.iOrder ?? a.sectionOrder ?? a._Order ?? 0);
      const orderB = Number(b.iorder ?? b.iOrder ?? b.sectionOrder ?? b._Order ?? 0);
      return orderA - orderB;
    });
    let currentIndex = -1;
    const progressSteps = sortedSections.map((section, index) => {
      const sectionIdStr = String(section.sectionId || section.id || "");
      const numericId = String(section.id || "");
      const customTableName = section.sCustomTableName;
      const isActive = sectionIdStr === currentSectionIdStr || currentSectionIdStr && customTableName && String(customTableName) === currentSectionIdStr;
      const isCompleted = section.completed || completedSectionList.includes(numericId);
      if (isActive) {
        currentIndex = index;
      }
      const rawSectionLabel = section.name || section.title || section.sectionName || section.sDisplayName || "";
      return {
        id: sectionIdStr,
        label: rawSectionLabel ? translate(rawSectionLabel) : "",
        completed: isCompleted,
        active: isActive,
        clickable: isCompleted && !isActive
      };
    });
    const total = progressSteps.length;
    const progress = total > 0 ? (currentIndex + 1) / total * 100 : 0;
    return {
      steps: progressSteps,
      currentStepIndex: currentIndex,
      totalSteps: total,
      progressPercentage: Math.min(progress, 100)
    };
  }, [visibleSections, currentSectionId, completedSectionList, translate]);
  const handleStepClick = useCallback(
    async (step) => {
      if (!step.clickable) return;
      setIsSheetOpen(false);
      const section = visibleSections.find((s) => String(s.sectionId || s.id || "") === step.id);
      if (!section) return;
      const clickedIndex = navigationPath.findIndex((id) => id === step.id);
      if (clickedIndex >= 0) {
        const newPath = navigationPath.slice(0, clickedIndex + 1);
        customFormService.navigationPath$.next(newPath);
      } else {
        const newPath = [...navigationPath, step.id];
        customFormService.navigationPath$.next(newPath);
      }
      const navigationResult = await customFormService.navigateToSection(
        section.sectionId || section.id,
        true
      );
      if (navigationResult) {
        navigate(navigationResult.url, { state: navigationResult.state });
      }
    },
    [visibleSections, navigationPath, customFormService, navigate]
  );
  const showProgress = visibleSections.length > 1;
  const StepListItem = ({
    step,
    index,
    onSelect
  }) => {
    const isCompleted = step.completed && !step.active;
    const isCurrent = step.active;
    const isUpcoming = !step.completed && !step.active;
    return /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: onSelect,
        disabled: !step.clickable,
        className: cn$2(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
          step.clickable && "hover:bg-accent cursor-pointer",
          !step.clickable && "cursor-default",
          isCurrent && "bg-primary/5"
        ),
        "aria-current": isCurrent ? "step" : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn$2(
                "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                isCompleted && "bg-primary text-primary-foreground",
                isCurrent && "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2",
                isUpcoming && "bg-muted text-muted-foreground"
              ),
              children: isCompleted ? /* @__PURE__ */ jsx(Check, { className: "w-3.5 h-3.5", "aria-hidden": "true" }) : /* @__PURE__ */ jsx("span", { children: index + 1 })
            }
          ),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn$2(
                "flex-1 text-sm truncate",
                isCompleted && "text-foreground",
                isCurrent && "text-foreground font-semibold",
                isUpcoming && "text-muted-foreground"
              ),
              children: step.label
            }
          ),
          isCurrent && /* @__PURE__ */ jsx("span", { className: "text-xs text-primary font-medium", children: translate("Current") })
        ]
      }
    );
  };
  const StepTrigger = () => /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors", children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm", children: translate("Step {{Current}} of {{Total}}", {
      Current: String(currentStepIndex + 1),
      Total: String(totalSteps)
    }) }),
    /* @__PURE__ */ jsx(
      ChevronDown,
      {
        className: cn$2("w-4 h-4 transition-transform", isSheetOpen && "rotate-180"),
        "aria-hidden": "true"
      }
    )
  ] });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$2("w-full mb-6", className),
      role: "banner",
      "aria-label": translate("Section header"),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-1", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs uppercase tracking-wider font-medium text-muted-foreground", children: resolvedCategoryLabel ? translate(resolvedCategoryLabel) : "" }),
          showRequired && !isReadOnly && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground uppercase", children: translate("*required") })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-3", children: [
          /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-foreground", children: sectionTitle ? translate(sectionTitle) : "" }),
          showProgress && /* @__PURE__ */ jsx(Fragment, { children: isMobile ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setIsSheetOpen(true),
                className: cn$2("focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"),
                "aria-expanded": isSheetOpen,
                "aria-haspopup": "dialog",
                children: /* @__PURE__ */ jsx(StepTrigger, {})
              }
            ),
            /* @__PURE__ */ jsx(Sheet, { open: isSheetOpen, onOpenChange: setIsSheetOpen, children: /* @__PURE__ */ jsxs(SheetContent, { side: "bottom", className: "max-h-[70vh] rounded-t-xl", children: [
              /* @__PURE__ */ jsx(SheetHeader, { className: "px-4 pt-4 pb-2 border-b", children: /* @__PURE__ */ jsx(SheetTitle, { className: "text-base", children: translate("Application Progress") }) }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "overflow-y-auto py-2",
                  role: "list",
                  "aria-label": translate("Form sections"),
                  children: steps.map((step, index) => /* @__PURE__ */ jsx(
                    StepListItem,
                    {
                      step,
                      index,
                      onSelect: () => handleStepClick(step)
                    },
                    step.id
                  ))
                }
              )
            ] }) })
          ] }) : /* @__PURE__ */ jsxs(DropdownMenu, { children: [
            /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { type: "button", className: cn$2("focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"), children: /* @__PURE__ */ jsx(StepTrigger, {}) }) }),
            /* @__PURE__ */ jsx(
              DropdownMenuContent,
              {
                align: "end",
                className: "w-[280px] max-h-[300px] overflow-y-auto p-0",
                sideOffset: 8,
                children: /* @__PURE__ */ jsx("div", { role: "list", "aria-label": translate("Form sections"), children: steps.map((step, index) => {
                  const isCompleted = step.completed && !step.active;
                  const isCurrent = step.active;
                  const isUpcoming = !step.completed && !step.active;
                  return /* @__PURE__ */ jsxs(
                    DropdownMenuItem,
                    {
                      onClick: () => handleStepClick(step),
                      disabled: !step.clickable,
                      className: cn$2(
                        "flex items-center gap-3 px-3 py-2.5 cursor-default",
                        step.clickable && "cursor-pointer",
                        isCurrent && "bg-primary/5"
                      ),
                      children: [
                        /* @__PURE__ */ jsx(
                          "span",
                          {
                            className: cn$2(
                              "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium",
                              isCompleted && "bg-primary text-primary-foreground",
                              isCurrent && "bg-primary text-primary-foreground",
                              isUpcoming && "bg-muted text-muted-foreground"
                            ),
                            children: isCompleted ? /* @__PURE__ */ jsx(Check, { className: "w-3 h-3", "aria-hidden": "true" }) : /* @__PURE__ */ jsx("span", { children: index + 1 })
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "span",
                          {
                            className: cn$2(
                              "flex-1 truncate",
                              isCompleted && "text-foreground",
                              isCurrent && "text-foreground font-medium",
                              isUpcoming && "text-muted-foreground"
                            ),
                            children: step.label
                          }
                        )
                      ]
                    },
                    step.id
                  );
                }) })
              }
            )
          ] }) })
        ] }),
        showProgress && /* @__PURE__ */ jsx(
          Progress,
          {
            value: progressPercentage,
            className: "h-1.5 bg-gray-200",
            "aria-label": translate("Progress: {{Current}} of {{Total}} steps", {
              Current: String(currentStepIndex + 1),
              Total: String(totalSteps)
            })
          }
        )
      ]
    }
  );
};
const SUGGESTION_DROPDOWN_HEIGHT = 240;
const GEO_HINT_COUNTRIES = ["CAN", "AUS", "DEU"];
const US_STYLE_COUNTRIES = ["USA"];
function looksLikePostcode(s) {
  if (!s || s.length > 12) return false;
  const t = s.trim();
  return /^\d{5}(-\d{4})?$/.test(t) || /^\d{4,6}$/.test(t) || /^\d{3}-\d{4}$/.test(t) || /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(t) || /^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/i.test(t) || /^[A-Z0-9\s-]{3,10}$/i.test(t);
}
function parseSpaceSeparatedAddress(addressText, country) {
  const out = { street_line: "", city: "", state: "", zipcode: "" };
  if (!addressText || !addressText.trim()) return out;
  const tokens = addressText.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return out;
  let postIdx = -1;
  for (let i = tokens.length - 1; i >= 0; i--) {
    const candidate = tokens[i];
    if (looksLikePostcode(candidate) && (!country || isValidPostcodeForCountry(candidate, country))) {
      out.zipcode = candidate;
      postIdx = i;
      break;
    }
  }
  if (postIdx < 0) return out;
  let stateIdx = postIdx - 1;
  if (stateIdx >= 0) {
    out.state = tokens[stateIdx];
    stateIdx--;
  }
  if (stateIdx >= 0) {
    out.city = tokens[stateIdx];
    stateIdx--;
  }
  let premise = "";
  let thoroughfare = "";
  if (postIdx >= 0) {
    const partsBeforePost = tokens.slice(0, postIdx);
    if (partsBeforePost.length > 0) {
      if (/\d+/.test(partsBeforePost[0])) {
        premise = partsBeforePost[0];
        thoroughfare = partsBeforePost.slice(1).join(" ");
      } else {
        thoroughfare = partsBeforePost.join(" ");
      }
    }
  }
  out.street_line = stateIdx >= 0 ? tokens.slice(0, stateIdx + 1).join(" ") : "";
  if (!out.street_line) out.street_line = addressText.trim();
  return { ...out, premise, thoroughfare };
}
function parseAddressTextToComponents(addressText, country) {
  var _a;
  const out = { street_line: "", city: "", state: "", zipcode: "" };
  if (!addressText || !addressText.trim()) return out;
  if (!addressText.includes(",") && /\d{3}-\d{4}|\d{5}(-\d{4})?/.test(addressText)) {
    const parsed = parseSpaceSeparatedAddress(addressText.trim(), country);
    if (parsed.zipcode) return parsed;
  }
  const parts = addressText.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return out;
  let lastIdx = parts.length - 1;
  const last = parts[lastIdx];
  if (looksLikePostcode(last) && (!country || isValidPostcodeForCountry(last, country))) {
    out.zipcode = last;
    lastIdx--;
  } else {
    const match = last.match(/^(.+?)\s+([A-Z0-9\s-]{3,10})$/i);
    const postcodeCandidate = (_a = match == null ? void 0 : match[2]) == null ? void 0 : _a.trim();
    if (match && postcodeCandidate && looksLikePostcode(postcodeCandidate) && (!country || isValidPostcodeForCountry(postcodeCandidate, country))) {
      out.zipcode = postcodeCandidate;
      const remainder = match[1].trim();
      if (remainder.length <= 50) {
        if (remainder.indexOf(" ") < 0 && remainder.length <= 20) out.state = remainder;
        else out.city = remainder;
      }
      lastIdx--;
    }
  }
  if (lastIdx >= 0 && !out.state) {
    out.state = parts[lastIdx];
    lastIdx--;
  }
  if (lastIdx >= 0 && !out.city) {
    out.city = parts[lastIdx];
    lastIdx--;
  }
  if (out.city && out.city.includes(" ") && !out.state) {
    const tokens = out.city.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
      out.city = tokens[tokens.length - 1];
      out.street_line = tokens.slice(0, -1).join(" ");
    }
  }
  if (out.city && out.city.includes(" ") && !out.state && !out.street_line) {
    const tokens = out.city.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
      out.state = tokens[tokens.length - 1];
      out.city = tokens[tokens.length - 2];
      out.street_line = tokens.slice(0, -2).join(" ");
    }
  }
  if (!out.street_line) out.street_line = lastIdx >= 0 ? parts.slice(0, lastIdx + 1).join(", ") : "";
  if (!out.street_line) out.street_line = addressText.trim();
  return out;
}
function normaliseAddressSuggestion(raw, isUS) {
  if (isUS) {
    const street2 = raw.street_line ?? "";
    const secondary2 = raw.secondary ?? "";
    const city2 = raw.city ?? "";
    const state2 = raw.state ?? "";
    const zipcode2 = raw.zipcode ?? "";
    const parts2 = [street2, secondary2, city2, state2, zipcode2].filter(Boolean);
    const addressText2 = raw.addressText ?? parts2.join(", ");
    return { addressText: addressText2, street_line: street2, secondary: secondary2 || void 0, city: city2, state: state2, zipcode: zipcode2 };
  }
  const street = raw.street_line ?? raw.street ?? raw.streetLine ?? raw.address1 ?? "";
  const secondary = raw.secondary ?? raw.sub_building ?? raw.subBuilding ?? raw.address2 ?? void 0;
  const city = raw.city ?? raw.locality ?? raw.Locality ?? raw.municipality ?? "";
  const state = raw.state ?? raw.administrative_area ?? raw.administrativeArea ?? raw.province ?? raw.region ?? "";
  const zipcode = raw.zipcode ?? raw.postal_code ?? raw.postalCode ?? raw.zip ?? "";
  const countryIso3 = raw.country_iso3 ?? raw.countryIso3 ?? void 0;
  const parts = [street, secondary, city, state, zipcode].filter(Boolean);
  const addressText = raw.addressText ?? raw.text ?? raw.label ?? parts.join(", ");
  const address_id = raw.address_id ?? void 0;
  return {
    addressText,
    street_line: street,
    secondary: secondary || void 0,
    city,
    state,
    zipcode,
    address_id,
    street,
    locality: city || void 0,
    administrative_area: state || void 0,
    postal_code: zipcode || void 0,
    sub_building: secondary || void 0,
    country_iso3: countryIso3
  };
}
const AddressSuggestionInput = ({
  value,
  onChange,
  onSuggestionSelect,
  placeholder = "",
  required = false,
  className = "",
  floatLabelType = "Never",
  cssClass = "e-outline",
  country = "USA",
  maxResults = 0
}) => {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const debounceTimer = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const isUS = US_STYLE_COUNTRIES.includes((country ?? "USA").toUpperCase());
  const textBoxRef = useRef(null);
  const blurOffInputRef = useRef(null);
  const [userDismissedSuggestions, setUserDismissedSuggestions] = useState(false);
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  useEffect(() => () => {
    var _a;
    return (_a = blurOffInputRef.current) == null ? void 0 : _a.call(blurOffInputRef);
  }, []);
  const handleInput = (args) => {
    const newValue = args.value;
    setUserDismissedSuggestions(false);
    setLocalValue(newValue);
    setSelectedIndex(-1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      onChange(newValue);
      if (newValue && newValue.length > 2) {
        setLoading(true);
        try {
          const countryUpper = (country ?? "USA").toUpperCase();
          const geoParam = GEO_HINT_COUNTRIES.includes(countryUpper) ? "&geolocation=on" : "";
          const maxResultsParam = maxResults > 0 ? `&max_results=${maxResults}` : "";
          const response = await getConfigurableFormsConfigOrThrow().apiClient.get(
            `cafeformtemplates/smartystreets/autocomplete?searchstring=${encodeURIComponent(newValue)}&country=${encodeURIComponent(country ?? "USA")}${geoParam}${maxResultsParam}`
          );
          const raw = response.data ?? [];
          setSuggestions(raw.map((item) => normaliseAddressSuggestion(item, isUS)));
        } catch (error) {
          console.error("Error fetching address suggestions:", error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
        setLoading(false);
      }
    }, 500);
  };
  const handleSuggestionSelect = async (suggestion) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r;
    setUserDismissedSuggestions(true);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    const needsResolve = suggestion.address_id && (country ?? "USA").toUpperCase() !== "USA" && (country ?? "USA").toUpperCase() !== "US" && !suggestion.street_line && !suggestion.city;
    if (needsResolve && suggestion.address_id && country) {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          address_id: suggestion.address_id,
          country
        });
        if ((_a = suggestion.addressText) == null ? void 0 : _a.trim()) params.set("address_text", suggestion.addressText.trim());
        const res = await getConfigurableFormsConfigOrThrow().apiClient.get(
          `cafeformtemplates/smartystreets/resolve?${params.toString()}`
        );
        let resolved = normaliseAddressSuggestion(res.data ?? {}, false);
        if (suggestion.addressText) {
          let recoveredStreet = suggestion.addressText.trim();
          const componentsToRemove = [resolved.zipcode, resolved.state, resolved.city, country];
          componentsToRemove.forEach((comp) => {
            if (comp && comp.length > 1) {
              const safeComp = comp.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              const regex = new RegExp(`[,\\s]*\\b${safeComp}\\b[,\\s]*`, "gi");
              recoveredStreet = recoveredStreet.replace(regex, ", ");
            }
          });
          recoveredStreet = recoveredStreet.replace(/^[,\\s]+|[,\\s]+$/g, "").replace(/,\s*,/g, ",").trim();
          if (recoveredStreet.length > (((_b = resolved.street_line) == null ? void 0 : _b.length) || 0)) {
            if (!resolved.street_line || recoveredStreet.toLowerCase().includes(resolved.street_line.toLowerCase()) || resolved.street_line.length < 5) {
              resolved.street_line = recoveredStreet;
            }
          }
        }
        const missingComponents = !((_c = resolved.street_line) == null ? void 0 : _c.trim()) || !((_d = resolved.city) == null ? void 0 : _d.trim()) || !((_e = resolved.state) == null ? void 0 : _e.trim());
        if (missingComponents && ((_f = suggestion.addressText) == null ? void 0 : _f.trim())) {
          const parsed = parseAddressTextToComponents(suggestion.addressText, country);
          resolved = {
            ...resolved,
            street_line: ((_g = resolved.street_line) == null ? void 0 : _g.trim()) || parsed.street_line || ((_h = resolved.addressText) == null ? void 0 : _h.trim()) || "",
            city: ((_i = resolved.city) == null ? void 0 : _i.trim()) || parsed.city || "",
            state: ((_j = resolved.state) == null ? void 0 : _j.trim()) || parsed.state || "",
            zipcode: ((_k = resolved.zipcode) == null ? void 0 : _k.trim()) || parsed.zipcode || "",
            addressText: ((_l = resolved.addressText) == null ? void 0 : _l.trim()) || suggestion.addressText.trim()
          };
        }
        const sanitized = {
          ...resolved,
          zipcode: ((_m = resolved.zipcode) == null ? void 0 : _m.trim()) && isValidPostcodeForCountry(resolved.zipcode, country) ? resolved.zipcode.trim() : "",
          state: ((_n = resolved.state) == null ? void 0 : _n.trim()) && isStateValidForCountry(resolved.state, country) ? resolved.state.trim() : "",
          city: ((_o = resolved.city) == null ? void 0 : _o.trim()) && isCityReasonableForCountry(resolved.city) ? resolved.city.trim() : ""
        };
        setLocalValue(sanitized.addressText);
        onSuggestionSelect(sanitized);
      } catch (err) {
        console.error("Resolve international address failed:", err);
        setLocalValue(suggestion.addressText);
        onSuggestionSelect(suggestion);
      } finally {
        setLoading(false);
      }
    } else {
      const toSelect = country ? {
        ...suggestion,
        zipcode: ((_p = suggestion.zipcode) == null ? void 0 : _p.trim()) && isValidPostcodeForCountry(suggestion.zipcode, country) ? suggestion.zipcode.trim() : "",
        state: ((_q = suggestion.state) == null ? void 0 : _q.trim()) && isStateValidForCountry(suggestion.state, country) ? suggestion.state.trim() : "",
        city: ((_r = suggestion.city) == null ? void 0 : _r.trim()) && isCityReasonableForCountry(suggestion.city) ? suggestion.city.trim() : ""
      } : suggestion;
      setLocalValue(toSelect.addressText);
      onSuggestionSelect(toSelect);
    }
  };
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setUserDismissedSuggestions(true);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };
  useEffect(() => {
    if (suggestions.length === 0) {
      setShowSuggestions(false);
      return;
    }
    if (userDismissedSuggestions) return;
    setShowSuggestions(true);
  }, [suggestions.length, userDismissedSuggestions]);
  const onTextBoxCreated = useCallback(() => {
    var _a, _b, _c;
    (_a = blurOffInputRef.current) == null ? void 0 : _a.call(blurOffInputRef);
    blurOffInputRef.current = null;
    const tb = textBoxRef.current;
    const input = (tb == null ? void 0 : tb.inputElement) ?? ((_c = (_b = tb == null ? void 0 : tb.element) == null ? void 0 : _b.querySelector) == null ? void 0 : _c.call(_b, "input")) ?? null;
    if (!input) return;
    const onBlur = () => {
      requestAnimationFrame(() => {
        const root = wrapperRef.current;
        const ae = document.activeElement;
        if (!root || !ae || !root.contains(ae)) {
          setUserDismissedSuggestions(true);
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
      });
    };
    input.addEventListener("blur", onBlur);
    blurOffInputRef.current = () => input.removeEventListener("blur", onBlur);
  }, []);
  useEffect(() => {
    if (!showSuggestions && !loading) return;
    const onDown = (e) => {
      var _a;
      const t = e.target;
      if (t && ((_a = wrapperRef.current) == null ? void 0 : _a.contains(t))) return;
      setUserDismissedSuggestions(true);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
    };
  }, [showSuggestions, loading]);
  useEffect(() => {
    if (!showSuggestions) return;
    const checkDropUp = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < SUGGESTION_DROPDOWN_HEIGHT + 16);
    };
    checkDropUp();
    window.addEventListener("resize", checkDropUp);
    window.addEventListener("scroll", checkDropUp, true);
    return () => {
      window.removeEventListener("resize", checkDropUp);
      window.removeEventListener("scroll", checkDropUp, true);
    };
  }, [showSuggestions]);
  useEffect(() => {
    var _a;
    if (selectedIndex >= 0 && showSuggestions) {
      const buttons = (_a = wrapperRef.current) == null ? void 0 : _a.querySelectorAll("button[data-suggestion]");
      if (buttons == null ? void 0 : buttons[selectedIndex]) {
        buttons[selectedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex, showSuggestions]);
  return /* @__PURE__ */ jsxs("div", { className: `relative ${className}`, ref: wrapperRef, onKeyDown: handleKeyDown, children: [
    /* @__PURE__ */ jsx("div", { ref: inputRef, className: "relative flex items-center w-full gap-1.5", children: /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(
      TextBoxComponent,
      {
        ref: textBoxRef,
        created: onTextBoxCreated,
        value: localValue,
        input: handleInput,
        placeholder,
        floatLabelType,
        cssClass,
        className: "w-full",
        htmlAttributes: { autocomplete: "off" }
      }
    ) }) }),
    country && !US_STYLE_COUNTRIES.includes((country ?? "").toUpperCase()) && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-xs mt-1", role: "note", children: "Address suggestions are shown in English only." }),
    loading && /* @__PURE__ */ jsx(
      "div",
      {
        className: `absolute z-10 w-full flex items-center justify-center bg-background h-12 border border-border rounded-md shadow-lg max-h-60 overflow-auto ${dropUp ? "bottom-full mb-1" : "mt-1"}`,
        style: dropUp ? { top: "auto" } : {},
        children: /* @__PURE__ */ jsxs("svg", { className: "animate-spin h-8 w-8 text-muted-foreground", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", children: [
          /* @__PURE__ */ jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
          /* @__PURE__ */ jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" })
        ] })
      }
    ),
    showSuggestions && suggestions.length > 0 && /* @__PURE__ */ jsx(
      "div",
      {
        className: `absolute z-10 w-full bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto ${dropUp ? "bottom-full mb-1" : "mt-1"}`,
        style: dropUp ? { top: "auto" } : {},
        children: suggestions.map((suggestion, index) => /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            "data-suggestion": "true",
            className: `w-full flex items-center px-4 py-2 text-left transition-colors ${index === selectedIndex ? "bg-primary text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground text-foreground"} focus:outline-none`,
            onClick: () => handleSuggestionSelect(suggestion),
            onMouseEnter: () => setSelectedIndex(index),
            children: [
              /* @__PURE__ */ jsx(
                FontAwesomeIcon,
                {
                  icon: faMapMarkerAlt,
                  className: `mr-3 w-4 h-4 flex-shrink-0 ${index === selectedIndex ? "text-primary-foreground" : "text-muted-foreground"}`
                }
              ),
              /* @__PURE__ */ jsx("span", { children: suggestion.addressText })
            ]
          },
          index
        ))
      }
    )
  ] });
};
const DEFAULT_ALLOWED_FILE_TYPES = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".tif", ".tiff"];
const DEFAULT_MAX_FILE_SIZE_MB = 10;
function normalizeAllowedFileTypes(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.map(normalizeExtension).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value.split(/[,\s;|]+/).map(normalizeExtension).filter(Boolean);
  }
  return [];
}
function normalizeExtension(value) {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return "";
  }
  return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
}
function getFileExtension(fileName) {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex >= 0 ? normalizeExtension(fileName.slice(lastDotIndex)) : "";
}
function normalizeUploadedFile(value) {
  if (!value || typeof value !== "object") {
    return null;
  }
  const uploadId = String(
    value.uploadId ?? value.UploadId ?? value.reference ?? value.Reference ?? value.DocumentId ?? value.VoyagerAttachmentId ?? value.pmdocsid ?? ""
  );
  if (!uploadId) {
    return null;
  }
  return {
    uploadId,
    reference: String(value.reference ?? value.Reference ?? uploadId),
    DocumentId: String(value.DocumentId ?? value.documentId ?? ""),
    VoyagerAttachmentId: String(value.VoyagerAttachmentId ?? value.voyagerAttachmentId ?? ""),
    pmdocsid: String(value.pmdocsid ?? value.PmDocsId ?? value.pmDocsId ?? ""),
    LocalDocumentId: String(value.LocalDocumentId ?? value.localDocumentId ?? ""),
    SetupId: String(value.SetupId ?? value.setupId ?? ""),
    ProspectId: String(value.ProspectId ?? value.prospectId ?? ""),
    fileName: String(value.fileName ?? value.FileName ?? "Uploaded file"),
    fileExt: String(value.fileExt ?? value.FileExt ?? ""),
    fileSize: Number(value.fileSize ?? value.FileSize ?? 0),
    contentType: value.contentType ?? value.ContentType ?? "",
    uploadedAtUtc: value.uploadedAtUtc ?? value.UploadedAtUtc ?? "",
    fieldHmy: value.fieldHmy ?? value.FieldHmy ?? "",
    columnName: value.columnName ?? value.ColumnName ?? "",
    storagePath: value.storagePath ?? value.StoragePath ?? ""
  };
}
function parseUploadedFiles(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(normalizeUploadedFile).filter(Boolean);
  }
  if (typeof value === "string") {
    if (!value.trim()) {
      return [];
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeUploadedFile).filter(Boolean);
      }
      const normalized2 = normalizeUploadedFile(parsed);
      return normalized2 ? [normalized2] : [];
    } catch {
      return [];
    }
  }
  const normalized = normalizeUploadedFile(value);
  return normalized ? [normalized] : [];
}
function hasLegacyDocumentIdentifiers(file) {
  return Boolean(
    String(file.pmdocsid ?? "").trim() && String(file.SetupId ?? "").trim()
  );
}
function normalizeUploadPreview(value) {
  if (!value || typeof value !== "object") {
    return null;
  }
  const nestedResponse = value.response && typeof value.response === "object" ? value.response : {};
  return {
    uploadId: String(value.uploadId ?? value.UploadId ?? ""),
    fileName: String(value.fileName ?? value.FileName ?? nestedResponse.FileName ?? ""),
    fileExt: String(value.fileExt ?? value.FileExt ?? nestedResponse.FileExt ?? ""),
    contentType: String(value.contentType ?? value.ContentType ?? ""),
    fileBytes: String(value.fileBytes ?? value.FileBytes ?? nestedResponse.FileBytes ?? ""),
    response: nestedResponse
  };
}
function getPreviewMimeType(fileExt, contentType) {
  if (contentType && contentType.trim()) {
    return contentType;
  }
  switch (String(fileExt ?? "").toLowerCase()) {
    case ".pdf":
      return "application/pdf";
    case ".jpg":
    case ".jpeg":
      return "image/jpeg";
    case ".gif":
      return "image/gif";
    case ".bmp":
      return "image/bmp";
    case ".tif":
    case ".tiff":
      return "image/tiff";
    case ".png":
      return "image/png";
    default:
      return "application/octet-stream";
  }
}
function downloadPreviewFile(preview) {
  const normalizedBase64 = String(preview.fileBytes ?? "").includes(",") ? String(preview.fileBytes).split(",").pop() || "" : String(preview.fileBytes ?? "");
  const binaryString = window.atob(normalizedBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  const blob = new Blob([bytes], {
    type: getPreviewMimeType(preview.fileExt, preview.contentType)
  });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = preview.fileName || `document${preview.fileExt || ""}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
}
function resolveAcceptedFileTypes(field, explicitValue) {
  var _a, _b;
  const fieldValue = explicitValue ?? field.allowedFileTypes ?? field.acceptedFileTypes ?? ((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.AcceptedFileTypes) ?? field.sField1;
  const allowedFileTypes = normalizeAllowedFileTypes(fieldValue);
  return allowedFileTypes.length > 0 ? allowedFileTypes : DEFAULT_ALLOWED_FILE_TYPES;
}
function resolveMaxFileSizeMb(field, explicitValue) {
  var _a, _b;
  const configuredValue = explicitValue ?? field.maxFileSizeMb ?? ((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.MaxFileSize) ?? field.maxFileSize ?? void 0;
  const parsedValue = Number(configuredValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : DEFAULT_MAX_FILE_SIZE_MB;
}
function resolveMaxFiles(field, explicitValue) {
  var _a, _b, _c, _d;
  const configuredValue = explicitValue ?? field.maxFiles ?? ((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.MaxFileCount) ?? field.maxDocumentCount ?? void 0;
  const parsedValue = Number(configuredValue);
  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }
  const allowMultiple = field.allowMultiple === true || ((_d = (_c = field.documents) == null ? void 0 : _c[0]) == null ? void 0 : _d.AllowMultipleUpload) === true;
  return allowMultiple ? 10 : 1;
}
const FileUploadField = ({
  field,
  value,
  formId,
  isRequired,
  isReadOnly,
  errorMessage,
  allowedFileTypes,
  maxFileSizeMb,
  maxFiles,
  uploadFile,
  loadExistingFiles,
  previewFile,
  deleteFile,
  onChange
}) => {
  const { t: translate } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState(() => parseUploadedFiles(value));
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(void 0);
  const [reviewingUploadId, setReviewingUploadId] = useState(null);
  const effectiveAllowedFileTypes = useMemo(
    () => resolveAcceptedFileTypes(field, allowedFileTypes),
    [allowedFileTypes, field]
  );
  const effectiveMaxFileSizeMb = useMemo(
    () => resolveMaxFileSizeMb(field, maxFileSizeMb),
    [field, maxFileSizeMb]
  );
  const effectiveMaxFiles = useMemo(
    () => resolveMaxFiles(field, maxFiles),
    [field, maxFiles]
  );
  const effectiveErrorMessage = errorMessage ?? (!isReadOnly && isRequired && uploadedFiles.length === 0 ? translate("Please upload at least one document.") : void 0);
  useEffect(() => {
    setUploadedFiles(parseUploadedFiles(value));
  }, [value]);
  useEffect(() => {
    const savedFiles = parseUploadedFiles(value);
    const requiresHydration = savedFiles.length === 0 || savedFiles.some((file) => !hasLegacyDocumentIdentifiers(file));
    if (!requiresHydration || !formId || !field.hmy || !loadExistingFiles) {
      return;
    }
    let isMounted = true;
    const loadFiles = async () => {
      try {
        const response = await loadExistingFiles({
          formId,
          fieldHmy: String(field.hmy),
          sectionId: String(field.hCafeFormSectionsID ?? "")
        });
        if (!isMounted) {
          return;
        }
        const existingFiles = ((response == null ? void 0 : response.Files) ?? (response == null ? void 0 : response.Documents) ?? []).map(normalizeUploadedFile).filter(Boolean);
        if (existingFiles.length > 0) {
          setUploadedFiles(existingFiles);
        }
      } catch (error) {
        console.warn("Failed to load existing uploaded files", error);
      }
    };
    loadFiles();
    return () => {
      isMounted = false;
    };
  }, [field.hCafeFormSectionsID, field.hmy, formId, loadExistingFiles, value]);
  const acceptValue = effectiveAllowedFileTypes.join(",");
  const canUploadMore = uploadedFiles.length < effectiveMaxFiles;
  const validateFile = (file) => {
    const lastDotIndex = file.name.lastIndexOf(".");
    const fileExtension = lastDotIndex >= 0 ? normalizeExtension(file.name.slice(lastDotIndex)) : "";
    const maxSizeBytes = effectiveMaxFileSizeMb * 1024 * 1024;
    if (!effectiveAllowedFileTypes.includes(fileExtension)) {
      return translate("Please select a valid file type: {{types}}.", {
        types: effectiveAllowedFileTypes.join(", ")
      });
    }
    if (file.size > maxSizeBytes) {
      return translate("File size must be less than {{maxFileSize}} MB.", {
        maxFileSize: effectiveMaxFileSizeMb
      });
    }
    return null;
  };
  const commitFiles = (nextFiles) => {
    setUploadedFiles(nextFiles);
    onChange(nextFiles);
  };
  const handleUpload = async (candidateFiles) => {
    var _a, _b, _c, _d;
    if (candidateFiles.length === 0 || isReadOnly || isUploading) {
      return;
    }
    const availableSlots = Math.max(effectiveMaxFiles - uploadedFiles.length, 0);
    const filesToUpload = candidateFiles.slice(0, availableSlots);
    if (filesToUpload.length === 0) {
      return;
    }
    const validationErrors = filesToUpload.map((file) => validateFile(file)).filter((message) => Boolean(message));
    if (validationErrors.length > 0) {
      toast$1({
        title: translate("Error"),
        description: validationErrors[0],
        variant: "destructive"
      });
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    const successfulUploads = [];
    try {
      for (let index = 0; index < filesToUpload.length; index += 1) {
        const file = filesToUpload[index];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("FileData", file, file.name);
        formData.append("fieldHmy", String(field.hmy ?? ""));
        formData.append("FieldHMY", String(field.hmy ?? ""));
        formData.append("columnName", field.sColumnName ?? "");
        formData.append("sectionId", String(field.hCafeFormSectionsID ?? ""));
        formData.append("formId", formId ?? "");
        formData.append("ProspectID", formId ?? "");
        formData.append("SetupID", String(((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.SetupId) ?? ""));
        formData.append("SetupTitle", field.sFieldName ?? "Document");
        formData.append("UploadTOtenant", "false");
        formData.append("BeforeOrAfterRequiredPage", "false");
        formData.append("bSendEmailToFSDocs", "false");
        formData.append("allowedExtensions", effectiveAllowedFileTypes.join(","));
        formData.append("maxFileSizeBytes", String(effectiveMaxFileSizeMb * 1024 * 1024));
        setUploadProgress(Math.round(index / filesToUpload.length * 100));
        const response = await uploadFile(formData);
        if ((response == null ? void 0 : response.Status) === false || (response == null ? void 0 : response.Success) === false) {
          throw new Error((response == null ? void 0 : response.Message) || translate("Upload failed."));
        }
        const normalizedUpload = normalizeUploadedFile({
          ...response,
          fileName: (response == null ? void 0 : response.fileName) ?? (response == null ? void 0 : response.FileName) ?? file.name,
          fileExt: (response == null ? void 0 : response.fileExt) ?? (response == null ? void 0 : response.FileExt) ?? getFileExtension(file.name),
          fileSize: (response == null ? void 0 : response.fileSize) ?? (response == null ? void 0 : response.FileSize) ?? file.size,
          contentType: (response == null ? void 0 : response.contentType) ?? (response == null ? void 0 : response.ContentType) ?? file.type,
          uploadId: (response == null ? void 0 : response.uploadId) ?? (response == null ? void 0 : response.UploadId) ?? (response == null ? void 0 : response.reference) ?? (response == null ? void 0 : response.Reference) ?? (response == null ? void 0 : response.DocumentId) ?? (response == null ? void 0 : response.VoyagerAttachmentId) ?? (response == null ? void 0 : response.pmdocsid) ?? `${field.hmy ?? "file"}-${Date.now()}-${index}`,
          DocumentId: (response == null ? void 0 : response.DocumentId) ?? "",
          VoyagerAttachmentId: (response == null ? void 0 : response.VoyagerAttachmentId) ?? "",
          pmdocsid: (response == null ? void 0 : response.pmdocsid) ?? "",
          SetupId: (response == null ? void 0 : response.SetupId) ?? ((_d = (_c = field.documents) == null ? void 0 : _c[0]) == null ? void 0 : _d.SetupId) ?? "",
          ProspectId: (response == null ? void 0 : response.ProspectId) ?? formId ?? "",
          FieldHmy: (response == null ? void 0 : response.FieldHmy) ?? (response == null ? void 0 : response.FieldHMY) ?? String(field.hmy ?? ""),
          ColumnName: (response == null ? void 0 : response.ColumnName) ?? field.sColumnName ?? ""
        });
        if (!normalizedUpload) {
          throw new Error(translate("Upload failed."));
        }
        successfulUploads.push(normalizedUpload);
        setUploadProgress(Math.round((index + 1) / filesToUpload.length * 100));
      }
      let nextFiles = [...uploadedFiles, ...successfulUploads];
      if (loadExistingFiles && formId && field.hmy) {
        try {
          const refreshedResponse = await loadExistingFiles({
            formId,
            fieldHmy: String(field.hmy),
            sectionId: String(field.hCafeFormSectionsID ?? "")
          });
          const refreshedFiles = ((refreshedResponse == null ? void 0 : refreshedResponse.Files) ?? (refreshedResponse == null ? void 0 : refreshedResponse.Documents) ?? []).map(normalizeUploadedFile).filter(Boolean);
          if (refreshedFiles.length > 0) {
            nextFiles = refreshedFiles;
          }
        } catch (refreshError) {
          console.warn("Failed to refresh uploaded files after upload", refreshError);
        }
      }
      commitFiles(nextFiles);
      toast$1({
        title: translate("Success"),
        description: translate(
          successfulUploads.length > 1 ? "Files uploaded successfully." : "File uploaded successfully."
        )
      });
    } catch (error) {
      toast$1({
        title: translate("Error"),
        description: error instanceof Error ? error.message : translate("Document upload failed."),
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(void 0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  const handleInputChange = async (event) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    await handleUpload(selectedFiles);
  };
  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragActive(false);
    await handleUpload(Array.from(event.dataTransfer.files ?? []));
  };
  const handlePreview = async (file) => {
    var _a, _b;
    try {
      let actionFile = {
        ...file,
        SetupId: file.SetupId ?? ((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.SetupId) ?? "",
        pmdocsid: file.pmdocsid ?? file.VoyagerAttachmentId ?? file.DocumentId ?? ""
      };
      if (!hasLegacyDocumentIdentifiers(actionFile) && loadExistingFiles && formId && field.hmy) {
        const refreshedResponse = await loadExistingFiles({
          formId,
          fieldHmy: String(field.hmy),
          sectionId: String(field.hCafeFormSectionsID ?? "")
        });
        const refreshedFiles = ((refreshedResponse == null ? void 0 : refreshedResponse.Files) ?? (refreshedResponse == null ? void 0 : refreshedResponse.Documents) ?? []).map(normalizeUploadedFile).filter(Boolean);
        const matchedFile = refreshedFiles.find(
          (refreshedFile) => refreshedFile.uploadId === file.uploadId || refreshedFile.fileName === file.fileName && refreshedFile.fileSize === file.fileSize
        );
        if (matchedFile) {
          actionFile = matchedFile;
          setUploadedFiles(refreshedFiles);
          onChange(refreshedFiles);
        }
      }
      setReviewingUploadId(file.uploadId);
      const response = await previewFile(actionFile);
      const normalizedPreview = normalizeUploadPreview(response);
      if (!(normalizedPreview == null ? void 0 : normalizedPreview.fileBytes)) {
        throw new Error("Preview payload is empty.");
      }
      downloadPreviewFile({
        ...normalizedPreview,
        fileName: normalizedPreview.fileName || actionFile.fileName,
        fileExt: normalizedPreview.fileExt || actionFile.fileExt || getFileExtension(actionFile.fileName),
        contentType: normalizedPreview.contentType || actionFile.contentType || ""
      });
    } catch (error) {
      toast$1({
        title: translate("Error"),
        description: translate("Failed to load document preview."),
        variant: "destructive"
      });
    } finally {
      setReviewingUploadId(null);
    }
  };
  const handleDelete = async (file) => {
    var _a, _b;
    try {
      let actionFile = {
        ...file,
        SetupId: file.SetupId ?? ((_b = (_a = field.documents) == null ? void 0 : _a[0]) == null ? void 0 : _b.SetupId) ?? "",
        pmdocsid: file.pmdocsid ?? file.VoyagerAttachmentId ?? file.DocumentId ?? ""
      };
      if (!hasLegacyDocumentIdentifiers(actionFile) && loadExistingFiles && formId && field.hmy) {
        const refreshedResponse = await loadExistingFiles({
          formId,
          fieldHmy: String(field.hmy),
          sectionId: String(field.hCafeFormSectionsID ?? "")
        });
        const refreshedFiles = ((refreshedResponse == null ? void 0 : refreshedResponse.Files) ?? (refreshedResponse == null ? void 0 : refreshedResponse.Documents) ?? []).map(normalizeUploadedFile).filter(Boolean);
        const matchedFile = refreshedFiles.find(
          (refreshedFile) => refreshedFile.uploadId === file.uploadId || refreshedFile.fileName === file.fileName && refreshedFile.fileSize === file.fileSize
        );
        if (matchedFile) {
          actionFile = matchedFile;
        }
      }
      await deleteFile(actionFile);
      commitFiles(uploadedFiles.filter((uploadedFile) => uploadedFile.uploadId !== file.uploadId));
      toast$1({
        title: translate("Success"),
        description: translate("Document deleted successfully.")
      });
    } catch (error) {
      toast$1({
        title: translate("Error"),
        description: translate("Failed to delete document."),
        variant: "destructive"
      });
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx("div", { className: `e-input-group e-control-wrapper e-outline ${errorMessage ? "e-error" : ""}`, children: /* @__PURE__ */ jsx("div", { className: "p-4 flex w-full", children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: `flex-1 w-full border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${isUploading ? "border-blue-400 bg-blue-50" : isReadOnly ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60" : isDragActive ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"}`,
        onDrop: isReadOnly ? void 0 : handleDrop,
        onDragOver: isReadOnly ? void 0 : (event) => event.preventDefault(),
        onDragEnter: isReadOnly ? void 0 : (event) => {
          event.preventDefault();
          setIsDragActive(true);
        },
        onDragLeave: isReadOnly ? void 0 : (event) => {
          event.preventDefault();
          setIsDragActive(false);
        },
        onClick: () => {
          var _a;
          return !isReadOnly && !isUploading && canUploadMore && ((_a = fileInputRef.current) == null ? void 0 : _a.click());
        },
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: fileInputRef,
              type: "file",
              className: "hidden",
              accept: acceptValue,
              multiple: effectiveMaxFiles > 1,
              onChange: handleInputChange,
              disabled: isReadOnly || isUploading || !canUploadMore
            }
          ),
          isUploading ? /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faSpinner, className: "w-8 h-8 text-blue-500 animate-spin" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-blue-600", children: translate("Uploading...") }),
            uploadProgress !== void 0 && /* @__PURE__ */ jsx("div", { className: "w-full bg-gray-200 rounded-full h-2", children: /* @__PURE__ */ jsx(
              "div",
              {
                className: "bg-blue-600 h-2 rounded-full transition-all duration-300",
                style: { width: `${uploadProgress}%` }
              }
            ) })
          ] }) : /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
            /* @__PURE__ */ jsx(
              FontAwesomeIcon,
              {
                icon: faFileArrowUp,
                className: `w-12 h-12 ${isReadOnly ? "text-gray-300" : "text-blue-500"}`
              }
            ),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("p", { className: `text-base font-medium ${isReadOnly ? "text-gray-400" : "text-gray-900"}`, children: isReadOnly ? translate("File upload disabled") : translate("Click to upload or drag and drop") }),
              /* @__PURE__ */ jsx("p", { className: `text-sm ${isReadOnly ? "text-gray-400" : "text-gray-600"}`, children: isReadOnly ? translate("Read-only mode") : translate("{{types}} up to {{maxFileSize}} MB.", {
                types: effectiveAllowedFileTypes.join(", ").toUpperCase(),
                maxFileSize: effectiveMaxFileSizeMb
              }) }),
              !canUploadMore && !isReadOnly && /* @__PURE__ */ jsx("p", { className: "text-sm text-orange-600 mt-2", children: translate("Maximum {{maxFiles}} file(s) allowed.", { maxFiles: effectiveMaxFiles }) })
            ] })
          ] })
        ]
      }
    ) }) }),
    uploadedFiles.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-medium text-gray-900", children: translate("Uploaded Documents") }),
      uploadedFiles.map((file) => /* @__PURE__ */ jsx(Card, { className: "p-3", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 min-w-0", children: [
          /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faFileLines, className: "w-5 h-5 text-gray-400" }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-gray-900 truncate", children: file.fileName }),
            /* @__PURE__ */ jsxs("p", { className: "text-xs text-gray-500", children: [
              (file.fileSize / 1024 / 1024).toFixed(2),
              " MB"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-xs", children: translate("Uploaded") }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => handlePreview(file),
              className: "text-blue-500 hover:text-blue-700 p-1",
              title: translate("Download document"),
              disabled: reviewingUploadId === file.uploadId,
              children: reviewingUploadId === file.uploadId ? /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faSpinner, className: "w-4 h-4 animate-spin" }) : /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faDownload, className: "w-4 h-4" })
            }
          ),
          !isReadOnly && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => handleDelete(file),
              className: "text-red-500 hover:text-red-700 p-1",
              title: translate("Delete document"),
              children: /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faTrashCan, className: "w-4 h-4" })
            }
          )
        ] })
      ] }) }, file.uploadId))
    ] }),
    effectiveErrorMessage && /* @__PURE__ */ jsx("p", { className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: effectiveErrorMessage })
  ] });
};
function getDropdownOptionDisplayLabel(option) {
  if (!option || typeof option !== "object") return "";
  const o = option;
  const candidates = [o.Name, o.name, o.sName, o.SName, o.text, o.label, o.Description, o.sDescription];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c;
  }
  return "";
}
function shouldTranslateDropdownOption(field, option) {
  const fieldName = ((field == null ? void 0 : field.sFieldName) || "").toLowerCase();
  if (fieldName.includes("country")) {
    return false;
  }
  if (!option || typeof option !== "object") {
    return true;
  }
  const record = option;
  return !(typeof record.sCode === "string" && typeof record.sName === "string");
}
function getDropdownOptionText(field, option, translate) {
  const rawLabel = getDropdownOptionDisplayLabel(option);
  if (!rawLabel) {
    return "";
  }
  return shouldTranslateDropdownOption(field, option) ? translate(rawLabel) : rawLabel;
}
function normalizeDropdownOptions(field, options, translate) {
  return options.map((option) => {
    const text = getDropdownOptionText(field, option, translate);
    return {
      ...option,
      Name: text,
      id: (option == null ? void 0 : option.Id) ?? (option == null ? void 0 : option.id) ?? ""
    };
  });
}
const DateFormatUtils$1 = {
  monthNames: {
    "jan": 1,
    "january": 1,
    "feb": 2,
    "february": 2,
    "mar": 3,
    "march": 3,
    "apr": 4,
    "april": 4,
    "may": 5,
    "jun": 6,
    "june": 6,
    "jul": 7,
    "july": 7,
    "aug": 8,
    "august": 8,
    "sep": 9,
    "september": 9,
    "oct": 10,
    "october": 10,
    "nov": 11,
    "november": 11,
    "dec": 12,
    "december": 12
  },
  monthNamesReverse: {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec"
  },
  isNumericKey: (keyCode) => {
    return keyCode >= 48 && keyCode <= 57 || keyCode >= 96 && keyCode <= 105;
  },
  isLetterKey: (keyCode) => {
    return keyCode >= 65 && keyCode <= 90;
  },
  isDeleteKey: (keyCode) => {
    return keyCode === 8 || keyCode === 46;
  },
  isSpaceKey: (keyCode) => {
    return keyCode === 32;
  },
  getKeyFromCode: (keyCode) => {
    if (keyCode >= 48 && keyCode <= 57) {
      return String.fromCharCode(keyCode);
    }
    if (keyCode >= 96 && keyCode <= 105) {
      return String.fromCharCode(keyCode - 48);
    }
    if (keyCode >= 65 && keyCode <= 90) {
      return String.fromCharCode(keyCode + 32);
    }
    if (keyCode === 32) {
      return " ";
    }
    return "";
  },
  insertCharAtPosition: (str, char, position) => {
    return str.slice(0, position) + char + str.slice(position);
  },
  parseFlexibleDate: (input, countryCode) => {
    if (!input.trim()) return null;
    const cleanInput = input.trim().toLowerCase().replace(/[,]/g, "");
    const getDateOrder = (country) => {
      const code = ((country == null ? void 0 : country.toLowerCase()) || "us").trim();
      return COUNTRY_DATE_FORMATS[code] ?? "DMY";
    };
    const dateOrder = countryCode ? getDateOrder(countryCode) : null;
    const patterns = [
      // MMM DD, YYYY (Aug 19, 1998) - Display format - Always allowed
      /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2}),?\s+(\d{4})$/,
      // DD MMM YYYY or D MMM YYYY (19 aug 1999, 5 jan 2020) - Always allowed
      /^(\d{1,2})\s+(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{4})$/,
      // MMM DD YYYY or MMM D YYYY (aug 19 1999, jan 5 2020) - Always allowed
      /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2})\s+(\d{4})$/,
      // YYYY MMM DD or YYYY MMM D (1999 aug 19, 2020 jan 5) - Always allowed
      /^(\d{4})\s+(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2})$/,
      // Numeric with separators (any separator: /, -, .) - MUST match country format
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
      // YYYY with separators (YYYY/MM/DD, YYYY-MM-DD, YYYY.MM.DD) - Only for Asian countries
      /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/
    ];
    for (let i = 0; i < patterns.length; i++) {
      const match = cleanInput.match(patterns[i]);
      if (match) {
        let day, month, year;
        switch (i) {
          case 0:
            month = DateFormatUtils$1.monthNames[match[1]] || 0;
            day = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            break;
          case 1:
            day = parseInt(match[1], 10);
            month = DateFormatUtils$1.monthNames[match[2]] || 0;
            year = parseInt(match[3], 10);
            break;
          case 2:
            month = DateFormatUtils$1.monthNames[match[1]] || 0;
            day = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            break;
          case 3:
            year = parseInt(match[1], 10);
            month = DateFormatUtils$1.monthNames[match[2]] || 0;
            day = parseInt(match[3], 10);
            break;
          case 4:
            if (!dateOrder) {
              continue;
            }
            const firstNum = parseInt(match[1], 10);
            const secondNum = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            if (dateOrder === "MDY") {
              month = firstNum;
              day = secondNum;
            } else if (dateOrder === "YMD") {
              continue;
            } else {
              day = firstNum;
              month = secondNum;
            }
            break;
          case 5:
            if (!dateOrder || dateOrder !== "YMD") {
              continue;
            }
            year = parseInt(match[1], 10);
            month = parseInt(match[2], 10);
            day = parseInt(match[3], 10);
            break;
          default:
            continue;
        }
        if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
          continue;
        }
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }
    return null;
  },
  formatDateForDisplay: (date) => {
    const month = DateFormatUtils$1.monthNamesReverse[date.getMonth() + 1];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  },
  formatMMDDYYYY: (numbers, separator) => {
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return numbers.slice(0, 2) + separator + numbers.slice(2);
    } else {
      return numbers.slice(0, 2) + separator + numbers.slice(2, 4) + separator + numbers.slice(4, 8);
    }
  },
  autoFormatDateInput: (input, cursorPos, separator = "/") => {
    if (/[a-zA-Z]/.test(input)) {
      const parsedDate = DateFormatUtils$1.parseFlexibleDate(input);
      if (parsedDate) {
        const formatted2 = DateFormatUtils$1.formatDateForDisplay(parsedDate);
        return { value: formatted2, cursorPosition: formatted2.length };
      }
      return { value: input, cursorPosition: cursorPos };
    }
    const numbersOnly = input.replace(/\D/g, "");
    if (!numbersOnly) {
      return { value: "", cursorPosition: 0 };
    }
    const formatted = DateFormatUtils$1.formatMMDDYYYY(numbersOnly, separator);
    const originalSeparators = (input.slice(0, cursorPos).match(/[^0-9]/g) || []).length;
    const formattedSeparators = (formatted.slice(0, cursorPos + (formatted.length - input.length)).match(/[^0-9]/g) || []).length;
    const newCursorPos = cursorPos + (formattedSeparators - originalSeparators);
    return { value: formatted, cursorPosition: newCursorPos };
  },
  isAlreadyFormatted: (value, separator = "/") => {
    if (!value) return false;
    if (/^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value)) {
      return true;
    }
    const hasLetters = /[a-zA-Z]/.test(value);
    const hasSeparators = value.includes(separator);
    return hasLetters || hasSeparators && DateFormatUtils$1.isValidDateFormat(value, separator);
  },
  isValidDateFormat: (value, separator = "/") => {
    if (/^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value)) {
      return true;
    }
    const parts = value.split(separator);
    return parts.length <= 3 && parts.every((part) => /^\d*$/.test(part));
  }
};
const CustomFormControlComponent = React__default.forwardRef(({
  Id,
  objectUUId = "",
  customAppData,
  showSaveButton = true,
  showPreviousButton = true,
  onSaveData,
  onModelChange,
  onAllSectionsCompleted,
  onSaveProgress,
  onSectionLoadStateChange,
  queryParams = [],
  stepID = 0,
  // StepID from getFormIDSectionID API - ONLY used for saving/getting section progress
  stepName
}, ref) => {
  var _a, _b, _c, _d;
  const { t: translate } = useTranslation();
  const phonePreferredCountries = useMemo(() => ["us", "ca", "gb", "au"], []);
  const normalizeCheckboxValue = useCallback((value) => {
    if (value === true || value === "true" || value === 1 || value === "1" || value === "Yes") {
      return "true";
    }
    if (value === false || value === "false" || value === 0 || value === "0" || value === "No") {
      return "false";
    }
    return String(value || "");
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const initialValuesRef = useRef({});
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);
  const [state, setState] = useState({
    warningMessage: "",
    bReadOnly: false,
    inProgress: false,
    errorMessage: "",
    hasDataLoaded: false,
    fields: {},
    customfields: { rowdata: [] },
    customdata: {},
    serverError: "",
    saving: false,
    editable: false,
    isPreview: false,
    HideEditSave: false,
    attachmentsOnly: false,
    pageCode: "",
    isTablePresent: false,
    tableColumns: [],
    tableItems: [],
    len: 1,
    // Single column layout for all screen sizes
    hiddenFields: [],
    nonHiddenFields: [],
    AddressLine: false,
    isFirstSection: false,
    showSaveButton,
    showPreviousButton,
    isCustomTable: false,
    sCustomTableName: "",
    ObjType: null,
    ObjPointer: null,
    foreigndb: null
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [initCountry, setInitCountry] = useState("");
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [dynamicFieldTypes, setDynamicFieldTypes] = useState({});
  React__default.useEffect(() => {
    console.log("📍 initCountry updated:", initCountry || "(none)");
  }, [initCountry]);
  const visibilityDataStore = useRef({});
  const getStateFieldLabelByCountry = useCallback((countryName, defaultLabel) => {
    if (!countryName) return defaultLabel || "State";
    const countryNameLower = countryName.toLowerCase().trim();
    const countryToLabelMap = {
      "united states": "State",
      "usa": "State",
      "us": "State",
      "mexico": "State",
      "mx": "State",
      "australia": "State",
      "au": "State",
      "canada": "Province",
      "ca": "Province",
      "united kingdom": "County",
      "uk": "County",
      "great britain": "County",
      "gb": "County",
      "ireland": "County",
      "ie": "County",
      "india": "State",
      "in": "State",
      "brazil": "State",
      "br": "State",
      "germany": "State",
      "de": "State",
      "france": "Region",
      "fr": "Region",
      "italy": "Region",
      "it": "Region",
      "spain": "Region",
      "es": "Region",
      "portugal": "Region",
      "pt": "Region",
      "japan": "Prefecture",
      "jp": "Prefecture",
      "china": "Province",
      "cn": "Province",
      "south korea": "Province",
      "korea": "Province",
      "kr": "Province",
      "thailand": "Province",
      "th": "Province",
      "vietnam": "Province",
      "vn": "Province",
      "philippines": "Province",
      "ph": "Province",
      "indonesia": "Province",
      "id": "Province",
      "malaysia": "State",
      "my": "State",
      "south africa": "Province",
      "za": "Province",
      "argentina": "Province",
      "ar": "Province",
      "chile": "Region",
      "cl": "Region",
      "colombia": "Department",
      "co": "Department",
      "peru": "Region",
      "pe": "Region",
      "russia": "Region",
      "ru": "Region",
      "poland": "Voivodeship",
      "pl": "Voivodeship",
      "ukraine": "Oblast",
      "ua": "Oblast",
      "turkey": "Province",
      "tr": "Province",
      "egypt": "Governorate",
      "eg": "Governorate",
      "saudi arabia": "Province",
      "sa": "Province",
      "united arab emirates": "Emirate",
      "uae": "Emirate",
      "ae": "Emirate",
      "qatar": "Municipality",
      "qa": "Municipality",
      "kuwait": "Governorate",
      "kw": "Governorate",
      "israel": "District",
      "il": "District",
      "pakistan": "Province",
      "pk": "Province",
      "bangladesh": "Division",
      "bd": "Division",
      "afghanistan": "Province",
      "af": "Province",
      "iran": "Province",
      "ir": "Province",
      "iraq": "Governorate",
      "iq": "Governorate",
      "new zealand": "Region",
      "nz": "Region",
      "switzerland": "Canton",
      "ch": "Canton",
      "belgium": "Province",
      "be": "Province",
      "netherlands": "Province",
      "nl": "Province",
      "austria": "State",
      "at": "State",
      "sweden": "County",
      "se": "County",
      "norway": "County",
      "no": "County",
      "denmark": "Region",
      "dk": "Region",
      "finland": "Region",
      "fi": "Region",
      "greece": "Region",
      "gr": "Region",
      "singapore": "Region",
      "sg": "Region",
      "hong kong": "Region",
      "hk": "Region",
      "taiwan": "County",
      "tw": "County"
    };
    let countryLabel = "State";
    if (countryToLabelMap[countryNameLower]) {
      countryLabel = countryToLabelMap[countryNameLower];
    } else {
      for (const [key, label] of Object.entries(countryToLabelMap)) {
        if (countryNameLower.includes(key) || key.includes(countryNameLower)) {
          countryLabel = label;
          break;
        }
      }
    }
    if (defaultLabel) {
      defaultLabel.toLowerCase();
      const stateRegex = /\bstate\b/gi;
      if (stateRegex.test(defaultLabel)) {
        return defaultLabel.replace(stateRegex, countryLabel);
      }
    }
    return countryLabel;
  }, []);
  const isCityOrStateField = useCallback((field) => {
    if (!field) return false;
    const name = (field.sFieldName || field.sColumnName || "").toLowerCase();
    return name.includes("city") || name.includes("town") || name.includes("municipality") || name.includes("locality") || name.includes("state") || name.includes("province") || name.includes("county") || name.includes("region") || name.includes("prefecture") || name.includes("department") || name.includes("emirate") || name.includes("district") || name.includes("governorate") || name.includes("oblast");
  }, []);
  const getDateFormatByCountry = useCallback((countryCode) => {
    return "dd MMM yyyy";
  }, []);
  const getDateOrderByCountry = useCallback((countryCode) => {
    const code = ((countryCode == null ? void 0 : countryCode.toLowerCase()) || "us").trim();
    return COUNTRY_DATE_FORMATS[code] ?? "DMY";
  }, []);
  const [pageData, setPageData] = useState(null);
  const [parentFieldSetup, setParentFieldSetup] = useState([]);
  const [fieldValidationSettings, setFieldValidationSettings] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const [isSectionHidden, setIsSectionHidden] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset,
    setError,
    clearErrors,
    control
  } = useForm();
  const formRef = useRef(null);
  const customFormService = useCustomFormService();
  const isConditionalShow = useCallback((fielduuid) => {
    var _a2, _b2, _c2, _d2;
    const parentSetup = parentFieldSetup.find((p) => p.hcafeFormFieldID === fielduuid);
    if (parentSetup) {
      if (parentSetup.hcafeFormFieldID !== parentSetup.hCafeFormParentFieldID) {
        const parentKey = `custom_${parentSetup.hCafeFormParentFieldID}`;
        const childField = (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.find((f) => f.hmy === fielduuid);
        const parentField = (_b2 = state.nonHiddenFields) == null ? void 0 : _b2.find((f) => String(f.hmy) === String(parentSetup.hCafeFormParentFieldID));
        const isGovIdTypeParent = ((parentField == null ? void 0 : parentField.sControlType) || "").toString().toLowerCase() === "drop down" && (() => {
          const n = ((parentField == null ? void 0 : parentField.sFieldName) || "").toString().toLowerCase();
          return n.includes("government") && n.includes("id") && n.includes("type");
        })();
        const isGovIdNumberConditional = !!childField && (childField.sControlType || "").toString().toLowerCase() === "text box" && isGovIdTypeParent;
        const currentValue = isGovIdNumberConditional ? getValues(parentKey) ?? state.customdata[parentKey] ?? state.customdata[parentKey.toLowerCase()] ?? ((_c2 = visibilityDataStore.current) == null ? void 0 : _c2[parentKey.toLowerCase()]) : state.customdata[parentKey];
        const expectedValue = parentSetup.ParentFieldValue;
        const normalizedCurrentValue = normalizeCheckboxValue(currentValue);
        const normalizedExpectedValue = normalizeCheckboxValue(expectedValue);
        let valuesMatch = normalizedCurrentValue === normalizedExpectedValue;
        if (isGovIdNumberConditional && !valuesMatch) {
          const isParentDropdown = ((parentField == null ? void 0 : parentField.sControlType) || "").toString().toLowerCase() === "drop down";
          if (isParentDropdown) {
            const options = ((_d2 = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[parentField.hmy]) == null ? void 0 : _d2.dropdownData) || (parentField == null ? void 0 : parentField.dropdownlist) || [];
            const norm = (s) => String(s ?? "").trim().toLowerCase();
            const getId = (opt) => (opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id);
            const getName = (opt) => (opt == null ? void 0 : opt.Name) ?? (opt == null ? void 0 : opt.name);
            const currentById = options.find((opt) => String(getId(opt)) === String(currentValue));
            const currentName = currentById ? getName(currentById) : "";
            const currentByName = typeof currentValue === "string" ? options.find((opt) => norm(getName(opt)) === norm(currentValue)) : void 0;
            const currentId = currentById ? getId(currentById) : currentByName ? getId(currentByName) : void 0;
            const expectedById = options.find((opt) => String(getId(opt)) === String(expectedValue));
            const expectedName = expectedById ? getName(expectedById) : "";
            const expectedByName = typeof expectedValue === "string" ? options.find((opt) => norm(getName(opt)) === norm(expectedValue)) : void 0;
            const expectedId = expectedById ? getId(expectedById) : expectedByName ? getId(expectedByName) : void 0;
            valuesMatch = currentId !== void 0 && expectedId !== void 0 && String(currentId) === String(expectedId) || !!norm(currentName) && !!norm(expectedName) && norm(currentName) === norm(expectedName) || typeof expectedValue === "string" && !!norm(currentName) && norm(currentName) === norm(expectedValue) || typeof currentValue === "string" && !!norm(expectedName) && norm(expectedName) === norm(currentValue);
          }
        }
        if (parentSetup.sparentfieldvsisbility === "visible") {
          return valuesMatch;
        } else if (parentSetup.sparentfieldvsisbility === "hide") {
          return !(currentValue === void 0 || valuesMatch);
        }
      }
    }
    return true;
  }, [parentFieldSetup, state.customdata, state.nonHiddenFields, normalizeCheckboxValue, getValues, dynamicFieldTypes]);
  const isConditionalRequired = useCallback((fielduuid) => {
    const parentSetup = parentFieldSetup.find((p) => p.CustomScreenFieldSetupUUID === fielduuid);
    if (parentSetup) {
      if (parentSetup.ParentFieldRequired === 1) {
        const currentValue = state.customdata[`custom_${parentSetup.hCafeFormParentFieldID}`];
        const expectedValue = parentSetup.ParentFieldValue;
        const normalizedCurrentValue = normalizeCheckboxValue(currentValue);
        const normalizedExpectedValue = normalizeCheckboxValue(expectedValue);
        return normalizedCurrentValue === normalizedExpectedValue;
      }
    }
    return false;
  }, [parentFieldSetup, state.customdata, normalizeCheckboxValue]);
  const isConditionalReadOnly = useCallback((fielduuid) => {
    const parentSetup = parentFieldSetup.find((p) => p.hcafeFormFieldID === fielduuid);
    if (parentSetup) {
      if (parentSetup.sparentfieldvsisbility === "readonly") {
        const currentValue = state.customdata[`custom_${parentSetup.hCafeFormParentFieldID}`];
        const expectedValue = parentSetup.ParentFieldValue;
        const normalizedCurrentValue = normalizeCheckboxValue(currentValue);
        const normalizedExpectedValue = normalizeCheckboxValue(expectedValue);
        return normalizedCurrentValue === normalizedExpectedValue;
      }
    }
    return false;
  }, [parentFieldSetup, state.customdata, normalizeCheckboxValue]);
  const handleModelChange = useCallback((fieldname) => {
    if (onModelChange) {
      onModelChange({ fieldname, data: state.customdata });
    }
  }, [state.customdata, onModelChange]);
  const getDisplayValue = useCallback((hmy) => {
    var _a2, _b2, _c2, _d2, _e, _f, _g;
    const fieldName = `custom_${hmy}`;
    let value = state.customdata[fieldName] ?? watch(fieldName);
    const currentField = (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.find((f) => f.hmy === hmy);
    const isMultiselect = currentField && (((_b2 = currentField.sControlType) == null ? void 0 : _b2.toLowerCase().includes("multi select")) || ((_c2 = currentField.sControlType) == null ? void 0 : _c2.toLowerCase()) === "multiselect" || ((_d2 = currentField.sControlType) == null ? void 0 : _d2.toLowerCase()) === "multiselect dropdown");
    if (isMultiselect) {
      if (value === void 0 || value === null || value === "undefined" || value === "null" || value === "") {
        return [];
      }
      if (!Array.isArray(value)) {
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      }
      return value;
    }
    value = value ?? "";
    if (value === void 0 || value === null || value === "undefined" || value === "null") {
      return "";
    }
    const isGovernmentIdTypeDropdown = currentField && (currentField.sControlType || "").toString().toLowerCase() === "drop down" && (() => {
      const n = (currentField.sFieldName || "").toString().toLowerCase();
      return n.includes("government") && n.includes("id") && n.includes("type");
    })();
    const isGovernmentIdNumberField = currentField && (currentField.sControlType || "").toString().toLowerCase() === "text box" && (() => {
      var _a3;
      const setup = parentFieldSetup.find((p) => String(p == null ? void 0 : p.hcafeFormFieldID) === String(hmy));
      if (!setup) return false;
      const parentField = (_a3 = state.nonHiddenFields) == null ? void 0 : _a3.find((f) => String(f == null ? void 0 : f.hmy) === String(setup == null ? void 0 : setup.hCafeFormParentFieldID));
      if (!parentField) return false;
      if (parentField.hCafeFormSectionsID !== currentField.hCafeFormSectionsID) return false;
      const isParentGovIdType = (parentField.sControlType || "").toString().toLowerCase() === "drop down" && (() => {
        const n = (parentField.sFieldName || "").toString().toLowerCase();
        return n.includes("government") && n.includes("id") && n.includes("type");
      })();
      return isParentGovIdType;
    })();
    if ((isGovernmentIdTypeDropdown || isGovernmentIdNumberField) && (value === "" || value === null || value === void 0)) {
      const modelValue = visibilityDataStore.current[fieldName.toLowerCase()];
      if (modelValue !== void 0 && modelValue !== null && modelValue !== "") {
        value = modelValue;
      }
    }
    if (isGovernmentIdNumberField && typeof value === "string") {
      const trimmedLower = value.trim().toLowerCase();
      if (trimmedLower === "undefined" || trimmedLower === "null") return "";
    }
    if (typeof value === "string" && value.startsWith("1900-01-01")) {
      return "";
    }
    if (currentField && ((_e = currentField.sControlType) == null ? void 0 : _e.toLowerCase()) === "date" && value) {
      if (typeof value === "string") {
        if (/^\d{1,2}\s+[A-Za-z]{3}\s+\d{4}$/.test(value)) {
          const parsedDate2 = DateFormatUtils$1.parseFlexibleDate(value);
          if (parsedDate2 && !isNaN(parsedDate2.getTime())) {
            return parsedDate2;
          }
        }
        const dateMatch = value.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const [, year, month, day] = dateMatch;
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        const parsedDate = DateFormatUtils$1.parseFlexibleDate(value);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
        const standardParsedDate = /* @__PURE__ */ new Date(value + "T00:00:00");
        if (!isNaN(standardParsedDate.getTime())) {
          return standardParsedDate;
        }
      }
    }
    if (currentField && ((_f = currentField.sControlType) == null ? void 0 : _f.toLowerCase()) === "drop down") {
      const optionsSource = ((_g = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[hmy]) == null ? void 0 : _g.dropdownData) || currentField.dropdownlist || [];
      if (!optionsSource.length) return value;
      const getOptionId = (option) => (option == null ? void 0 : option.id) ?? (option == null ? void 0 : option.Id);
      if (typeof value === "number") {
        const matchingOption = optionsSource.find(
          (option) => typeof getOptionId(option) === "string" && String(getOptionId(option)) === value.toString()
        );
        if (matchingOption) return value.toString();
      }
      if (typeof value === "string" && value.trim() !== "") {
        const matchingOption = optionsSource.find(
          (option) => typeof getOptionId(option) === "number" && String(getOptionId(option)) === value
        );
        if (matchingOption) return getOptionId(matchingOption);
        if (isGovernmentIdTypeDropdown) {
          const byName = optionsSource.find((option) => {
            const optName = (option == null ? void 0 : option.Name) ?? (option == null ? void 0 : option.name);
            return optName && String(optName).trim().toLowerCase() === value.trim().toLowerCase();
          });
          if (byName) return getOptionId(byName);
        }
        const isCountryDropdown = (currentField.sFieldName || "").toLowerCase().includes("country");
        if (isCountryDropdown) {
          const byCode = optionsSource.find((option) => {
            const optCode = (option == null ? void 0 : option.code) ?? (option == null ? void 0 : option.Code);
            return optCode && String(optCode).toLowerCase() === value.toLowerCase();
          });
          if (byCode) return getOptionId(byCode);
        }
      }
    }
    return value;
  }, [state.customdata, watch, state.nonHiddenFields, dynamicFieldTypes, visibilityDataStore, parentFieldSetup]);
  const getSelectedCountryCode = useCallback(() => {
    return initCountry || "us";
  }, [initCountry]);
  const getCountryNameForZipValidation = useCallback((zipField) => {
    var _a2, _b2;
    const countryFieldInSection = state.nonHiddenFields.find((f) => {
      var _a3;
      const isCountryField = (_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country");
      const sameSection = f.hCafeFormSectionsID === zipField.hCafeFormSectionsID;
      return isCountryField && sameSection;
    });
    if (countryFieldInSection) {
      const countryValue = state.customdata[`custom_${countryFieldInSection.hmy}`];
      if (countryValue) {
        if (((_a2 = countryFieldInSection.sControlType) == null ? void 0 : _a2.toLowerCase()) === "drop down" && ((_b2 = countryFieldInSection.dropdownlist) == null ? void 0 : _b2.length) > 0) {
          const selectedCountry = countryFieldInSection.dropdownlist.find(
            (country) => String(country.id) === String(countryValue) || String(country.Id) === String(countryValue)
          );
          if (selectedCountry == null ? void 0 : selectedCountry.name) {
            console.log("✅ Found country for zip validation:", selectedCountry.name);
            return selectedCountry.name;
          }
        } else {
          console.log("✅ Found country name for zip validation:", String(countryValue));
          return String(countryValue);
        }
      }
    }
    const allSections2 = customFormService.sections$.value || [];
    const currentSectionIndex = allSections2.findIndex((s) => s.sectionId === zipField.hCafeFormSectionsID);
    if (currentSectionIndex > 0) {
      for (let i = currentSectionIndex - 1; i >= 0; i--) {
        const prevSection = allSections2[i];
        if (prevSection.data) {
          const countryEntry = Object.entries(prevSection.data).find(
            ([key, value]) => key.toLowerCase().includes("country") && value !== void 0 && value !== ""
          );
          if (countryEntry) {
            const value = countryEntry[1];
            if (typeof value === "string" && value.trim() && isNaN(Number(value))) {
              console.log("✅ Found country name from previous section:", value.trim());
              return value.trim();
            } else {
              const anyCountryField = state.nonHiddenFields.find(
                (f) => {
                  var _a3, _b3;
                  return ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country")) && ((_b3 = f.dropdownlist) == null ? void 0 : _b3.length) > 0;
                }
              );
              if (anyCountryField == null ? void 0 : anyCountryField.dropdownlist) {
                const selectedCountry = anyCountryField.dropdownlist.find(
                  (country) => String(country.id) === String(value) || String(country.Id) === String(value)
                );
                if (selectedCountry == null ? void 0 : selectedCountry.name) {
                  console.log("✅ Found country from previous section:", selectedCountry.name);
                  return selectedCountry.name;
                }
              }
            }
          }
        }
      }
    }
    console.log("⚠️ No country found for zip validation, will use generic pattern");
    return "";
  }, [state.nonHiddenFields, state.customdata, customFormService.sections$]);
  const getLocaleFromCountryCode = useCallback((countryCode) => {
    if (!countryCode) return "en-US";
    const code = countryCode.toLowerCase().trim();
    const countryToLocaleMap = {
      "us": "en-US",
      "usa": "en-US",
      "ca": "en-CA",
      "canada": "en-CA",
      "mx": "es-MX",
      "mexico": "es-MX",
      "gb": "en-GB",
      "uk": "en-GB",
      "united kingdom": "en-GB",
      "in": "en-IN",
      "india": "en-IN",
      "cn": "zh-CN",
      "china": "zh-CN",
      "jp": "ja-JP",
      "japan": "ja-JP",
      "au": "en-AU",
      "australia": "en-AU",
      "nz": "en-NZ",
      "new zealand": "en-NZ",
      "de": "de-DE",
      "germany": "de-DE",
      "fr": "fr-FR",
      "france": "fr-FR",
      "it": "it-IT",
      "italy": "it-IT",
      "es": "es-ES",
      "spain": "es-ES",
      "nl": "nl-NL",
      "netherlands": "nl-NL",
      "be": "fr-BE",
      "belgium": "fr-BE",
      "at": "de-AT",
      "austria": "de-AT",
      "pt": "pt-PT",
      "portugal": "pt-PT",
      "ie": "en-IE",
      "ireland": "en-IE",
      "gr": "el-GR",
      "greece": "el-GR",
      "fi": "fi-FI",
      "finland": "fi-FI",
      "pl": "pl-PL",
      "poland": "pl-PL",
      "br": "pt-BR",
      "brazil": "pt-BR",
      "ar": "es-AR",
      "argentina": "es-AR",
      "il": "he-IL",
      "israel": "he-IL",
      "za": "en-ZA",
      "south africa": "en-ZA",
      "sg": "en-SG",
      "singapore": "en-SG",
      "hk": "zh-HK",
      "hong kong": "zh-HK",
      "ae": "ar-AE",
      "uae": "ar-AE",
      "united arab emirates": "ar-AE",
      "sa": "ar-SA",
      "saudi arabia": "ar-SA",
      "kw": "ar-KW",
      "kuwait": "ar-KW",
      "qa": "ar-QA",
      "qatar": "ar-QA",
      "eg": "ar-EG",
      "egypt": "ar-EG",
      "ru": "ru-RU",
      "russia": "ru-RU",
      "ch": "de-CH",
      "switzerland": "de-CH",
      "se": "sv-SE",
      "sweden": "sv-SE",
      "no": "nb-NO",
      "norway": "nb-NO",
      "dk": "da-DK",
      "denmark": "da-DK",
      "kr": "ko-KR",
      "south korea": "ko-KR",
      "korea": "ko-KR",
      "th": "th-TH",
      "thailand": "th-TH",
      "my": "ms-MY",
      "malaysia": "ms-MY",
      "id": "id-ID",
      "indonesia": "id-ID",
      "ph": "en-PH",
      "philippines": "en-PH",
      "vn": "vi-VN",
      "vietnam": "vi-VN",
      "pk": "ur-PK",
      "pakistan": "ur-PK",
      "bd": "bn-BD",
      "bangladesh": "bn-BD",
      "tr": "tr-TR",
      "turkey": "tr-TR",
      "cl": "es-CL",
      "chile": "es-CL",
      "co": "es-CO",
      "colombia": "es-CO",
      "pe": "es-PE",
      "peru": "es-PE"
    };
    return countryToLocaleMap[code] || "en-US";
  }, []);
  const getCurrencyCodeFromCountryCode = useCallback((countryCode) => {
    if (!countryCode) return "USD";
    const code = countryCode.toLowerCase().trim();
    const countryToCurrencyMap = {
      "us": "USD",
      "usa": "USD",
      "ca": "CAD",
      "canada": "CAD",
      "mx": "MXN",
      "mexico": "MXN",
      "gb": "GBP",
      "uk": "GBP",
      "united kingdom": "GBP",
      "in": "INR",
      "india": "INR",
      "cn": "CNY",
      "china": "CNY",
      "jp": "JPY",
      "japan": "JPY",
      "au": "AUD",
      "australia": "AUD",
      "nz": "NZD",
      "new zealand": "NZD",
      "de": "EUR",
      "germany": "EUR",
      "fr": "EUR",
      "france": "EUR",
      "it": "EUR",
      "italy": "EUR",
      "es": "EUR",
      "spain": "EUR",
      "nl": "EUR",
      "netherlands": "EUR",
      "be": "EUR",
      "belgium": "EUR",
      "at": "EUR",
      "austria": "EUR",
      "pt": "EUR",
      "portugal": "EUR",
      "ie": "EUR",
      "ireland": "EUR",
      "gr": "EUR",
      "greece": "EUR",
      "fi": "EUR",
      "finland": "EUR",
      "pl": "PLN",
      "poland": "PLN",
      "br": "BRL",
      "brazil": "BRL",
      "ar": "ARS",
      "argentina": "ARS",
      "il": "ILS",
      "israel": "ILS",
      "za": "ZAR",
      "south africa": "ZAR",
      "sg": "SGD",
      "singapore": "SGD",
      "hk": "HKD",
      "hong kong": "HKD",
      "ae": "AED",
      "uae": "AED",
      "united arab emirates": "AED",
      "sa": "SAR",
      "saudi arabia": "SAR",
      "kw": "KWD",
      "kuwait": "KWD",
      "qa": "QAR",
      "qatar": "QAR",
      "eg": "EGP",
      "egypt": "EGP",
      "ru": "RUB",
      "russia": "RUB",
      "ch": "CHF",
      "switzerland": "CHF",
      "se": "SEK",
      "sweden": "SEK",
      "no": "NOK",
      "norway": "NOK",
      "dk": "DKK",
      "denmark": "DKK",
      "kr": "KRW",
      "south korea": "KRW",
      "korea": "KRW",
      "th": "THB",
      "thailand": "THB",
      "my": "MYR",
      "malaysia": "MYR",
      "id": "IDR",
      "indonesia": "IDR",
      "ph": "PHP",
      "philippines": "PHP",
      "vn": "VND",
      "vietnam": "VND",
      "pk": "PKR",
      "pakistan": "PKR",
      "bd": "BDT",
      "bangladesh": "BDT",
      "tr": "TRY",
      "turkey": "TRY",
      "cl": "CLP",
      "chile": "CLP",
      "co": "COP",
      "colombia": "COP",
      "pe": "PEN",
      "peru": "PEN"
    };
    return countryToCurrencyMap[code] || "USD";
  }, []);
  const getCountryCodeForField = useCallback((field, defaultWhenMissing = "us") => {
    var _a2;
    if (initCountry) {
      return initCountry;
    }
    const countryFieldInSection = state.nonHiddenFields.find((f) => {
      var _a3, _b2;
      const isCountryField = (_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country");
      const sameSection = f.hCafeFormSectionsID === field.hCafeFormSectionsID;
      const isDropdown = ((_b2 = f.sControlType) == null ? void 0 : _b2.toLowerCase()) === "drop down";
      return isCountryField && sameSection && isDropdown;
    });
    if (countryFieldInSection) {
      const countryValue = state.customdata[`custom_${countryFieldInSection.hmy}`];
      if (countryValue && ((_a2 = countryFieldInSection.dropdownlist) == null ? void 0 : _a2.length) > 0) {
        const selectedCountry = countryFieldInSection.dropdownlist.find((country) => country.id === countryValue);
        if (selectedCountry) {
          let countryCode = selectedCountry.code || selectedCountry.Code || selectedCountry.sCode || selectedCountry.SCODE || selectedCountry.isoCode || selectedCountry.iso2 || selectedCountry.iso || selectedCountry.countryCode || selectedCountry.CountryCode;
          if (countryCode) {
            const normalizedCode = countryCode.toString().toLowerCase();
            const countryCodeMapping = {
              "uae": "ae",
              "UAE": "ae",
              // United Arab Emirates
              "uk": "gb",
              "UK": "gb",
              // United Kingdom
              "kp": "kp",
              "KP": "kp",
              // North Korea
              "kr": "kr",
              "KR": "kr",
              // South Korea
              // Territories and special regions
              "an": "an",
              "AN": "an",
              // Netherlands Antilles
              "aq": "aq",
              "AQ": "aq",
              // Antarctica
              "as": "as",
              "AS": "as",
              // American Samoa
              "ax": "ax",
              "AX": "ax",
              // Åland Islands
              "bv": "bv",
              "BV": "bv",
              // Bouvet Island
              "eh": "eh",
              "EH": "eh",
              // Western Sahara
              "sj": "sj",
              "SJ": "sj",
              // Svalbard and Jan Mayen
              "um": "um",
              "UM": "um",
              // US Minor Outlying Islands
              "pn": "pn",
              "PN": "pn"
              // Pitcairn
            };
            return countryCodeMapping[normalizedCode] || normalizedCode;
          }
          const countryName = selectedCountry.name || selectedCountry.Name;
          if (countryName) {
            const countryNameToCodeMap = {
              "united states": "us",
              "usa": "us",
              "us": "us",
              "united states of america": "us",
              "united states minor outlying islands": "um",
              "canada": "ca",
              "mexico": "mx",
              "united kingdom": "gb",
              "uk": "gb",
              "great britain": "gb",
              "india": "in",
              "china": "cn",
              "japan": "jp",
              "australia": "au",
              "new zealand": "nz",
              "germany": "de",
              "france": "fr",
              "italy": "it",
              "spain": "es",
              "brazil": "br",
              "argentina": "ar",
              "israel": "il",
              "south africa": "za",
              "singapore": "sg",
              "hong kong": "hk",
              "united arab emirates": "ae",
              "uae": "ae",
              "saudi arabia": "sa",
              "egypt": "eg",
              "russia": "ru",
              "poland": "pl",
              "netherlands": "nl",
              "belgium": "be",
              "switzerland": "ch",
              "austria": "at",
              "sweden": "se",
              "norway": "no",
              "denmark": "dk",
              "finland": "fi",
              "ireland": "ie",
              "portugal": "pt",
              "greece": "gr",
              "south korea": "kr",
              "korea": "kr",
              "thailand": "th",
              "malaysia": "my",
              "indonesia": "id",
              "philippines": "ph",
              "vietnam": "vn",
              "pakistan": "pk",
              "bangladesh": "bd",
              "turkey": "tr",
              "chile": "cl",
              "colombia": "co",
              "peru": "pe",
              // Territories
              "antarctica": "aq",
              "åland islands": "ax",
              "aland islands": "ax",
              "bouvet island": "bv",
              "western sahara": "eh",
              "svalbard and jan mayen": "sj",
              "pitcairn": "pn"
            };
            return countryNameToCodeMap[countryName.toLowerCase()] || "us";
          }
        }
      }
    }
    const allSections2 = customFormService.sections$.value || [];
    const currentSectionIndex = allSections2.findIndex((s) => s.sectionId === field.hCafeFormSectionsID);
    if (currentSectionIndex > 0) {
      for (let i = currentSectionIndex - 1; i >= 0; i--) {
        const prevSection = allSections2[i];
        if (prevSection.data) {
          const countryEntry = Object.entries(prevSection.data).find(
            ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
          );
          if (countryEntry) {
            const anyCountryField = state.nonHiddenFields.find(
              (f) => {
                var _a3, _b2;
                return ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country")) && ((_b2 = f.dropdownlist) == null ? void 0 : _b2.length) > 0;
              }
            );
            if (anyCountryField == null ? void 0 : anyCountryField.dropdownlist) {
              const selectedCountry = anyCountryField.dropdownlist.find((country) => country.id === countryEntry[1]);
              if (selectedCountry) {
                let countryCode = selectedCountry.code || selectedCountry.Code || selectedCountry.sCode || selectedCountry.SCODE || selectedCountry.isoCode || selectedCountry.iso2 || selectedCountry.iso || selectedCountry.countryCode || selectedCountry.CountryCode;
                if (countryCode) {
                  const normalizedCode = countryCode.toString().toLowerCase();
                  const countryCodeMapping = {
                    "uae": "ae",
                    "UAE": "ae",
                    // United Arab Emirates
                    "uk": "gb",
                    "UK": "gb",
                    // United Kingdom
                    "kp": "kp",
                    "KP": "kp",
                    // North Korea
                    "kr": "kr",
                    "KR": "kr",
                    // South Korea
                    // Territories and special regions
                    "an": "an",
                    "AN": "an",
                    // Netherlands Antilles
                    "aq": "aq",
                    "AQ": "aq",
                    // Antarctica
                    "as": "as",
                    "AS": "as",
                    // American Samoa
                    "ax": "ax",
                    "AX": "ax",
                    // Åland Islands
                    "bv": "bv",
                    "BV": "bv",
                    // Bouvet Island
                    "eh": "eh",
                    "EH": "eh",
                    // Western Sahara
                    "sj": "sj",
                    "SJ": "sj",
                    // Svalbard and Jan Mayen
                    "um": "um",
                    "UM": "um",
                    // US Minor Outlying Islands
                    "pn": "pn",
                    "PN": "pn"
                    // Pitcairn
                  };
                  return countryCodeMapping[normalizedCode] || normalizedCode;
                }
                const countryName = selectedCountry.name || selectedCountry.Name;
                if (countryName) {
                  const countryNameToCodeMap = {
                    "united states": "us",
                    "usa": "us",
                    "us": "us",
                    "united states of america": "us",
                    "united states minor outlying islands": "um",
                    "canada": "ca",
                    "mexico": "mx",
                    "united kingdom": "gb",
                    "uk": "gb",
                    "great britain": "gb",
                    "india": "in",
                    "china": "cn",
                    "japan": "jp",
                    "australia": "au",
                    "new zealand": "nz",
                    "germany": "de",
                    "france": "fr",
                    "italy": "it",
                    "spain": "es",
                    "brazil": "br",
                    "argentina": "ar",
                    "israel": "il",
                    "south africa": "za",
                    "singapore": "sg",
                    "hong kong": "hk",
                    "united arab emirates": "ae",
                    "uae": "ae",
                    "saudi arabia": "sa",
                    "egypt": "eg",
                    "russia": "ru",
                    "poland": "pl",
                    "netherlands": "nl",
                    "belgium": "be",
                    "switzerland": "ch",
                    "austria": "at",
                    "sweden": "se",
                    "norway": "no",
                    "denmark": "dk",
                    "finland": "fi",
                    "ireland": "ie",
                    "portugal": "pt",
                    "greece": "gr",
                    "south korea": "kr",
                    "korea": "kr",
                    "thailand": "th",
                    "malaysia": "my",
                    "indonesia": "id",
                    "philippines": "ph",
                    "vietnam": "vn",
                    "pakistan": "pk",
                    "bangladesh": "bd",
                    "turkey": "tr",
                    "chile": "cl",
                    "colombia": "co",
                    "peru": "pe",
                    // Territories
                    "antarctica": "aq",
                    "åland islands": "ax",
                    "aland islands": "ax",
                    "bouvet island": "bv",
                    "western sahara": "eh",
                    "svalbard and jan mayen": "sj",
                    "pitcairn": "pn"
                  };
                  return countryNameToCodeMap[countryName.toLowerCase()] || "us";
                }
              }
            }
          }
        }
      }
    }
    return defaultWhenMissing;
  }, [initCountry, state.nonHiddenFields, state.customdata, customFormService.sections$]);
  const getIso2FromConfigurableCountryId = useCallback((countryId, dropdownField) => {
    var _a2;
    if (countryId === null || countryId === void 0 || countryId === "") return null;
    const raw = String(countryId).trim();
    if (!raw) return null;
    const codeKeyMap = {
      uae: "ae",
      uk: "gb",
      kp: "kp",
      kr: "kr",
      an: "an",
      aq: "aq",
      as: "as",
      ax: "ax",
      bv: "bv",
      eh: "eh",
      sj: "sj",
      um: "um",
      pn: "pn"
    };
    if (/^[a-zA-Z]{2}$/.test(raw)) {
      const lc = raw.toLowerCase();
      return codeKeyMap[lc] || lc;
    }
    if (!((_a2 = dropdownField == null ? void 0 : dropdownField.dropdownlist) == null ? void 0 : _a2.length)) return null;
    const selectedCountry = dropdownField.dropdownlist.find(
      (country) => country.id === countryId || country.Id === countryId || String(country.id) === String(countryId)
    );
    if (!selectedCountry) return null;
    let countryCode = selectedCountry.code || selectedCountry.Code || selectedCountry.sCode || selectedCountry.SCODE || selectedCountry.isoCode || selectedCountry.iso2 || selectedCountry.iso || selectedCountry.countryCode || selectedCountry.CountryCode;
    if (countryCode) {
      const normalizedCode = countryCode.toString().toLowerCase();
      return codeKeyMap[normalizedCode] || normalizedCode;
    }
    const countryName = selectedCountry.name || selectedCountry.Name;
    if (!countryName) return null;
    const countryNameToCodeMap = {
      "united states": "us",
      usa: "us",
      us: "us",
      "united states of america": "us",
      "united states minor outlying islands": "um",
      canada: "ca",
      mexico: "mx",
      "united kingdom": "gb",
      uk: "gb",
      "great britain": "gb",
      india: "in",
      china: "cn",
      japan: "jp",
      australia: "au",
      "new zealand": "nz",
      germany: "de",
      france: "fr",
      italy: "it",
      spain: "es",
      brazil: "br",
      argentina: "ar",
      israel: "il",
      "south africa": "za",
      singapore: "sg",
      "hong kong": "hk",
      "united arab emirates": "ae",
      uae: "ae",
      "saudi arabia": "sa",
      egypt: "eg",
      russia: "ru",
      poland: "pl",
      netherlands: "nl",
      belgium: "be",
      switzerland: "ch",
      austria: "at",
      sweden: "se",
      norway: "no",
      denmark: "dk",
      finland: "fi",
      ireland: "ie",
      portugal: "pt",
      greece: "gr",
      "south korea": "kr",
      korea: "kr",
      thailand: "th",
      malaysia: "my",
      indonesia: "id",
      philippines: "ph",
      vietnam: "vn",
      pakistan: "pk",
      bangladesh: "bd",
      turkey: "tr",
      chile: "cl",
      colombia: "co",
      peru: "pe",
      antarctica: "aq",
      "åland islands": "ax",
      "aland islands": "ax",
      "bouvet island": "bv",
      "western sahara": "eh",
      "svalbard and jan mayen": "sj",
      pitcairn: "pn"
    };
    return countryNameToCodeMap[countryName.toLowerCase()] || null;
  }, []);
  const getCurrencyCodeForField = useCallback((field) => {
    const countryCode = getCountryCodeForField(field);
    return getCurrencyCodeFromCountryCode(countryCode);
  }, [getCountryCodeForField, getCurrencyCodeFromCountryCode]);
  const getLocaleForField = useCallback((field) => {
    const countryCode = getCountryCodeForField(field);
    return getLocaleFromCountryCode(countryCode);
  }, [getCountryCodeForField, getLocaleFromCountryCode]);
  const isSectionVisibleByQuery = useCallback((section, savedFieldData) => {
    if (!section.hideQuerySettings) {
      return true;
    }
    const { fieldId, expectedValue, condition, calendarCondition, action } = section.hideQuerySettings;
    const actualValue = savedFieldData[`custom_${fieldId}`];
    const conditionCode = condition ? parseInt(condition.toString()) : 3;
    const calendarCode = calendarCondition ? parseInt(calendarCondition.toString()) : 0;
    const condName = conditionCode === 1 ? "<" : conditionCode === 2 ? ">" : "=";
    const calName = calendarCode === 1 ? "Years" : calendarCode === 2 ? "Months" : calendarCode === 3 ? "Days" : "None";
    console.log(`  Checking field custom_${fieldId}: actual="${actualValue}", expected="${expectedValue}", condition=${conditionCode}(${condName}), calendarCondition=${calendarCode}(${calName}), action=${action}`);
    if (actualValue === void 0 || actualValue === null || actualValue === "") {
      const iAction2 = action !== void 0 ? action : 0;
      console.log(`  Field value is empty, returning default visibility (action=${iAction2}): ${iAction2 === 0 ? "visible" : "hidden"}`);
      return iAction2 === 0 ? true : false;
    }
    let conditionMet = false;
    if (calendarCode > 0) {
      try {
        const actualDate = new Date(actualValue);
        const today = /* @__PURE__ */ new Date();
        const expectedNum = parseFloat(expectedValue);
        console.log(`  📅 Using calendar calculation: actualDate=${actualDate.toISOString()}, today=${today.toISOString()}, expected=${expectedNum} ${calName}`);
        if (!isNaN(actualDate.getTime()) && !isNaN(expectedNum)) {
          let diffValue = 0;
          switch (calendarCode) {
            case 1:
              diffValue = today.getFullYear() - actualDate.getFullYear();
              break;
            case 2:
              diffValue = (today.getFullYear() - actualDate.getFullYear()) * 12 + (today.getMonth() - actualDate.getMonth());
              break;
            case 3:
              diffValue = Math.floor((today.getTime() - actualDate.getTime()) / (1e3 * 60 * 60 * 24));
              break;
          }
          console.log(`  📅 Calculated difference: ${diffValue} ${calName}`);
          switch (conditionCode) {
            case 1:
              conditionMet = diffValue < expectedNum;
              break;
            // <
            case 2:
              conditionMet = diffValue > expectedNum;
              break;
            // >
            case 3:
              conditionMet = diffValue === expectedNum;
              break;
          }
          console.log(`  📅 Condition check: ${diffValue} ${condName} ${expectedNum} = ${conditionMet}`);
        } else {
          console.warn(`  ⚠️ Invalid date or expected value: actualDate valid=${!isNaN(actualDate.getTime())}, expectedNum valid=${!isNaN(expectedNum)}`);
        }
      } catch (error) {
        console.error(`  ❌ Error in calendar calculation:`, error);
        return true;
      }
    } else {
      const looksLikeDate = typeof actualValue === "string" && (actualValue.includes("T") || actualValue.includes("-") || actualValue.match(/^\d{4}-\d{2}-\d{2}/) || !isNaN(Date.parse(actualValue)));
      if (looksLikeDate && !isNaN(parseFloat(expectedValue))) {
        console.log(`  ⚠️ Field value looks like a date but calendarCondition is not set. Attempting calendar calculation anyway...`);
        try {
          const actualDate = new Date(actualValue);
          const today = /* @__PURE__ */ new Date();
          const expectedNum = parseFloat(expectedValue);
          if (!isNaN(actualDate.getTime()) && !isNaN(expectedNum)) {
            const diffMonths = (today.getFullYear() - actualDate.getFullYear()) * 12 + (today.getMonth() - actualDate.getMonth());
            console.log(`  📅 Auto-detected date field: calculated ${diffMonths} months difference`);
            switch (conditionCode) {
              case 1:
                conditionMet = diffMonths < expectedNum;
                break;
              case 2:
                conditionMet = diffMonths > expectedNum;
                break;
              case 3:
                conditionMet = diffMonths === expectedNum;
                break;
            }
            console.log(`  📅 Auto-calendar condition check: ${diffMonths} ${condName} ${expectedNum} = ${conditionMet}`);
          }
        } catch (error) {
          console.warn(`  ⚠️ Failed to parse as date, falling back to string comparison:`, error);
        }
      }
      if (!looksLikeDate || isNaN(Date.parse(actualValue))) {
        console.log(`  🔢 Using non-calendar comparison (numeric/string)`);
        const actualNum = parseFloat(actualValue);
        const expectedNum = parseFloat(expectedValue);
        const isNumericComparison = !isNaN(actualNum) && !isNaN(expectedNum);
        if (isNumericComparison) {
          switch (conditionCode) {
            case 1:
              conditionMet = actualNum < expectedNum;
              break;
            case 2:
              conditionMet = actualNum > expectedNum;
              break;
            case 3:
              conditionMet = actualNum === expectedNum;
              break;
          }
          console.log(`  🔢 Numeric comparison: ${actualNum} ${condName} ${expectedNum} = ${conditionMet}`);
        } else {
          conditionMet = (actualValue == null ? void 0 : actualValue.toString()) === (expectedValue == null ? void 0 : expectedValue.toString());
          console.log(`  🔢 String comparison: "${actualValue}" === "${expectedValue}" = ${conditionMet}`);
        }
      }
    }
    const iAction = action !== void 0 ? action : 0;
    const finalVisibility = iAction === 0 ? !conditionMet : conditionMet;
    console.log(`  ✅ Final visibility: ${finalVisibility ? "VISIBLE" : "HIDDEN"} (conditionMet=${conditionMet}, action=${iAction} ${iAction === 0 ? "=Hide when met" : "=Show when met"})`);
    return finalVisibility;
  }, []);
  const isSectionVisibleForCountry = useCallback((section, countryName) => {
    if (!countryName) {
      return true;
    }
    const normalizedCountryName = countryName.toLowerCase().trim();
    if (section.sectionCountrySettings && section.sectionCountrySettings.length > 0) {
      const setting = section.sectionCountrySettings.find(
        (s) => s.countryName.toLowerCase().trim() === normalizedCountryName
      );
      if (setting) {
        console.log(`📍 Section "${section.sectionName}" visibility for "${countryName}":`, setting.isVisible);
        return setting.isVisible;
      }
      console.log(`📍 Section "${section.sectionName}" has no specific setting for "${countryName}", showing by default`);
    }
    return true;
  }, []);
  const [visibilitySettingsLoaded, setVisibilitySettingsLoaded] = useState(false);
  const govIdTypeHydrationProcessedRef = useRef("");
  const govIdLabelHydrationProcessedRef = useRef("");
  const govIdConditionalHydrationProcessedRef = useRef("");
  useEffect(() => {
    const loadVisibilitySettings = async () => {
      var _a2, _b2;
      if (visibilitySettingsLoaded) return;
      const templateId = customFormService.getTemplateId();
      if (!templateId) return;
      const allSections2 = customFormService.sections$.value || [];
      if (allSections2.length === 0) return;
      try {
        const sectionIds = allSections2.map((s) => s.id).filter((id) => id).join(",");
        if (!sectionIds) return;
        console.log("🔍 Loading visibility settings for all sections (ONE-TIME CALL)");
        setVisibilitySettingsLoaded(true);
        const [hideQuerySettings, countrySettingsData] = await Promise.all([
          configurableFormsApi.getSectionHideQuerySettings(sectionIds),
          configurableFormsApi.getCountrySettingsData(Number(templateId), sectionIds, "")
        ]);
        if (hideQuerySettings && hideQuerySettings.length > 0) {
          console.log("✅ Loaded hide query settings:", hideQuerySettings);
          allSections2.forEach((section) => {
            const setting = hideQuerySettings.find((s) => {
              var _a3;
              return s.sectionId === ((_a3 = section.id) == null ? void 0 : _a3.toString());
            });
            if (setting) {
              section.hideQuerySettings = {
                fieldId: setting.fieldId,
                fieldName: setting.fieldName,
                expectedValue: setting.expectedValue,
                condition: setting.condition,
                // sCondition: 1=<, 2=>, 3==
                calendarCondition: setting.calendarCondition,
                // sCalendarCondition: 1=Years, 2=Months, 3=Days
                action: setting.action,
                // iAction: 0=Hide when met, 1=Show when met
                hideIfNotMatch: setting.hideIfNotMatch
                // Derived from iAction
              };
              const condName = setting.condition == 1 ? "<" : setting.condition == 2 ? ">" : "=";
              const calName = setting.calendarCondition == 1 ? "Years" : setting.calendarCondition == 2 ? "Months" : setting.calendarCondition == 3 ? "Days" : "None";
              const actName = setting.action === 0 ? "Hide" : "Show";
              console.log(`  Applied hide query to "${section.sectionName}": fieldId=${setting.fieldId}, expectedValue="${setting.expectedValue}", ${calName} (${setting.calendarCondition || "not set"}), ${condName}, ${actName} when condition met`);
            }
          });
        }
        if (countrySettingsData && countrySettingsData.length > 0) {
          console.log("✅ Loaded country settings:", countrySettingsData);
          const settingsBySection = countrySettingsData.filter((s) => s.ObjectType === 2).reduce((acc, setting) => {
            var _a3;
            const sectionId = (_a3 = setting.ObjectPointer) == null ? void 0 : _a3.toString();
            if (!acc[sectionId]) {
              acc[sectionId] = [];
            }
            acc[sectionId].push({
              countryId: setting.Country,
              countryName: "",
              isVisible: true
            });
            return acc;
          }, {});
          allSections2.forEach((section) => {
            if (section.id && settingsBySection[section.id.toString()]) {
              section.sectionCountrySettings = settingsBySection[section.id.toString()];
              console.log(`  Applied country settings to section "${section.sectionName}"`);
            }
          });
        }
        allSections2.forEach((section) => {
          if (section.data) {
            Object.assign(visibilityDataStore.current, section.data);
          }
        });
        console.log("🔍 Loading form data for all sections to evaluate visibility conditions...");
        const applicantId = Id || localStorage.getItem("LoginApplicantId") || "0";
        const sectionDataPromises = allSections2.map(async (section) => {
          var _a3;
          try {
            if (!section.data && section.sectionId) {
              const formData = await configurableFormsApi.getCustomFormData(applicantId, section.sectionId);
              if ((formData == null ? void 0 : formData.Success) && ((_a3 = formData == null ? void 0 : formData.Data) == null ? void 0 : _a3.customData)) {
                const processedData = Object.fromEntries(
                  Object.entries(formData.Data.customData).map(([k, v]) => [String(k).toLowerCase(), v])
                );
                section.data = processedData;
                Object.assign(visibilityDataStore.current, processedData);
                console.log(`✅ Loaded data for section "${section.sectionName}" (${Object.keys(processedData).length} fields)`);
                return { section, data: processedData };
              }
            } else if (section.data) {
              Object.assign(visibilityDataStore.current, section.data);
            }
            return null;
          } catch (error) {
            console.warn(`⚠️ Failed to load data for section "${section.sectionName}":`, error);
            return null;
          }
        });
        const loadedData = await Promise.allSettled(sectionDataPromises);
        const successfulLoads = loadedData.filter(
          (result) => result.status === "fulfilled" && result.value !== null
        ).length;
        console.log(`✅ Loaded form data for ${successfulLoads} sections`);
        console.log(`📊 Visibility data store now contains ${Object.keys(visibilityDataStore.current).length} field values`);
        allSections2.forEach((section) => {
          if (section.hideQuerySettings) {
            section.isVisibleByQuery = isSectionVisibleByQuery(section, visibilityDataStore.current);
            console.log(`  Section "${section.sectionName}" visibility: ${section.isVisibleByQuery ? "VISIBLE" : "HIDDEN"}`);
          } else {
            section.isVisibleByQuery = true;
          }
        });
        const completedList = ((_a2 = customFormService.completedSectionList$) == null ? void 0 : _a2.value) || [];
        const incompleteList = ((_b2 = customFormService.incompleteSectionList$) == null ? void 0 : _b2.value) || [];
        let updatedCompletedList = [...completedList];
        let updatedIncompleteList = [...incompleteList];
        allSections2.forEach((section) => {
          const sectionIdStr = String(section.id || "");
          if (!sectionIdStr) return;
          const isVisibleByQuery = section.hideQuerySettings ? section.isVisibleByQuery === true : section.isVisibleByQuery !== false;
          const isVisibleForCountry = section.isVisibleForCountry !== false;
          const isHidden = !isVisibleByQuery || !isVisibleForCountry;
          if (isHidden) {
            section.completed = true;
            if (!updatedCompletedList.includes(sectionIdStr)) {
              updatedCompletedList.push(sectionIdStr);
            }
            if (updatedIncompleteList.includes(sectionIdStr)) {
              updatedIncompleteList = updatedIncompleteList.filter((id) => id !== sectionIdStr);
            }
            console.log(`[Initial Load] Auto-marking hidden section "${section.sectionName}" (ID: ${sectionIdStr}) as completed`);
          }
        });
        if (updatedCompletedList.length !== completedList.length || updatedIncompleteList.length !== incompleteList.length) {
          customFormService.completedSectionList$.next(updatedCompletedList);
          customFormService.incompleteSectionList$.next(updatedIncompleteList);
          console.log(`[Initial Load] Updated progress lists - Completed: ${updatedCompletedList.length}, Incomplete: ${updatedIncompleteList.length}`);
        }
        customFormService.sections$.next([...allSections2]);
        console.log("✅ Visibility settings loaded and evaluated (will not call again)");
      } catch (error) {
        console.error("Error loading visibility settings:", error);
        setVisibilitySettingsLoaded(false);
      }
    };
    if (pageData && !visibilitySettingsLoaded) {
      loadVisibilitySettings();
    }
  }, [pageData, visibilitySettingsLoaded]);
  useEffect(() => {
    var _a2, _b2, _c2, _d2;
    if (!visibilitySettingsLoaded) return;
    if (!state.hasDataLoaded || !((_a2 = state.nonHiddenFields) == null ? void 0 : _a2.length)) return;
    const govIdField = state.nonHiddenFields.find(
      (f) => (f.sControlType || "").toString().toLowerCase() === "drop down" && (f.sFieldName || "").toLowerCase().includes("government") && (f.sFieldName || "").toLowerCase().includes("id") && (f.sFieldName || "").toLowerCase().includes("type")
    );
    if (!govIdField) return;
    const key = `custom_${govIdField.hmy}`;
    const raw = ((_b2 = state.customdata) == null ? void 0 : _b2[key]) ?? ((_c2 = state.customdata) == null ? void 0 : _c2[key.toLowerCase()]);
    if (typeof raw !== "string" || raw.trim() === "") return;
    const options = ((_d2 = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[govIdField.hmy]) == null ? void 0 : _d2.dropdownData) || govIdField.dropdownlist || [];
    if (!options.length) return;
    const isAlreadyId = options.some((opt) => String((opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id)) === raw);
    if (isAlreadyId) return;
    const processKey = `${govIdField.hmy}|${raw}`;
    if (govIdTypeHydrationProcessedRef.current === processKey) return;
    govIdTypeHydrationProcessedRef.current = processKey;
    setTimeout(() => {
      const matchByName = options.find((opt) => {
        const optName = (opt == null ? void 0 : opt.Name) ?? (opt == null ? void 0 : opt.name);
        return optName && String(optName).trim().toLowerCase() === raw.trim().toLowerCase();
      });
      if (!matchByName) return;
      const matchedId = (matchByName == null ? void 0 : matchByName.Id) ?? (matchByName == null ? void 0 : matchByName.id);
      setValue(key, matchedId, { shouldValidate: false });
      setState((prev) => ({
        ...prev,
        customdata: {
          ...prev.customdata,
          [key]: matchedId
        }
      }));
    }, 0);
  }, [visibilitySettingsLoaded, state.hasDataLoaded, state.nonHiddenFields, state.customdata, dynamicFieldTypes, setValue]);
  useEffect(() => {
    var _a2, _b2;
    if (!visibilitySettingsLoaded) return;
    if (!state.hasDataLoaded || !((_a2 = state.nonHiddenFields) == null ? void 0 : _a2.length)) return;
    const govIdField = state.nonHiddenFields.find(
      (f) => (f.sControlType || "").toString().toLowerCase() === "drop down" && (f.sFieldName || "").toLowerCase().includes("government") && (f.sFieldName || "").toLowerCase().includes("id") && (f.sFieldName || "").toLowerCase().includes("type")
    );
    if (!govIdField) return;
    const idNumberFieldFromSetup = parentFieldSetup.filter((p) => String(p == null ? void 0 : p.hCafeFormParentFieldID) === String(govIdField.hmy)).map((p) => {
      var _a3;
      return (_a3 = state.nonHiddenFields) == null ? void 0 : _a3.find((f) => String(f == null ? void 0 : f.hmy) === String(p == null ? void 0 : p.hcafeFormFieldID));
    }).find((f) => ((f == null ? void 0 : f.sControlType) || "").toString().toLowerCase() === "text box");
    const idNumberField = idNumberFieldFromSetup || state.nonHiddenFields.find(
      (f) => (f.sControlType || "").toString().toLowerCase() === "text box" && ((f.sFieldName || "").toLowerCase().includes("id") && (f.sFieldName || "").toLowerCase().includes("number") || (f.sFieldName || "").toLowerCase().includes("enter") && (f.sFieldName || "").toLowerCase().includes("number") || (f.sFieldName || "").toLowerCase().includes("identification number"))
    );
    if (!idNumberField) return;
    const govKey = `custom_${govIdField.hmy}`;
    const govVal = (_b2 = state.customdata) == null ? void 0 : _b2[govKey];
    const processKey = `${govIdField.hmy}|${String(govVal ?? "")}|${idNumberField.hmy}`;
    if (govIdLabelHydrationProcessedRef.current === processKey) return;
    govIdLabelHydrationProcessedRef.current = processKey;
    setTimeout(() => {
      var _a3;
      const options = ((_a3 = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[govIdField.hmy]) == null ? void 0 : _a3.dropdownData) || govIdField.dropdownlist || [];
      if (!options.length) return;
      const selectedOption = options.find((opt) => String((opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id)) === String(govVal)) || options.find((opt) => {
        const optName = (opt == null ? void 0 : opt.Name) ?? (opt == null ? void 0 : opt.name);
        return optName && String(optName).trim().toLowerCase() === String(govVal).trim().toLowerCase();
      });
      const selectedText = (selectedOption == null ? void 0 : selectedOption.Name) ?? (selectedOption == null ? void 0 : selectedOption.name) ?? "";
      if (selectedText) {
        setDynamicFieldTypes((prev) => ({
          ...prev,
          [idNumberField.hmy]: {
            ...prev[idNumberField.hmy],
            label: `Enter ${selectedText} Number`,
            controlType: idNumberField.sControlType
          }
        }));
      } else {
        setDynamicFieldTypes((prev) => ({
          ...prev,
          [idNumberField.hmy]: {
            ...prev[idNumberField.hmy],
            label: idNumberField.sFieldName,
            controlType: idNumberField.sControlType
          }
        }));
      }
    }, 0);
  }, [visibilitySettingsLoaded, state.hasDataLoaded, state.nonHiddenFields, state.customdata, dynamicFieldTypes, parentFieldSetup]);
  useEffect(() => {
    var _a2, _b2, _c2, _d2, _e, _f;
    if (!visibilitySettingsLoaded) return;
    if (!state.hasDataLoaded || !((_a2 = state.nonHiddenFields) == null ? void 0 : _a2.length)) return;
    const govIdField = state.nonHiddenFields.find(
      (f) => (f.sControlType || "").toString().toLowerCase() === "drop down" && (f.sFieldName || "").toLowerCase().includes("government") && (f.sFieldName || "").toLowerCase().includes("id") && (f.sFieldName || "").toLowerCase().includes("type")
    );
    if (!govIdField) return;
    const isGovIdVisible = isConditionalShow(govIdField.hmy) && (govIdField.bVisible || govIdField.bAlwaysVisible);
    if (!isGovIdVisible) return;
    const resolveDropdownValue = (raw, options) => {
      if (raw === null || raw === void 0 || raw === "") return "";
      if (!(options == null ? void 0 : options.length)) return raw;
      const getId = (opt) => (opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id);
      const matchesId = (val) => options.some((opt) => String(getId(opt)) === String(val));
      if (matchesId(raw)) return raw;
      if (typeof raw === "string" && raw.trim()) {
        const trimmed = raw.trim();
        if (matchesId(trimmed)) return trimmed;
        const byName = options.find((opt) => {
          const optName = (opt == null ? void 0 : opt.Name) ?? (opt == null ? void 0 : opt.name);
          return optName && String(optName).trim().toLowerCase() === trimmed.toLowerCase();
        });
        if (byName) return getId(byName);
      }
      return raw;
    };
    const govKey = `custom_${govIdField.hmy}`;
    const govKeyLc = govKey.toLowerCase();
    const govOptions = ((_b2 = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[govIdField.hmy]) == null ? void 0 : _b2.dropdownData) || govIdField.dropdownlist || [];
    const currentGov = getValues(govKey) ?? ((_c2 = state.customdata) == null ? void 0 : _c2[govKey]) ?? ((_d2 = state.customdata) == null ? void 0 : _d2[govKeyLc]) ?? "";
    const lookupModelValue = (keyLc) => {
      var _a3, _b3;
      const fromStore = (_a3 = visibilityDataStore.current) == null ? void 0 : _a3[keyLc];
      if (fromStore !== void 0 && fromStore !== null && fromStore !== "") return fromStore;
      const sections = customFormService.sections$.value || [];
      for (const s of sections) {
        const v = (_b3 = s == null ? void 0 : s.data) == null ? void 0 : _b3[keyLc];
        if (v !== void 0 && v !== null && v !== "") return v;
      }
      return void 0;
    };
    const modelGov = lookupModelValue(govKeyLc);
    const govToSet = resolveDropdownValue(modelGov ?? currentGov, govOptions);
    const idNumberFieldFromSetup = parentFieldSetup.filter((p) => String(p == null ? void 0 : p.hCafeFormParentFieldID) === String(govIdField.hmy)).map((p) => {
      var _a3;
      return (_a3 = state.nonHiddenFields) == null ? void 0 : _a3.find((f) => String(f == null ? void 0 : f.hmy) === String(p == null ? void 0 : p.hcafeFormFieldID));
    }).find((f) => ((f == null ? void 0 : f.sControlType) || "").toString().toLowerCase() === "text box" && (f == null ? void 0 : f.hCafeFormSectionsID) === govIdField.hCafeFormSectionsID);
    const idNumberField = idNumberFieldFromSetup || state.nonHiddenFields.find(
      (f) => (f.sControlType || "").toString().toLowerCase() === "text box" && f.hCafeFormSectionsID === govIdField.hCafeFormSectionsID && ((f.sFieldName || "").toLowerCase().includes("id") && (f.sFieldName || "").toLowerCase().includes("number") || (f.sFieldName || "").toLowerCase().includes("enter") && (f.sFieldName || "").toLowerCase().includes("number") || (f.sFieldName || "").toLowerCase().includes("identification number"))
    );
    const numKey = idNumberField ? `custom_${idNumberField.hmy}` : "";
    const numKeyLc = numKey ? numKey.toLowerCase() : "";
    const isNumVisible = idNumberField ? isConditionalShow(idNumberField.hmy) && (idNumberField.bVisible || idNumberField.bAlwaysVisible) : false;
    const currentNum = idNumberField ? getValues(numKey) ?? ((_e = state.customdata) == null ? void 0 : _e[numKey]) ?? ((_f = state.customdata) == null ? void 0 : _f[numKeyLc]) ?? "" : "";
    const modelNum = idNumberField && numKeyLc ? lookupModelValue(numKeyLc) : void 0;
    const numToSetRaw = !currentNum && modelNum !== void 0 ? modelNum : "";
    const numToSet = typeof numToSetRaw === "string" ? ["undefined", "null"].includes(numToSetRaw.trim().toLowerCase()) ? "" : numToSetRaw : numToSetRaw !== null && numToSetRaw !== void 0 && numToSetRaw !== "" ? String(numToSetRaw) : "";
    const processKey = `${govIdField.hmy}|${String(govToSet)}|${(idNumberField == null ? void 0 : idNumberField.hmy) ?? ""}|${String(numToSet)}`;
    if (govIdConditionalHydrationProcessedRef.current === processKey) return;
    govIdConditionalHydrationProcessedRef.current = processKey;
    setTimeout(() => {
      if (govToSet !== null && govToSet !== void 0 && String(govToSet) !== "" && String(currentGov ?? "") !== String(govToSet)) {
        setValue(govKey, govToSet, { shouldValidate: false, shouldDirty: false });
        visibilityDataStore.current[govKeyLc] = govToSet;
        setState((prev) => ({
          ...prev,
          customdata: {
            ...prev.customdata,
            [govKey]: govToSet,
            [govKeyLc]: govToSet
          }
        }));
        handleModelChange(govIdField.sColumnName);
      }
      if (idNumberField && isNumVisible && numToSet && String(currentNum ?? "") !== String(numToSet)) {
        setValue(numKey, numToSet, { shouldValidate: false, shouldDirty: false });
        visibilityDataStore.current[numKeyLc] = numToSet;
        setState((prev) => ({
          ...prev,
          customdata: {
            ...prev.customdata,
            [numKey]: numToSet,
            [numKeyLc]: numToSet
          }
        }));
      }
    }, 0);
  }, [
    visibilitySettingsLoaded,
    state.hasDataLoaded,
    state.nonHiddenFields,
    state.customdata,
    dynamicFieldTypes,
    parentFieldSetup,
    getValues,
    setValue,
    isConditionalShow,
    handleModelChange
  ]);
  const updateSectionsVisibilityForCountry = useCallback(async (countryName) => {
    const allSections2 = customFormService.sections$.value || [];
    if (allSections2.length === 0) return;
    console.log(`🌍 Updating section visibility for country: "${countryName}"`);
    let visibilityChanged = false;
    allSections2.forEach((section) => {
      const wasVisible = section.isVisibleForCountry !== false;
      const isVisible = isSectionVisibleForCountry(section, countryName);
      if (wasVisible !== isVisible) {
        visibilityChanged = true;
        console.log(`  Section "${section.sectionName}": ${wasVisible ? "visible" : "hidden"} → ${isVisible ? "visible" : "hidden"}`);
      }
      section.isVisibleForCountry = isVisible;
    });
    if (visibilityChanged) {
      customFormService.sections$.next([...allSections2]);
      console.log("✅ Section visibility updated");
    }
  }, [customFormService.sections$, isSectionVisibleForCountry]);
  const checkSectionVisibilityAfterSave = useCallback(async (savedSectionId, savedFieldData) => {
    var _a2, _b2, _c2;
    try {
      const allSections2 = customFormService.sections$.value || [];
      if (allSections2.length === 0) return;
      const currentSectionIndex = allSections2.findIndex((s) => s.id === savedSectionId);
      if (currentSectionIndex === -1) return;
      const allFieldData = { ...visibilityDataStore.current };
      allSections2.forEach((section, idx) => {
        if (idx <= currentSectionIndex && section.data) {
          Object.assign(allFieldData, section.data);
        }
      });
      Object.assign(allFieldData, savedFieldData);
      console.log(`[checkSectionVisibilityAfterSave] Evaluating visibility with field data from ${currentSectionIndex + 1} completed sections`);
      const completedList = ((_a2 = customFormService.completedSectionList$) == null ? void 0 : _a2.value) || [];
      const incompleteList = ((_b2 = customFormService.incompleteSectionList$) == null ? void 0 : _b2.value) || [];
      let updatedCompletedList = [...completedList];
      let updatedIncompleteList = [...incompleteList];
      let completionStatusChanged = false;
      allSections2.forEach((section, idx) => {
        if (idx <= currentSectionIndex) return;
        if (section.hideQuerySettings) {
          const isVisible = isSectionVisibleByQuery(section, allFieldData);
          const wasVisible = section.isVisibleByQuery !== false;
          section.isVisibleByQuery = isVisible;
          if (wasVisible !== isVisible) {
            console.log(`[${section.sectionName}] Visibility changed: ${wasVisible ? "visible" : "hidden"} → ${isVisible ? "visible" : "hidden"}`);
          } else {
            console.log(`[${section.sectionName}] isVisibleByQuery set to: ${isVisible}`);
          }
        }
        const sectionIdStr = String(section.id || "");
        if (sectionIdStr) {
          const isVisibleByQuery = section.hideQuerySettings ? section.isVisibleByQuery === true : section.isVisibleByQuery !== false;
          const isVisibleForCountry = section.isVisibleForCountry !== false;
          const isHidden = !isVisibleByQuery || !isVisibleForCountry;
          if (isHidden) {
            section.completed = true;
            if (!updatedCompletedList.includes(sectionIdStr)) {
              updatedCompletedList.push(sectionIdStr);
              completionStatusChanged = true;
            }
            if (updatedIncompleteList.includes(sectionIdStr)) {
              updatedIncompleteList = updatedIncompleteList.filter((id) => id !== sectionIdStr);
              completionStatusChanged = true;
            }
            console.log(`[After Save] Auto-marking hidden section "${section.sectionName}" (ID: ${sectionIdStr}) as completed`);
          } else {
            const wasHiddenByQuery = !section.hideQuerySettings || section.isVisibleByQuery !== false;
            if (wasHiddenByQuery && updatedCompletedList.includes(sectionIdStr) && !section.completed) {
              updatedCompletedList = updatedCompletedList.filter((id) => id !== sectionIdStr);
              if (!updatedIncompleteList.includes(sectionIdStr)) {
                updatedIncompleteList.push(sectionIdStr);
              }
              completionStatusChanged = true;
              console.log(`[After Save] Unmarking section "${section.sectionName}" (ID: ${sectionIdStr}) as it's now visible`);
            }
          }
        }
      });
      if (completionStatusChanged) {
        customFormService.completedSectionList$.next(updatedCompletedList);
        customFormService.incompleteSectionList$.next(updatedIncompleteList);
        console.log(`[After Save] Updated progress lists - Completed: ${updatedCompletedList.length}, Incomplete: ${updatedIncompleteList.length}`);
      }
      customFormService.sections$.next([...allSections2]);
      const countryField = state.nonHiddenFields.find(
        (f) => {
          var _a3, _b3;
          return f.hCafeFormSectionsID === savedSectionId && ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country")) && ((_b3 = f.sControlType) == null ? void 0 : _b3.toLowerCase()) === "drop down";
        }
      );
      if (countryField) {
        const countryValue = savedFieldData[`custom_${countryField.hmy}`];
        if (countryValue && ((_c2 = countryField.dropdownlist) == null ? void 0 : _c2.length) > 0) {
          const selectedCountry = countryField.dropdownlist.find((country) => country.id === countryValue);
          if (selectedCountry == null ? void 0 : selectedCountry.name) {
            console.log("🌍 Country changed, updating section visibility (no API call):", selectedCountry.name);
            setSelectedCountryName(selectedCountry.name);
            await updateSectionsVisibilityForCountry(selectedCountry.name);
          }
        }
      }
    } catch (error) {
      console.error("Error checking section visibility:", error);
    }
  }, [customFormService, state.nonHiddenFields, isSectionVisibleByQuery, updateSectionsVisibilityForCountry]);
  const setDisplayValue = useCallback((hmy, value) => {
    const fieldName = `custom_${hmy}`;
    try {
      setState((prev) => ({
        ...prev,
        customdata: {
          ...prev.customdata,
          [fieldName]: value
        }
      }));
      setValue(fieldName, value);
      clearErrors(fieldName);
    } catch (error) {
      console.error("Error setting display value:", error);
    }
  }, [setValue, clearErrors]);
  const validateExistingData = useCallback(() => {
    var _a2;
    if (Object.keys(fieldValidationSettings).length === 0 || !state.nonHiddenFields) {
      return;
    }
    console.log("🔍 Validating existing data on load...");
    const newValidationErrors = {};
    (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.forEach((field) => {
      var _a3, _b2;
      const fieldName = `custom_${field.hmy}`;
      const value = state.customdata[fieldName];
      if (value && value !== "" && value !== null && value !== void 0) {
        const fieldNameLower = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase()) || "";
        const isTextBox = ((_b2 = field.sControlType) == null ? void 0 : _b2.toLowerCase()) === "text box";
        const isQuestionField = fieldNameLower.startsWith("do you") || fieldNameLower.startsWith("have you") || fieldNameLower.includes("do you have");
        const isSSNField = isTextBox && !isQuestionField && (fieldNameLower.includes("ssn") || fieldNameLower.includes("social security")) && !fieldNameLower.includes("do you");
        if (isSSNField) {
          const stringValue = (value == null ? void 0 : value.toString()) || "";
          if (stringValue.trim()) {
            const digits = stringValue.replace(/\D/g, "");
            if (digits.length !== 9) {
              const error2 = "SSN must be exactly 9 digits (XXX-XX-XXXX)";
              newValidationErrors[field.hmy] = error2;
              console.log(`❌ SSN validation error for ${field.sFieldName}:`, error2);
              setError(fieldName, {
                type: "validation",
                message: error2
              });
              return;
            }
          }
        }
        if (isCityOrStateField(field)) return;
        const error = validateField(field.hmy, value, field.sControlType, fieldValidationSettings);
        if (error) {
          newValidationErrors[field.hmy] = error;
          console.log(`❌ Validation error for ${field.sFieldName}:`, error);
          setError(fieldName, {
            type: "validation",
            message: error
          });
        } else {
          clearErrors(fieldName);
        }
      }
    });
    setValidationErrors(newValidationErrors);
    if (Object.keys(newValidationErrors).length > 0) {
      console.log(`⚠️ Found ${Object.keys(newValidationErrors).length} validation errors on load`);
    } else {
      console.log("✅ All existing data is valid");
    }
  }, [state.nonHiddenFields, state.customdata, fieldValidationSettings, setError, clearErrors, isCityOrStateField, translate]);
  useEffect(() => {
    if (Object.keys(fieldValidationSettings).length > 0 && state.customdata && Object.keys(state.customdata).length > 0) {
      console.log("🔄 Validation settings loaded, validating existing data...");
      validateExistingData();
    }
  }, [fieldValidationSettings, validateExistingData]);
  const validateForm = useCallback(() => {
    var _a2, _b2;
    let hasErrors = false;
    let errorFields = [];
    const errorTermsAndConditionsFieldNames = /* @__PURE__ */ new Set();
    let firstErrorField = null;
    const fieldsToValidate = state.nonHiddenFields || [];
    fieldsToValidate.forEach((field) => {
      var _a3, _b3, _c2, _d2, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D;
      const fieldName = `custom_${field.hmy}`;
      const isMultiselect = ((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase().includes("multi select")) || ((_b3 = field.sControlType) == null ? void 0 : _b3.toLowerCase()) === "multiselect" || ((_c2 = field.sControlType) == null ? void 0 : _c2.toLowerCase()) === "multiselect dropdown";
      const value = isMultiselect ? getDisplayValue(field.hmy) : state.customdata[fieldName] ?? watch(fieldName);
      const isReadOnly = field._bIsReadOnly || isConditionalReadOnly(field.hmy) || ReadOnlyService.isReadOnly();
      const passesConditionalCheck = isConditionalShow(field.hmy);
      const isMarkedVisible = field.bVisible || field.bAlwaysVisible;
      const isVisible = passesConditionalCheck && isMarkedVisible;
      if (isReadOnly || !isVisible) {
        clearErrors(fieldName);
        return;
      }
      if (isCityOrStateField(field)) {
        clearErrors(fieldName);
        return;
      }
      const isRequired = (field._bRequired || field._bAlwaysRequired) && !isConditionalReadOnly(field.hmy);
      let isEmpty = value === null || value === void 0 || value === "";
      let isPhoneCountryCodeOnly = false;
      if (((_d2 = field.sControlType) == null ? void 0 : _d2.toLowerCase()) === "phone number" || ((_e = field.sControlType) == null ? void 0 : _e.toLowerCase()) === "phone") {
        if (typeof value === "string" && value.trim() !== "") {
          const digitsOnly = value.replace(/[^\d]/g, "");
          if (value === "+" || digitsOnly.length <= 3) {
            isEmpty = true;
            isPhoneCountryCodeOnly = true;
          }
        }
      }
      if (((_f = field.sControlType) == null ? void 0 : _f.toLowerCase().includes("drop")) && !((_g = field.sControlType) == null ? void 0 : _g.toLowerCase().includes("multi"))) {
        const valueExistsInList = (_h = field.dropdownlist) == null ? void 0 : _h.some(
          (option) => (option.Id ?? option.id) == value || (option.Id ?? option.id) === value
        );
        if (!valueExistsInList) {
          isEmpty = isEmpty || value === "" || value === "Select..." || value === "Please select" || value === "Select Title";
        }
      }
      if (((_i = field.sControlType) == null ? void 0 : _i.toLowerCase().includes("multi select")) || ((_j = field.sControlType) == null ? void 0 : _j.toLowerCase()) === "multiselect" || ((_k = field.sControlType) == null ? void 0 : _k.toLowerCase()) === "multiselect dropdown") {
        let arrayValue = value;
        if (!Array.isArray(value)) {
          if (typeof value === "string" && value.trim() !== "") {
            try {
              arrayValue = JSON.parse(value);
            } catch {
              arrayValue = [];
            }
          } else {
            arrayValue = [];
          }
        }
        isEmpty = !Array.isArray(arrayValue) || arrayValue.length === 0;
        if (isRequired) {
          console.log(`[Multiselect Validation] Field "${field.sFieldName}" (${field.hmy}):`, {
            originalValue: value,
            arrayValue,
            isEmpty,
            isRequired,
            valueType: typeof value,
            isArray: Array.isArray(value)
          });
        }
      }
      if (((_l = field.sControlType) == null ? void 0 : _l.toLowerCase()) === "currency" || ((_m = field.sControlType) == null ? void 0 : _m.toLowerCase()) === "decimal" || ((_n = field.sControlType) == null ? void 0 : _n.toLowerCase()) === "money") {
        isEmpty = isEmpty || value === 0 || value === "0" || value === 0 || value === "0.0" || value === "0.00";
      }
      if (((_o = field.sControlType) == null ? void 0 : _o.toLowerCase().includes("text")) && typeof value === "string") {
        isEmpty = isEmpty || value.trim() === "";
      }
      if (((_p = field.sControlType) == null ? void 0 : _p.toLowerCase()) === "terms and conditions" || ((_q = field.sControlType) == null ? void 0 : _q.toLowerCase()) === "check box" || ((_r = field.sControlType) == null ? void 0 : _r.toLowerCase()) === "checkbox") {
        const isChecked = value === true || value === "true" || value === 1 || value === "1";
        isEmpty = !isChecked;
      }
      if ((_s = field.sFieldName) == null ? void 0 : _s.toLowerCase().includes("university")) {
        isEmpty = isEmpty || value === "0" || value === 0 || value === 0 || value === "0.0";
      }
      if (isRequired && isEmpty) {
        hasErrors = true;
        errorFields.push(field.sFieldName);
        const isTermsAndConditions = ((_t = field.sControlType) == null ? void 0 : _t.toLowerCase()) === "terms and conditions";
        if (isTermsAndConditions) {
          errorTermsAndConditionsFieldNames.add(field.sFieldName);
        }
        if (!firstErrorField) {
          firstErrorField = fieldName;
        }
        setError(fieldName, {
          type: "required",
          message: isTermsAndConditions ? translate("You must accept the terms and conditions to continue") : translate("This field is required")
        });
      } else if (isRequired && !isEmpty) {
        if (!hasValidationError(field.hmy)) {
          clearErrors(fieldName);
        }
      }
      if (value && field.sControlType.toLowerCase() === "email") {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailRegex.test(value)) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Invalid email address")
          });
        }
      }
      if (!isReadOnly && !isPhoneCountryCodeOnly && value && (((_u = field.sControlType) == null ? void 0 : _u.toLowerCase()) === "phone number" || ((_v = field.sControlType) == null ? void 0 : _v.toLowerCase()) === "phone")) {
        if (typeof value === "string" && value.trim() !== "") {
          const digitsOnly = value.replace(/[^\d]/g, "");
          if (value !== "+" && digitsOnly.length > 3) {
            try {
              const { parsePhoneNumberFromString: parsePhoneNumberFromString2 } = require("libphonenumber-js");
              const phoneNumber = parsePhoneNumberFromString2(value);
              if (!phoneNumber || !phoneNumber.isPossible()) {
                hasErrors = true;
                errorFields.push(field.sFieldName);
                setError(fieldName, {
                  type: "pattern",
                  message: translate("Please enter a valid phone number")
                });
              } else {
                if (((_w = errors[fieldName]) == null ? void 0 : _w.type) === "pattern") {
                  clearErrors(fieldName);
                }
              }
            } catch (error) {
              if (digitsOnly.length < 10) {
                hasErrors = true;
                errorFields.push(field.sFieldName);
                setError(fieldName, {
                  type: "pattern",
                  message: translate("Please enter a valid phone number")
                });
              }
            }
          }
        }
      }
      const fieldNameLower = ((_x = field.sFieldName) == null ? void 0 : _x.toLowerCase()) || "";
      const isQuestionField = fieldNameLower.startsWith("do you") || fieldNameLower.startsWith("have you") || fieldNameLower.includes("do you have");
      const isSSNField = !isQuestionField && ((_y = field.sControlType) == null ? void 0 : _y.toLowerCase()) === "text box" && (fieldNameLower.includes("ssn") || fieldNameLower.includes("social security")) && !fieldNameLower.includes("do you");
      if (isSSNField && isVisible && value && typeof value === "string" && value.trim() !== "") {
        const digits = value.replace(/\D/g, "");
        if (digits.length !== 9) {
          hasErrors = true;
          if (isRequired) {
            errorFields.push(field.sFieldName);
          }
          setError(fieldName, {
            type: "pattern",
            message: translate("SSN must be exactly 9 digits (XXX-XX-XXXX)")
          });
        }
      }
      if (value && field.sControlType.toLowerCase() === "date") {
        const parsedDate = DateFormatUtils$1.parseFlexibleDate(value);
        const isStandardDate = !isNaN(new Date(value).getTime());
        const isDisplayFormat = /^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value);
        if (!parsedDate && !isStandardDate && !isDisplayFormat) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Please enter a valid date ")
          });
        }
      }
      if (value && field.sControlType.toLowerCase() === "currency") {
        const numericValue = value.toString().replace(/[^0-9.-]/g, "");
        if (!numericValue || isNaN(parseFloat(numericValue)) || parseFloat(numericValue) < 0) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Please enter a valid currency amount")
          });
        }
      }
      if (value && (field.sControlType.toLowerCase() === "number" || field.sControlType.toLowerCase() === "decimal")) {
        if (isNaN(parseFloat(value))) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Please enter a valid number")
          });
        }
      }
      const isZipField = (((_z = field.sFieldName) == null ? void 0 : _z.toLowerCase().includes("zip")) || ((_A = field.sFieldName) == null ? void 0 : _A.toLowerCase().includes("post code")) || ((_B = field.sFieldName) == null ? void 0 : _B.toLowerCase().includes("postal"))) && (((_C = field.sControlType) == null ? void 0 : _C.toLowerCase()) === "text box" || ((_D = field.sControlType) == null ? void 0 : _D.toLowerCase()) === "textbox");
      if (isZipField && value && typeof value === "string" && value.trim() !== "") {
        const countryName = getCountryNameForZipValidation(field);
        const zipValidation = validateZipCode(value.trim(), countryName);
        if (!zipValidation.isValid) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          if (!firstErrorField) firstErrorField = fieldName;
          setError(fieldName, {
            type: "pattern",
            message: zipValidation.message || translate("Invalid zip/post code format")
          });
        }
      }
    });
    if (Object.keys(fieldValidationSettings).length > 0) {
      (_b2 = (_a2 = state.customfields) == null ? void 0 : _a2.rowdata) == null ? void 0 : _b2.forEach((row) => {
        row.forEach((field) => {
          if (isCityOrStateField(field)) return;
          const fieldName = `custom_${field.hmy}`;
          const value = state.customdata[fieldName];
          if (value && value !== "" && value !== null && value !== void 0) {
            const error = validateField(field.hmy, value, field.sControlType, fieldValidationSettings);
            if (error) {
              hasErrors = true;
              errorFields.push(field.sFieldName);
              if (!firstErrorField) {
                firstErrorField = fieldName;
              }
              setError(fieldName, {
                type: "validation",
                message: error
              });
              console.log(`❌ Validation error for ${field.sFieldName}:`, error);
            }
          }
        });
      });
    }
    if (hasErrors) {
      const uniqueErrorFields = [...new Set(errorFields)];
      if (uniqueErrorFields.length > 0) {
        const hasTermsAndConditionsError = errorTermsAndConditionsFieldNames.size > 0;
        const otherErrorFields = uniqueErrorFields.filter((name) => !errorTermsAndConditionsFieldNames.has(name));
        const bannerMessage = hasTermsAndConditionsError ? otherErrorFields.length > 0 ? translate("You must accept the terms and conditions to continue. Please also fill out: {{fields}}", { fields: otherErrorFields.join(", ") }) : translate("You must accept the terms and conditions to continue") : translate("Please fill out all required(*) fields: {{fields}}", { fields: uniqueErrorFields.join(", ") });
        setState((prev) => ({
          ...prev,
          errorMessage: bannerMessage
        }));
        window.scrollTo(0, 0);
      } else {
        setState((prev) => ({
          ...prev,
          errorMessage: ""
        }));
      }
      if (firstErrorField) {
        setTimeout(() => {
          let errorElement = document.querySelector(`[name="${firstErrorField}"]`);
          if (!errorElement) {
            const fieldId = (firstErrorField == null ? void 0 : firstErrorField.replace("custom_", "")) || "";
            const errorMsg = document.querySelector(`#error_${fieldId}`);
            if (errorMsg) {
              const container = errorMsg.closest(".space-y-2") || errorMsg.closest("div");
              if (container) {
                errorElement = container.querySelector('input, select, textarea, .e-input, .e-dropdownlist, [tabindex="0"]');
              }
            }
          }
          if (!errorElement) {
            const allInputs = document.querySelectorAll("input, .e-input, .e-dropdownlist");
            const fieldIndex = errorFields.indexOf(errorFields[0]);
            if (allInputs[fieldIndex]) {
              errorElement = allInputs[fieldIndex];
            }
          }
          if (errorElement) {
            errorElement.focus();
            errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
    } else {
      setState((prev) => ({
        ...prev,
        errorMessage: ""
      }));
    }
    return !hasErrors;
  }, [state.customfields, state.nonHiddenFields, state.customdata, isConditionalReadOnly, setError, fieldValidationSettings, validationErrors, clearErrors, isCityOrStateField, getCountryNameForZipValidation, translate]);
  const checkCountryChange = useCallback(async (field, selectedValue) => {
    var _a2, _b2, _c2, _d2, _e, _f;
    if ((_a2 = field.sFieldName) == null ? void 0 : _a2.toLowerCase().includes("country")) {
      const idProperties = ["id", "Id", "ID", "value", "Value", "hmy", "HMY"];
      const codeProperties = ["sCode", "SCODE", "code", "Code", "isoCode", "iso2", "iso", "countryCode", "CountryCode"];
      const nameProperties = ["name", "Name", "sDesc", "SDESC", "text", "label"];
      let selectedCountry = null;
      for (const prop of idProperties) {
        selectedCountry = (_b2 = field.dropdownlist) == null ? void 0 : _b2.find((country) => country[prop] == selectedValue);
        if (selectedCountry) {
          break;
        }
      }
      if (!selectedCountry) {
        for (const prop of codeProperties) {
          selectedCountry = (_c2 = field.dropdownlist) == null ? void 0 : _c2.find((country) => country[prop] == selectedValue);
          if (selectedCountry) {
            break;
          }
        }
      }
      if (!selectedCountry) {
        for (const prop of nameProperties) {
          selectedCountry = (_d2 = field.dropdownlist) == null ? void 0 : _d2.find(
            (country) => {
              var _a3;
              return ((_a3 = country[prop]) == null ? void 0 : _a3.toString().toLowerCase()) === (selectedValue == null ? void 0 : selectedValue.toString().toLowerCase());
            }
          );
          if (selectedCountry) {
            break;
          }
        }
      }
      if (!selectedCountry) {
        const numericValue = parseInt(selectedValue);
        const stringValue = selectedValue == null ? void 0 : selectedValue.toString();
        if (!isNaN(numericValue)) {
          for (const prop of idProperties) {
            selectedCountry = (_e = field.dropdownlist) == null ? void 0 : _e.find((country) => parseInt(country[prop]) === numericValue);
            if (selectedCountry) {
              break;
            }
          }
        }
        if (!selectedCountry && stringValue) {
          for (const prop of [...codeProperties, ...nameProperties]) {
            selectedCountry = (_f = field.dropdownlist) == null ? void 0 : _f.find(
              (country) => {
                var _a3, _b3;
                return ((_a3 = country[prop]) == null ? void 0 : _a3.toString().toLowerCase().includes(stringValue.toLowerCase())) || stringValue.toLowerCase().includes((_b3 = country[prop]) == null ? void 0 : _b3.toString().toLowerCase());
              }
            );
            if (selectedCountry) {
              break;
            }
          }
        }
      }
      const countryName = (selectedCountry == null ? void 0 : selectedCountry.name) || (selectedCountry == null ? void 0 : selectedCountry.Name) || (selectedCountry == null ? void 0 : selectedCountry.sDesc) || (selectedCountry == null ? void 0 : selectedCountry.SDESC) || (selectedCountry == null ? void 0 : selectedCountry.text) || (selectedCountry == null ? void 0 : selectedCountry.label) || "";
      field.dValue = selectedValue;
      let countryCode = null;
      const codeProperties2 = [
        "code",
        "Code",
        "sCode",
        "SCODE",
        "isoCode",
        "iso2",
        "iso",
        "countryCode",
        "CountryCode",
        "IsoCode",
        "Iso2",
        "ISOCode",
        "ISO2"
      ];
      for (const prop of codeProperties2) {
        if (selectedCountry && selectedCountry[prop]) {
          countryCode = selectedCountry[prop];
          break;
        }
      }
      if (!countryCode && countryName) {
        const countryNameToCodeMap = {
          // Match database SDESC to SCODE
          "united states": "us",
          "usa": "us",
          "us": "us",
          "united states of america": "us",
          "netherlands": "nl",
          "andorra": "ad",
          "united arab emirates": "uae",
          "uae": "uae",
          "afghanistan": "af",
          "antigua and barbuda": "ag",
          "anguilla": "ai",
          "albania": "al",
          "armenia": "am",
          "netherlands antilles": "an",
          "angola": "ao",
          "antarctica": "aq",
          "argentina": "ar",
          "american samoa": "as",
          "austria": "at",
          "australia": "au",
          "aruba": "aw",
          "åland islands": "ax",
          "azerbaijan": "az",
          "bosnia and herzegovina": "ba",
          "barbados": "bb",
          "bangladesh": "bd",
          "belgium": "be",
          "burkina faso": "bf",
          "bulgaria": "bg",
          "bahrain": "bh",
          "burundi": "bi",
          "benin": "bj",
          "brunei darussalam": "bn",
          "bolivia": "bo",
          "brazil": "br",
          "bahamas": "bs",
          "bhutan": "bt",
          "bouvet island": "bv",
          "botswana": "bw",
          "belarus": "by",
          "belize": "bz",
          "canada": "ca",
          "cocos (keeling) islands": "cc",
          "congo, the democratic republic of the": "cd",
          "central african republic": "cf",
          "congo": "cg",
          "switzerland": "ch",
          "cote d'ivoire": "ci",
          "cook islands": "ck",
          "chile": "cl",
          "cameroon": "cm",
          "china": "cn",
          "colombia": "co",
          "costa rica": "cr",
          "cuba": "cu",
          "cape verde": "cv",
          "christmas island": "cx",
          "cyprus": "cy",
          "czech republic": "cz",
          "germany": "de",
          "djibouti": "dj",
          "denmark": "dk",
          "dominica": "dm",
          "dominican republic": "do",
          "algeria": "dz",
          "ecuador": "ec",
          "estonia": "ee",
          "egypt": "eg",
          "western sahara": "eh",
          "eritrea": "er",
          "spain": "es",
          "ethiopia": "et",
          "finland": "fi",
          "fiji": "fj",
          "falkland islands (malvinas)": "fk",
          "micronesia, federated states of": "fm",
          "faroe islands": "fo",
          "france": "fr",
          "gabon": "ga",
          "united kingdom": "uk",
          "uk": "uk",
          "great britain": "gb",
          "britain": "gb",
          "grenada": "gd",
          "georgia": "ge",
          "french guiana": "gf",
          "guernsey": "gg",
          "ghana": "gh",
          "gibraltar": "gi",
          "greenland": "gl",
          "gambia": "gm",
          "guinea": "gn",
          "guadeloupe": "gp",
          "equatorial guinea": "gq",
          "greece": "gr",
          "south georgia and the south sandwich islands": "gs",
          "guatemala": "gt",
          "guam": "gu",
          "guinea-bissau": "gw",
          "guyana": "gy",
          "hong kong": "hk",
          "heard island and mcdonald islands": "hm",
          "honduras": "hn",
          "croatia": "hr",
          "haiti": "ht",
          "hungary": "hu",
          "indonesia": "id",
          "ireland": "ie",
          "israel": "il",
          "isle of man": "im",
          "india": "in",
          "british indian ocean territory": "io",
          "iraq": "iq",
          "iran, islamic republic of": "ir",
          "iceland": "is",
          "italy": "it",
          "jersey": "je",
          "jamaica": "jm",
          "jordan": "jo",
          "japan": "jp",
          "kenya": "ke",
          "kyrgyzstan": "kg",
          "cambodia": "kh",
          "kiribati": "ki",
          "comoros": "km",
          "saint kitts and nevis": "kn",
          "korea, democratic people's republic of": "kp",
          "kuwait": "kw",
          "cayman islands": "ky",
          "kazakhstan": "kz",
          "lao people's democratic republic": "la",
          "lebanon": "lb",
          "saint lucia": "lc",
          "liechtenstein": "li",
          "sri lanka": "lk",
          "liberia": "lr",
          "lesotho": "ls",
          "lithuania": "lt",
          "luxembourg": "lu",
          "latvia": "lv",
          "libyan arab jamahiriya": "ly",
          "morocco": "ma",
          "monaco": "mc",
          "moldova, republic of": "md",
          "montenegro": "me",
          "madagascar": "mg",
          "marshall islands": "mh",
          "macedonia, the former yugoslav republic of": "mk",
          "mali": "ml",
          "myanmar": "mm",
          "mongolia": "mn",
          "macao": "mo",
          "northern mariana islands": "mp",
          "martinique": "mq",
          "mauritania": "mr",
          "montserrat": "ms",
          "malta": "mt",
          "mauritius": "mu",
          "maldives": "mv",
          "malawi": "mw",
          "mexico": "mx",
          "malaysia": "my",
          "mozambique": "mz",
          "namibia": "na",
          "new caledonia": "nc",
          "niger": "ne",
          "norfolk island": "nf",
          "nigeria": "ng",
          "nicaragua": "ni",
          "norway": "no",
          "nepal": "np",
          "nauru": "nr",
          "niue": "nu",
          "new zealand": "nz",
          "oman": "om",
          "panama": "pa",
          "peru": "pe",
          "french polynesia": "pf",
          "papua new guinea": "pg",
          "philippines": "ph",
          "pakistan": "pk",
          "poland": "pl",
          "saint pierre and miquelon": "pm",
          "pitcairn": "pn",
          "puerto rico": "pr",
          "palestinian territory, occupied": "ps",
          "portugal": "pt",
          "palau": "pw",
          "paraguay": "py",
          "qatar": "qa",
          "reunion": "re",
          "romania": "ro",
          "serbia": "rs",
          "russian federation": "ru",
          "russia": "ru",
          "rwanda": "rw",
          "saudi arabia": "sa",
          "solomon islands": "sb",
          "seychelles": "sc",
          "sudan": "sd",
          "sweden": "se",
          "singapore": "sg",
          "saint helena": "sh",
          "slovenia": "si",
          "svalbard and jan mayen": "sj",
          "slovakia": "sk",
          "sierra leone": "sl",
          "san marino": "sm",
          "senegal": "sn",
          "somalia": "so",
          "suriname": "sr",
          "sao tome and principe": "st",
          "el salvador": "sv",
          "syrian arab republic": "sy",
          "swaziland": "sz",
          "turks and caicos islands": "tc",
          "chad": "td",
          "french southern territories": "tf",
          "togo": "tg",
          "thailand": "th",
          "tajikistan": "tj",
          "tokelau": "tk",
          "timor-leste": "tl",
          "turkmenistan": "tm",
          "tunisia": "tn",
          "tonga": "to",
          "turkey": "tr",
          "trinidad and tobago": "tt",
          "tuvalu": "tv",
          "taiwan, province of china": "tw",
          "tanzania, united republic of": "tz",
          "ukraine": "ua",
          "uganda": "ug",
          "united states minor outlying islands": "um",
          "uruguay": "uy",
          "uzbekistan": "uz",
          "holy see (vatican city state)": "va",
          "saint vincent and the grenadines": "vc",
          "venezuela": "ve",
          "virgin islands, british": "vg",
          "virgin islands, u.s.": "vi",
          "viet nam": "vn",
          "vietnam": "vn",
          "vanuatu": "vu",
          "wallis and futuna": "wf",
          "samoa": "ws",
          "yemen": "ye",
          "mayotte": "yt",
          "south africa": "za",
          "zambia": "zm",
          "zimbabwe": "zw",
          "korea, republic of": "kr",
          "south korea": "kr",
          "korea": "kr"
        };
        countryCode = countryNameToCodeMap[countryName.toLowerCase().trim()];
        if (!countryCode) {
          const normalizedName = countryName.toLowerCase().trim();
          for (const [key, code] of Object.entries(countryNameToCodeMap)) {
            if (normalizedName.includes(key) || key.includes(normalizedName)) {
              countryCode = code;
              break;
            }
          }
        }
      }
      if (countryCode) {
        const normalizedCode = countryCode.toString().toLowerCase();
        const countryCodeMapping = {
          "uae": "ae",
          "UAE": "ae",
          // United Arab Emirates
          "uk": "gb",
          "UK": "gb",
          // United Kingdom
          "kp": "kp",
          "KP": "kp",
          // North Korea
          "kr": "kr",
          "KR": "kr",
          // South Korea
          // Territories and special regions
          "an": "an",
          "AN": "an",
          // Netherlands Antilles (deprecated but may exist in DB)
          "aq": "aq",
          "AQ": "aq",
          // Antarctica
          "as": "as",
          "AS": "as",
          // American Samoa
          "ax": "ax",
          "AX": "ax",
          // Åland Islands
          "bv": "bv",
          "BV": "bv",
          // Bouvet Island
          "eh": "eh",
          "EH": "eh",
          // Western Sahara
          "sj": "sj",
          "SJ": "sj",
          // Svalbard and Jan Mayen
          "um": "um",
          "UM": "um",
          // US Minor Outlying Islands
          "pn": "pn",
          "PN": "pn"
          // Pitcairn
        };
        const mappedCode = countryCodeMapping[normalizedCode] || normalizedCode;
        setInitCountry(mappedCode);
      } else {
        setInitCountry("");
      }
      if (countryName) {
        setSelectedCountryName(countryName);
      }
      const phoneField = state.nonHiddenFields.find(
        (f) => {
          var _a3;
          return ((_a3 = f.sControlType) == null ? void 0 : _a3.toLowerCase()) === "phone number";
        }
      );
      if (phoneField && countryCode) {
        const phoneFieldName = `custom_${phoneField.hmy}`;
        const currentPhoneValue = state.customdata[phoneFieldName];
        if (currentPhoneValue && currentPhoneValue !== "") {
          setDisplayValue(phoneField.hmy, "");
          setValue(phoneFieldName, "", { shouldValidate: false });
          clearErrors(phoneFieldName);
        }
      }
      const addressRelatedFields = state.nonHiddenFields.filter((f) => {
        const fName = (f.sFieldName || f.sColumnName || "").toLowerCase();
        const isAddress = fName.includes("address") && !fName.includes("email") && !fName.includes("ip address");
        const isCity = fName.includes("city") || fName.includes("town");
        const isState = fName.includes("state") || fName.includes("province") || fName.includes("region") || fName.includes("county") || fName.includes("prefecture") || fName.includes("department") || fName.includes("emirate") || fName.includes("district") || fName.includes("governorate") || fName.includes("oblast");
        const isZip = fName.includes("zip") || fName.includes("post code") || fName.includes("postal");
        return (isAddress || isCity || isState || isZip) && !fName.includes("country");
      });
      if (addressRelatedFields.length > 0) {
        const updates = {};
        addressRelatedFields.forEach((f) => {
          const key = `custom_${f.hmy}`;
          updates[key] = "";
          setValue(key, "", { shouldValidate: false });
          clearErrors(key);
        });
        if (Object.keys(updates).length > 0) {
          setState((prev) => ({ ...prev, customdata: { ...prev.customdata, ...updates } }));
          setValidationErrors((prev) => {
            const next = { ...prev };
            addressRelatedFields.forEach((f) => delete next[f.hmy]);
            return next;
          });
        }
      }
      const stateField = state.nonHiddenFields.find((f) => {
        var _a3, _b3;
        const isStateField = ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("state")) || ((_b3 = f.sColumnName) == null ? void 0 : _b3.toLowerCase().includes("state"));
        return isStateField;
      });
      if (stateField) {
        const countryFieldInSection = state.nonHiddenFields.find((f) => {
          var _a3;
          const isCountryField = (_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country");
          const sameSection = f.hCafeFormSectionsID === stateField.hCafeFormSectionsID;
          return isCountryField && sameSection;
        });
        if (!countryFieldInSection) {
          const allSections2 = customFormService.sections$.value || [];
          const currentSectionIndex = allSections2.findIndex((s) => s.sectionId === stateField.hCafeFormSectionsID);
          if (currentSectionIndex > 0) {
            for (let i = currentSectionIndex - 1; i >= 0; i--) {
              const prevSection = allSections2[i];
              if (prevSection.data) {
                const countryValue = Object.entries(prevSection.data).find(
                  ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
                );
                if (countryValue) {
                  field = {
                    ...field,
                    dValue: countryValue[1],
                    dropdownlist: field.dropdownlist
                  };
                  break;
                }
              }
            }
          }
        }
      }
      if (stateField) {
        try {
          let newControlType = "text box";
          let newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
          let dropdownData = [];
          if (countryName === "United States" || countryName === "Mexico" || countryName === "Australia") {
            newControlType = "drop down";
            newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
            const statesResponse = await configurableFormsApi.getStatesProvinces(selectedValue, true, false);
            if (statesResponse) {
              let dataArray = null;
              if (Array.isArray(statesResponse)) {
                dataArray = statesResponse;
              } else if (statesResponse.Response && Array.isArray(statesResponse.Response)) {
                dataArray = statesResponse.Response;
              } else if (typeof statesResponse === "object") {
                const possibleArrayKeys = ["data", "items", "states", "provinces", "results"];
                for (const key of possibleArrayKeys) {
                  if (statesResponse[key] && Array.isArray(statesResponse[key])) {
                    dataArray = statesResponse[key];
                    break;
                  }
                }
              }
              if (dataArray && dataArray.length > 0) {
                dropdownData = dataArray.map((state2) => ({
                  id: state2.id || state2.hmy || state2.Id || state2.value || "",
                  name: state2.Name || state2.name || state2.sName || state2.text || state2.label || ""
                })).filter((item) => item.id && item.name);
              } else {
                console.warn("?? No valid data array found in states response");
              }
            }
          } else if (countryName === "Canada") {
            newControlType = "drop down";
            newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
            const provincesResponse = await configurableFormsApi.getStatesProvinces(selectedValue, false, true);
            if (provincesResponse) {
              let dataArray = null;
              if (Array.isArray(provincesResponse)) {
                dataArray = provincesResponse;
              } else if (provincesResponse.Response && Array.isArray(provincesResponse.Response)) {
                dataArray = provincesResponse.Response;
              } else if (typeof provincesResponse === "object") {
                const possibleArrayKeys = ["data", "items", "states", "provinces", "results"];
                for (const key of possibleArrayKeys) {
                  if (provincesResponse[key] && Array.isArray(provincesResponse[key])) {
                    dataArray = provincesResponse[key];
                    break;
                  }
                }
              }
              if (dataArray && dataArray.length > 0) {
                dropdownData = dataArray.map((province) => ({
                  id: province.id || province.hmy || province.Id || province.value || "",
                  name: province.Name || province.name || province.sName || province.text || province.label || ""
                })).filter((item) => item.id && item.name);
              } else {
                console.warn("?? No valid data array found in provinces response");
              }
            }
          } else {
            newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
          }
          const currentStateValue = state.customdata[`custom_${stateField.hmy}`];
          let preservedValue = "";
          preservedValue = "";
          setDynamicFieldTypes((prev) => ({
            ...prev,
            [stateField.hmy]: {
              controlType: newControlType,
              label: newLabel,
              dropdownData
            }
          }));
          if (preservedValue !== void 0 && preservedValue !== "") {
            setState((prevState) => ({
              ...prevState,
              customdata: {
                ...prevState.customdata,
                [`custom_${stateField.hmy}`]: preservedValue
              }
            }));
            setValue(`custom_${stateField.hmy}`, preservedValue);
            console.log("🏛️ Set preserved state value:", preservedValue);
          }
        } catch (error) {
          console.error("Error fetching states/provinces:", error);
        }
      }
      const zipFields = state.nonHiddenFields.filter((f) => {
        var _a3, _b3, _c3, _d3;
        const isZipField = ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("zip")) || ((_b3 = f.sFieldName) == null ? void 0 : _b3.toLowerCase().includes("post code")) || ((_c3 = f.sFieldName) == null ? void 0 : _c3.toLowerCase().includes("postal"));
        const isTextBox = ((_d3 = f.sControlType) == null ? void 0 : _d3.toLowerCase()) === "text box";
        return isZipField && isTextBox;
      });
      if (zipFields.length > 0) {
        console.log("🔍 Re-validating", zipFields.length, "zip code field(s) after country change");
        zipFields.forEach((zipField) => {
          const fieldName = `custom_${zipField.hmy}`;
          const currentZipValue = state.customdata[fieldName];
          clearErrors(fieldName);
          setValidationErrors((prev) => {
            const updated = { ...prev };
            delete updated[zipField.hmy];
            return updated;
          });
          if (currentZipValue && currentZipValue.toString().trim()) {
            const countryName2 = getCountryNameForZipValidation(zipField);
            const zipValidation = validateZipCode(currentZipValue.toString(), countryName2);
            console.log("  Validating zip:", currentZipValue, "for country:", countryName2);
            if (!zipValidation.isValid) {
              setError(fieldName, {
                type: "pattern",
                message: zipValidation.message || "Invalid zip/post code format"
              });
              console.log("  ❌ Validation error:", zipValidation.message);
            } else {
              console.log("  ✅ Zip code is valid for country:", countryName2);
            }
          }
        });
      }
    }
  }, [state.nonHiddenFields, state.customdata, setDisplayValue, setError, clearErrors, setValidationErrors, getCountryNameForZipValidation, updateSectionsVisibilityForCountry, getStateFieldLabelByCountry, translate]);
  const preProcessCountryFields = useCallback(async (fields) => {
    var _a2;
    const stateFields = fields.filter((field) => {
      var _a3, _b2;
      const isStateField = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("state")) || ((_b2 = field.sColumnName) == null ? void 0 : _b2.toLowerCase().includes("state"));
      if (isStateField) {
        field.isStateField = true;
      }
      return isStateField;
    });
    const countryFields = fields.filter((field) => {
      var _a3, _b2;
      const isCountryField = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country")) && ((_b2 = field.sControlType) == null ? void 0 : _b2.toLowerCase()) === "drop down";
      if (isCountryField) {
        field.isCountryField = true;
        field.dValue = state.customdata[`custom_${field.hmy}`];
      }
      return isCountryField;
    });
    for (const stateField of stateFields) {
      const countryFieldInSection = countryFields.find(
        (f) => f.hCafeFormSectionsID === stateField.hCafeFormSectionsID
      );
      if (countryFieldInSection) {
        stateField.countryFieldId = countryFieldInSection.hmy;
        stateField.countryFieldSectionId = countryFieldInSection.hCafeFormSectionsID;
        stateField.countryFieldValue = countryFieldInSection.dValue;
      }
      if (!countryFieldInSection) {
        const allSections2 = customFormService.sections$.value || [];
        const currentSectionIndex = allSections2.findIndex((s) => s.sectionId === stateField.hCafeFormSectionsID);
        if (currentSectionIndex > 0) {
          for (let i = currentSectionIndex - 1; i >= 0; i--) {
            const prevSection = allSections2[i];
            if (prevSection.data) {
              const countryValue = Object.entries(prevSection.data).find(
                ([key, value]) => key.toLowerCase().includes("country") && value !== void 0
              );
              if (countryValue) {
                stateField.countryFieldId = countryValue[0];
                stateField.countryFieldSectionId = prevSection.sectionId;
                stateField.countryFieldValue = countryValue[1];
                countryFields.push({
                  ...countryFields[0],
                  // Use first country field as template
                  dValue: countryValue[1],
                  hCafeFormSectionsID: stateField.hCafeFormSectionsID,
                  isCountryField: true
                });
                break;
              }
            }
          }
        }
      }
    }
    for (const countryField of countryFields) {
      const countryValue = state.customdata[`custom_${countryField.hmy}`];
      if (countryValue && ((_a2 = countryField.dropdownlist) == null ? void 0 : _a2.length) > 0) {
        const selectedCountry = countryField.dropdownlist.find((country) => country.id === countryValue);
        const countryName = (selectedCountry == null ? void 0 : selectedCountry.name) || "";
        const stateField = fields.find(
          (f) => {
            var _a3, _b2;
            return ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("state")) || ((_b2 = f.sColumnName) == null ? void 0 : _b2.toLowerCase().includes("state"));
          }
        );
        if (stateField && (countryName === "United States" || countryName === "Mexico" || countryName === "Australia" || countryName === "Canada")) {
          try {
            let dropdownData = [];
            let newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
            if (countryName === "United States" || countryName === "Mexico" || countryName === "Australia") {
              newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
              const statesResponse = await configurableFormsApi.getStatesProvinces(countryValue, true, false);
              if ((statesResponse == null ? void 0 : statesResponse.Response) && Array.isArray(statesResponse.Response)) {
                dropdownData = statesResponse.Response.map((state2) => ({
                  id: state2.id || state2.hmy || state2.Id || "",
                  name: state2.Name || state2.name || state2.sName || ""
                })).filter((item) => item.id && item.name);
              }
            } else if (countryName === "Canada") {
              newLabel = getStateFieldLabelByCountry(countryName, stateField.sFieldName);
              const provincesResponse = await configurableFormsApi.getStatesProvinces(countryValue, false, true);
              if ((provincesResponse == null ? void 0 : provincesResponse.Response) && Array.isArray(provincesResponse.Response)) {
                dropdownData = provincesResponse.Response.map((province) => ({
                  id: province.id || province.hmy || province.Id || "",
                  name: province.Name || province.name || province.sName || ""
                })).filter((item) => item.id && item.name);
              }
            }
            if (dropdownData.length > 0) {
              setDynamicFieldTypes((prev) => ({
                ...prev,
                [stateField.hmy]: {
                  controlType: "drop down",
                  label: newLabel,
                  dropdownData
                }
              }));
            }
          } catch (error) {
            console.error("Error pre-processing country field:", error);
          }
        }
      }
    }
  }, [state.customdata, getStateFieldLabelByCountry]);
  const loadValidationSettings = useCallback(async (sectionId) => {
    try {
      console.log("🔍 Loading validation settings for section:", sectionId);
      const response = await configurableFormsApi.getSectionFieldValidationSettings(sectionId);
      if (response && response.Response && response.Response.length > 0) {
        const validationMap = {};
        response.Response.forEach((setting) => {
          if (!validationMap[setting.FieldId]) {
            validationMap[setting.FieldId] = [];
          }
          validationMap[setting.FieldId].push(setting);
        });
        setFieldValidationSettings(validationMap);
        console.log(`✅ Loaded validation settings for ${Object.keys(validationMap).length} fields`);
      } else {
        console.log("ℹ️ No validation settings found for this section");
      }
    } catch (error) {
      console.error("Error loading validation settings:", error);
    }
  }, []);
  const onFieldValueChange = useCallback((fieldId, value, controlType) => {
    var _a2;
    const field = (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.find((f) => f.hmy === fieldId);
    if (field && isCityOrStateField(field)) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
      return;
    }
    const error = validateField(fieldId, value, controlType, fieldValidationSettings);
    if (error) {
      setValidationErrors((prev) => ({ ...prev, [fieldId]: error }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  }, [fieldValidationSettings, state.nonHiddenFields, isCityOrStateField]);
  const getValidationError = (fieldId) => {
    return validationErrors[fieldId] || null;
  };
  const hasValidationError = (fieldId) => {
    return !!validationErrors[fieldId];
  };
  const handleSave = async (formData) => {
    var _a2, _b2, _c2, _d2, _e;
    const isValid = validateForm();
    if (!isValid) {
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: false, inProgressText: "" });
      return;
    }
    try {
      setState((prev) => ({ ...prev, saving: true, errorMessage: "" }));
      setSuccessMessage("");
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: true, inProgressText: "Saving..." });
      const latestFormValues = getValues();
      const currentSectionData = {};
      const fieldValueMap = /* @__PURE__ */ new Map();
      (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.forEach((field) => {
        const fieldKey = `custom_${field.hmy}`;
        const latestValue = latestFormValues[fieldKey] ?? state.customdata[fieldKey];
        fieldValueMap.set(fieldKey, latestValue);
      });
      const fieldsByColumnName = /* @__PURE__ */ new Map();
      (_b2 = state.nonHiddenFields) == null ? void 0 : _b2.forEach((field) => {
        if (field.sColumnName) {
          const columnName = field.sColumnName.toLowerCase();
          if (!fieldsByColumnName.has(columnName)) {
            fieldsByColumnName.set(columnName, []);
          }
          fieldsByColumnName.get(columnName).push(field);
        }
      });
      fieldsByColumnName.forEach((fields, columnName) => {
        if (fields.length > 1) {
          const modifiedFields = fields.filter((field) => {
            const fieldKey = `custom_${field.hmy}`;
            const currentValue = fieldValueMap.get(fieldKey);
            const initialValue = initialValuesRef.current[fieldKey];
            const normalizeValue = (val) => {
              if (val === void 0 || val === null) return "";
              if (typeof val === "string") return val.trim();
              if (Array.isArray(val)) return JSON.stringify(val);
              return String(val);
            };
            const normalizedCurrent = normalizeValue(currentValue);
            const normalizedInitial = normalizeValue(initialValue);
            const hasChanged = normalizedCurrent !== normalizedInitial;
            console.log(`Field ${field.hmy} (${field.sFieldName}): current="${normalizedCurrent}", initial="${normalizedInitial}", changed=${hasChanged}`);
            return hasChanged;
          });
          let valueToUse = null;
          let fieldToUse = null;
          if (modifiedFields.length > 0) {
            modifiedFields.sort((a, b) => (b.hmy || 0) - (a.hmy || 0));
            fieldToUse = modifiedFields[0];
            const fieldKeyToUse = `custom_${fieldToUse.hmy}`;
            valueToUse = fieldValueMap.get(fieldKeyToUse);
            console.log(`Using modified field ${fieldToUse.hmy} (${fieldToUse.sFieldName}) with value:`, valueToUse);
          } else {
            fields.sort((a, b) => (b.hmy || 0) - (a.hmy || 0));
            fieldToUse = fields[0];
            const fieldKeyToUse = `custom_${fieldToUse.hmy}`;
            valueToUse = fieldValueMap.get(fieldKeyToUse);
            console.log(`No fields modified for column ${columnName}, using highest ID field ${fieldToUse.hmy} (${fieldToUse.sFieldName}) with value:`, valueToUse);
          }
          console.log(`Applying value "${valueToUse}" from field ${fieldToUse.hmy} to all fields with sColumnName="${columnName}"`);
          fields.forEach((field) => {
            const fieldKey = `custom_${field.hmy}`;
            if (valueToUse !== void 0 && valueToUse !== null) {
              fieldValueMap.set(fieldKey, valueToUse);
            }
          });
        }
      });
      (_c2 = state.nonHiddenFields) == null ? void 0 : _c2.forEach((field) => {
        var _a3, _b3, _c3;
        const fieldKey = `custom_${field.hmy}`;
        const value = fieldValueMap.get(fieldKey) ?? latestFormValues[fieldKey] ?? state.customdata[fieldKey];
        const controlType = (_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase();
        const isMultiselect = controlType === "multiselect" || controlType === "multi select" || controlType === "multiselect dropdown";
        if (isMultiselect) {
          currentSectionData[fieldKey] = Array.isArray(value) ? value : [];
          return;
        }
        if (value !== void 0 && value !== null) {
          let processedValue = value;
          if (((_b3 = field.sControlType) == null ? void 0 : _b3.toLowerCase()) === "text box" && typeof value === "string") {
            processedValue = value.trim();
          }
          const isDropdown = controlType === "drop down";
          const isCountryField = (field.sFieldName || "").toLowerCase().includes("country");
          const isStringColumn = typeof field.sColumnName === "string" && field.sColumnName.trim().toLowerCase().startsWith("s");
          if (isDropdown && isCountryField && isStringColumn && processedValue !== "" && processedValue !== null && processedValue !== void 0) {
            const selectedOption = (field.dropdownlist || []).find((opt) => {
              const optId = (opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id);
              return String(optId) === String(processedValue);
            });
            const countryCode = (selectedOption == null ? void 0 : selectedOption.code) ?? (selectedOption == null ? void 0 : selectedOption.Code);
            if (countryCode) {
              processedValue = String(countryCode);
            }
          }
          const isGovernmentIdTypeField = (field.sFieldName || "").toLowerCase().includes("government") && (field.sFieldName || "").toLowerCase().includes("id") && (field.sFieldName || "").toLowerCase().includes("type");
          if (isDropdown && isGovernmentIdTypeField && isStringColumn && processedValue !== "" && processedValue !== null && processedValue !== void 0) {
            const selectedOption = (field.dropdownlist || []).find((opt) => {
              const optId = (opt == null ? void 0 : opt.Id) ?? (opt == null ? void 0 : opt.id);
              return String(optId) === String(processedValue);
            });
            const governmentIdTypeName = (selectedOption == null ? void 0 : selectedOption.Name) ?? (selectedOption == null ? void 0 : selectedOption.name);
            if (governmentIdTypeName) {
              processedValue = String(governmentIdTypeName);
            }
          }
          if (((_c3 = field.sControlType) == null ? void 0 : _c3.toLowerCase()) === "date" && typeof processedValue === "string") {
            if (processedValue.startsWith("1900-01-01") || processedValue === "") {
              return;
            }
          }
          if (controlType === "phone number" || controlType === "phone") {
            if (processedValue === "" || typeof processedValue === "string" && processedValue.trim() === "") {
              currentSectionData[fieldKey] = "";
              return;
            }
            if (typeof processedValue === "string") {
              const digitsOnly = processedValue.replace(/[^\d]/g, "");
              if (processedValue === "+" || digitsOnly.length <= 3) {
                currentSectionData[fieldKey] = "";
                return;
              }
            }
          }
          currentSectionData[fieldKey] = processedValue;
        }
      });
      if (queryParams.length > 0) {
        queryParams.forEach(([key, value]) => {
          currentSectionData[key] = value;
        });
      }
      console.log("Saving ONLY current section data:", currentSectionData);
      const allSections2 = customFormService.sections$.value || [];
      const currentSection2 = allSections2.find((s) => s.id === pageData.sectionId);
      if (currentSection2) {
        currentSection2.data = currentSectionData;
        customFormService.sections$.next([...allSections2]);
        Object.assign(visibilityDataStore.current, currentSectionData);
      }
      const previousSections = allSections2.filter((section) => section.completed && section.id !== pageData.sectionId).map((section) => ({
        sectionId: section.sectionId,
        formData: section.data || {}
      }));
      console.log("Previous sections data:", previousSections);
      const saveData = {
        formData: currentSectionData,
        // Only current section data
        sectionId: pageData.sectionId,
        Id: Id || 0,
        allSectionsData: pageData.fields,
        appData: customAppData,
        table_customData: state.tableItems,
        previousSectionData: previousSections
      };
      const response = await configurableFormsApi.saveCustomFormData(saveData);
      console.log("saveCustomFormData response:", response);
      if (onSaveData && response) {
        onSaveData(response);
      }
      const sectionToSave = allSections2.find(
        (s) => s.id === pageData.sectionId || s.sectionId === pageData.sectionId || String(s.id) === String(pageData.sectionId) || String(s.sectionId) === String(pageData.sectionId)
      );
      const sectionIdToSave = (sectionToSave == null ? void 0 : sectionToSave.id) || pageData.sectionId;
      console.log("About to call saveSectionProgress:", {
        sectionIdToSave,
        sectionToSave: sectionToSave ? { id: sectionToSave.id, sectionId: sectionToSave.sectionId, UUID: sectionToSave.UUID, name: sectionToSave.name } : null,
        pageDataSectionId: pageData.sectionId,
        allSectionsCount: allSections2.length
      });
      let progressResult;
      try {
        progressResult = await customFormService.saveSectionProgress(sectionIdToSave, response, stepID);
        console.log("Progress result:", {
          allSectionsCompleted: progressResult == null ? void 0 : progressResult.allSectionsCompleted,
          hasCallback: !!onAllSectionsCompleted,
          sectionId: pageData.sectionId
        });
      } catch (progressError) {
        console.error("Error saving section progress:", progressError);
        progressResult = { allSectionsCompleted: false };
      }
      const updatedSections = customFormService.sections$.value || [];
      const updatedSection = updatedSections.find((s) => s.id === pageData.sectionId);
      if (updatedSection) {
        updatedSection.data = currentSectionData;
        customFormService.sections$.next([...updatedSections]);
      }
      if (pageData.sectionId) {
        await checkSectionVisibilityAfterSave(Number(pageData.sectionId), currentSectionData);
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
      const allSectionsAfterSave = customFormService.sections$.value || [];
      const currentSectionIndex = allSectionsAfterSave.findIndex((s) => s.id === pageData.sectionId);
      const remainingSections = allSectionsAfterSave.slice(currentSectionIndex + 1);
      const remainingHiddenSections = remainingSections.filter((s) => {
        const isVisibleByQuery = s.hideQuerySettings ? s.isVisibleByQuery === true : s.isVisibleByQuery !== false;
        const isVisibleForCountry = s.isVisibleForCountry !== false;
        return !isVisibleByQuery || !isVisibleForCountry;
      });
      if (remainingHiddenSections.length > 0) {
        try {
          const userId = localStorage.getItem("LoginApplicantId");
          const propertyId = getConfigurableFormsConfigOrThrow().getPropertyId();
          const templateId = customFormService.getTemplateId();
          const progressId = customFormService.sectionProgressId$.value;
          const finalCompleted = customFormService.completedSectionList$.value;
          const finalIncomplete = customFormService.incompleteSectionList$.value;
          if (userId && propertyId && templateId) {
            await configurableFormsApi.saveSectionProgress({
              UserId: 0,
              PropertyId: propertyId,
              TemplateId: templateId,
              CompletedSectionList: finalCompleted.join(","),
              IncompleteSectionList: finalIncomplete.join(","),
              hmy: progressId || 0,
              StepID: stepID,
              hObjPointer: userId
            });
            console.log(
              "[After Save] Persisted all hidden sections in one batch call:",
              remainingHiddenSections.map((s) => s.name).join(", ")
            );
          }
        } catch (err) {
          console.warn("[After Save] Failed to persist hidden sections batch:", err);
        }
      }
      const allRemainingHidden = remainingSections.length > 0 && remainingSections.every((s) => {
        const isVisibleByQuery = s.hideQuerySettings ? s.isVisibleByQuery === true : s.isVisibleByQuery !== false;
        const isVisibleForCountry = s.isVisibleForCountry !== false;
        return !isVisibleByQuery || !isVisibleForCountry;
      });
      const finalIncompleteList = ((_d2 = customFormService.incompleteSectionList$) == null ? void 0 : _d2.value) || [];
      const allSectionsNowCompleted = finalIncompleteList.length === 0;
      const isReadOnly = ReadOnlyService.isReadOnly();
      if (pageData.sectionName === "Personal Information") {
        window.dispatchEvent(new CustomEvent("menuRefreshRequested", {
          detail: {
            source: "CustomFormControl-PersonalInformation",
            fetchMenu: true
          }
        }));
      }
      const allowCompletedSections = isReadOnly || allSectionsNowCompleted;
      const navigationResult = await customFormService.nextButtonClick(pageData.sectionName, pageData.sectionId, allowCompletedSections);
      console.log("Callback Debug Info:", {
        hasNavigationResult: !!navigationResult,
        allSectionsCompleted: progressResult == null ? void 0 : progressResult.allSectionsCompleted,
        hasCallbackFunction: !!onAllSectionsCompleted,
        progressResult,
        isReadOnly,
        allowCompletedSections
      });
      if (navigationResult && !allRemainingHidden) {
        toast$1({
          title: translate("Success"),
          description: translate("Section saved successfully."),
          variant: "default",
          duration: 3e3
        });
        onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: false, inProgressText: "" });
        setSuccessMessage("");
        navigate(navigationResult.url, { replace: true, state: navigationResult.state });
        return;
      }
      if (!navigationResult && ((progressResult == null ? void 0 : progressResult.allSectionsCompleted) || allSectionsNowCompleted || allRemainingHidden) && onAllSectionsCompleted) {
        console.log("Last section reached (no next section) - triggering callback with data:", progressResult);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setSuccessMessage(translate("Congratulations! All sections completed successfully!"));
        console.log("Set completion success message");
        const hasFullData = progressResult && "completedSections" in progressResult;
        const finalCompletedList = ((_e = customFormService.completedSectionList$) == null ? void 0 : _e.value) || [];
        const finalAllSections = customFormService.sections$.value || [];
        onAllSectionsCompleted({
          allSectionsCompleted: true,
          completedSections: hasFullData ? progressResult.completedSections : finalCompletedList,
          allSectionData: hasFullData ? progressResult.allSectionData : finalAllSections,
          templateId: hasFullData ? progressResult.templateId : null,
          finalSectionData: response
        });
      } else {
        console.log("Setting regular section save message - Callback NOT triggered because:", {
          hasNavigationResult: !!navigationResult,
          allSectionsCompleted: progressResult == null ? void 0 : progressResult.allSectionsCompleted,
          hasCallbackFunction: !!onAllSectionsCompleted,
          isReadOnly
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setSuccessMessage(translate("Section saved successfully!"));
      }
    } catch (error) {
      console.error("Error saving form:", error);
      let errorMessage = translate("An error occurred while saving. Please try again.");
      let toastDescription = translate("Failed to save form data. Please try again.");
      if (error instanceof Error) {
        console.error("Error details:", error.message, error.stack);
        if (error.message.includes("navigation") || error.message.includes("section")) {
          console.log("Navigation-related error detected, but form data may have been saved successfully");
        }
      }
      setState((prev) => ({
        ...prev,
        errorMessage
      }));
      toast$1({
        title: translate("Error"),
        description: toastDescription,
        variant: "destructive",
        duration: 5e3
      });
    } finally {
      setState((prev) => ({ ...prev, saving: false }));
      setIsSaving(false);
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: false, inProgressText: "" });
    }
  };
  const handlePrevious = async () => {
    var _a2, _b2, _c2, _d2;
    try {
      clearErrors();
      setState((prev) => ({ ...prev, errorMessage: "" }));
      setSuccessMessage("");
      const allSections2 = customFormService.sections$.value || [];
      if (allSections2.length > 0 && (pageData == null ? void 0 : pageData.sectionId)) {
        const allFieldData = { ...visibilityDataStore.current };
        allSections2.forEach((section) => {
          if (section.completed && section.data) {
            Object.assign(allFieldData, section.data);
          }
        });
        allSections2.forEach((section) => {
          if (section.hideQuerySettings) {
            const isVisible = isSectionVisibleByQuery(section, allFieldData);
            section.isVisibleByQuery = isVisible;
            console.log(`[handlePrevious] Re-evaluated "${section.name}" visibility: ${isVisible}`);
          }
        });
        customFormService.sections$.next([...allSections2]);
      }
      const navigationResult = await customFormService.previousButtonClick(pageData.sectionName, pageData.sectionId);
      if (navigationResult) {
        navigate(navigationResult.url, { replace: true, state: navigationResult.state });
      } else {
        const LastCompletedStepPagenName = localStorage.getItem("LastCompletedStepPagenName");
        const sitename = (((_b2 = (_a2 = getConfigurableFormsConfigOrThrow()).getSiteName) == null ? void 0 : _b2.call(_a2)) ?? ((_c2 = localStorage.getItem("sitename")) == null ? void 0 : _c2.replace(/"/g, "")) ?? "") || "";
        if (LastCompletedStepPagenName && sitename) {
          const cfg = getConfigurableFormsConfigOrThrow();
          const redirectUrl = (_d2 = cfg.buildRedirectUrl) == null ? void 0 : _d2.call(cfg, { siteName: sitename, completedStepPageName: LastCompletedStepPagenName });
          if (redirectUrl) {
            navigate(redirectUrl, { replace: true });
          } else {
            console.warn("buildRedirectUrl not configured; cannot redirect to completed step.");
          }
        } else {
          console.warn("Cannot navigate to previous step - no navigation data available:", { LastCompletedStepPagenName, sitename });
          toast$1({
            title: translate("Info"),
            description: translate("You are on the first section."),
            variant: "default",
            duration: 3e3
          });
        }
      }
    } catch (error) {
      console.error("Error navigating to previous section:", error);
      toast$1({
        title: translate("Error"),
        description: translate("Could not navigate to the previous step. Please try again."),
        variant: "destructive",
        duration: 5e3
      });
    }
  };
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [documentsSetup, setDocumentsSetup] = useState([]);
  const getUploadSetup = async (sectionId) => {
    var _a2;
    try {
      const request = {
        WorkflowStep: sectionId,
        ObjectID: localStorage.getItem("LoginApplicantId"),
        ObjectType: 103,
        ShowAdendumBeforeOrAfterPage: 0,
        OpenInSeperatePage: false,
        ProspectCurrentStep: 60,
        IsSIgnOnlyApplication: false,
        IsSiteManagerPreview: false
      };
      const response = await configurableFormsApi.getUploadSetup(request);
      if ((_a2 = response == null ? void 0 : response.response) == null ? void 0 : _a2.IsDocumentSetupAvailbleAtThisStep) {
        setDocumentsSetup(response.response.Setups);
      }
    } catch (error) {
      console.error("Error getting upload setup:", error);
      toast$1({
        title: translate("Error"),
        description: translate("Failed to get upload setup"),
        variant: "destructive"
      });
    }
  };
  const handleFileSelect = async (event, sectionId) => {
    var _a2;
    const file = (_a2 = event.target.files) == null ? void 0 : _a2[0];
    if (file) {
      setSelectedFile(file);
      await getUploadSetup(sectionId);
      if (documentsSetup.length > 0 && documentsSetup[0]) {
        handleUpload(file, documentsSetup[0].SetupId, documentsSetup[0].SetupTitle);
      }
    }
  };
  const handleUpload = async (file, setupId, setupTitle) => {
    try {
      const formData = new FormData();
      formData.append("FileData", file, file.name);
      formData.append("SetupID", setupId.toString());
      formData.append("SetupTitle", setupTitle);
      formData.append("UploadTOtenant", "false");
      formData.append("BeforeOrAfterRequiredPage", "false");
      formData.append("bSendEmailToFSDocs", "false");
      const response = await configurableFormsApi.uploadDocument(formData);
      if (response == null ? void 0 : response.Status) {
        toast$1({
          title: translate("Success"),
          description: translate("Document uploaded successfully")
        });
      } else {
        toast$1({
          title: translate("Error"),
          description: translate((response == null ? void 0 : response.Message) || "Document upload failed"),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast$1({
        title: translate("Error"),
        description: translate("Failed to upload document"),
        variant: "destructive"
      });
    }
  };
  const getErrorMessage = (fieldName) => {
    const error = errors[fieldName];
    if (!error) return "";
    return typeof error.message === "string" ? translate(error.message) : translate("This field is required");
  };
  const renderField = useCallback((field) => {
    var _a2, _b2, _c2;
    if (!isConditionalShow(field.hmy) || !(field.bVisible || field.bAlwaysVisible)) {
      return null;
    }
    const isRequired = (field._bRequired || field._bAlwaysRequired) && !isConditionalReadOnly(field.hmy);
    const isReadOnly = field._bIsReadOnly || isConditionalReadOnly(field.hmy);
    const fieldName = `custom_${field.hmy}`;
    const dynamicField = dynamicFieldTypes[field.hmy];
    const effectiveControlType = (dynamicField == null ? void 0 : dynamicField.controlType) || field.sControlType;
    (dynamicField == null ? void 0 : dynamicField.label) || field.sFieldName;
    switch (effectiveControlType.toLowerCase()) {
      case "text box": {
        const fieldNameLower = ((_a2 = field.sFieldName) == null ? void 0 : _a2.toLowerCase()) || "";
        const isQuestionField = fieldNameLower.startsWith("do you") || fieldNameLower.startsWith("have you") || fieldNameLower.includes("do you have");
        const isSSNField = !isQuestionField && (fieldNameLower.includes("ssn") || fieldNameLower.includes("social security")) && !fieldNameLower.includes("do you");
        const isAddressField = fieldNameLower.includes("address") && !fieldNameLower.includes("line 2") && !fieldNameLower.includes("address 2") && !fieldNameLower.includes("email") && !fieldNameLower.includes("phone") && !fieldNameLower.includes("website") && !fieldNameLower.includes("url");
        if (isAddressField) {
          const iso2ToIso3 = {
            "us": "USA",
            "uk": "GBR",
            "gb": "GBR",
            "ca": "CAN",
            "au": "AUS",
            "nz": "NZL",
            "de": "DEU",
            "fr": "FRA",
            "it": "ITA",
            "es": "ESP",
            "pt": "PRT",
            "nl": "NLD",
            "be": "BEL",
            "ch": "CHE",
            "at": "AUT",
            "se": "SWE",
            "no": "NOR",
            "dk": "DNK",
            "fi": "FIN",
            "ie": "IRL",
            "pl": "POL",
            "cz": "CZE",
            "sk": "SVK",
            "hu": "HUN",
            "ro": "ROU",
            "bg": "BGR",
            "hr": "HRV",
            "si": "SVN",
            "lt": "LTU",
            "lv": "LVA",
            "ee": "EST",
            "gr": "GRC",
            "cy": "CYP",
            "mt": "MLT",
            "lu": "LUX",
            "rs": "SRB",
            "in": "IND",
            "cn": "CHN",
            "jp": "JPN",
            "kr": "KOR",
            "sg": "SGP",
            "hk": "HKG",
            "tw": "TWN",
            "my": "MYS",
            "id": "IDN",
            "ph": "PHL",
            "th": "THA",
            "vn": "VNM",
            "pk": "PAK",
            "bd": "BGD",
            "lk": "LKA",
            "np": "NPL",
            "mm": "MMR",
            "ae": "ARE",
            "sa": "SAU",
            "eg": "EGY",
            "il": "ISR",
            "tr": "TUR",
            "qa": "QAT",
            "kw": "KWT",
            "bh": "BHR",
            "om": "OMN",
            "jo": "JOR",
            "lb": "LBN",
            "ir": "IRN",
            "iq": "IRQ",
            "mx": "MEX",
            "br": "BRA",
            "ar": "ARG",
            "cl": "CHL",
            "co": "COL",
            "pe": "PER",
            "uy": "URY",
            "ve": "VEN",
            "ec": "ECU",
            "bo": "BOL",
            "py": "PRY",
            "cr": "CRI",
            "pa": "PAN",
            "gt": "GTM",
            "hn": "HND",
            "sv": "SLV",
            "ni": "NIC",
            "cu": "CUB",
            "do": "DOM",
            "za": "ZAF",
            "ng": "NGA",
            "ke": "KEN",
            "gh": "GHA",
            "tz": "TZA",
            "ug": "UGA",
            "et": "ETH",
            "ma": "MAR",
            "dz": "DZA",
            "tn": "TUN",
            "ly": "LBY",
            "sd": "SDN",
            "ao": "AGO",
            "cm": "CMR",
            "ci": "CIV",
            "sn": "SEN",
            "zm": "ZMB",
            "zw": "ZWE",
            "mz": "MOZ",
            "mg": "MDG",
            "bw": "BWA",
            "na": "NAM",
            "rw": "RWA",
            "mu": "MUS",
            "sc": "SYC",
            "fj": "FJI",
            "pg": "PNG",
            "sb": "SLB",
            "vu": "VUT",
            "ws": "WSM",
            "to": "TON",
            "tv": "TUV",
            "ru": "RUS",
            "ua": "UKR",
            "by": "BLR",
            "kz": "KAZ"
          };
          const rawIso2 = (getCountryCodeForField(field) || "us").toLowerCase().trim();
          const smartyCountryCode = rawIso2.length === 3 ? rawIso2.toUpperCase() : iso2ToIso3[rawIso2] ?? rawIso2.toUpperCase();
          const fillAddressSuggestionIntoSectionFields = (suggestion) => {
            const sectionId = String(field.hCafeFormSectionsID);
            const sectionFields = (state.nonHiddenFields || []).filter(
              (f) => String(f.hCafeFormSectionsID) === sectionId
            );
            const streetValue = suggestion.street_line ?? suggestion.addressText ?? "";
            setDisplayValue(field.hmy, streetValue);
            setValue(fieldName, streetValue);
            if (errors[fieldName]) clearErrors(fieldName);
            onFieldValueChange(field.hmy, streetValue, field.sControlType);
            const name = (f) => (f.sFieldName || "").toLowerCase();
            sectionFields.forEach((f) => {
              var _a3, _b3;
              if (f.hmy === field.hmy) return;
              const key = `custom_${f.hmy}`;
              const controlType = (((_a3 = dynamicFieldTypes[f.hmy]) == null ? void 0 : _a3.controlType) || f.sControlType || "").toLowerCase();
              if (name(f).includes("address") && (name(f).includes("line 2") || name(f).includes("line two") || name(f).includes("address 2"))) {
                const rawSecondary = suggestion.secondary ?? "";
                const val = /^(Road|Street|St|Avenue|Ave|Boulevard|Blvd|Lane|Ln|Drive|Dr|Way|Close|Court|Ct|Place|Pl|Crescent|Terrace|Gardens|Grove|Hill|Row|Square|Rd)$/i.test(rawSecondary.trim()) ? "" : rawSecondary;
                setDisplayValue(f.hmy, val);
                setValue(key, val, { shouldValidate: false });
                clearErrors(key);
                onFieldValueChange(f.hmy, val, f.sControlType);
                return;
              }
              if (name(f).includes("house number") || name(f).includes("street number") || name(f) === "number") {
                const val = suggestion.premise || "";
                if (val) {
                  setDisplayValue(f.hmy, val);
                  setValue(key, val, { shouldValidate: false });
                  clearErrors(key);
                  onFieldValueChange(f.hmy, val, f.sControlType);
                }
                return;
              }
              if (name(f).includes("street name") || name(f) === "street") {
                const val = suggestion.thoroughfare || suggestion.street_line || "";
                if (val) {
                  setDisplayValue(f.hmy, val);
                  setValue(key, val, { shouldValidate: false });
                  clearErrors(key);
                  onFieldValueChange(f.hmy, val, f.sControlType);
                }
                return;
              }
              if (name(f).includes("city") || name(f).includes("town") || name(f).includes("municipality") || name(f).includes("locality")) {
                let cityVal = suggestion.locality || suggestion.city || "";
                if (!isCityReasonableForCountry(cityVal)) cityVal = "";
                if (cityVal && /^(.+)-[Ss]hi$/.test(cityVal)) {
                  const base = cityVal.replace(/-[Ss]hi$/, "");
                  cityVal = `${base} City (${base}-shi)`;
                }
                setDisplayValue(f.hmy, cityVal);
                setValue(key, cityVal, { shouldValidate: false });
                clearErrors(key);
                onFieldValueChange(f.hmy, cityVal, f.sControlType);
                return;
              }
              if (name(f).includes("state") || name(f).includes("prefecture") || name(f).includes("province") || name(f).includes("region") || name(f).includes("county") || name(f).includes("department") || name(f).includes("emirate") || name(f).includes("district") || name(f).includes("governorate") || name(f).includes("oblast")) {
                let stateRaw = suggestion.state || suggestion.administrative_area || suggestion.province || suggestion.region || "";
                if (smartyCountryCode && !isStateValidForCountry(stateRaw, smartyCountryCode)) stateRaw = "";
                if (controlType === "drop down") {
                  const options = ((_b3 = dynamicFieldTypes[f.hmy]) == null ? void 0 : _b3.dropdownData) || f.dropdownlist || [];
                  const optList = Array.isArray(options) ? options : [];
                  const stateUpper = stateRaw.trim().toUpperCase();
                  const match = optList.find(
                    (opt) => (
                      // Match by abbreviation/code (US-style)
                      String(opt.code || opt.Code || opt.sCode || "").toUpperCase() === stateUpper || // Match by full name (international — Smarty returns e.g. "New South Wales")
                      String(opt.name || opt.Name || "").toLowerCase() === stateRaw.toLowerCase() || // Match by id as string
                      String(opt.id ?? opt.Id ?? "").toString() === String(stateRaw)
                    )
                  );
                  const val = match ? match.id ?? match.Id ?? "" : stateRaw;
                  setDisplayValue(f.hmy, match ? match.name || match.Name || val : stateRaw);
                  setValue(key, val, { shouldValidate: false });
                  clearErrors(key);
                  onFieldValueChange(f.hmy, val, f.sControlType);
                } else {
                  setDisplayValue(f.hmy, stateRaw);
                  setValue(key, stateRaw, { shouldValidate: false });
                  clearErrors(key);
                  onFieldValueChange(f.hmy, stateRaw, f.sControlType);
                }
                return;
              }
              const isPostcodeField = name(f).includes("zip") || name(f).includes("post code") || name(f).includes("postcode") || name(f).includes("postal") || name(f).includes("pincode") || name(f).includes("eircode") || name(f).includes("pin") && (name(f).includes("code") || name(f) === "pin");
              if (isPostcodeField) {
                let postcodeVal = suggestion.zipcode || suggestion.postal_code || "";
                if (!postcodeVal && suggestion.addressText && smartyCountryCode) {
                  const text = suggestion.addressText;
                  const ukFull = text.match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i);
                  const ukOutcode = text.match(/\b([A-Z]{1,2}\d[A-Z\d]?)\b(?!\s*\d[A-Z]{2})/i);
                  if (ukFull) postcodeVal = ukFull[1].replace(/\s+/g, " ").trim();
                  else if (ukOutcode) postcodeVal = ukOutcode[1];
                  if (!postcodeVal) {
                    const usMatch = text.match(/\b(\d{5}(?:-\d{4})?)\b/);
                    if (usMatch) postcodeVal = usMatch[1];
                  }
                  if (!postcodeVal) {
                    const auMatch = text.match(/\b((?:[A-Za-z]{2,3}\s+)?\d{4})\b/);
                    if (auMatch) postcodeVal = auMatch[1].trim();
                  }
                }
                if (smartyCountryCode && postcodeVal && !isValidPostcodeForCountry(postcodeVal, smartyCountryCode)) postcodeVal = "";
                setDisplayValue(f.hmy, postcodeVal);
                setValue(key, postcodeVal, { shouldValidate: false });
                clearErrors(key);
                onFieldValueChange(f.hmy, postcodeVal, f.sControlType);
              }
            });
          };
          return /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx(
              AddressSuggestionInput,
              {
                value: getDisplayValue(field.hmy) ?? "",
                onChange: (val) => {
                  setDisplayValue(field.hmy, val);
                  setValue(fieldName, val);
                  if (errors[fieldName]) clearErrors(fieldName);
                  onFieldValueChange(field.hmy, val, field.sControlType);
                },
                onSuggestionSelect: (suggestion) => fillAddressSuggestionIntoSectionFields(suggestion),
                country: smartyCountryCode,
                placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
                required: isRequired,
                className: `${errors[fieldName] || hasValidationError(field.hmy) ? "form-field-error-glow" : ""}`,
                cssClass: `${styles.input.base} ${styles.input.valid} ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly" : ""} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error" : ""}`
              }
            ),
            !isReadOnly && !ReadOnlyService.isReadOnly() && (getValidationError(field.hmy) || errors[fieldName]) && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", children: getValidationError(field.hmy) || getErrorMessage(fieldName) })
          ] });
        }
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            YardiControlTextBox,
            {
              ...register(fieldName),
              value: getDisplayValue(field.hmy),
              onChange: (e) => {
                let newValue = (e == null ? void 0 : e.value) ?? "";
                if (isSSNField) {
                  const digits = newValue.replace(/\D/g, "");
                  const limitedDigits = digits.substring(0, 9);
                  if (limitedDigits.length <= 3) {
                    newValue = limitedDigits;
                  } else if (limitedDigits.length <= 5) {
                    newValue = `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
                  } else {
                    newValue = `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 5)}-${limitedDigits.slice(5)}`;
                  }
                }
                setDisplayValue(field.hmy, newValue);
                setValue(fieldName, newValue);
                if (errors[fieldName]) {
                  clearErrors(fieldName);
                }
                onFieldValueChange(field.hmy, newValue, field.sControlType);
              },
              onBlur: (_e) => {
                var _a3, _b3, _c3;
                const currentValue = getDisplayValue(field.hmy);
                const stringValue = (currentValue == null ? void 0 : currentValue.toString()) || "";
                const isZipField = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("zip")) || ((_b3 = field.sFieldName) == null ? void 0 : _b3.toLowerCase().includes("post code")) || ((_c3 = field.sFieldName) == null ? void 0 : _c3.toLowerCase().includes("postal"));
                if (isRequired && stringValue && stringValue.trim() === "") {
                  setError(fieldName, {
                    type: "required",
                    message: "Please enter valid content (spaces only is not allowed)"
                  });
                } else if (isRequired && (!currentValue || currentValue === "") && !isZipField && !isSSNField) {
                  setError(fieldName, {
                    type: "required",
                    message: translate("This field is required")
                  });
                } else if (isSSNField && stringValue.trim()) {
                  const digits = stringValue.replace(/\D/g, "");
                  if (digits.length > 0 && digits.length !== 9) {
                    setError(fieldName, {
                      type: "pattern",
                      message: translate("SSN must be exactly 9 digits (XXX-XX-XXXX)")
                    });
                  } else if (isRequired && digits.length === 0) {
                    setError(fieldName, {
                      type: "required",
                      message: translate("This field is required")
                    });
                  } else {
                    clearErrors(fieldName);
                  }
                } else if (isZipField) {
                  if (stringValue.trim()) {
                    const countryName = getCountryNameForZipValidation(field);
                    const zipValidation = validateZipCode(stringValue, countryName);
                    if (!zipValidation.isValid) {
                      setError(fieldName, {
                        type: "pattern",
                        message: zipValidation.message || "Invalid zip/post code format"
                      });
                    } else {
                      clearErrors(fieldName);
                    }
                  } else if (isRequired) {
                    setError(fieldName, {
                      type: "required",
                      message: translate("This field is required")
                    });
                  } else {
                    clearErrors(fieldName);
                  }
                } else {
                  clearErrors(fieldName);
                }
                onFieldValueChange(field.hmy, currentValue, field.sControlType);
              },
              placeholder: isSSNField ? translate("XXX-XX-XXXX") : field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
              readonly: isReadOnly,
              enabled: !ReadOnlyService.isReadOnly(),
              disabled: ReadOnlyService.isReadOnly(),
              overrideFwReadOnlyFlag: true,
              divClassName: "w-full",
              cssClass: `${styles.input.base} ${styles.input.valid} ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly" : ""} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error form-field-error-glow" : ""}`,
              floatLabelType: "Never",
              "aria-required": isRequired,
              "aria-describedby": `error_${field.hmy}`
            }
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && (getValidationError(field.hmy) || errors[fieldName]) && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: getValidationError(field.hmy) || getErrorMessage(fieldName) })
        ] });
      }
      case "drop down":
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            YardiControlDropdown,
            {
              value: getDisplayValue(field.hmy) ?? "",
              onChange: (args) => {
                var _a3, _b3, _c3, _d2, _e, _f;
                const newValue = (args == null ? void 0 : args.value) != null ? String(args.value) : "";
                setDisplayValue(field.hmy, newValue);
                setValue(fieldName, newValue);
                const fieldNameLower = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase()) || "";
                const isQuestionField = fieldNameLower.startsWith("do you") || fieldNameLower.startsWith("have you") || fieldNameLower.includes("do you have");
                if (isQuestionField && (fieldNameLower.includes("ssn") || fieldNameLower.includes("social security"))) {
                  clearErrors(fieldName);
                }
                let isValidValue = newValue !== null && newValue !== void 0 && newValue !== "";
                if ((_b3 = field.sFieldName) == null ? void 0 : _b3.toLowerCase().includes("university")) {
                  const zeroVal = String(newValue).trim();
                  isValidValue = isValidValue && zeroVal !== "0" && zeroVal !== "0.0";
                }
                if (isValidValue) {
                  clearErrors(fieldName);
                } else if (isRequired) {
                  setError(fieldName, {
                    type: "required",
                    message: translate("This field is required")
                  });
                }
                if (((_c3 = field.sFieldName) == null ? void 0 : _c3.toLowerCase().includes("government")) && ((_d2 = field.sFieldName) == null ? void 0 : _d2.toLowerCase().includes("id")) && ((_e = field.sFieldName) == null ? void 0 : _e.toLowerCase().includes("type"))) {
                  const idNumberField = (_f = state.nonHiddenFields) == null ? void 0 : _f.find(
                    (f) => {
                      var _a4, _b4, _c4, _d3, _e2;
                      return ((_a4 = f.sFieldName) == null ? void 0 : _a4.toLowerCase().includes("id")) && ((_b4 = f.sFieldName) == null ? void 0 : _b4.toLowerCase().includes("number")) || ((_c4 = f.sFieldName) == null ? void 0 : _c4.toLowerCase().includes("enter")) && ((_d3 = f.sFieldName) == null ? void 0 : _d3.toLowerCase().includes("number")) || ((_e2 = f.sFieldName) == null ? void 0 : _e2.toLowerCase().includes("identification number"));
                    }
                  );
                  if (idNumberField) {
                    if (newValue !== null && newValue !== void 0 && newValue !== "") {
                      const selectedOption = ((dynamicField == null ? void 0 : dynamicField.dropdownData) || field.dropdownlist || []).find(
                        (option) => String(option.Id ?? option.id) === String(newValue)
                      );
                      const selectedText = (selectedOption == null ? void 0 : selectedOption.Name) || (selectedOption == null ? void 0 : selectedOption.name) || "";
                      if (selectedText) {
                        setDynamicFieldTypes((prev) => ({
                          ...prev,
                          [idNumberField.hmy]: {
                            ...prev[idNumberField.hmy],
                            label: `Enter ${selectedText} Number`,
                            controlType: idNumberField.sControlType
                          }
                        }));
                      }
                    } else {
                      setDynamicFieldTypes((prev) => ({
                        ...prev,
                        [idNumberField.hmy]: {
                          ...prev[idNumberField.hmy],
                          label: idNumberField.sFieldName,
                          controlType: idNumberField.sControlType
                        }
                      }));
                    }
                  }
                }
                checkCountryChange(field, newValue);
                handleModelChange(field.sColumnName);
              },
              dataSource: normalizeDropdownOptions(
                field,
                (dynamicField == null ? void 0 : dynamicField.dropdownData) || field.dropdownlist || [],
                translate
              ),
              fields: { text: "Name", value: "id" },
              placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : translate("Select..."),
              allowFiltering: true,
              divClassName: "w-full",
              cssClass: `${styles.input.base} ${styles.input.valid} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error form-field-error-glow" : ""}`,
              floatLabelType: "Never",
              readonly: isReadOnly,
              disabled: ReadOnlyService.isReadOnly(),
              overrideFwReadOnlyFlag: true,
              enableMobileDrawer: true,
              mobileDrawerTitle: translate(field.sFieldName || ""),
              "aria-required": isRequired,
              "aria-describedby": `error_${field.hmy}`,
              ...isMobile ? { popupHeight: "300px", popupWidth: "100%" } : {}
            },
            `dropdown-${field.hmy}-${dynamicField ? "dynamic" : "static"}`
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "multi select":
      case "multiselect":
      case "multiselect dropdown":
        return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
          /* @__PURE__ */ jsx(
            MultiSelectComponent,
            {
              id: `field_${field.hmy}`,
              value: getDisplayValue(field.hmy) ?? [],
              change: (args) => {
                const newValue = (args == null ? void 0 : args.value) ?? [];
                setDisplayValue(field.hmy, newValue);
                setValue(fieldName, newValue);
                if (Array.isArray(newValue) && newValue.length > 0) {
                  clearErrors(fieldName);
                } else if (isRequired) {
                  setError(fieldName, {
                    type: "required",
                    message: translate("This field is required")
                  });
                }
                handleModelChange(field.sColumnName);
              },
              dataSource: normalizeDropdownOptions(
                field,
                (dynamicField == null ? void 0 : dynamicField.dropdownData) || field.dropdownlist || [],
                translate
              ),
              fields: { text: "Name", value: "id" },
              placeholder: field.sPlaceHolderText || translate("Select..."),
              mode: "Box",
              showDropDownIcon: true,
              showClearButton: true,
              allowFiltering: true,
              filterType: "Contains",
              filterBarPlaceholder: translate("Search..."),
              ignoreAccent: true,
              cssClass: `${styles.input.base} ${styles.input.valid} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error form-field-error-glow" : ""}`,
              floatLabelType: "Never",
              readonly: isReadOnly,
              enabled: !isReadOnly && !ReadOnlyService.isReadOnly(),
              ...isMobile ? { popupHeight: "300px", popupWidth: "100%" } : {}
            },
            `multiselect-${field.hmy}-${dynamicField ? "dynamic" : "static"}`
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "date":
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              onKeyDown: (event) => {
                if (isReadOnly) return;
                const input = event.target;
                if (input.tagName !== "INPUT") return;
                const keyCode = event.keyCode || event.which;
                const allowedKeys = [
                  ...Array.from({ length: 10 }, (_, i) => 48 + i),
                  // 0-9
                  ...Array.from({ length: 10 }, (_, i) => 96 + i),
                  // Numpad 0-9
                  ...Array.from({ length: 26 }, (_, i) => 65 + i),
                  // A-Z
                  32,
                  // Space
                  8,
                  46,
                  // Backspace, Delete
                  9,
                  13,
                  27,
                  // Tab, Enter, Escape
                  37,
                  38,
                  39,
                  40,
                  // Arrow keys
                  191,
                  189,
                  190
                  // /, -, .
                ];
                if (!allowedKeys.includes(keyCode)) {
                  event.preventDefault();
                }
              },
              onBlur: (event) => {
                if (isReadOnly) return;
                const input = event.target;
                if (input.tagName !== "INPUT") return;
                const currentValue = input.value || "";
                if (!currentValue.trim()) {
                  clearErrors(fieldName);
                  return;
                }
                const trimmedValue = currentValue.trim().toLowerCase();
                let convertedDate = null;
                const countryCode = getCountryCodeForField(field);
                const dateOrder = getDateOrderByCountry(countryCode);
                const numbersOnly = trimmedValue.replace(/\D/g, "");
                if (numbersOnly.length === 8 && trimmedValue === numbersOnly) {
                  let day, month, year;
                  const countryCode2 = getCountryCodeForField(field);
                  const isCountryDetected = countryCode2 !== "us" || initCountry !== "";
                  if (dateOrder === "MDY") {
                    month = parseInt(numbersOnly.substring(0, 2));
                    day = parseInt(numbersOnly.substring(2, 4));
                    year = parseInt(numbersOnly.substring(4, 8));
                  } else if (dateOrder === "YMD") {
                    year = parseInt(numbersOnly.substring(0, 4));
                    month = parseInt(numbersOnly.substring(4, 6));
                    day = parseInt(numbersOnly.substring(6, 8));
                  } else {
                    day = parseInt(numbersOnly.substring(0, 2));
                    month = parseInt(numbersOnly.substring(2, 4));
                    year = parseInt(numbersOnly.substring(4, 8));
                  }
                  const isValidRange = day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100;
                  if (isValidRange) {
                    const testDate = new Date(year, month - 1, day);
                    if (!isNaN(testDate.getTime()) && testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                      convertedDate = testDate;
                    }
                  }
                  if (!convertedDate && !isCountryDetected) {
                    month = parseInt(numbersOnly.substring(0, 2));
                    day = parseInt(numbersOnly.substring(2, 4));
                    year = parseInt(numbersOnly.substring(4, 8));
                    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                      const testDate = new Date(year, month - 1, day);
                      if (!isNaN(testDate.getTime()) && testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                        convertedDate = testDate;
                      }
                    }
                    if (!convertedDate) {
                      day = parseInt(numbersOnly.substring(0, 2));
                      month = parseInt(numbersOnly.substring(2, 4));
                      year = parseInt(numbersOnly.substring(4, 8));
                      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                        const testDate = new Date(year, month - 1, day);
                        if (!isNaN(testDate.getTime()) && testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                          convertedDate = testDate;
                        }
                      }
                    }
                    if (!convertedDate) {
                      year = parseInt(numbersOnly.substring(0, 4));
                      month = parseInt(numbersOnly.substring(4, 6));
                      day = parseInt(numbersOnly.substring(6, 8));
                      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                        const testDate = new Date(year, month - 1, day);
                        if (!isNaN(testDate.getTime()) && testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                          convertedDate = testDate;
                        }
                      }
                    }
                  }
                  if (!convertedDate) {
                    const finalCountryCode = getSelectedCountryCode();
                    const finalDateOrder = getDateOrderByCountry(finalCountryCode);
                    let formatMessage = "";
                    if (finalDateOrder === "MDY") {
                      formatMessage = "MM/DD/YYYY or MMDDYYYY";
                    } else if (finalDateOrder === "YMD") {
                      formatMessage = "YYYY/MM/DD or YYYYMMDD";
                    } else {
                      formatMessage = "DD/MM/YYYY or DDMMYYYY";
                    }
                    setError(fieldName, {
                      type: "pattern",
                      message: `Please enter date in ${formatMessage} format`
                    });
                    return;
                  }
                }
                if (!convertedDate) {
                  const textMatch1 = trimmedValue.match(/(\d{1,2})\s*([a-zA-Z]{3,9})\s*,?\s*(\d{4})/);
                  const textMatch2 = trimmedValue.match(/([a-zA-Z]{3,9})\s*(\d{1,2})\s*,?\s*(\d{4})/);
                  if (textMatch1) {
                    const day = parseInt(textMatch1[1]);
                    const monthName = textMatch1[2];
                    const year = parseInt(textMatch1[3]);
                    const month = DateFormatUtils$1.monthNames[monthName] || DateFormatUtils$1.monthNames[monthName.substring(0, 3)];
                    if (month && day >= 1 && day <= 31 && year >= 1900) {
                      convertedDate = new Date(year, month - 1, day);
                    }
                  } else if (textMatch2) {
                    const monthName = textMatch2[1];
                    const day = parseInt(textMatch2[2]);
                    const year = parseInt(textMatch2[3]);
                    const month = DateFormatUtils$1.monthNames[monthName] || DateFormatUtils$1.monthNames[monthName.substring(0, 3)];
                    if (month && day >= 1 && day <= 31 && year >= 1900) {
                      convertedDate = new Date(year, month - 1, day);
                    }
                  }
                }
                if (!convertedDate) {
                  const currentCountry = getCountryCodeForField(field);
                  convertedDate = DateFormatUtils$1.parseFlexibleDate(currentValue, currentCountry);
                }
                if (convertedDate && !isNaN(convertedDate.getTime())) {
                  const formatted = DateFormatUtils$1.formatDateForDisplay(convertedDate);
                  input.value = formatted;
                  setDisplayValue(field.hmy, formatted);
                  setValue(fieldName, formatted);
                  clearErrors(fieldName);
                  onFieldValueChange(field.hmy, formatted, field.sControlType);
                } else {
                  const countryCode2 = getCountryCodeForField(field);
                  const dateOrder2 = getDateOrderByCountry(countryCode2);
                  let expectedFormat = "";
                  if (dateOrder2 === "MDY") {
                    expectedFormat = "MM/DD/YYYY or MMDDYYYY";
                  } else if (dateOrder2 === "YMD") {
                    expectedFormat = "YYYY/MM/DD or YYYYMMDD";
                  } else {
                    expectedFormat = "DD/MM/YYYY or DDMMYYYY";
                  }
                  setError(fieldName, {
                    type: "pattern",
                    message: `Please enter date in ${expectedFormat} format`
                  });
                }
              },
              onInput: (event) => {
                if (isReadOnly) return;
                const input = event.target;
                if (input.tagName !== "INPUT") return;
                const currentValue = input.value || "";
                if (currentValue.trim() && errors[fieldName]) ;
                if (currentValue.trim() && !/^[\d\s\/\-\.a-zA-Z,]*$/.test(currentValue)) {
                  const countryCode = getCountryCodeForField(field);
                  const dateOrder = getDateOrderByCountry(countryCode);
                  let formatMessage = "";
                  if (dateOrder === "MDY") {
                    formatMessage = "MM/DD/YYYY or MMDDYYYY";
                  } else if (dateOrder === "YMD") {
                    formatMessage = "YYYY/MM/DD or YYYYMMDD";
                  } else {
                    formatMessage = "DD/MM/YYYY or DDMMYYYY";
                  }
                  setError(fieldName, {
                    type: "pattern",
                    message: `Please enter date in ${formatMessage} format`
                  });
                } else if (currentValue.trim() && /^[a-zA-Z]{3,}$/.test(currentValue.trim()) && !Object.keys(DateFormatUtils$1.monthNames).includes(currentValue.trim().toLowerCase())) {
                  const countryCode = getCountryCodeForField(field);
                  const dateOrder = getDateOrderByCountry(countryCode);
                  let formatMessage = "";
                  if (dateOrder === "MDY") {
                    formatMessage = "MM/DD/YYYY or MMDDYYYY";
                  } else if (dateOrder === "YMD") {
                    formatMessage = "YYYY/MM/DD or YYYYMMDD";
                  } else {
                    formatMessage = "DD/MM/YYYY or DDMMYYYY";
                  }
                  setError(fieldName, {
                    type: "pattern",
                    message: `Please enter date in ${formatMessage} format`
                  });
                } else if (currentValue.trim()) {
                  const countryCode = getCountryCodeForField(field);
                  const numbersOnly = currentValue.replace(/\D/g, "");
                  if (numbersOnly.length === 8 && currentValue === numbersOnly) {
                    const dateOrder = getDateOrderByCountry(countryCode);
                    let day, month, year;
                    if (dateOrder === "MDY") {
                      month = parseInt(numbersOnly.substr(0, 2), 10);
                      day = parseInt(numbersOnly.substr(2, 2), 10);
                      year = parseInt(numbersOnly.substr(4, 4), 10);
                    } else if (dateOrder === "YMD") {
                      year = parseInt(numbersOnly.substr(0, 4), 10);
                      month = parseInt(numbersOnly.substr(4, 2), 10);
                      day = parseInt(numbersOnly.substr(6, 2), 10);
                    } else {
                      day = parseInt(numbersOnly.substr(0, 2), 10);
                      month = parseInt(numbersOnly.substr(2, 2), 10);
                      year = parseInt(numbersOnly.substr(4, 4), 10);
                    }
                    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                      const parsedNumeric = new Date(year, month - 1, day);
                      if (parsedNumeric.getFullYear() === year && parsedNumeric.getMonth() === month - 1 && parsedNumeric.getDate() === day) {
                        const formatted = DateFormatUtils$1.formatDateForDisplay(parsedNumeric);
                        input.value = formatted;
                        setDisplayValue(field.hmy, formatted);
                        setValue(fieldName, formatted);
                        clearErrors(fieldName);
                        onFieldValueChange(field.hmy, formatted, field.sControlType);
                        return;
                      }
                    }
                  }
                  const parsedDate = DateFormatUtils$1.parseFlexibleDate(currentValue, countryCode);
                  if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
                    const formatted = DateFormatUtils$1.formatDateForDisplay(parsedDate);
                    if (formatted && formatted !== currentValue) {
                      input.value = formatted;
                      setDisplayValue(field.hmy, formatted);
                      setValue(fieldName, formatted);
                      clearErrors(fieldName);
                      onFieldValueChange(field.hmy, formatted, field.sControlType);
                    }
                  }
                }
              },
              children: /* @__PURE__ */ jsx(
                YardiControlDatePicker,
                {
                  value: getDisplayValue(field.hmy),
                  width: "100%",
                  onChange: (args) => {
                    const dateValue = args == null ? void 0 : args.value;
                    if (dateValue instanceof Date) {
                      const formatted = DateFormatUtils$1.formatDateForDisplay(dateValue);
                      setDisplayValue(field.hmy, formatted);
                      setValue(fieldName, formatted);
                      clearErrors(fieldName);
                      onFieldValueChange(field.hmy, formatted, field.sControlType);
                    } else {
                      const typedText = ((args == null ? void 0 : args.text) ?? "").toString().trim();
                      const countryCode = getCountryCodeForField(field);
                      const parsed = typedText ? DateFormatUtils$1.parseFlexibleDate(typedText, countryCode) : null;
                      if (parsed instanceof Date && !isNaN(parsed.getTime())) {
                        const formatted = DateFormatUtils$1.formatDateForDisplay(parsed);
                        setDisplayValue(field.hmy, formatted);
                        setValue(fieldName, formatted);
                        clearErrors(fieldName);
                        onFieldValueChange(field.hmy, formatted, field.sControlType);
                        return;
                      }
                      const stringValue = dateValue ?? typedText ?? "";
                      setDisplayValue(field.hmy, stringValue);
                      setValue(fieldName, stringValue);
                      const isEmpty = stringValue === null || stringValue === void 0 || stringValue === "";
                      if (isEmpty && isRequired) {
                        setError(fieldName, { type: "required", message: "This field is required" });
                      } else {
                        clearErrors(fieldName);
                      }
                      onFieldValueChange(field.hmy, stringValue, field.sControlType);
                    }
                  },
                  onBlur: (args) => {
                    var _a3;
                    const inputElement = (_a3 = args == null ? void 0 : args.model) == null ? void 0 : _a3.inputElement;
                    if (inputElement == null ? void 0 : inputElement.value) {
                      const inputValue = inputElement.value.trim();
                      if (!inputValue) {
                        if (isRequired) {
                          setError(fieldName, {
                            type: "required",
                            message: "This field is required"
                          });
                        } else {
                          clearErrors(fieldName);
                        }
                        return;
                      }
                      const numbersOnly = inputValue.replace(/\D/g, "");
                      const currentCountry = getCountryCodeForField(field);
                      const dateOrder = getDateOrderByCountry(currentCountry);
                      if (numbersOnly.length === 8 && inputValue === numbersOnly) {
                        console.log("📅 Parsing date:", inputValue, "| Country:", currentCountry, "| Format:", dateOrder);
                        let day, month, year;
                        if (dateOrder === "MDY") {
                          month = parseInt(numbersOnly.substr(0, 2), 10);
                          day = parseInt(numbersOnly.substr(2, 2), 10);
                          year = parseInt(numbersOnly.substr(4, 4), 10);
                        } else if (dateOrder === "YMD") {
                          year = parseInt(numbersOnly.substr(0, 4), 10);
                          month = parseInt(numbersOnly.substr(4, 2), 10);
                          day = parseInt(numbersOnly.substr(6, 2), 10);
                        } else {
                          day = parseInt(numbersOnly.substr(0, 2), 10);
                          month = parseInt(numbersOnly.substr(2, 2), 10);
                          year = parseInt(numbersOnly.substr(4, 4), 10);
                        }
                        console.log("📅 Parsed values:", { day, month, year });
                        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                          const parsedDate = new Date(year, month - 1, day);
                          if (parsedDate.getFullYear() === year && parsedDate.getMonth() === month - 1 && parsedDate.getDate() === day) {
                            const formatted = DateFormatUtils$1.formatDateForDisplay(parsedDate);
                            setDisplayValue(field.hmy, formatted);
                            setValue(fieldName, formatted);
                            clearErrors(fieldName);
                            onFieldValueChange(field.hmy, formatted, field.sControlType);
                          } else {
                            const countryCode = getCountryCodeForField(field);
                            const dateOrder2 = getDateOrderByCountry(countryCode);
                            let expectedFormat = "";
                            if (dateOrder2 === "MDY") {
                              expectedFormat = "MM/DD/YYYY or MMDDYYYY";
                            } else if (dateOrder2 === "YMD") {
                              expectedFormat = "YYYY/MM/DD or YYYYMMDD";
                            } else {
                              expectedFormat = "DD/MM/YYYY or DDMMYYYY";
                            }
                            setError(fieldName, {
                              type: "pattern",
                              message: `Invalid date. Please enter date in ${expectedFormat} format`
                            });
                          }
                        } else {
                          const countryCode = getCountryCodeForField(field);
                          const dateOrder2 = getDateOrderByCountry(countryCode);
                          let expectedFormat = "";
                          if (dateOrder2 === "MDY") {
                            expectedFormat = "MM/DD/YYYY or MMDDYYYY";
                          } else if (dateOrder2 === "YMD") {
                            expectedFormat = "YYYY/MM/DD or YYYYMMDD";
                          } else {
                            expectedFormat = "DD/MM/YYYY or DDMMYYYY";
                          }
                          setError(fieldName, {
                            type: "pattern",
                            message: `Invalid date format. Please enter date in ${expectedFormat} format`
                          });
                        }
                      } else if (inputValue && numbersOnly.length > 0) {
                        const parsedDate = DateFormatUtils$1.parseFlexibleDate(inputValue, currentCountry);
                        if (!parsedDate || isNaN(parsedDate.getTime())) {
                          let expectedFormat = "";
                          if (dateOrder === "MDY") {
                            expectedFormat = "MM/DD/YYYY or MMDDYYYY";
                          } else if (dateOrder === "YMD") {
                            expectedFormat = "YYYY/MM/DD or YYYYMMDD";
                          } else {
                            expectedFormat = "DD/MM/YYYY or DDMMYYYY";
                          }
                          setError(fieldName, {
                            type: "pattern",
                            message: `Invalid date format. Please enter date in ${expectedFormat} format`
                          });
                        } else {
                          clearErrors(fieldName);
                          const formatted = DateFormatUtils$1.formatDateForDisplay(parsedDate);
                          setDisplayValue(field.hmy, formatted);
                          setValue(fieldName, formatted);
                          onFieldValueChange(field.hmy, formatted, field.sControlType);
                        }
                      }
                    } else if (isRequired) {
                      setError(fieldName, {
                        type: "required",
                        message: "This field is required"
                      });
                    } else {
                      clearErrors(fieldName);
                    }
                  },
                  placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
                  format: getDateFormatByCountry(getCountryCodeForField(field)),
                  strictMode: false,
                  invalidFormatErrorMessage: "",
                  divClassName: `w-full cf-hide-internal-date-error ${!isReadOnly && !ReadOnlyService.isReadOnly() && (errors[fieldName] || hasValidationError(field.hmy)) ? "cf-external-date-error" : "cf-no-external-date-error"}`,
                  cssClass: `${styles.input.base} ${styles.input.valid} ${!isReadOnly && !ReadOnlyService.isReadOnly() && (errors[fieldName] || hasValidationError(field.hmy)) ? "e-error form-field-error-glow" : ""}`,
                  floatLabelType: "Never",
                  readonly: isReadOnly,
                  disabled: ReadOnlyService.isReadOnly(),
                  overrideFwReadOnlyFlag: true,
                  "aria-required": isRequired,
                  "aria-describedby": `error_${field.hmy}`,
                  min: (() => {
                    var _a3;
                    const validationSettings = fieldValidationSettings[field.hmy];
                    if (validationSettings && validationSettings.length > 0) {
                      const dateSettings = parseBackendDateSettings(validationSettings);
                      if (dateSettings.settings.disablePastDate) {
                        return /* @__PURE__ */ new Date();
                      }
                      if ((_a3 = dateSettings.settings.customDateRange) == null ? void 0 : _a3.startDate) {
                        return new Date(dateSettings.settings.customDateRange.startDate);
                      }
                    }
                    return void 0;
                  })(),
                  max: (() => {
                    var _a3;
                    const validationSettings = fieldValidationSettings[field.hmy];
                    if (validationSettings && validationSettings.length > 0) {
                      const dateSettings = parseBackendDateSettings(validationSettings);
                      if (dateSettings.settings.disableFutureDate) {
                        return /* @__PURE__ */ new Date();
                      }
                      if ((_a3 = dateSettings.settings.customDateRange) == null ? void 0 : _a3.endDate) {
                        return new Date(dateSettings.settings.customDateRange.endDate);
                      }
                    }
                    return void 0;
                  })()
                },
                `date-${field.hmy}-${getCountryCodeForField(field)}`
              )
            }
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && (errors[fieldName] || hasValidationError(field.hmy)) && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-red-500 text-sm mt-1", role: "alert", "aria-live": "polite", children: getValidationError(field.hmy) || getErrorMessage(fieldName) || translate("Invalid value") })
        ] });
      case "phone number":
        getDisplayValue(field.hmy);
        const countryDropdownField = state.nonHiddenFields.find(
          (f) => {
            var _a3, _b3;
            return ((_a3 = f.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country")) && ((_b3 = f.dropdownlist) == null ? void 0 : _b3.length) > 0;
          }
        );
        let defaultCountry = getCountryCodeForField(field, "") || getIso2FromConfigurableCountryId(customAppData == null ? void 0 : customAppData.CountryId, countryDropdownField) || "us";
        let phoneCountryList = void 0;
        if (countryDropdownField == null ? void 0 : countryDropdownField.dropdownlist) {
          const countryCodeMapping = {
            "uae": "ae",
            "UAE": "ae",
            // United Arab Emirates
            "uk": "gb",
            "UK": "gb",
            // United Kingdom
            "kp": "kp",
            "KP": "kp",
            // North Korea (already correct, just normalize case)
            "kr": "kr",
            "KR": "kr",
            // South Korea (already correct, just normalize case)
            // Additional mappings for any other non-standard codes
            "an": "an",
            "AN": "an",
            // Netherlands Antilles (deprecated but may exist in DB)
            "aq": "aq",
            "AQ": "aq",
            // Antarctica
            "as": "as",
            "AS": "as",
            // American Samoa
            "ax": "ax",
            "AX": "ax",
            // Åland Islands
            "bv": "bv",
            "BV": "bv",
            // Bouvet Island
            "cc": "cc",
            "CC": "cc",
            // Cocos (Keeling) Islands
            "eh": "eh",
            "EH": "eh",
            // Western Sahara
            "fk": "fk",
            "FK": "fk",
            // Falkland Islands (Malvinas)
            "fm": "fm",
            "FM": "fm",
            // Micronesia, Federated States of
            "fo": "fo",
            "FO": "fo",
            // Faroe Islands
            "gf": "gf",
            "GF": "gf",
            // French Guiana
            "gg": "gg",
            "GG": "gg",
            // Guernsey
            "gi": "gi",
            "GI": "gi",
            // Gibraltar
            "gl": "gl",
            "GL": "gl",
            // Greenland
            "gp": "gp",
            "GP": "gp",
            // Guadeloupe
            "gs": "gs",
            "GS": "gs",
            // South Georgia and the South Sandwich Islands
            "gu": "gu",
            "GU": "gu",
            // Guam
            "hm": "hm",
            "HM": "hm",
            // Heard Island and McDonald Islands
            "io": "io",
            "IO": "io",
            // British Indian Ocean Territory
            "je": "je",
            "JE": "je",
            // Jersey
            "lc": "lc",
            "LC": "lc",
            // Saint Lucia
            "li": "li",
            "LI": "li",
            // Liechtenstein
            "lk": "lk",
            "LK": "lk",
            // Sri Lanka
            "lr": "lr",
            "LR": "lr",
            // Liberia
            "ls": "ls",
            "LS": "ls",
            // Lesotho
            "ly": "ly",
            "LY": "ly",
            // Libya
            "ma": "ma",
            "MA": "ma",
            // Morocco
            "mc": "mc",
            "MC": "mc",
            // Monaco
            "md": "md",
            "MD": "md",
            // Moldova, Republic of
            "me": "me",
            "ME": "me",
            // Montenegro
            "mf": "mf",
            "MF": "mf",
            // Saint Martin (French part)
            "mg": "mg",
            "MG": "mg",
            // Madagascar
            "mh": "mh",
            "MH": "mh",
            // Marshall Islands
            "mk": "mk",
            "MK": "mk",
            // North Macedonia
            "ml": "ml",
            "ML": "ml",
            // Mali
            "mm": "mm",
            "MM": "mm",
            // Myanmar
            "mn": "mn",
            "MN": "mn",
            // Mongolia
            "mo": "mo",
            "MO": "mo",
            // Macao
            "mp": "mp",
            "MP": "mp",
            // Northern Mariana Islands
            "mq": "mq",
            "MQ": "mq",
            // Martinique
            "mr": "mr",
            "MR": "mr",
            // Mauritania
            "ms": "ms",
            "MS": "ms",
            // Montserrat
            "mt": "mt",
            "MT": "mt",
            // Malta
            "mu": "mu",
            "MU": "mu",
            // Mauritius
            "mv": "mv",
            "MV": "mv",
            // Maldives
            "mw": "mw",
            "MW": "mw",
            // Malawi
            "mz": "mz",
            "MZ": "mz",
            // Mozambique
            "na": "na",
            "NA": "na",
            // Namibia
            "nc": "nc",
            "NC": "nc",
            // New Caledonia
            "ne": "ne",
            "NE": "ne",
            // Niger
            "nf": "nf",
            "NF": "nf",
            // Norfolk Island
            "ng": "ng",
            "NG": "ng",
            // Nigeria
            "ni": "ni",
            "NI": "ni",
            // Nicaragua
            "np": "np",
            "NP": "np",
            // Nepal
            "nr": "nr",
            "NR": "nr",
            // Nauru
            "nu": "nu",
            "NU": "nu",
            // Niue
            "nz": "nz",
            "NZ": "nz",
            // New Zealand
            "om": "om",
            "OM": "om",
            // Oman
            "pa": "pa",
            "PA": "pa",
            // Panama
            "pe": "pe",
            "PE": "pe",
            // Peru
            "pf": "pf",
            "PF": "pf",
            // French Polynesia
            "pg": "pg",
            "PG": "pg",
            // Papua New Guinea
            "ph": "ph",
            "PH": "ph",
            // Philippines
            "pk": "pk",
            "PK": "pk",
            // Pakistan
            "pl": "pl",
            "PL": "pl",
            // Poland
            "pm": "pm",
            "PM": "pm",
            // Saint Pierre and Miquelon
            "pn": "pn",
            "PN": "pn",
            // Pitcairn
            "pr": "pr",
            "PR": "pr",
            // Puerto Rico
            "ps": "ps",
            "PS": "ps",
            // Palestine, State of
            "pt": "pt",
            "PT": "pt",
            // Portugal
            "pw": "pw",
            "PW": "pw",
            // Palau
            "py": "py",
            "PY": "py",
            // Paraguay
            "qa": "qa",
            "QA": "qa",
            // Qatar
            "re": "re",
            "RE": "re",
            // Réunion
            "ro": "ro",
            "RO": "ro",
            // Romania
            "rs": "rs",
            "RS": "rs",
            // Serbia
            "ru": "ru",
            "RU": "ru",
            // Russian Federation
            "rw": "rw",
            "RW": "rw",
            // Rwanda
            "sa": "sa",
            "SA": "sa",
            // Saudi Arabia
            "sb": "sb",
            "SB": "sb",
            // Solomon Islands
            "sc": "sc",
            "SC": "sc",
            // Seychelles
            "sd": "sd",
            "SD": "sd",
            // Sudan
            "se": "se",
            "SE": "se",
            // Sweden
            "sg": "sg",
            "SG": "sg",
            // Singapore
            "sh": "sh",
            "SH": "sh",
            // Saint Helena, Ascension and Tristan da Cunha
            "si": "si",
            "SI": "si",
            // Slovenia
            "sj": "sj",
            "SJ": "sj",
            // Svalbard and Jan Mayen
            "sk": "sk",
            "SK": "sk",
            // Slovakia
            "sl": "sl",
            "SL": "sl",
            // Sierra Leone
            "sm": "sm",
            "SM": "sm",
            // San Marino
            "sn": "sn",
            "SN": "sn",
            // Senegal
            "so": "so",
            "SO": "so",
            // Somalia
            "sr": "sr",
            "SR": "sr",
            // Suriname
            "ss": "ss",
            "SS": "ss",
            // South Sudan
            "st": "st",
            "ST": "st",
            // Sao Tome and Principe
            "sv": "sv",
            "SV": "sv",
            // El Salvador
            "sx": "sx",
            "SX": "sx",
            // Sint Maarten (Dutch part)
            "sy": "sy",
            "SY": "sy",
            // Syrian Arab Republic
            "sz": "sz",
            "SZ": "sz",
            // Eswatini (formerly Swaziland)
            "tc": "tc",
            "TC": "tc",
            // Turks and Caicos Islands
            "td": "td",
            "TD": "td",
            // Chad
            "tf": "tf",
            "TF": "tf",
            // French Southern Territories
            "tg": "tg",
            "TG": "tg",
            // Togo
            "th": "th",
            "TH": "th",
            // Thailand
            "tj": "tj",
            "TJ": "tj",
            // Tajikistan
            "tk": "tk",
            "TK": "tk",
            // Tokelau
            "tl": "tl",
            "TL": "tl",
            // Timor-Leste
            "tm": "tm",
            "TM": "tm",
            // Turkmenistan
            "tn": "tn",
            "TN": "tn",
            // Tunisia
            "to": "to",
            "TO": "to",
            // Tonga
            "tr": "tr",
            "TR": "tr",
            // Turkey
            "tt": "tt",
            "TT": "tt",
            // Trinidad and Tobago
            "tv": "tv",
            "TV": "tv",
            // Tuvalu
            "tw": "tw",
            "TW": "tw",
            // Taiwan, Province of China
            "tz": "tz",
            "TZ": "tz",
            // Tanzania, United Republic of
            "ua": "ua",
            "UA": "ua",
            // Ukraine
            "ug": "ug",
            "UG": "ug",
            // Uganda
            "um": "um",
            "UM": "um",
            // United States Minor Outlying Islands
            "uy": "uy",
            "UY": "uy",
            // Uruguay
            "uz": "uz",
            "UZ": "uz",
            // Uzbekistan
            "va": "va",
            "VA": "va",
            // Holy See (Vatican City State)
            "vc": "vc",
            "VC": "vc",
            // Saint Vincent and the Grenadines
            "ve": "ve",
            "VE": "ve",
            // Venezuela, Bolivarian Republic of
            "vg": "vg",
            "VG": "vg",
            // Virgin Islands, British
            "vi": "vi",
            "VI": "vi",
            // Virgin Islands, U.S.
            "vn": "vn",
            "VN": "vn",
            // Viet Nam
            "vu": "vu",
            "VU": "vu",
            // Vanuatu
            "wf": "wf",
            "WF": "wf",
            // Wallis and Futuna
            "ws": "ws",
            "WS": "ws",
            // Samoa
            "ye": "ye",
            "YE": "ye",
            // Yemen
            "yt": "yt",
            "YT": "yt",
            // Mayotte
            "za": "za",
            "ZA": "za",
            // South Africa
            "zm": "zm",
            "ZM": "zm",
            // Zambia
            "zw": "zw",
            "ZW": "zw"
            // Zimbabwe
          };
          phoneCountryList = countryDropdownField.dropdownlist.map((country) => {
            let code = country.sCode || country.SCODE || country.code || country.Code || country.isoCode || country.iso2 || country.iso || country.countryCode || country.CountryCode;
            const name = country.name || country.Name || "";
            if (code && typeof code === "string") {
              const normalizedCode = code.trim();
              const mappedCode = countryCodeMapping[normalizedCode] || normalizedCode.toLowerCase();
              if (mappedCode && mappedCode.length === 2) {
                return { code: mappedCode.toLowerCase(), name };
              }
            }
            return null;
          }).filter((item) => item !== null);
        }
        const phoneInputKey = `phone-${field.hmy}-${defaultCountry}`;
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            PhoneInputComponent,
            {
              value: (() => {
                const currentValue = getDisplayValue(field.hmy);
                if (currentValue && currentValue.startsWith("+0")) {
                  setDisplayValue(field.hmy, "");
                  return "";
                }
                return currentValue;
              })(),
              onChange: (value, country) => {
                if (value && value.startsWith("+0")) {
                  setDisplayValue(field.hmy, "");
                  setValue(fieldName, "", { shouldDirty: true });
                } else {
                  setDisplayValue(field.hmy, value);
                  setValue(fieldName, value, { shouldDirty: true });
                }
                clearErrors(fieldName);
              },
              onBlur: (e) => {
                const value = getDisplayValue(field.hmy);
                setValue(fieldName, value);
                let isEmpty = value === null || value === void 0 || value === "";
                if (typeof value === "string" && value.trim() !== "") {
                  const digitsOnly = value.replace(/[^\d]/g, "");
                  if (value === "+" || digitsOnly.length <= 3) {
                    isEmpty = true;
                  }
                }
                if (isEmpty && isRequired) {
                  setError(fieldName, {
                    type: "required",
                    message: translate("This field is required")
                  });
                } else {
                  clearErrors(fieldName);
                }
              },
              placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : translate("Enter phone number"),
              disabled: ReadOnlyService.isReadOnly(),
              className: `w-full ${errors[fieldName] ? "border-red-400 border rounded" : ""}`,
              country: defaultCountry,
              preferredCountries: phonePreferredCountries,
              countryList: phoneCountryList,
              inputProps: {
                name: fieldName,
                id: `phone_${field.hmy}`,
                "aria-describedby": `error_${field.hmy}`,
                autoComplete: "tel",
                maxLength: 20,
                required: isRequired
              }
            },
            phoneInputKey
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "mt-1 text-sm text-red-600", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "email":
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            YardiControlTextBox,
            {
              value: getDisplayValue(field.hmy),
              onChange: (args) => {
                const newValue = (args == null ? void 0 : args.value) ?? "";
                setDisplayValue(field.hmy, newValue);
                setValue(fieldName, newValue);
              },
              placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : translate("Enter email"),
              floatLabelType: "Never",
              divClassName: "w-full",
              cssClass: `${styles.input.base} ${styles.input.valid} ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly" : ""} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error form-field-error-glow" : ""}`,
              readonly: isReadOnly,
              "aria-required": isRequired,
              enabled: !isReadOnly,
              disabled: ReadOnlyService.isReadOnly(),
              overrideFwReadOnlyFlag: true,
              type: "email",
              "aria-describedby": `error_${field.hmy}`
            }
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-sm text-red-500 mt-1", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "currency":
        const currencyCode = getCurrencyCodeForField(field);
        const locale = getLocaleForField(field);
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            YardiControlCurrency,
            {
              value: getDisplayValue(field.hmy),
              onChange: (e) => {
                const newValue = e == null ? void 0 : e.value;
                setDisplayValue(field.hmy, newValue);
                setValue(fieldName, newValue);
              },
              placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
              readonly: isReadOnly,
              enabled: !isReadOnly,
              disabled: ReadOnlyService.isReadOnly(),
              overrideFwReadOnlyFlag: true,
              divClassName: "w-full",
              cssClass: `${styles.input.base} ${styles.input.valid} ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly" : ""} ${errors[fieldName] || hasValidationError(field.hmy) ? "e-error form-field-error-glow" : ""}`,
              floatLabelType: "Never",
              "aria-describedby": `error_${field.hmy}`,
              precision: 2,
              currency: currencyCode,
              locale,
              format: "c2",
              showSpinButton: false
            }
          ),
          !isReadOnly && !ReadOnlyService.isReadOnly() && (getValidationError(field.hmy) || errors[fieldName]) && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-sm text-red-500 mt-1", role: "alert", "aria-live": "polite", children: getValidationError(field.hmy) || getErrorMessage(fieldName) })
        ] });
      case "number":
      case "decimal":
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            YardiControlTextBox,
            {
              value: getDisplayValue(field.hmy),
              onChange: (e) => {
                var _a3;
                let newValue = (e == null ? void 0 : e.value) ?? "";
                if (newValue && !isNaN(parseFloat(newValue))) {
                  const numericValue = ((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase()) === "decimal" ? parseFloat(newValue) : parseInt(newValue, 10);
                  setDisplayValue(field.hmy, numericValue);
                  setValue(fieldName, numericValue);
                } else {
                  setDisplayValue(field.hmy, newValue);
                  setValue(fieldName, newValue);
                }
              },
              placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
              readonly: isReadOnly,
              enabled: !isReadOnly,
              disabled: ReadOnlyService.isReadOnly(),
              overrideFwReadOnlyFlag: true,
              divClassName: "w-full",
              cssClass: `e-outline ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly" : ""} ${errors[fieldName] ? "e-error" : ""}`,
              floatLabelType: "Never",
              "aria-required": isRequired,
              type: ((_b2 = field.sControlType) == null ? void 0 : _b2.toLowerCase()) === "decimal" ? "number" : "number",
              "aria-describedby": `error_${field.hmy}`
            }
          ),
          errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-sm text-red-500 mt-1", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "text area":
      case "textarea":
        return /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsxs("div", { className: "relative w-full", children: [
            (isReadOnly || ReadOnlyService.isReadOnly()) && /* @__PURE__ */ jsx("div", { className: "absolute left-2 top-2 z-10 pointer-events-none", children: /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center shadow-md", children: /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faLock, className: "text-white text-xs" }) }) }),
            /* @__PURE__ */ jsx(
              YardiControlTextBox,
              {
                value: getDisplayValue(field.hmy),
                onChange: (e) => {
                  const newValue = (e == null ? void 0 : e.value) ?? "";
                  setDisplayValue(field.hmy, newValue);
                },
                placeholder: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : "",
                readonly: isReadOnly,
                enabled: !isReadOnly,
                disabled: ReadOnlyService.isReadOnly(),
                overrideFwReadOnlyFlag: true,
                divClassName: "w-full",
                cssClass: `e-outline ${isReadOnly || ReadOnlyService.isReadOnly() ? "e-input-readonly bg-gray-100" : ""} ${errors[fieldName] ? "e-error" : ""}`,
                floatLabelType: "Never",
                "aria-required": isRequired,
                multiline: true,
                "aria-describedby": `error_${field.hmy}`
              }
            )
          ] }),
          errors[fieldName] && /* @__PURE__ */ jsx("p", { id: `error_${field.hmy}`, className: "text-sm text-red-500 mt-1", role: "alert", "aria-live": "polite", children: getErrorMessage(fieldName) })
        ] });
      case "check box":
      case "checkbox":
        return /* @__PURE__ */ jsx("div", { className: "flex items-center", children: /* @__PURE__ */ jsx(
          YardiCheckbox,
          {
            checked: getDisplayValue(field.hmy) === true || getDisplayValue(field.hmy) === "true" || getDisplayValue(field.hmy) === 1,
            onChange: (args) => {
              var _a3, _b3;
              const currentValue = getDisplayValue(field.hmy);
              let newValue;
              if (typeof currentValue === "number" || currentValue === 1 || currentValue === 0) {
                newValue = args.checked ? 1 : 0;
              } else {
                newValue = args.checked ? true : false;
              }
              setDisplayValue(field.hmy, newValue);
              setValue(fieldName, newValue);
              const fieldNameLower = ((_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase()) || "";
              const isMiddleNameCheckbox = fieldNameLower.includes("middle") && (fieldNameLower.includes("no") || fieldNameLower.includes("do not have"));
              if (isMiddleNameCheckbox && args.checked) {
                const middleNameField = (_b3 = state.nonHiddenFields) == null ? void 0 : _b3.find((f) => {
                  var _a4, _b4;
                  const textFieldName = ((_a4 = f.sFieldName) == null ? void 0 : _a4.toLowerCase()) || "";
                  return ((_b4 = f.sControlType) == null ? void 0 : _b4.toLowerCase()) === "text box" && (textFieldName.includes("middle name") || textFieldName.includes("middlename"));
                });
                if (middleNameField) {
                  const middleNameFieldKey = `custom_${middleNameField.hmy}`;
                  setDisplayValue(middleNameField.hmy, "");
                  setValue(middleNameFieldKey, "");
                  setState((prev) => ({
                    ...prev,
                    customdata: {
                      ...prev.customdata,
                      [middleNameFieldKey]: ""
                    }
                  }));
                  console.log(`🧹 Cleared middle name field because "I do not have middle name" was checked`);
                }
              }
              handleModelChange(field.sColumnName);
            },
            label: field.sPlaceHolderText ? translate(field.sPlaceHolderText) : translate(field.sFieldName || ""),
            disabled: isReadOnly || ReadOnlyService.isReadOnly(),
            overrideFwReadOnlyFlag: true
          }
        ) });
      case "file upload":
        const currentFileValue = watch(fieldName) ?? state.customdata[fieldName] ?? state.customdata[field.sColumnName ?? ""];
        const fileUploadFormId = String(Id || localStorage.getItem("LoginApplicantId") || "");
        return /* @__PURE__ */ jsx(
          FileUploadField,
          {
            field,
            value: currentFileValue,
            formId: fileUploadFormId,
            isRequired,
            isReadOnly: ReadOnlyService.isReadOnly(),
            errorMessage: getValidationError(field.hmy) || getErrorMessage(fieldName) || void 0,
            uploadFile: async (formData) => {
              var _a3, _b3, _c3, _d2, _e, _f, _g, _h;
              let setupId = String(((_b3 = (_a3 = field.documents) == null ? void 0 : _a3[0]) == null ? void 0 : _b3.SetupId) ?? "").trim();
              let setupTitle = String(((_d2 = (_c3 = field.documents) == null ? void 0 : _c3[0]) == null ? void 0 : _d2.SetupTitle) ?? field.sFieldName ?? "Document").trim();
              if (!setupId && field.hCafeFormSectionsID) {
                const uploadSetupResponse = await configurableFormsApi.getUploadSetup({
                  WorkflowStep: String(field.hCafeFormSectionsID),
                  ObjectID: fileUploadFormId,
                  ObjectType: 103,
                  ShowAdendumBeforeOrAfterPage: 0,
                  OpenInSeperatePage: false,
                  ProspectCurrentStep: 60,
                  IsSIgnOnlyApplication: false,
                  IsSiteManagerPreview: false
                });
                const matchingSetup = (_f = (_e = uploadSetupResponse == null ? void 0 : uploadSetupResponse.response) == null ? void 0 : _e.Setups) == null ? void 0 : _f.find(
                  (setup) => String((setup == null ? void 0 : setup.SetupTitle) ?? "").trim().toLowerCase() === String(field.sFieldName ?? "").trim().toLowerCase()
                );
                const fallbackSetup = matchingSetup ?? ((_h = (_g = uploadSetupResponse == null ? void 0 : uploadSetupResponse.response) == null ? void 0 : _g.Setups) == null ? void 0 : _h[0]);
                setupId = String((fallbackSetup == null ? void 0 : fallbackSetup.SetupId) ?? "").trim();
                setupTitle = String((fallbackSetup == null ? void 0 : fallbackSetup.SetupTitle) ?? setupTitle).trim();
              }
              formData.set("ProspectID", fileUploadFormId);
              formData.set("FieldHMY", String(field.hmy ?? ""));
              formData.set("SetupID", setupId);
              formData.set("SetupTitle", setupTitle || "Document");
              return configurableFormsApi.uploadDocument(formData);
            },
            loadExistingFiles: async ({ formId, fieldHmy }) => configurableFormsApi.getDocumentsByField(formId, fieldHmy),
            previewFile: async (file) => configurableFormsApi.getConfigurableFormFilePreview(file),
            deleteFile: async (file) => configurableFormsApi.deleteConfigurableFormFile(file),
            onChange: (files) => {
              const serializedFiles = JSON.stringify(files);
              setValue(fieldName, serializedFiles, { shouldDirty: true, shouldValidate: isRequired });
              clearErrors(fieldName);
              if (field.sColumnName) {
                handleModelChange(field.sColumnName);
              }
            }
          }
        );
      case "terms and conditions": {
        const termsValue = getDisplayValue(field.hmy);
        const isTermsCheckedFromLoad = termsValue === true || termsValue === "true" || termsValue === 1 || termsValue === "1";
        const isScreeningSection = ((_c2 = pageData == null ? void 0 : pageData.sectionName) == null ? void 0 : _c2.toLowerCase().includes("screening")) ?? false;
        const effectiveReadOnlyForTerms = isReadOnly || isScreeningSection && isTermsCheckedFromLoad;
        return /* @__PURE__ */ jsx(
          TermsAndConditionsField,
          {
            field,
            isRequired,
            isReadOnly: effectiveReadOnlyForTerms,
            value: termsValue,
            onChange: (checked) => {
              setDisplayValue(field.hmy, checked);
              setValue(fieldName, checked);
            },
            error: void 0,
            fieldName
          }
        );
      }
      default:
        return null;
    }
  }, [isConditionalShow, isConditionalRequired, isConditionalReadOnly, getDisplayValue, setDisplayValue, setValue, errors, uploadProgress, handleFileSelect, pageData == null ? void 0 : pageData.sectionName, translate]);
  const countryProcessedRef = useRef(/* @__PURE__ */ new Set());
  const loadPageRef = useRef(false);
  const currentSectionRef = useRef("");
  const navigationInProgressRef = useRef(false);
  useEffect(() => {
    if (!objectUUId) return;
    navigationInProgressRef.current = false;
    const checkVisibility = (sections) => {
      if (navigationInProgressRef.current) {
        return;
      }
      const currentSection2 = sections.find(
        (s) => s.sectionId === objectUUId || s.UUID === objectUUId || String(s.id) === String(objectUUId)
      );
      if (currentSection2) {
        const isVisibleByQuery = currentSection2.hideQuerySettings ? currentSection2.isVisibleByQuery === true : currentSection2.isVisibleByQuery !== false;
        const isVisibleForCountry = currentSection2.isVisibleForCountry !== false;
        const isHidden = !isVisibleByQuery || !isVisibleForCountry;
        if (isHidden) {
          setIsSectionHidden(true);
        } else {
          setIsSectionHidden(false);
        }
      }
    };
    const allSections2 = customFormService.sections$.value || [];
    checkVisibility(allSections2);
    let timeoutId;
    const subscription = customFormService.sections$.subscribe((sections) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        checkVisibility(sections);
      }, 100);
    });
    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [objectUUId]);
  useEffect(() => {
    if (objectUUId && (objectUUId !== currentSectionRef.current || !loadPageRef.current)) {
      console.log("🔄 Loading page for section:", objectUUId);
      const allSections2 = customFormService.sections$.value || [];
      const currentSection2 = allSections2.find(
        (s) => s.sectionId === objectUUId || s.UUID === objectUUId || String(s.id) === String(objectUUId)
      );
      if (currentSection2) {
        const isVisibleByQuery = currentSection2.hideQuerySettings ? currentSection2.isVisibleByQuery === true : currentSection2.isVisibleByQuery !== false;
        const isVisibleForCountry = currentSection2.isVisibleForCountry !== false;
        const isHidden = !isVisibleByQuery || !isVisibleForCountry;
        if (isHidden) {
          setIsSectionHidden(true);
          return;
        } else {
          setIsSectionHidden(false);
        }
      } else {
        if (isSectionHidden) {
          console.warn(`🚫 Section (${objectUUId}) not found but marked as hidden. Blocking load.`);
          return;
        }
      }
      console.log("✅ Section is visible, proceeding with load");
      const checkAndLoad = async () => {
        clearErrors();
        setState((prev) => ({ ...prev, errorMessage: "" }));
        setSuccessMessage("");
        currentSectionRef.current = objectUUId;
        loadPageRef.current = true;
        countryProcessedRef.current.clear();
        loadPage();
      };
      checkAndLoad();
    } else {
      console.log("⏭️ Skipping duplicate loadPage call for section:", objectUUId);
    }
  }, [objectUUId, navigate]);
  useEffect(() => {
    Object.keys(dynamicFieldTypes).forEach((fieldHmy) => {
      var _a2;
      const fieldType = dynamicFieldTypes[fieldHmy];
      const savedValue = state.customdata[`custom_${fieldHmy}`];
      if (fieldType.controlType === "drop down" && savedValue && fieldType.dropdownData && fieldType.dropdownData.length > 0) {
        const matchingOption = (_a2 = fieldType.dropdownData) == null ? void 0 : _a2.find(
          (option) => option.id === savedValue || option.name === savedValue || option.id === savedValue.toString() || option.name === savedValue.toString()
        );
        if (matchingOption) {
          setValue(`custom_${fieldHmy}`, matchingOption.id);
          console.log("🔄 Restored saved value for dynamic field:", {
            fieldHmy,
            savedValue,
            matchingOption: matchingOption.id
          });
        }
      }
    });
  }, [dynamicFieldTypes, setValue]);
  useEffect(() => {
    const processCountryFieldsAfterDataLoad = async () => {
      var _a2;
      if (!state.hasDataLoaded || !state.customdata || !((_a2 = state.nonHiddenFields) == null ? void 0 : _a2.length)) {
        return;
      }
      const countryFields = state.nonHiddenFields.filter(
        (field) => {
          var _a3;
          return (_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country");
        }
      );
      for (const countryField of countryFields) {
        const countryValue = state.customdata[`custom_${countryField.hmy}`];
        const processKey = `${countryField.hmy}-${countryValue}`;
        if (countryValue && !countryProcessedRef.current.has(processKey)) {
          try {
            countryProcessedRef.current.add(processKey);
            await checkCountryChange(countryField, countryValue);
          } catch (error) {
            console.warn("Error during country processing:", error);
            countryProcessedRef.current.delete(processKey);
          }
        }
      }
    };
    processCountryFieldsAfterDataLoad();
  }, [state.hasDataLoaded, state.customdata]);
  useEffect(() => {
    const checkFirstSection = async () => {
      const sections = await customFormService.sections$.value;
      if (sections && sections.length > 0) {
        const isFirst = sections[0].sectionId === objectUUId;
        setState((prev) => ({ ...prev, isFirstSection: isFirst }));
      }
    };
    if (objectUUId) {
      checkFirstSection();
    }
  }, [objectUUId, customFormService.sections$]);
  useEffect(() => {
    const setMultiselectClearTooltip = () => {
      const clearIcons = document.querySelectorAll(".e-multiselect .e-close-hooker");
      const tooltipText = "Clear all selections";
      clearIcons.forEach((el) => {
        if (el.getAttribute("title") !== tooltipText) {
          el.setAttribute("title", tooltipText);
        }
      });
    };
    const t = setTimeout(setMultiselectClearTooltip, 100);
    return () => clearTimeout(t);
  }, [state.nonHiddenFields, state.customdata]);
  const loadPage = async () => {
    var _a2, _b2, _c2, _d2;
    if (isSectionHidden) {
      console.warn(`🚫 loadPage blocked: Section (${objectUUId}) is marked as HIDDEN`);
      return;
    }
    if (state.inProgress) {
      console.log("⏭️ LoadPage already in progress, skipping duplicate call");
      return;
    }
    const allSections2 = customFormService.sections$.value || [];
    const currentSection2 = allSections2.find(
      (s) => s.sectionId === objectUUId || s.UUID === objectUUId || String(s.id) === String(objectUUId)
    );
    if (currentSection2) {
      const isVisibleByQuery = currentSection2.hideQuerySettings ? currentSection2.isVisibleByQuery === true : currentSection2.isVisibleByQuery !== false;
      const isVisibleForCountry = currentSection2.isVisibleForCountry !== false;
      const isHidden = !isVisibleByQuery || !isVisibleForCountry;
      if (isHidden) {
        console.warn(`🚫 loadPage blocked: Section "${currentSection2.name || currentSection2.sectionName}" (${objectUUId}) is HIDDEN`);
        setIsSectionHidden(true);
        return;
      }
    }
    try {
      console.log("🚀 Starting loadPage for section:", objectUUId);
      setState((prev) => ({ ...prev, inProgress: true }));
      onSectionLoadStateChange == null ? void 0 : onSectionLoadStateChange(true);
      if (!objectUUId) {
        throw new Error("Section UUID is required");
      }
      const response = await configurableFormsApi.loadFieldsForEndUser(objectUUId, customAppData);
      const parentSetupResponse = await configurableFormsApi.getParentFieldSetup(objectUUId);
      if (parentSetupResponse) {
        setParentFieldSetup(parentSetupResponse);
      }
      await loadValidationSettings(objectUUId);
      if ((_a2 = response == null ? void 0 : response.Response) == null ? void 0 : _a2[0]) {
        const data = response.Response[0];
        setPageData(data);
        const nonHiddenFields = data.fields.filter(
          (field) => field.isAlwaysHidden === null || field.isAlwaysHidden === 0
        );
        const hiddenFields = data.fields.filter(
          (field) => field.isAlwaysHidden === -1
        );
        const len = 1;
        const totalLength = nonHiddenFields.length;
        const fieldsPerColumn = Math.ceil(totalLength / 1);
        const numRows = fieldsPerColumn;
        const rowdata = Array.from({ length: numRows }, () => []);
        const sortedFields = [...nonHiddenFields].sort((a, b) => (a.iorder || 0) - (b.iorder || 0));
        sortedFields.forEach((field, index) => {
          const columnIndex = Math.floor(index / fieldsPerColumn);
          const rowInColumn = index % fieldsPerColumn;
          const rowIndex = rowInColumn;
          if (!rowdata[rowIndex]) {
            rowdata[rowIndex] = [];
          }
          if (columnIndex === 0) {
            rowdata[rowIndex].unshift(field);
          } else {
            rowdata[rowIndex].push(field);
          }
        });
        const customdata = {};
        nonHiddenFields.forEach((field) => {
          var _a3;
          let defaultValue = field.sDefaultValue || "";
          const controlType = (_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase();
          if (controlType === "multiselect" || controlType === "multi select" || controlType === "multiselect dropdown") {
            defaultValue = field.sDefaultValue || [];
          }
          customdata[`custom_${field.hmy}`] = defaultValue;
        });
        hiddenFields.forEach((field) => {
          customdata[`custom_${field.hmy}`] = null;
        });
        const allSections3 = customFormService.sections$.value || [];
        const currentSection3 = allSections3.find((s) => s.sectionId === data.UUID);
        if (currentSection3 && currentSection3.data) {
          console.log(`💾 Restoring previously saved data for section "${data.Label}":`, Object.keys(currentSection3.data));
          Object.keys(currentSection3.data).forEach((key) => {
            if (currentSection3.data[key] !== void 0 && currentSection3.data[key] !== null) {
              customdata[key] = currentSection3.data[key];
            }
          });
        }
        setState((prev) => ({
          ...prev,
          pageCode: data.pagecode || "",
          isTablePresent: false,
          tableColumns: [],
          tableItems: [],
          len,
          hiddenFields,
          nonHiddenFields,
          customfields: { rowdata },
          customdata,
          hasDataLoaded: true
        }));
        initialValuesRef.current = { ...customdata };
        nonHiddenFields.forEach((field) => {
          var _a3, _b3, _c3;
          if (((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase()) === "check box" || ((_b3 = field.sControlType) == null ? void 0 : _b3.toLowerCase()) === "checkbox") {
            const fieldName = ((_c3 = field.sFieldName) == null ? void 0 : _c3.toLowerCase()) || "";
            const isMiddleNameCheckbox = fieldName.includes("middle") || fieldName.includes("not have");
            if (isMiddleNameCheckbox) {
              let middleNameValue = "";
              nonHiddenFields.forEach((textField) => {
                var _a4, _b4;
                if (((_a4 = textField.sControlType) == null ? void 0 : _a4.toLowerCase()) === "text box") {
                  const textFieldName = ((_b4 = textField.sFieldName) == null ? void 0 : _b4.toLowerCase()) || "";
                  if (textFieldName.includes("middle name") || textFieldName.includes("middlename")) {
                    middleNameValue = customdata[`custom_${textField.hmy}`] || "";
                  }
                }
              });
              if (!middleNameValue || middleNameValue.trim() === "") {
                const checkboxKey = `custom_${field.hmy}`;
                customdata[checkboxKey] = true;
                setValue(checkboxKey, true);
                console.log(`✅ NEW FORM: Auto-checked "${field.sFieldName}" because middle name is empty`);
              }
            }
          }
        });
        const hasAddressData = Object.keys(customdata).some(
          (key) => key.toLowerCase().includes("address") && customdata[key] && customdata[key].toString().trim() !== ""
        );
        setState((prev) => ({
          ...prev,
          customdata,
          AddressLine: prev.AddressLine || hasAddressData
        }));
        await loadValidationSettings(objectUUId);
        await preProcessCountryFields(nonHiddenFields);
        if (Id) {
          const formData = await configurableFormsApi.getCustomFormData(Id, objectUUId);
          if ((formData == null ? void 0 : formData.Success) && ((_b2 = formData == null ? void 0 : formData.Data) == null ? void 0 : _b2.customData)) {
            const processedCustomData = Object.fromEntries(
              Object.entries(formData.Data.customData).map(([k, v]) => [String(k).toLowerCase(), v])
            );
            Object.keys(processedCustomData).forEach((key) => {
              const value = processedCustomData[key];
              if (value === null || value === void 0 || value === "undefined" || value === "null") {
                const isImportantField = key.toLowerCase().includes("address") || key.toLowerCase().includes("street") || key.toLowerCase().includes("city") || key.toLowerCase().includes("state") || key.toLowerCase().includes("zip") || key.toLowerCase().includes("postal");
                if (!isImportantField || value === null || value === void 0) {
                  processedCustomData[key] = "";
                  console.log(`Cleaned null/undefined value for ${key}:`, value, "→ empty string");
                }
              }
            });
            try {
              const columnGroups = /* @__PURE__ */ new Map();
              nonHiddenFields.forEach((f) => {
                const col = (f.sColumnName || "").toString().trim().toLowerCase();
                if (!col) return;
                const ct = (f.sControlType || "").toString().trim().toLowerCase();
                const groupKey = `${col}|${ct}`;
                if (!columnGroups.has(groupKey)) columnGroups.set(groupKey, []);
                columnGroups.get(groupKey).push(f);
              });
              columnGroups.forEach((fields, groupKey) => {
                if (!fields || fields.length < 2) return;
                let sourceValue = void 0;
                for (const f of fields) {
                  const k = `custom_${f.hmy}`.toLowerCase();
                  const v = processedCustomData[k];
                  if (v !== void 0 && v !== null && v !== "") {
                    sourceValue = v;
                    break;
                  }
                }
                if (sourceValue === void 0 || sourceValue === null || sourceValue === "") return;
                let copied = 0;
                for (const f of fields) {
                  const k = `custom_${f.hmy}`.toLowerCase();
                  const v = processedCustomData[k];
                  if (v === void 0 || v === null || v === "") {
                    processedCustomData[k] = sourceValue;
                    copied++;
                  }
                }
                if (copied > 0) {
                  console.log(`🔁 Copied value across duplicate column fields (${groupKey}):`, {
                    copied,
                    sourceValue
                  });
                }
              });
            } catch (e) {
              console.warn("Duplicate column hydration failed (non-fatal):", e);
            }
            const govIdTypeField = nonHiddenFields.find((f) => {
              const isDropdown = (f.sControlType || "").toString().toLowerCase() === "drop down";
              if (!isDropdown) return false;
              const n = (f.sFieldName || "").toString().toLowerCase();
              return n.includes("government") && n.includes("id") && n.includes("type");
            });
            if (govIdTypeField) {
              const govIdNumberFieldFromSetup = parentFieldSetup.filter((p) => String(p == null ? void 0 : p.hCafeFormParentFieldID) === String(govIdTypeField.hmy)).map((p) => nonHiddenFields.find((f) => String(f == null ? void 0 : f.hmy) === String(p == null ? void 0 : p.hcafeFormFieldID))).find(
                (f) => ((f == null ? void 0 : f.sControlType) || "").toString().toLowerCase() === "text box" && (f == null ? void 0 : f.hCafeFormSectionsID) === govIdTypeField.hCafeFormSectionsID
              );
              const govIdNumberField = govIdNumberFieldFromSetup || nonHiddenFields.find((f) => {
                const isTextBox = (f.sControlType || "").toString().toLowerCase() === "text box";
                if (!isTextBox) return false;
                if (f.hCafeFormSectionsID !== govIdTypeField.hCafeFormSectionsID) return false;
                const n = (f.sFieldName || "").toString().trim().toLowerCase();
                return n.includes("government") && n.includes("id") && n.includes("number") || n.includes("id") && n.includes("number") || n.includes("identification number") || n.includes("enter") && n.includes("number");
              });
              if (govIdNumberField) {
                const kLc = `custom_${govIdNumberField.hmy}`.toLowerCase();
                const v = processedCustomData[kLc];
                if (typeof v === "string") {
                  const trimmedLower = v.trim().toLowerCase();
                  if (trimmedLower === "undefined" || trimmedLower === "null") {
                    processedCustomData[kLc] = "";
                  }
                } else if (v !== null && v !== void 0 && v !== "") {
                  processedCustomData[kLc] = String(v);
                }
              }
            }
            const { rowdata: rowdata2 } = state.customfields;
            if (rowdata2) {
              rowdata2.forEach((row) => {
                row.forEach((field) => {
                  var _a3, _b3, _c3, _d3, _e, _f;
                  const key = `custom_${field.hmy}`;
                  let value = processedCustomData[key];
                  const dropdownList = field.dropdownlist || [];
                  if ((dropdownList == null ? void 0 : dropdownList.length) > 0) {
                    if (typeof value === "number") {
                      const matchingOption = dropdownList.find(
                        (option) => typeof option.id === "string" && option.id === value.toString()
                      );
                      if (matchingOption) {
                        processedCustomData[key] = value.toString();
                      }
                    }
                    if (typeof value === "string" && value.trim() !== "") {
                      const matchingOption = dropdownList.find(
                        (option) => typeof option.id === "number" && String(option.id) === value
                      );
                      if (matchingOption) {
                        processedCustomData[key] = matchingOption.id;
                      }
                      const isGovernmentIdTypeField = (field.sFieldName || "").toLowerCase().includes("government") && (field.sFieldName || "").toLowerCase().includes("id") && (field.sFieldName || "").toLowerCase().includes("type");
                      if (isGovernmentIdTypeField) {
                        const optionsForMatch = ((_a3 = dynamicFieldTypes == null ? void 0 : dynamicFieldTypes[field.hmy]) == null ? void 0 : _a3.dropdownData) || dropdownList;
                        const matchByName = optionsForMatch.find((option) => {
                          const optName = (option == null ? void 0 : option.Name) ?? (option == null ? void 0 : option.name);
                          return optName && String(optName).trim().toLowerCase() === value.trim().toLowerCase();
                        });
                        if (matchByName) {
                          const matchedId = (matchByName == null ? void 0 : matchByName.Id) ?? (matchByName == null ? void 0 : matchByName.id);
                          processedCustomData[key] = matchedId;
                          value = String(matchedId);
                        }
                      }
                      const isCountryDropdown = (field.sFieldName || "").toLowerCase().includes("country");
                      if (isCountryDropdown) {
                        const matchByCode = dropdownList.find((option) => {
                          const optCode = (option == null ? void 0 : option.code) ?? (option == null ? void 0 : option.Code);
                          return optCode && String(optCode).toLowerCase() === value.toLowerCase();
                        });
                        if (matchByCode) {
                          const matchedId = (matchByCode == null ? void 0 : matchByCode.Id) ?? (matchByCode == null ? void 0 : matchByCode.id);
                          processedCustomData[key] = matchedId;
                          value = String(matchedId);
                        }
                      }
                    }
                  }
                  if (((_b3 = field.sControlType) == null ? void 0 : _b3.toLowerCase()) === "check box" || ((_c3 = field.sControlType) == null ? void 0 : _c3.toLowerCase()) === "checkbox") {
                    const fieldName = ((_d3 = field.sFieldName) == null ? void 0 : _d3.toLowerCase()) || "";
                    const placeholderText = ((_e = field.sPlaceHolderText) == null ? void 0 : _e.toLowerCase()) || "";
                    const columnName = ((_f = field.sColumnName) == null ? void 0 : _f.toLowerCase()) || "";
                    const isMiddleNameCheckbox = fieldName.includes("i do not have a middle name") || fieldName.includes("i do not have middle name") || fieldName.includes("no middle name") || fieldName.includes("middlename") || placeholderText.includes("i do not have a middle name") || placeholderText.includes("i do not have middle name") || placeholderText.includes("no middle name") || placeholderText.includes("middlename") || columnName.includes("middlename") || fieldName.includes("middle") && fieldName.includes("name") || placeholderText.includes("middle") && placeholderText.includes("name");
                    console.log(`🔍 Checking existing data checkbox field: ${field.sFieldName}`, {
                      fieldName,
                      placeholderText,
                      columnName,
                      isMiddleNameCheckbox,
                      currentValue: value,
                      controlType: field.sControlType
                    });
                    if (isMiddleNameCheckbox) {
                      console.log(`🔍 Found middle name checkbox: ${field.sFieldName}`);
                      let middleNameValue = "";
                      nonHiddenFields.forEach((textField) => {
                        var _a4, _b4;
                        if (((_a4 = textField.sControlType) == null ? void 0 : _a4.toLowerCase()) === "text box") {
                          const textFieldName = ((_b4 = textField.sFieldName) == null ? void 0 : _b4.toLowerCase()) || "";
                          if (textFieldName.includes("middle name") || textFieldName.includes("middlename")) {
                            middleNameValue = processedCustomData[`custom_${textField.hmy}`] || "";
                            console.log(`📝 Found middle name field "${textField.sFieldName}" with value: "${middleNameValue}"`);
                          }
                        }
                      });
                      if (!middleNameValue || middleNameValue.trim() === "") {
                        processedCustomData[key] = true;
                        console.log(`✅ Auto-checked "${field.sFieldName}" because middle name is empty`);
                      } else {
                        console.log(`ℹ️ Not auto-checking "${field.sFieldName}" because middle name has value: "${middleNameValue}"`);
                      }
                    }
                  }
                });
              });
            }
            nonHiddenFields.forEach((field) => {
              var _a3, _b3, _c3;
              if (((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase()) === "check box" || ((_b3 = field.sControlType) == null ? void 0 : _b3.toLowerCase()) === "checkbox") {
                const fieldName = ((_c3 = field.sFieldName) == null ? void 0 : _c3.toLowerCase()) || "";
                const isMiddleNameCheckbox = fieldName.includes("middle") || fieldName.includes("not have");
                if (isMiddleNameCheckbox) {
                  let middleNameValue = "";
                  nonHiddenFields.forEach((textField) => {
                    var _a4, _b4;
                    if (((_a4 = textField.sControlType) == null ? void 0 : _a4.toLowerCase()) === "text box") {
                      const textFieldName = ((_b4 = textField.sFieldName) == null ? void 0 : _b4.toLowerCase()) || "";
                      if (textFieldName.includes("middle name") || textFieldName.includes("middlename")) {
                        middleNameValue = processedCustomData[`custom_${textField.hmy}`] || "";
                      }
                    }
                  });
                  if (!middleNameValue || middleNameValue.trim() === "") {
                    const checkboxKey = `custom_${field.hmy}`;
                    processedCustomData[checkboxKey] = true;
                    setValue(checkboxKey, true);
                    console.log(`✅ AFTER DATA LOAD: Auto-checked "${field.sFieldName}" because middle name is empty`);
                  }
                }
              }
            });
            const countryFields = nonHiddenFields.filter(
              (field) => {
                var _a3;
                return (_a3 = field.sFieldName) == null ? void 0 : _a3.toLowerCase().includes("country");
              }
            );
            let detectedCountryCode = "";
            let firstCountryField = null;
            let firstCountryValue = null;
            for (const countryField of countryFields) {
              const countryValue = processedCustomData[`custom_${countryField.hmy}`];
              if (countryValue) {
                const selectedCountry = (_c2 = countryField.dropdownlist) == null ? void 0 : _c2.find(
                  (country) => {
                    var _a3, _b3;
                    return country.id === countryValue || country.Id === countryValue || ((_a3 = country.id) == null ? void 0 : _a3.toString()) === (countryValue == null ? void 0 : countryValue.toString()) || ((_b3 = country.Id) == null ? void 0 : _b3.toString()) === (countryValue == null ? void 0 : countryValue.toString());
                  }
                );
                const countryCode = (selectedCountry == null ? void 0 : selectedCountry.code) || (selectedCountry == null ? void 0 : selectedCountry.Code);
                if (countryCode) {
                  detectedCountryCode = countryCode.toString().toLowerCase();
                  firstCountryField = countryField;
                  firstCountryValue = countryValue;
                  break;
                }
              }
            }
            if (!detectedCountryCode) {
              const phoneField2 = nonHiddenFields.find(
                (f) => {
                  var _a3;
                  return ((_a3 = f.sControlType) == null ? void 0 : _a3.toLowerCase()) === "phone number";
                }
              );
              if (phoneField2) {
                const phoneValue = processedCustomData[`custom_${phoneField2.hmy}`];
                if (phoneValue && phoneValue.startsWith("+")) {
                  try {
                    const { parsePhoneNumberFromString: parsePhoneNumberFromString2 } = require("libphonenumber-js");
                    const phoneNumber = parsePhoneNumberFromString2(phoneValue);
                    if (phoneNumber && phoneNumber.country) {
                      detectedCountryCode = phoneNumber.country.toLowerCase();
                    }
                  } catch (error) {
                    console.warn("Could not detect country from phone number:", error);
                  }
                }
              }
            }
            if (detectedCountryCode) {
              setInitCountry(detectedCountryCode);
              console.log("🌍 Country detected early from", firstCountryField ? "dropdown" : "phone", ":", detectedCountryCode);
              if (firstCountryField && firstCountryValue) {
                setTimeout(() => {
                  checkCountryChange(firstCountryField, firstCountryValue).catch((err) => {
                    console.warn("Error in early country change:", err);
                  });
                }, 0);
              }
            }
            setState((prev) => ({
              ...prev,
              customdata: processedCustomData
            }));
            const phoneField = nonHiddenFields.find(
              (f) => {
                var _a3;
                return ((_a3 = f.sControlType) == null ? void 0 : _a3.toLowerCase()) === "phone number";
              }
            );
            if (phoneField) {
              const phoneValue = processedCustomData[`custom_${phoneField.hmy}`];
              if (phoneValue) {
                setDisplayValue(phoneField.hmy, phoneValue);
              }
            }
            clearErrors();
            const allSections4 = customFormService.sections$.value || [];
            const currentSectionToUpdate = allSections4.find((s) => s.sectionId === data.UUID);
            if (currentSectionToUpdate) {
              currentSectionToUpdate.data = { ...currentSectionToUpdate.data, ...processedCustomData };
              customFormService.sections$.next([...allSections4]);
              console.log(`💾 Stored loaded form data in section "${data.Label}"`);
            }
            setTimeout(() => {
              reset(processedCustomData);
              clearErrors();
              Object.keys(processedCustomData).forEach((key) => {
                var _a3;
                const value = processedCustomData[key];
                if (value !== null && value !== void 0 && value !== "") {
                  setValue(key, value, { shouldValidate: false });
                  clearErrors(key);
                  const fieldHmy = key.replace("custom_", "");
                  const field = nonHiddenFields.find((f) => f.hmy.toString() === fieldHmy);
                  if (field && ((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase()) === "phone number") {
                    setDisplayValue(field.hmy, value);
                  }
                }
              });
              nonHiddenFields == null ? void 0 : nonHiddenFields.forEach((field) => {
                var _a3, _b3, _c3, _d3, _e, _f, _g, _h, _i;
                if (((_a3 = field.sControlType) == null ? void 0 : _a3.toLowerCase()) === "text box" && ((_b3 = field.sFieldName) == null ? void 0 : _b3.toLowerCase().includes("address")) && !((_c3 = field.sFieldName) == null ? void 0 : _c3.toLowerCase().includes("line 2")) && !((_d3 = field.sFieldName) == null ? void 0 : _d3.toLowerCase().includes("address 2")) && !((_e = field.sFieldName) == null ? void 0 : _e.toLowerCase().includes("secondary")) && !((_f = field.sFieldName) == null ? void 0 : _f.toLowerCase().includes("email")) && !((_g = field.sFieldName) == null ? void 0 : _g.toLowerCase().includes("phone")) && !((_h = field.sFieldName) == null ? void 0 : _h.toLowerCase().includes("website")) && !((_i = field.sFieldName) == null ? void 0 : _i.toLowerCase().includes("url"))) {
                  const fieldName = `custom_${field.hmy}`;
                  const value = processedCustomData[fieldName];
                  if (value !== null && value !== void 0 && value !== "") {
                    setDisplayValue(field.hmy, value);
                  }
                }
              });
              initialValuesRef.current = { ...processedCustomData };
              console.log("📝 Updated initialValuesRef with loaded server data:", Object.keys(initialValuesRef.current).length, "fields");
              setTimeout(() => {
                validateExistingData();
              }, 200);
            }, 100);
            if (((_d2 = formData.tableItems) == null ? void 0 : _d2.length) > 0) {
              setState((prev) => ({
                ...prev,
                tableItems: formData.tableItems
              }));
            }
          }
        }
      } else {
        throw new Error("No field data returned from server");
      }
    } catch (error) {
      console.error("Error loading page:", error);
      setState((prev) => ({
        ...prev,
        serverError: error instanceof Error ? error.message : translate("Failed to load page data"),
        hasDataLoaded: false
      }));
      toast$1({
        title: translate("Error"),
        description: error instanceof Error ? translate(error.message) : translate("Failed to load page data"),
        variant: "destructive"
      });
    } finally {
      setState((prev) => ({ ...prev, inProgress: false }));
      onSectionLoadStateChange == null ? void 0 : onSectionLoadStateChange(false);
    }
  };
  React__default.useImperativeHandle(ref, () => ({
    handlePrevious: () => handlePrevious(),
    submitForm: () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        );
      }
    }
  }));
  const allSections = customFormService.sections$.value || [];
  const currentSection = allSections.find(
    (s) => s.sectionId === objectUUId || s.UUID === objectUUId || String(s.id) === String(objectUUId)
  );
  if (currentSection) {
    const isVisibleByQuery = currentSection.hideQuerySettings ? currentSection.isVisibleByQuery === true : currentSection.isVisibleByQuery !== false;
    const isVisibleForCountry = currentSection.isVisibleForCountry !== false;
    const isHiddenDuringRender = !isVisibleByQuery || !isVisibleForCountry;
    if (isHiddenDuringRender) {
      ((_a = customFormService.completedSectionList$) == null ? void 0 : _a.value) || [];
      const incompleteList = ((_b = customFormService.incompleteSectionList$) == null ? void 0 : _b.value) || [];
      const allSectionsCompleted = incompleteList.length === 0;
      if (allSectionsCompleted) {
        return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("All sections completed...") });
      }
      const currentIndex = allSections.findIndex(
        (s) => s.sectionId === objectUUId || s.UUID === objectUUId || String(s.id) === String(objectUUId)
      );
      if (currentIndex !== -1) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          const prevSection = allSections[i];
          const prevIsVisibleByQuery = prevSection.hideQuerySettings ? prevSection.isVisibleByQuery === true : prevSection.isVisibleByQuery !== false;
          const prevIsVisibleForCountry = prevSection.isVisibleForCountry !== false;
          if (prevIsVisibleByQuery && prevIsVisibleForCountry) {
            const prevSectionId = prevSection.sectionId || prevSection.id;
            if (String(prevSectionId) !== String(objectUUId)) {
              if (!navigationInProgressRef.current) {
                navigationInProgressRef.current = true;
                customFormService.navigateToSection(prevSectionId).then((navigationResult) => {
                  if (navigationResult) {
                    navigate(navigationResult.url, { replace: true, state: navigationResult.state });
                  }
                  setTimeout(() => {
                    navigationInProgressRef.current = false;
                  }, 100);
                }).catch((error) => {
                  console.error("Error navigating to previous section:", error);
                  navigationInProgressRef.current = false;
                });
              }
            }
            return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading previous section...") });
          }
        }
        for (let i = currentIndex + 1; i < allSections.length; i++) {
          const nextSection = allSections[i];
          const nextIsVisibleByQuery = nextSection.hideQuerySettings ? nextSection.isVisibleByQuery === true : nextSection.isVisibleByQuery !== false;
          const nextIsVisibleForCountry = nextSection.isVisibleForCountry !== false;
          if (nextIsVisibleByQuery && nextIsVisibleForCountry) {
            const nextSectionId = nextSection.sectionId || nextSection.id;
            if (String(nextSectionId) !== String(objectUUId)) {
              if (!navigationInProgressRef.current) {
                navigationInProgressRef.current = true;
                customFormService.navigateToSection(nextSectionId).then((navigationResult) => {
                  if (navigationResult) {
                    navigate(navigationResult.url, { replace: true, state: navigationResult.state });
                  }
                  setTimeout(() => {
                    navigationInProgressRef.current = false;
                  }, 100);
                }).catch((error) => {
                  console.error("Error navigating to next section:", error);
                  navigationInProgressRef.current = false;
                });
              }
            }
            return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading next section...") });
          }
        }
        for (let i = currentIndex + 1; i < allSections.length; i++) {
          const firstSection = allSections[i];
          const firstIsVisibleByQuery = firstSection.hideQuerySettings ? firstSection.isVisibleByQuery === true : firstSection.isVisibleByQuery !== false;
          const firstIsVisibleForCountry = firstSection.isVisibleForCountry !== false;
          if (firstIsVisibleByQuery && firstIsVisibleForCountry) {
            const firstSectionId = firstSection.sectionId || firstSection.id;
            if (String(firstSectionId) !== String(objectUUId)) {
              if (!navigationInProgressRef.current) {
                navigationInProgressRef.current = true;
                customFormService.navigateToSection(firstSectionId).then((navigationResult) => {
                  if (navigationResult) {
                    navigate(navigationResult.url, { replace: true, state: navigationResult.state });
                  }
                  setTimeout(() => {
                    navigationInProgressRef.current = false;
                  }, 100);
                }).catch((error) => {
                  console.error("Error navigating to first section:", error);
                  navigationInProgressRef.current = false;
                });
              }
            }
            return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading section...") });
          }
        }
      }
      return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading section...") });
    } else {
      if (isSectionHidden) {
        setIsSectionHidden(false);
      }
    }
  }
  if (isSectionHidden) {
    return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading section...") });
  }
  if (state.inProgress) {
    return /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading your form...") });
  }
  return /* @__PURE__ */ jsx("div", { className: "configurable-form-container overflow-visible", children: state.inProgress ? /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: translate("Loading your form...") }) : /* @__PURE__ */ jsxs("div", { className: "w-full pb-4", children: [
    /* @__PURE__ */ jsx("div", { className: "desktop-max-width", children: /* @__PURE__ */ jsx(
      ConfigurableFormBreadcrumb,
      {
        currentSectionId: objectUUId,
        sectionTitle: pageData == null ? void 0 : pageData.sectionName,
        categoryLabel: stepName ? ((_c = getConfigurableFormsConfigOrThrow().stepLabels) == null ? void 0 : _c[stepName]) ?? "Student Info" : "Student Info",
        showRequired: !state.bReadOnly,
        isReadOnly: state.bReadOnly
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "desktop-max-width", children: /* @__PURE__ */ jsxs("form", { ref: formRef, onSubmit: handleSubmit(handleSave, () => {
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: false, inProgressText: "" });
    }), children: [
      state.errorMessage && /* @__PURE__ */ jsx("div", { className: "bg-red-50 border border-red-200 rounded-lg px-4 py-1 mb-6", role: "alert", "aria-live": "polite", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-red-800", children: state.errorMessage }) }),
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ml-4 text-red-400 hover:text-red-500 leading-none !h-auto",
            onClick: () => setState((prev) => ({ ...prev, errorMessage: "" })),
            children: [
              /* @__PURE__ */ jsx("span", { className: "sr-only", children: translate("Dismiss") }),
              /* @__PURE__ */ jsx("svg", { className: "h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })
            ]
          }
        )
      ] }) }),
      successMessage && /* @__PURE__ */ jsx("div", { className: "bg-green-50 border border-green-200 rounded-lg px-4 py-1 mb-6", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx("div", { className: "flex items-center", children: /* @__PURE__ */ jsx("svg", { className: "h-5 w-5 text-green-400 mr-2", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z", clipRule: "evenodd" }) }) }),
        /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-green-800", children: successMessage }) }),
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ml-4 text-green-400 hover:text-green-500 leading-none",
            onClick: () => setSuccessMessage(""),
            children: [
              /* @__PURE__ */ jsx("span", { className: "sr-only", children: translate("Dismiss") }),
              /* @__PURE__ */ jsx("svg", { className: "h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })
            ]
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "space-y-4   overflow-visible", children: (_d = state.nonHiddenFields) == null ? void 0 : _d.filter((field) => field.Field_iType !== 2).sort((a, b) => (a.iorder || 0) - (b.iorder || 0)).map((field, j) => {
        var _a2, _b2;
        return /* @__PURE__ */ jsx("div", { className: "space-y-2 w-full", children: /* @__PURE__ */ jsxs(Fragment, { children: [
          field.sControlType.toLowerCase() !== "checkbox" && field.sControlType.toLowerCase() !== "check box" && ((_a2 = field.sControlType) == null ? void 0 : _a2.toLowerCase()) !== "terms and conditions" && isConditionalShow(field.hmy) && (field.bVisible || field.bAlwaysVisible) && /* @__PURE__ */ jsxs("div", { className: "mb-1", children: [
            /* @__PURE__ */ jsxs(Label, { htmlFor: `field_${field.hmy}`, className: "block mb-1 !text-base w-full", children: [
              /* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: translate((_b2 = dynamicFieldTypes[field.hmy]) == null ? void 0 : _b2.label) || translate(field.sFieldName) || "" } }),
              (field._bRequired || field._bAlwaysRequired) && !isConditionalReadOnly(field.hmy) && /* @__PURE__ */ jsx("span", { className: "required-asterisk ml-1", children: "*" })
            ] }),
            field.sCustomFieldNarrative && /* @__PURE__ */ jsxs("div", { className: "text-sm text-gray-500 mt-0.5", children: [
              /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faCircleInfo, className: "w-3 h-3 mr-1 inline-block align-text-bottom" }),
              /* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: translate(field.sCustomFieldNarrative || "") } })
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "mt-1 w-full", children: renderField(field) })
        ] }) }, j);
      }) }),
      /* @__PURE__ */ jsx("div", { className: "fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 hidden", children: /* @__PURE__ */ jsxs("div", { className: "max-w-md mx-auto px-4 py-4 flex justify-end gap-4", children: [
        !state.isFirstSection && showPreviousButton && /* @__PURE__ */ jsx("div", { style: { width: "100px" }, children: /* @__PURE__ */ jsx(
          YardiControlButton,
          {
            variant: "modal-secondary",
            size: "lg",
            onClick: handlePrevious,
            disabled: state.saving,
            cssClass: state.saving ? "opacity-50 cursor-not-allowed" : "",
            children: translate("Back")
          }
        ) }),
        showSaveButton && /* @__PURE__ */ jsx("div", { style: { width: "140px" }, children: /* @__PURE__ */ jsx(
          YardiControlButton,
          {
            variant: "modal-primary",
            size: "lg",
            type: "submit",
            disabled: state.saving || isSaving,
            cssClass: state.saving || isSaving ? "opacity-50 cursor-not-allowed" : "",
            onClick: () => {
              if (!isSaving) {
                setIsSaving(true);
              }
            },
            children: translate("Continue")
          }
        ) })
      ] }) })
    ] }) })
  ] }) });
});
const CustomFormControl = CustomFormControlComponent;
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
var intlTelInput$2 = { exports: {} };
var hasRequiredIntlTelInput$1;
function requireIntlTelInput$1() {
  if (hasRequiredIntlTelInput$1) return intlTelInput$2.exports;
  hasRequiredIntlTelInput$1 = 1;
  (function(module) {
    (function(factory) {
      if (module.exports) module.exports = factory();
      else window.intlTelInput = factory();
    })(function(undefined$1) {
      return (function() {
        var allCountries = [["Afghanistan (‫افغانستان‬‎)", "af", "93"], ["Albania (Shqipëri)", "al", "355"], ["Algeria (‫الجزائر‬‎)", "dz", "213"], ["American Samoa", "as", "1", 5, ["684"]], ["Andorra", "ad", "376"], ["Angola", "ao", "244"], ["Anguilla", "ai", "1", 6, ["264"]], ["Antigua and Barbuda", "ag", "1", 7, ["268"]], ["Argentina", "ar", "54"], ["Armenia (Հայաստան)", "am", "374"], ["Aruba", "aw", "297"], ["Ascension Island", "ac", "247"], ["Australia", "au", "61", 0], ["Austria (Österreich)", "at", "43"], ["Azerbaijan (Azərbaycan)", "az", "994"], ["Bahamas", "bs", "1", 8, ["242"]], ["Bahrain (‫البحرين‬‎)", "bh", "973"], ["Bangladesh (বাংলাদেশ)", "bd", "880"], ["Barbados", "bb", "1", 9, ["246"]], ["Belarus (Беларусь)", "by", "375"], ["Belgium (België)", "be", "32"], ["Belize", "bz", "501"], ["Benin (Bénin)", "bj", "229"], ["Bermuda", "bm", "1", 10, ["441"]], ["Bhutan (འབྲུག)", "bt", "975"], ["Bolivia", "bo", "591"], ["Bosnia and Herzegovina (Босна и Херцеговина)", "ba", "387"], ["Botswana", "bw", "267"], ["Brazil (Brasil)", "br", "55"], ["British Indian Ocean Territory", "io", "246"], ["British Virgin Islands", "vg", "1", 11, ["284"]], ["Brunei", "bn", "673"], ["Bulgaria (България)", "bg", "359"], ["Burkina Faso", "bf", "226"], ["Burundi (Uburundi)", "bi", "257"], ["Cambodia (កម្ពុជា)", "kh", "855"], ["Cameroon (Cameroun)", "cm", "237"], ["Canada", "ca", "1", 1, ["204", "226", "236", "249", "250", "289", "306", "343", "365", "387", "403", "416", "418", "431", "437", "438", "450", "506", "514", "519", "548", "579", "581", "587", "604", "613", "639", "647", "672", "705", "709", "742", "778", "780", "782", "807", "819", "825", "867", "873", "902", "905"]], ["Cape Verde (Kabu Verdi)", "cv", "238"], ["Caribbean Netherlands", "bq", "599", 1, ["3", "4", "7"]], ["Cayman Islands", "ky", "1", 12, ["345"]], ["Central African Republic (République centrafricaine)", "cf", "236"], ["Chad (Tchad)", "td", "235"], ["Chile", "cl", "56"], ["China (中国)", "cn", "86"], ["Christmas Island", "cx", "61", 2, ["89164"]], ["Cocos (Keeling) Islands", "cc", "61", 1, ["89162"]], ["Colombia", "co", "57"], ["Comoros (‫جزر القمر‬‎)", "km", "269"], ["Congo (DRC) (Jamhuri ya Kidemokrasia ya Kongo)", "cd", "243"], ["Congo (Republic) (Congo-Brazzaville)", "cg", "242"], ["Cook Islands", "ck", "682"], ["Costa Rica", "cr", "506"], ["Côte d’Ivoire", "ci", "225"], ["Croatia (Hrvatska)", "hr", "385"], ["Cuba", "cu", "53"], ["Curaçao", "cw", "599", 0], ["Cyprus (Κύπρος)", "cy", "357"], ["Czech Republic (Česká republika)", "cz", "420"], ["Denmark (Danmark)", "dk", "45"], ["Djibouti", "dj", "253"], ["Dominica", "dm", "1", 13, ["767"]], ["Dominican Republic (República Dominicana)", "do", "1", 2, ["809", "829", "849"]], ["Ecuador", "ec", "593"], ["Egypt (‫مصر‬‎)", "eg", "20"], ["El Salvador", "sv", "503"], ["Equatorial Guinea (Guinea Ecuatorial)", "gq", "240"], ["Eritrea", "er", "291"], ["Estonia (Eesti)", "ee", "372"], ["Eswatini", "sz", "268"], ["Ethiopia", "et", "251"], ["Falkland Islands (Islas Malvinas)", "fk", "500"], ["Faroe Islands (Føroyar)", "fo", "298"], ["Fiji", "fj", "679"], ["Finland (Suomi)", "fi", "358", 0], ["France", "fr", "33"], ["French Guiana (Guyane française)", "gf", "594"], ["French Polynesia (Polynésie française)", "pf", "689"], ["Gabon", "ga", "241"], ["Gambia", "gm", "220"], ["Georgia (საქართველო)", "ge", "995"], ["Germany (Deutschland)", "de", "49"], ["Ghana (Gaana)", "gh", "233"], ["Gibraltar", "gi", "350"], ["Greece (Ελλάδα)", "gr", "30"], ["Greenland (Kalaallit Nunaat)", "gl", "299"], ["Grenada", "gd", "1", 14, ["473"]], ["Guadeloupe", "gp", "590", 0], ["Guam", "gu", "1", 15, ["671"]], ["Guatemala", "gt", "502"], ["Guernsey", "gg", "44", 1, ["1481", "7781", "7839", "7911"]], ["Guinea (Guinée)", "gn", "224"], ["Guinea-Bissau (Guiné Bissau)", "gw", "245"], ["Guyana", "gy", "592"], ["Haiti", "ht", "509"], ["Honduras", "hn", "504"], ["Hong Kong (香港)", "hk", "852"], ["Hungary (Magyarország)", "hu", "36"], ["Iceland (Ísland)", "is", "354"], ["India (भारत)", "in", "91"], ["Indonesia", "id", "62"], ["Iran (‫ایران‬‎)", "ir", "98"], ["Iraq (‫العراق‬‎)", "iq", "964"], ["Ireland", "ie", "353"], ["Isle of Man", "im", "44", 2, ["1624", "74576", "7524", "7924", "7624"]], ["Israel (‫ישראל‬‎)", "il", "972"], ["Italy (Italia)", "it", "39", 0], ["Jamaica", "jm", "1", 4, ["876", "658"]], ["Japan (日本)", "jp", "81"], ["Jersey", "je", "44", 3, ["1534", "7509", "7700", "7797", "7829", "7937"]], ["Jordan (‫الأردن‬‎)", "jo", "962"], ["Kazakhstan (Казахстан)", "kz", "7", 1, ["33", "7"]], ["Kenya", "ke", "254"], ["Kiribati", "ki", "686"], ["Kosovo", "xk", "383"], ["Kuwait (‫الكويت‬‎)", "kw", "965"], ["Kyrgyzstan (Кыргызстан)", "kg", "996"], ["Laos (ລາວ)", "la", "856"], ["Latvia (Latvija)", "lv", "371"], ["Lebanon (‫لبنان‬‎)", "lb", "961"], ["Lesotho", "ls", "266"], ["Liberia", "lr", "231"], ["Libya (‫ليبيا‬‎)", "ly", "218"], ["Liechtenstein", "li", "423"], ["Lithuania (Lietuva)", "lt", "370"], ["Luxembourg", "lu", "352"], ["Macau (澳門)", "mo", "853"], ["Madagascar (Madagasikara)", "mg", "261"], ["Malawi", "mw", "265"], ["Malaysia", "my", "60"], ["Maldives", "mv", "960"], ["Mali", "ml", "223"], ["Malta", "mt", "356"], ["Marshall Islands", "mh", "692"], ["Martinique", "mq", "596"], ["Mauritania (‫موريتانيا‬‎)", "mr", "222"], ["Mauritius (Moris)", "mu", "230"], ["Mayotte", "yt", "262", 1, ["269", "639"]], ["Mexico (México)", "mx", "52"], ["Micronesia", "fm", "691"], ["Moldova (Republica Moldova)", "md", "373"], ["Monaco", "mc", "377"], ["Mongolia (Монгол)", "mn", "976"], ["Montenegro (Crna Gora)", "me", "382"], ["Montserrat", "ms", "1", 16, ["664"]], ["Morocco (‫المغرب‬‎)", "ma", "212", 0], ["Mozambique (Moçambique)", "mz", "258"], ["Myanmar (Burma) (မြန်မာ)", "mm", "95"], ["Namibia (Namibië)", "na", "264"], ["Nauru", "nr", "674"], ["Nepal (नेपाल)", "np", "977"], ["Netherlands (Nederland)", "nl", "31"], ["New Caledonia (Nouvelle-Calédonie)", "nc", "687"], ["New Zealand", "nz", "64"], ["Nicaragua", "ni", "505"], ["Niger (Nijar)", "ne", "227"], ["Nigeria", "ng", "234"], ["Niue", "nu", "683"], ["Norfolk Island", "nf", "672"], ["North Korea (조선 민주주의 인민 공화국)", "kp", "850"], ["North Macedonia (Северна Македонија)", "mk", "389"], ["Northern Mariana Islands", "mp", "1", 17, ["670"]], ["Norway (Norge)", "no", "47", 0], ["Oman (‫عُمان‬‎)", "om", "968"], ["Pakistan (‫پاکستان‬‎)", "pk", "92"], ["Palau", "pw", "680"], ["Palestine (‫فلسطين‬‎)", "ps", "970"], ["Panama (Panamá)", "pa", "507"], ["Papua New Guinea", "pg", "675"], ["Paraguay", "py", "595"], ["Peru (Perú)", "pe", "51"], ["Philippines", "ph", "63"], ["Poland (Polska)", "pl", "48"], ["Portugal", "pt", "351"], ["Puerto Rico", "pr", "1", 3, ["787", "939"]], ["Qatar (‫قطر‬‎)", "qa", "974"], ["Réunion (La Réunion)", "re", "262", 0], ["Romania (România)", "ro", "40"], ["Russia (Россия)", "ru", "7", 0], ["Rwanda", "rw", "250"], ["Saint Barthélemy", "bl", "590", 1], ["Saint Helena", "sh", "290"], ["Saint Kitts and Nevis", "kn", "1", 18, ["869"]], ["Saint Lucia", "lc", "1", 19, ["758"]], ["Saint Martin (Saint-Martin (partie française))", "mf", "590", 2], ["Saint Pierre and Miquelon (Saint-Pierre-et-Miquelon)", "pm", "508"], ["Saint Vincent and the Grenadines", "vc", "1", 20, ["784"]], ["Samoa", "ws", "685"], ["San Marino", "sm", "378"], ["São Tomé and Príncipe (São Tomé e Príncipe)", "st", "239"], ["Saudi Arabia (‫المملكة العربية السعودية‬‎)", "sa", "966"], ["Senegal (Sénégal)", "sn", "221"], ["Serbia (Србија)", "rs", "381"], ["Seychelles", "sc", "248"], ["Sierra Leone", "sl", "232"], ["Singapore", "sg", "65"], ["Sint Maarten", "sx", "1", 21, ["721"]], ["Slovakia (Slovensko)", "sk", "421"], ["Slovenia (Slovenija)", "si", "386"], ["Solomon Islands", "sb", "677"], ["Somalia (Soomaaliya)", "so", "252"], ["South Africa", "za", "27"], ["South Korea (대한민국)", "kr", "82"], ["South Sudan (‫جنوب السودان‬‎)", "ss", "211"], ["Spain (España)", "es", "34"], ["Sri Lanka (ශ්‍රී ලංකාව)", "lk", "94"], ["Sudan (‫السودان‬‎)", "sd", "249"], ["Suriname", "sr", "597"], ["Svalbard and Jan Mayen", "sj", "47", 1, ["79"]], ["Sweden (Sverige)", "se", "46"], ["Switzerland (Schweiz)", "ch", "41"], ["Syria (‫سوريا‬‎)", "sy", "963"], ["Taiwan (台灣)", "tw", "886"], ["Tajikistan", "tj", "992"], ["Tanzania", "tz", "255"], ["Thailand (ไทย)", "th", "66"], ["Timor-Leste", "tl", "670"], ["Togo", "tg", "228"], ["Tokelau", "tk", "690"], ["Tonga", "to", "676"], ["Trinidad and Tobago", "tt", "1", 22, ["868"]], ["Tunisia (‫تونس‬‎)", "tn", "216"], ["Turkey (Türkiye)", "tr", "90"], ["Turkmenistan", "tm", "993"], ["Turks and Caicos Islands", "tc", "1", 23, ["649"]], ["Tuvalu", "tv", "688"], ["U.S. Virgin Islands", "vi", "1", 24, ["340"]], ["Uganda", "ug", "256"], ["Ukraine (Україна)", "ua", "380"], ["United Arab Emirates (‫الإمارات العربية المتحدة‬‎)", "ae", "971"], ["United Kingdom", "gb", "44", 0], ["United States", "us", "1", 0], ["Uruguay", "uy", "598"], ["Uzbekistan (Oʻzbekiston)", "uz", "998"], ["Vanuatu", "vu", "678"], ["Vatican City (Città del Vaticano)", "va", "39", 1, ["06698"]], ["Venezuela", "ve", "58"], ["Vietnam (Việt Nam)", "vn", "84"], ["Wallis and Futuna (Wallis-et-Futuna)", "wf", "681"], ["Western Sahara (‫الصحراء الغربية‬‎)", "eh", "212", 1, ["5288", "5289"]], ["Yemen (‫اليمن‬‎)", "ye", "967"], ["Zambia", "zm", "260"], ["Zimbabwe", "zw", "263"], ["Åland Islands", "ax", "358", 1, ["18"]]];
        for (var i = 0; i < allCountries.length; i++) {
          var c = allCountries[i];
          allCountries[i] = {
            name: c[0],
            iso2: c[1],
            dialCode: c[2],
            priority: c[3] || 0,
            areaCodes: c[4] || null
          };
        }
        function _classCallCheck(instance, Constructor) {
          if (!(instance instanceof Constructor)) {
            throw new TypeError("Cannot call a class as a function");
          }
        }
        function _defineProperties(target, props) {
          for (var i2 = 0; i2 < props.length; i2++) {
            var descriptor = props[i2];
            descriptor.enumerable = descriptor.enumerable || false;
            descriptor.configurable = true;
            if ("value" in descriptor) descriptor.writable = true;
            Object.defineProperty(target, descriptor.key, descriptor);
          }
        }
        function _createClass(Constructor, protoProps, staticProps) {
          if (protoProps) _defineProperties(Constructor.prototype, protoProps);
          return Constructor;
        }
        var intlTelInputGlobals = {
          getInstance: function getInstance(input) {
            var id2 = input.getAttribute("data-intl-tel-input-id");
            return window.intlTelInputGlobals.instances[id2];
          },
          instances: {},
          // using a global like this allows us to mock it in the tests
          documentReady: function documentReady() {
            return document.readyState === "complete";
          }
        };
        if (typeof window === "object") window.intlTelInputGlobals = intlTelInputGlobals;
        var id = 0;
        var defaults = {
          // whether or not to allow the dropdown
          allowDropdown: true,
          // if there is just a dial code in the input: remove it on blur
          autoHideDialCode: true,
          // add a placeholder in the input with an example number for the selected country
          autoPlaceholder: "polite",
          // modify the parentClass
          customContainer: "",
          // modify the auto placeholder
          customPlaceholder: null,
          // append menu to specified element
          dropdownContainer: null,
          // don't display these countries
          excludeCountries: [],
          // format the input value during initialisation and on setNumber
          formatOnDisplay: true,
          // geoIp lookup function
          geoIpLookup: null,
          // inject a hidden input with this name, and on submit, populate it with the result of getNumber
          hiddenInput: "",
          // initial country
          initialCountry: "",
          // localized country names e.g. { 'de': 'Deutschland' }
          localizedCountries: null,
          // don't insert international dial codes
          nationalMode: true,
          // display only these countries
          onlyCountries: [],
          // number type to use for placeholders
          placeholderNumberType: "MOBILE",
          // the countries at the top of the list. defaults to united states and united kingdom
          preferredCountries: ["us", "gb"],
          // display the country dial code next to the selected flag so it's not part of the typed number
          separateDialCode: false,
          // specify the path to the libphonenumber script to enable validation/formatting
          utilsScript: ""
        };
        var regionlessNanpNumbers = ["800", "822", "833", "844", "855", "866", "877", "880", "881", "882", "883", "884", "885", "886", "887", "888", "889"];
        var forEachProp = function forEachProp2(obj, callback) {
          var keys = Object.keys(obj);
          for (var i2 = 0; i2 < keys.length; i2++) {
            callback(keys[i2], obj[keys[i2]]);
          }
        };
        var forEachInstance = function forEachInstance2(method) {
          forEachProp(window.intlTelInputGlobals.instances, function(key) {
            window.intlTelInputGlobals.instances[key][method]();
          });
        };
        var Iti = /* @__PURE__ */ (function() {
          function Iti2(input, options) {
            var _this = this;
            _classCallCheck(this, Iti2);
            this.id = id++;
            this.telInput = input;
            this.activeItem = null;
            this.highlightedItem = null;
            var customOptions = options || {};
            this.options = {};
            forEachProp(defaults, function(key, value) {
              _this.options[key] = customOptions.hasOwnProperty(key) ? customOptions[key] : value;
            });
            this.hadInitialPlaceholder = Boolean(input.getAttribute("placeholder"));
          }
          _createClass(Iti2, [{
            key: "_init",
            value: function _init() {
              var _this2 = this;
              if (this.options.nationalMode) this.options.autoHideDialCode = false;
              if (this.options.separateDialCode) {
                this.options.autoHideDialCode = this.options.nationalMode = false;
              }
              this.isMobile = /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
              if (this.isMobile) {
                document.body.classList.add("iti-mobile");
                if (!this.options.dropdownContainer) this.options.dropdownContainer = document.body;
              }
              if (typeof Promise !== "undefined") {
                var autoCountryPromise = new Promise(function(resolve, reject) {
                  _this2.resolveAutoCountryPromise = resolve;
                  _this2.rejectAutoCountryPromise = reject;
                });
                var utilsScriptPromise = new Promise(function(resolve, reject) {
                  _this2.resolveUtilsScriptPromise = resolve;
                  _this2.rejectUtilsScriptPromise = reject;
                });
                this.promise = Promise.all([autoCountryPromise, utilsScriptPromise]);
              } else {
                this.resolveAutoCountryPromise = this.rejectAutoCountryPromise = function() {
                };
                this.resolveUtilsScriptPromise = this.rejectUtilsScriptPromise = function() {
                };
              }
              this.selectedCountryData = {};
              this._processCountryData();
              this._generateMarkup();
              this._setInitialState();
              this._initListeners();
              this._initRequests();
            }
          }, {
            key: "_processCountryData",
            value: function _processCountryData() {
              this._processAllCountries();
              this._processCountryCodes();
              this._processPreferredCountries();
              if (this.options.localizedCountries) this._translateCountriesByLocale();
              if (this.options.onlyCountries.length || this.options.localizedCountries) {
                this.countries.sort(this._countryNameSort);
              }
            }
          }, {
            key: "_addCountryCode",
            value: function _addCountryCode(iso2, countryCode, priority) {
              if (countryCode.length > this.countryCodeMaxLen) {
                this.countryCodeMaxLen = countryCode.length;
              }
              if (!this.countryCodes.hasOwnProperty(countryCode)) {
                this.countryCodes[countryCode] = [];
              }
              for (var i2 = 0; i2 < this.countryCodes[countryCode].length; i2++) {
                if (this.countryCodes[countryCode][i2] === iso2) return;
              }
              var index = priority !== undefined$1 ? priority : this.countryCodes[countryCode].length;
              this.countryCodes[countryCode][index] = iso2;
            }
          }, {
            key: "_processAllCountries",
            value: function _processAllCountries() {
              if (this.options.onlyCountries.length) {
                var lowerCaseOnlyCountries = this.options.onlyCountries.map(function(country) {
                  return country.toLowerCase();
                });
                this.countries = allCountries.filter(function(country) {
                  return lowerCaseOnlyCountries.indexOf(country.iso2) > -1;
                });
              } else if (this.options.excludeCountries.length) {
                var lowerCaseExcludeCountries = this.options.excludeCountries.map(function(country) {
                  return country.toLowerCase();
                });
                this.countries = allCountries.filter(function(country) {
                  return lowerCaseExcludeCountries.indexOf(country.iso2) === -1;
                });
              } else {
                this.countries = allCountries;
              }
            }
          }, {
            key: "_translateCountriesByLocale",
            value: function _translateCountriesByLocale() {
              for (var i2 = 0; i2 < this.countries.length; i2++) {
                var iso = this.countries[i2].iso2.toLowerCase();
                if (this.options.localizedCountries.hasOwnProperty(iso)) {
                  this.countries[i2].name = this.options.localizedCountries[iso];
                }
              }
            }
          }, {
            key: "_countryNameSort",
            value: function _countryNameSort(a, b) {
              return a.name.localeCompare(b.name);
            }
          }, {
            key: "_processCountryCodes",
            value: function _processCountryCodes() {
              this.countryCodeMaxLen = 0;
              this.dialCodes = {};
              this.countryCodes = {};
              for (var i2 = 0; i2 < this.countries.length; i2++) {
                var c2 = this.countries[i2];
                if (!this.dialCodes[c2.dialCode]) this.dialCodes[c2.dialCode] = true;
                this._addCountryCode(c2.iso2, c2.dialCode, c2.priority);
              }
              for (var _i = 0; _i < this.countries.length; _i++) {
                var _c = this.countries[_i];
                if (_c.areaCodes) {
                  var rootCountryCode = this.countryCodes[_c.dialCode][0];
                  for (var j = 0; j < _c.areaCodes.length; j++) {
                    var areaCode = _c.areaCodes[j];
                    for (var k = 1; k < areaCode.length; k++) {
                      var partialDialCode = _c.dialCode + areaCode.substr(0, k);
                      this._addCountryCode(rootCountryCode, partialDialCode);
                      this._addCountryCode(_c.iso2, partialDialCode);
                    }
                    this._addCountryCode(_c.iso2, _c.dialCode + areaCode);
                  }
                }
              }
            }
          }, {
            key: "_processPreferredCountries",
            value: function _processPreferredCountries() {
              this.preferredCountries = [];
              for (var i2 = 0; i2 < this.options.preferredCountries.length; i2++) {
                var countryCode = this.options.preferredCountries[i2].toLowerCase();
                var countryData = this._getCountryData(countryCode, false, true);
                if (countryData) this.preferredCountries.push(countryData);
              }
            }
          }, {
            key: "_createEl",
            value: function _createEl(name, attrs, container) {
              var el = document.createElement(name);
              if (attrs) forEachProp(attrs, function(key, value) {
                return el.setAttribute(key, value);
              });
              if (container) container.appendChild(el);
              return el;
            }
          }, {
            key: "_generateMarkup",
            value: function _generateMarkup() {
              if (!this.telInput.hasAttribute("autocomplete") && !(this.telInput.form && this.telInput.form.hasAttribute("autocomplete"))) {
                this.telInput.setAttribute("autocomplete", "off");
              }
              var parentClass = "iti";
              if (this.options.allowDropdown) parentClass += " iti--allow-dropdown";
              if (this.options.separateDialCode) parentClass += " iti--separate-dial-code";
              if (this.options.customContainer) {
                parentClass += " ";
                parentClass += this.options.customContainer;
              }
              var wrapper = this._createEl("div", {
                "class": parentClass
              });
              this.telInput.parentNode.insertBefore(wrapper, this.telInput);
              this.flagsContainer = this._createEl("div", {
                "class": "iti__flag-container"
              }, wrapper);
              wrapper.appendChild(this.telInput);
              this.selectedFlag = this._createEl("div", {
                "class": "iti__selected-flag",
                role: "combobox",
                "aria-controls": "iti-".concat(this.id, "__country-listbox"),
                "aria-owns": "iti-".concat(this.id, "__country-listbox"),
                "aria-expanded": "false"
              }, this.flagsContainer);
              this.selectedFlagInner = this._createEl("div", {
                "class": "iti__flag"
              }, this.selectedFlag);
              if (this.options.separateDialCode) {
                this.selectedDialCode = this._createEl("div", {
                  "class": "iti__selected-dial-code"
                }, this.selectedFlag);
              }
              if (this.options.allowDropdown) {
                this.selectedFlag.setAttribute("tabindex", "0");
                this.dropdownArrow = this._createEl("div", {
                  "class": "iti__arrow"
                }, this.selectedFlag);
                this.countryList = this._createEl("ul", {
                  "class": "iti__country-list iti__hide",
                  id: "iti-".concat(this.id, "__country-listbox"),
                  role: "listbox",
                  "aria-label": "List of countries"
                });
                if (this.preferredCountries.length) {
                  this._appendListItems(this.preferredCountries, "iti__preferred", true);
                  this._createEl("li", {
                    "class": "iti__divider",
                    role: "separator",
                    "aria-disabled": "true"
                  }, this.countryList);
                }
                this._appendListItems(this.countries, "iti__standard");
                if (this.options.dropdownContainer) {
                  this.dropdown = this._createEl("div", {
                    "class": "iti iti--container"
                  });
                  this.dropdown.appendChild(this.countryList);
                } else {
                  this.flagsContainer.appendChild(this.countryList);
                }
              }
              if (this.options.hiddenInput) {
                var hiddenInputName = this.options.hiddenInput;
                var name = this.telInput.getAttribute("name");
                if (name) {
                  var i2 = name.lastIndexOf("[");
                  if (i2 !== -1) hiddenInputName = "".concat(name.substr(0, i2), "[").concat(hiddenInputName, "]");
                }
                this.hiddenInput = this._createEl("input", {
                  type: "hidden",
                  name: hiddenInputName
                });
                wrapper.appendChild(this.hiddenInput);
              }
            }
          }, {
            key: "_appendListItems",
            value: function _appendListItems(countries, className, preferred) {
              var tmp = "";
              for (var i2 = 0; i2 < countries.length; i2++) {
                var c2 = countries[i2];
                var idSuffix = preferred ? "-preferred" : "";
                tmp += "<li class='iti__country ".concat(className, "' tabIndex='-1' id='iti-").concat(this.id, "__item-").concat(c2.iso2).concat(idSuffix, "' role='option' data-dial-code='").concat(c2.dialCode, "' data-country-code='").concat(c2.iso2, "' aria-selected='false'>");
                tmp += "<div class='iti__flag-box'><div class='iti__flag iti__".concat(c2.iso2, "'></div></div>");
                tmp += "<span class='iti__country-name'>".concat(c2.name, "</span>");
                tmp += "<span class='iti__dial-code'>+".concat(c2.dialCode, "</span>");
                tmp += "</li>";
              }
              this.countryList.insertAdjacentHTML("beforeend", tmp);
            }
          }, {
            key: "_setInitialState",
            value: function _setInitialState() {
              var attributeValue = this.telInput.getAttribute("value");
              var inputValue = this.telInput.value;
              var useAttribute = attributeValue && attributeValue.charAt(0) === "+" && (!inputValue || inputValue.charAt(0) !== "+");
              var val = useAttribute ? attributeValue : inputValue;
              var dialCode = this._getDialCode(val);
              var isRegionlessNanp = this._isRegionlessNanp(val);
              var _this$options = this.options, initialCountry = _this$options.initialCountry, nationalMode = _this$options.nationalMode, autoHideDialCode = _this$options.autoHideDialCode, separateDialCode = _this$options.separateDialCode;
              if (dialCode && !isRegionlessNanp) {
                this._updateFlagFromNumber(val);
              } else if (initialCountry !== "auto") {
                if (initialCountry) {
                  this._setFlag(initialCountry.toLowerCase());
                } else {
                  if (dialCode && isRegionlessNanp) {
                    this._setFlag("us");
                  } else {
                    this.defaultCountry = this.preferredCountries.length ? this.preferredCountries[0].iso2 : this.countries[0].iso2;
                    if (!val) {
                      this._setFlag(this.defaultCountry);
                    }
                  }
                }
                if (!val && !nationalMode && !autoHideDialCode && !separateDialCode) {
                  this.telInput.value = "+".concat(this.selectedCountryData.dialCode);
                }
              }
              if (val) this._updateValFromNumber(val);
            }
          }, {
            key: "_initListeners",
            value: function _initListeners() {
              this._initKeyListeners();
              if (this.options.autoHideDialCode) this._initBlurListeners();
              if (this.options.allowDropdown) this._initDropdownListeners();
              if (this.hiddenInput) this._initHiddenInputListener();
            }
          }, {
            key: "_initHiddenInputListener",
            value: function _initHiddenInputListener() {
              var _this3 = this;
              this._handleHiddenInputSubmit = function() {
                _this3.hiddenInput.value = _this3.getNumber();
              };
              if (this.telInput.form) this.telInput.form.addEventListener("submit", this._handleHiddenInputSubmit);
            }
          }, {
            key: "_getClosestLabel",
            value: function _getClosestLabel() {
              var el = this.telInput;
              while (el && el.tagName !== "LABEL") {
                el = el.parentNode;
              }
              return el;
            }
          }, {
            key: "_initDropdownListeners",
            value: function _initDropdownListeners() {
              var _this4 = this;
              this._handleLabelClick = function(e) {
                if (_this4.countryList.classList.contains("iti__hide")) _this4.telInput.focus();
                else e.preventDefault();
              };
              var label = this._getClosestLabel();
              if (label) label.addEventListener("click", this._handleLabelClick);
              this._handleClickSelectedFlag = function() {
                if (_this4.countryList.classList.contains("iti__hide") && !_this4.telInput.disabled && !_this4.telInput.readOnly) {
                  _this4._showDropdown();
                }
              };
              this.selectedFlag.addEventListener("click", this._handleClickSelectedFlag);
              this._handleFlagsContainerKeydown = function(e) {
                var isDropdownHidden = _this4.countryList.classList.contains("iti__hide");
                if (isDropdownHidden && ["ArrowUp", "Up", "ArrowDown", "Down", " ", "Enter"].indexOf(e.key) !== -1) {
                  e.preventDefault();
                  e.stopPropagation();
                  _this4._showDropdown();
                }
                if (e.key === "Tab") _this4._closeDropdown();
              };
              this.flagsContainer.addEventListener("keydown", this._handleFlagsContainerKeydown);
            }
          }, {
            key: "_initRequests",
            value: function _initRequests() {
              var _this5 = this;
              if (this.options.utilsScript && !window.intlTelInputUtils) {
                if (window.intlTelInputGlobals.documentReady()) {
                  window.intlTelInputGlobals.loadUtils(this.options.utilsScript);
                } else {
                  window.addEventListener("load", function() {
                    window.intlTelInputGlobals.loadUtils(_this5.options.utilsScript);
                  });
                }
              } else this.resolveUtilsScriptPromise();
              if (this.options.initialCountry === "auto") this._loadAutoCountry();
              else this.resolveAutoCountryPromise();
            }
          }, {
            key: "_loadAutoCountry",
            value: function _loadAutoCountry() {
              if (window.intlTelInputGlobals.autoCountry) {
                this.handleAutoCountry();
              } else if (!window.intlTelInputGlobals.startedLoadingAutoCountry) {
                window.intlTelInputGlobals.startedLoadingAutoCountry = true;
                if (typeof this.options.geoIpLookup === "function") {
                  this.options.geoIpLookup(function(countryCode) {
                    window.intlTelInputGlobals.autoCountry = countryCode.toLowerCase();
                    setTimeout(function() {
                      return forEachInstance("handleAutoCountry");
                    });
                  }, function() {
                    return forEachInstance("rejectAutoCountryPromise");
                  });
                }
              }
            }
          }, {
            key: "_initKeyListeners",
            value: function _initKeyListeners() {
              var _this6 = this;
              this._handleKeyupEvent = function() {
                if (_this6._updateFlagFromNumber(_this6.telInput.value)) {
                  _this6._triggerCountryChange();
                }
              };
              this.telInput.addEventListener("keyup", this._handleKeyupEvent);
              this._handleClipboardEvent = function() {
                setTimeout(_this6._handleKeyupEvent);
              };
              this.telInput.addEventListener("cut", this._handleClipboardEvent);
              this.telInput.addEventListener("paste", this._handleClipboardEvent);
            }
          }, {
            key: "_cap",
            value: function _cap(number) {
              var max = this.telInput.getAttribute("maxlength");
              return max && number.length > max ? number.substr(0, max) : number;
            }
          }, {
            key: "_initBlurListeners",
            value: function _initBlurListeners() {
              var _this7 = this;
              this._handleSubmitOrBlurEvent = function() {
                _this7._removeEmptyDialCode();
              };
              if (this.telInput.form) this.telInput.form.addEventListener("submit", this._handleSubmitOrBlurEvent);
              this.telInput.addEventListener("blur", this._handleSubmitOrBlurEvent);
            }
          }, {
            key: "_removeEmptyDialCode",
            value: function _removeEmptyDialCode() {
              if (this.telInput.value.charAt(0) === "+") {
                var numeric = this._getNumeric(this.telInput.value);
                if (!numeric || this.selectedCountryData.dialCode === numeric) {
                  this.telInput.value = "";
                }
              }
            }
          }, {
            key: "_getNumeric",
            value: function _getNumeric(s) {
              return s.replace(/\D/g, "");
            }
          }, {
            key: "_trigger",
            value: function _trigger(name) {
              var e = document.createEvent("Event");
              e.initEvent(name, true, true);
              this.telInput.dispatchEvent(e);
            }
          }, {
            key: "_showDropdown",
            value: function _showDropdown() {
              this.countryList.classList.remove("iti__hide");
              this.selectedFlag.setAttribute("aria-expanded", "true");
              this._setDropdownPosition();
              if (this.activeItem) {
                this._highlightListItem(this.activeItem, false);
                this._scrollTo(this.activeItem, true);
              }
              this._bindDropdownListeners();
              this.dropdownArrow.classList.add("iti__arrow--up");
              this._trigger("open:countrydropdown");
            }
          }, {
            key: "_toggleClass",
            value: function _toggleClass(el, className, shouldHaveClass) {
              if (shouldHaveClass && !el.classList.contains(className)) el.classList.add(className);
              else if (!shouldHaveClass && el.classList.contains(className)) el.classList.remove(className);
            }
          }, {
            key: "_setDropdownPosition",
            value: function _setDropdownPosition() {
              var _this8 = this;
              if (this.options.dropdownContainer) {
                this.options.dropdownContainer.appendChild(this.dropdown);
              }
              if (!this.isMobile) {
                var pos = this.telInput.getBoundingClientRect();
                var windowTop = window.pageYOffset || document.documentElement.scrollTop;
                var inputTop = pos.top + windowTop;
                var dropdownHeight = this.countryList.offsetHeight;
                var dropdownFitsBelow = inputTop + this.telInput.offsetHeight + dropdownHeight < windowTop + window.innerHeight;
                var dropdownFitsAbove = inputTop - dropdownHeight > windowTop;
                this._toggleClass(this.countryList, "iti__country-list--dropup", !dropdownFitsBelow && dropdownFitsAbove);
                if (this.options.dropdownContainer) {
                  var extraTop = !dropdownFitsBelow && dropdownFitsAbove ? 0 : this.telInput.offsetHeight;
                  this.dropdown.style.top = "".concat(inputTop + extraTop, "px");
                  this.dropdown.style.left = "".concat(pos.left + document.body.scrollLeft, "px");
                  this._handleWindowScroll = function() {
                    return _this8._closeDropdown();
                  };
                  window.addEventListener("scroll", this._handleWindowScroll);
                }
              }
            }
          }, {
            key: "_getClosestListItem",
            value: function _getClosestListItem(target) {
              var el = target;
              while (el && el !== this.countryList && !el.classList.contains("iti__country")) {
                el = el.parentNode;
              }
              return el === this.countryList ? null : el;
            }
          }, {
            key: "_bindDropdownListeners",
            value: function _bindDropdownListeners() {
              var _this9 = this;
              this._handleMouseoverCountryList = function(e) {
                var listItem = _this9._getClosestListItem(e.target);
                if (listItem) _this9._highlightListItem(listItem, false);
              };
              this.countryList.addEventListener("mouseover", this._handleMouseoverCountryList);
              this._handleClickCountryList = function(e) {
                var listItem = _this9._getClosestListItem(e.target);
                if (listItem) _this9._selectListItem(listItem);
              };
              this.countryList.addEventListener("click", this._handleClickCountryList);
              var isOpening = true;
              this._handleClickOffToClose = function() {
                if (!isOpening) _this9._closeDropdown();
                isOpening = false;
              };
              document.documentElement.addEventListener("click", this._handleClickOffToClose);
              var query = "";
              var queryTimer = null;
              this._handleKeydownOnDropdown = function(e) {
                e.preventDefault();
                if (e.key === "ArrowUp" || e.key === "Up" || e.key === "ArrowDown" || e.key === "Down") _this9._handleUpDownKey(e.key);
                else if (e.key === "Enter") _this9._handleEnterKey();
                else if (e.key === "Escape") _this9._closeDropdown();
                else if (/^[a-zA-ZÀ-ÿа-яА-Я ]$/.test(e.key)) {
                  if (queryTimer) clearTimeout(queryTimer);
                  query += e.key.toLowerCase();
                  _this9._searchForCountry(query);
                  queryTimer = setTimeout(function() {
                    query = "";
                  }, 1e3);
                }
              };
              document.addEventListener("keydown", this._handleKeydownOnDropdown);
            }
          }, {
            key: "_handleUpDownKey",
            value: function _handleUpDownKey(key) {
              var next = key === "ArrowUp" || key === "Up" ? this.highlightedItem.previousElementSibling : this.highlightedItem.nextElementSibling;
              if (next) {
                if (next.classList.contains("iti__divider")) {
                  next = key === "ArrowUp" || key === "Up" ? next.previousElementSibling : next.nextElementSibling;
                }
                this._highlightListItem(next, true);
              }
            }
          }, {
            key: "_handleEnterKey",
            value: function _handleEnterKey() {
              if (this.highlightedItem) this._selectListItem(this.highlightedItem);
            }
          }, {
            key: "_searchForCountry",
            value: function _searchForCountry(query) {
              for (var i2 = 0; i2 < this.countries.length; i2++) {
                if (this._startsWith(this.countries[i2].name, query)) {
                  var listItem = this.countryList.querySelector("#iti-".concat(this.id, "__item-").concat(this.countries[i2].iso2));
                  this._highlightListItem(listItem, false);
                  this._scrollTo(listItem, true);
                  break;
                }
              }
            }
          }, {
            key: "_startsWith",
            value: function _startsWith(a, b) {
              return a.substr(0, b.length).toLowerCase() === b;
            }
          }, {
            key: "_updateValFromNumber",
            value: function _updateValFromNumber(originalNumber) {
              var number = originalNumber;
              if (this.options.formatOnDisplay && window.intlTelInputUtils && this.selectedCountryData) {
                var useNational = !this.options.separateDialCode && (this.options.nationalMode || number.charAt(0) !== "+");
                var _intlTelInputUtils$nu = intlTelInputUtils.numberFormat, NATIONAL = _intlTelInputUtils$nu.NATIONAL, INTERNATIONAL = _intlTelInputUtils$nu.INTERNATIONAL;
                var format = useNational ? NATIONAL : INTERNATIONAL;
                number = intlTelInputUtils.formatNumber(number, this.selectedCountryData.iso2, format);
              }
              number = this._beforeSetNumber(number);
              this.telInput.value = number;
            }
          }, {
            key: "_updateFlagFromNumber",
            value: function _updateFlagFromNumber(originalNumber) {
              var number = originalNumber;
              var selectedDialCode = this.selectedCountryData.dialCode;
              var isNanp = selectedDialCode === "1";
              if (number && this.options.nationalMode && isNanp && number.charAt(0) !== "+") {
                if (number.charAt(0) !== "1") number = "1".concat(number);
                number = "+".concat(number);
              }
              if (this.options.separateDialCode && selectedDialCode && number.charAt(0) !== "+") {
                number = "+".concat(selectedDialCode).concat(number);
              }
              var dialCode = this._getDialCode(number, true);
              var numeric = this._getNumeric(number);
              var countryCode = null;
              if (dialCode) {
                var countryCodes = this.countryCodes[this._getNumeric(dialCode)];
                var alreadySelected = countryCodes.indexOf(this.selectedCountryData.iso2) !== -1 && numeric.length <= dialCode.length - 1;
                var isRegionlessNanpNumber = selectedDialCode === "1" && this._isRegionlessNanp(numeric);
                if (!isRegionlessNanpNumber && !alreadySelected) {
                  for (var j = 0; j < countryCodes.length; j++) {
                    if (countryCodes[j]) {
                      countryCode = countryCodes[j];
                      break;
                    }
                  }
                }
              } else if (number.charAt(0) === "+" && numeric.length) {
                countryCode = "";
              } else if (!number || number === "+") {
                countryCode = this.defaultCountry;
              }
              if (countryCode !== null) {
                return this._setFlag(countryCode);
              }
              return false;
            }
          }, {
            key: "_isRegionlessNanp",
            value: function _isRegionlessNanp(number) {
              var numeric = this._getNumeric(number);
              if (numeric.charAt(0) === "1") {
                var areaCode = numeric.substr(1, 3);
                return regionlessNanpNumbers.indexOf(areaCode) !== -1;
              }
              return false;
            }
          }, {
            key: "_highlightListItem",
            value: function _highlightListItem(listItem, shouldFocus) {
              var prevItem = this.highlightedItem;
              if (prevItem) prevItem.classList.remove("iti__highlight");
              this.highlightedItem = listItem;
              this.highlightedItem.classList.add("iti__highlight");
              if (shouldFocus) this.highlightedItem.focus();
            }
          }, {
            key: "_getCountryData",
            value: function _getCountryData(countryCode, ignoreOnlyCountriesOption, allowFail) {
              var countryList = ignoreOnlyCountriesOption ? allCountries : this.countries;
              for (var i2 = 0; i2 < countryList.length; i2++) {
                if (countryList[i2].iso2 === countryCode) {
                  return countryList[i2];
                }
              }
              if (allowFail) {
                return null;
              }
              throw new Error("No country data for '".concat(countryCode, "'"));
            }
          }, {
            key: "_setFlag",
            value: function _setFlag(countryCode) {
              var prevCountry = this.selectedCountryData.iso2 ? this.selectedCountryData : {};
              this.selectedCountryData = countryCode ? this._getCountryData(countryCode, false, false) : {};
              if (this.selectedCountryData.iso2) {
                this.defaultCountry = this.selectedCountryData.iso2;
              }
              this.selectedFlagInner.setAttribute("class", "iti__flag iti__".concat(countryCode));
              var title = countryCode ? "".concat(this.selectedCountryData.name, ": +").concat(this.selectedCountryData.dialCode) : "Unknown";
              this.selectedFlag.setAttribute("title", title);
              if (this.options.separateDialCode) {
                var dialCode = this.selectedCountryData.dialCode ? "+".concat(this.selectedCountryData.dialCode) : "";
                this.selectedDialCode.innerHTML = dialCode;
                var selectedFlagWidth = this.selectedFlag.offsetWidth || this._getHiddenSelectedFlagWidth();
                this.telInput.style.paddingLeft = "".concat(selectedFlagWidth + 6, "px");
              }
              this._updatePlaceholder();
              if (this.options.allowDropdown) {
                var prevItem = this.activeItem;
                if (prevItem) {
                  prevItem.classList.remove("iti__active");
                  prevItem.setAttribute("aria-selected", "false");
                }
                if (countryCode) {
                  var nextItem = this.countryList.querySelector("#iti-".concat(this.id, "__item-").concat(countryCode, "-preferred")) || this.countryList.querySelector("#iti-".concat(this.id, "__item-").concat(countryCode));
                  nextItem.setAttribute("aria-selected", "true");
                  nextItem.classList.add("iti__active");
                  this.activeItem = nextItem;
                  this.selectedFlag.setAttribute("aria-activedescendant", nextItem.getAttribute("id"));
                }
              }
              return prevCountry.iso2 !== countryCode;
            }
          }, {
            key: "_getHiddenSelectedFlagWidth",
            value: function _getHiddenSelectedFlagWidth() {
              var containerClone = this.telInput.parentNode.cloneNode();
              containerClone.style.visibility = "hidden";
              document.body.appendChild(containerClone);
              var flagsContainerClone = this.flagsContainer.cloneNode();
              containerClone.appendChild(flagsContainerClone);
              var selectedFlagClone = this.selectedFlag.cloneNode(true);
              flagsContainerClone.appendChild(selectedFlagClone);
              var width = selectedFlagClone.offsetWidth;
              containerClone.parentNode.removeChild(containerClone);
              return width;
            }
          }, {
            key: "_updatePlaceholder",
            value: function _updatePlaceholder() {
              var shouldSetPlaceholder = this.options.autoPlaceholder === "aggressive" || !this.hadInitialPlaceholder && this.options.autoPlaceholder === "polite";
              if (window.intlTelInputUtils && shouldSetPlaceholder) {
                var numberType = intlTelInputUtils.numberType[this.options.placeholderNumberType];
                var placeholder = this.selectedCountryData.iso2 ? intlTelInputUtils.getExampleNumber(this.selectedCountryData.iso2, this.options.nationalMode, numberType) : "";
                placeholder = this._beforeSetNumber(placeholder);
                if (typeof this.options.customPlaceholder === "function") {
                  placeholder = this.options.customPlaceholder(placeholder, this.selectedCountryData);
                }
                this.telInput.setAttribute("placeholder", placeholder);
              }
            }
          }, {
            key: "_selectListItem",
            value: function _selectListItem(listItem) {
              var flagChanged = this._setFlag(listItem.getAttribute("data-country-code"));
              this._closeDropdown();
              this._updateDialCode(listItem.getAttribute("data-dial-code"), true);
              this.telInput.focus();
              var len = this.telInput.value.length;
              this.telInput.setSelectionRange(len, len);
              if (flagChanged) {
                this._triggerCountryChange();
              }
            }
          }, {
            key: "_closeDropdown",
            value: function _closeDropdown() {
              this.countryList.classList.add("iti__hide");
              this.selectedFlag.setAttribute("aria-expanded", "false");
              this.dropdownArrow.classList.remove("iti__arrow--up");
              document.removeEventListener("keydown", this._handleKeydownOnDropdown);
              document.documentElement.removeEventListener("click", this._handleClickOffToClose);
              this.countryList.removeEventListener("mouseover", this._handleMouseoverCountryList);
              this.countryList.removeEventListener("click", this._handleClickCountryList);
              if (this.options.dropdownContainer) {
                if (!this.isMobile) window.removeEventListener("scroll", this._handleWindowScroll);
                if (this.dropdown.parentNode) this.dropdown.parentNode.removeChild(this.dropdown);
              }
              this._trigger("close:countrydropdown");
            }
          }, {
            key: "_scrollTo",
            value: function _scrollTo(element, middle) {
              var container = this.countryList;
              var windowTop = window.pageYOffset || document.documentElement.scrollTop;
              var containerHeight = container.offsetHeight;
              var containerTop = container.getBoundingClientRect().top + windowTop;
              var containerBottom = containerTop + containerHeight;
              var elementHeight = element.offsetHeight;
              var elementTop = element.getBoundingClientRect().top + windowTop;
              var elementBottom = elementTop + elementHeight;
              var newScrollTop = elementTop - containerTop + container.scrollTop;
              var middleOffset = containerHeight / 2 - elementHeight / 2;
              if (elementTop < containerTop) {
                if (middle) newScrollTop -= middleOffset;
                container.scrollTop = newScrollTop;
              } else if (elementBottom > containerBottom) {
                if (middle) newScrollTop += middleOffset;
                var heightDifference = containerHeight - elementHeight;
                container.scrollTop = newScrollTop - heightDifference;
              }
            }
          }, {
            key: "_updateDialCode",
            value: function _updateDialCode(newDialCodeBare, hasSelectedListItem) {
              var inputVal = this.telInput.value;
              var newDialCode = "+".concat(newDialCodeBare);
              var newNumber;
              if (inputVal.charAt(0) === "+") {
                var prevDialCode = this._getDialCode(inputVal);
                if (prevDialCode) {
                  newNumber = inputVal.replace(prevDialCode, newDialCode);
                } else {
                  newNumber = newDialCode;
                }
              } else if (this.options.nationalMode || this.options.separateDialCode) {
                return;
              } else {
                if (inputVal) {
                  newNumber = newDialCode + inputVal;
                } else if (hasSelectedListItem || !this.options.autoHideDialCode) {
                  newNumber = newDialCode;
                } else {
                  return;
                }
              }
              this.telInput.value = newNumber;
            }
          }, {
            key: "_getDialCode",
            value: function _getDialCode(number, includeAreaCode) {
              var dialCode = "";
              if (number.charAt(0) === "+") {
                var numericChars = "";
                for (var i2 = 0; i2 < number.length; i2++) {
                  var c2 = number.charAt(i2);
                  if (!isNaN(parseInt(c2, 10))) {
                    numericChars += c2;
                    if (includeAreaCode) {
                      if (this.countryCodes[numericChars]) {
                        dialCode = number.substr(0, i2 + 1);
                      }
                    } else {
                      if (this.dialCodes[numericChars]) {
                        dialCode = number.substr(0, i2 + 1);
                        break;
                      }
                    }
                    if (numericChars.length === this.countryCodeMaxLen) {
                      break;
                    }
                  }
                }
              }
              return dialCode;
            }
          }, {
            key: "_getFullNumber",
            value: function _getFullNumber() {
              var val = this.telInput.value.trim();
              var dialCode = this.selectedCountryData.dialCode;
              var prefix;
              var numericVal = this._getNumeric(val);
              if (this.options.separateDialCode && val.charAt(0) !== "+" && dialCode && numericVal) {
                prefix = "+".concat(dialCode);
              } else {
                prefix = "";
              }
              return prefix + val;
            }
          }, {
            key: "_beforeSetNumber",
            value: function _beforeSetNumber(originalNumber) {
              var number = originalNumber;
              if (this.options.separateDialCode) {
                var dialCode = this._getDialCode(number);
                if (dialCode) {
                  dialCode = "+".concat(this.selectedCountryData.dialCode);
                  var start = number[dialCode.length] === " " || number[dialCode.length] === "-" ? dialCode.length + 1 : dialCode.length;
                  number = number.substr(start);
                }
              }
              return this._cap(number);
            }
          }, {
            key: "_triggerCountryChange",
            value: function _triggerCountryChange() {
              this._trigger("countrychange");
            }
          }, {
            key: "handleAutoCountry",
            value: function handleAutoCountry() {
              if (this.options.initialCountry === "auto") {
                this.defaultCountry = window.intlTelInputGlobals.autoCountry;
                if (!this.telInput.value) {
                  this.setCountry(this.defaultCountry);
                }
                this.resolveAutoCountryPromise();
              }
            }
          }, {
            key: "handleUtils",
            value: function handleUtils() {
              if (window.intlTelInputUtils) {
                if (this.telInput.value) {
                  this._updateValFromNumber(this.telInput.value);
                }
                this._updatePlaceholder();
              }
              this.resolveUtilsScriptPromise();
            }
          }, {
            key: "destroy",
            value: function destroy() {
              var form = this.telInput.form;
              if (this.options.allowDropdown) {
                this._closeDropdown();
                this.selectedFlag.removeEventListener("click", this._handleClickSelectedFlag);
                this.flagsContainer.removeEventListener("keydown", this._handleFlagsContainerKeydown);
                var label = this._getClosestLabel();
                if (label) label.removeEventListener("click", this._handleLabelClick);
              }
              if (this.hiddenInput && form) form.removeEventListener("submit", this._handleHiddenInputSubmit);
              if (this.options.autoHideDialCode) {
                if (form) form.removeEventListener("submit", this._handleSubmitOrBlurEvent);
                this.telInput.removeEventListener("blur", this._handleSubmitOrBlurEvent);
              }
              this.telInput.removeEventListener("keyup", this._handleKeyupEvent);
              this.telInput.removeEventListener("cut", this._handleClipboardEvent);
              this.telInput.removeEventListener("paste", this._handleClipboardEvent);
              this.telInput.removeAttribute("data-intl-tel-input-id");
              var wrapper = this.telInput.parentNode;
              wrapper.parentNode.insertBefore(this.telInput, wrapper);
              wrapper.parentNode.removeChild(wrapper);
              delete window.intlTelInputGlobals.instances[this.id];
            }
          }, {
            key: "getExtension",
            value: function getExtension() {
              if (window.intlTelInputUtils) {
                return intlTelInputUtils.getExtension(this._getFullNumber(), this.selectedCountryData.iso2);
              }
              return "";
            }
          }, {
            key: "getNumber",
            value: function getNumber(format) {
              if (window.intlTelInputUtils) {
                var iso2 = this.selectedCountryData.iso2;
                return intlTelInputUtils.formatNumber(this._getFullNumber(), iso2, format);
              }
              return "";
            }
          }, {
            key: "getNumberType",
            value: function getNumberType() {
              if (window.intlTelInputUtils) {
                return intlTelInputUtils.getNumberType(this._getFullNumber(), this.selectedCountryData.iso2);
              }
              return -99;
            }
          }, {
            key: "getSelectedCountryData",
            value: function getSelectedCountryData() {
              return this.selectedCountryData;
            }
          }, {
            key: "getValidationError",
            value: function getValidationError() {
              if (window.intlTelInputUtils) {
                var iso2 = this.selectedCountryData.iso2;
                return intlTelInputUtils.getValidationError(this._getFullNumber(), iso2);
              }
              return -99;
            }
          }, {
            key: "isValidNumber",
            value: function isValidNumber() {
              var val = this._getFullNumber().trim();
              var countryCode = this.options.nationalMode ? this.selectedCountryData.iso2 : "";
              return window.intlTelInputUtils ? intlTelInputUtils.isValidNumber(val, countryCode) : null;
            }
          }, {
            key: "setCountry",
            value: function setCountry(originalCountryCode) {
              var countryCode = originalCountryCode.toLowerCase();
              if (!this.selectedFlagInner.classList.contains("iti__".concat(countryCode))) {
                this._setFlag(countryCode);
                this._updateDialCode(this.selectedCountryData.dialCode, false);
                this._triggerCountryChange();
              }
            }
          }, {
            key: "setNumber",
            value: function setNumber(number) {
              var flagChanged = this._updateFlagFromNumber(number);
              this._updateValFromNumber(number);
              if (flagChanged) {
                this._triggerCountryChange();
              }
            }
          }, {
            key: "setPlaceholderNumberType",
            value: function setPlaceholderNumberType(type) {
              this.options.placeholderNumberType = type;
              this._updatePlaceholder();
            }
          }]);
          return Iti2;
        })();
        intlTelInputGlobals.getCountryData = function() {
          return allCountries;
        };
        var injectScript = function injectScript2(path, handleSuccess, handleFailure) {
          var script = document.createElement("script");
          script.onload = function() {
            forEachInstance("handleUtils");
            if (handleSuccess) handleSuccess();
          };
          script.onerror = function() {
            forEachInstance("rejectUtilsScriptPromise");
            if (handleFailure) handleFailure();
          };
          script.className = "iti-load-utils";
          script.async = true;
          script.src = path;
          document.body.appendChild(script);
        };
        intlTelInputGlobals.loadUtils = function(path) {
          if (!window.intlTelInputUtils && !window.intlTelInputGlobals.startedLoadingUtilsScript) {
            window.intlTelInputGlobals.startedLoadingUtilsScript = true;
            if (typeof Promise !== "undefined") {
              return new Promise(function(resolve, reject) {
                return injectScript(path, resolve, reject);
              });
            }
            injectScript(path);
          }
          return null;
        };
        intlTelInputGlobals.defaults = defaults;
        intlTelInputGlobals.version = "17.0.21";
        return function(input, options) {
          var iti = new Iti(input, options);
          iti._init();
          input.setAttribute("data-intl-tel-input-id", iti.id);
          window.intlTelInputGlobals.instances[iti.id] = iti;
          return iti;
        };
      })();
    });
  })(intlTelInput$2);
  return intlTelInput$2.exports;
}
var intlTelInput$1;
var hasRequiredIntlTelInput;
function requireIntlTelInput() {
  if (hasRequiredIntlTelInput) return intlTelInput$1;
  hasRequiredIntlTelInput = 1;
  intlTelInput$1 = requireIntlTelInput$1();
  return intlTelInput$1;
}
var intlTelInputExports = requireIntlTelInput();
const intlTelInput = /* @__PURE__ */ getDefaultExportFromCjs(intlTelInputExports);
const utilsScriptUrl = "data:text/javascript;base64,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";
const countryCodeMap = {
  "uae": "ae",
  // United Arab Emirates
  "usa": "us",
  // United States
  "uk": "gb",
  // United Kingdom
  "england": "gb",
  "britain": "gb",
  "scotland": "gb",
  "wales": "gb",
  "northern ireland": "gb",
  "korea": "kr",
  "south korea": "kr",
  "republic of korea": "kr"
};
const normalizeCountryCode = (countryCode) => {
  if (!countryCode) return "us";
  const normalized = countryCode.toLowerCase().trim();
  if (countryCodeMap[normalized]) {
    return countryCodeMap[normalized];
  }
  if (normalized.length === 2) {
    return normalized;
  }
  return "us";
};
const getCountryPlaceholder = (country) => {
  const placeholders = {
    "ad": "312 345",
    "ae": "50 123 4567",
    "af": "070 123 4567",
    "ag": "(268) 464-1234",
    "ai": "(264) 498-1234",
    "al": "067 212 3456",
    "am": "077 123456",
    "ao": "923 123 456",
    "ar": "011 15-1234-5678",
    "as": "(684) 733-1234",
    "at": "0664 123456",
    "au": "0412 345 678",
    "aw": "560 1234",
    "ax": "018 123456",
    "az": "040 123 45 67",
    "ba": "061 123 456",
    "bb": "(246) 250-1234",
    "bd": "01812-345678",
    "be": "0470 12 34 56",
    "bf": "70 12 34 56",
    "bg": "087 123 4567",
    "bh": "3600 1234",
    "bi": "79 56 12 34",
    "bj": "90 01 12 34",
    "bl": "0690 00-12-34",
    "bm": "(441) 370-1234",
    "bn": "712 3456",
    "bo": "7012 3456",
    "bq": "318 1234",
    "br": "(11) 91234-5678",
    "bs": "(242) 359-1234",
    "bt": "17 12 34 56",
    "bw": "71 123 456",
    "by": "029 491-19-11",
    "bz": "622-1234",
    "ca": "(555) 123-4567",
    "cc": "0412 345 678",
    "cd": "0991 234 567",
    "cf": "70 01 23 45",
    "cg": "06 12 34 567",
    "ch": "078 123 45 67",
    "ci": "01 23 45 67",
    "ck": "71 234",
    "cl": "09 1234 5678",
    "cm": "6 71 23 45 67",
    "cn": "138 0013 8000",
    "co": "321 1234567",
    "cr": "8312 3456",
    "cu": "05 1234567",
    "cv": "991 12 34",
    "cw": "9 518 1234",
    "cx": "0412 345 678",
    "cy": "96 123456",
    "cz": "601 123 456",
    "de": "0151 23456789",
    "dj": "77 83 10 01",
    "dk": "20 12 34 56",
    "dm": "(767) 225-1234",
    "do": "(809) 234-5678",
    "dz": "0551 23 45 67",
    "ec": "099 123 4567",
    "ee": "5123 4567",
    "eg": "0100 123 4567",
    "eh": "0650-123456",
    "er": "07 123 456",
    "es": "612 34 56 78",
    "et": "091 123 4567",
    "fi": "041 2345678",
    "fj": "701 2345",
    "fk": "51234",
    "fm": "350 1234",
    "fo": "211234",
    "fr": "06 12 34 56 78",
    "ga": "06 03 12 34",
    "gb": "07400 123456",
    "gd": "(473) 403-1234",
    "ge": "555 12 34 56",
    "gf": "0694 20 12 34",
    "gg": "07781 123456",
    "gh": "024 123 4567",
    "gi": "57123456",
    "gl": "22 12 34",
    "gm": "301 2345",
    "gn": "601 12 34 56",
    "gp": "0690 00-12-34",
    "gq": "222 123 456",
    "gr": "691 234 5678",
    "gs": "",
    "gt": "5123 4567",
    "gu": "(671) 300-1234",
    "gw": "955 012 345",
    "gy": "609 1234",
    "hk": "5123 4567",
    "hn": "9123-4567",
    "hr": "091 234 5678",
    "ht": "34 12 345 6789",
    "hu": "(20) 123 4567",
    "id": "0812-345-678",
    "ie": "085 012 3456",
    "il": "050-123-4567",
    "im": "07624 123456",
    "in": "081234 56789",
    "io": "380 1234",
    "iq": "0791 234 5678",
    "ir": "0912 345 6789",
    "is": "611 1234",
    "it": "312 345 6789",
    "je": "07797 123456",
    "jm": "(876) 210-1234",
    "jo": "07 9012 3456",
    "jp": "090-1234-5678",
    "ke": "0712 345678",
    "kg": "0700 123 456",
    "kh": "091 234 567",
    "ki": "72001234",
    "km": "321 23 45",
    "kn": "(869) 765-2917",
    "kp": "0192 123 4567",
    "kr": "010-0000-0000",
    "kw": "500 12345",
    "ky": "(345) 323-1234",
    "kz": "8 (771) 000 9998",
    "la": "020 23 123 456",
    "lb": "71 123 456",
    "lc": "(758) 284-5678",
    "li": "660 234 567",
    "lk": "071 234 5678",
    "lr": "077 012 345",
    "ls": "5012 3456",
    "lt": "(8-612) 34567",
    "lu": "628 123 456",
    "lv": "21 234 567",
    "ly": "091-2345678",
    "ma": "0650-123456",
    "mc": "06 12 34 56 78",
    "md": "0621 12 345",
    "me": "067 622 901",
    "mf": "0690 00-12-34",
    "mg": "032 12 345 67",
    "mh": "235-1234",
    "mk": "072 345 678",
    "ml": "65 01 23 45",
    "mm": "09 212 3456",
    "mn": "8812 3456",
    "mo": "6612 3456",
    "mp": "(670) 234-5678",
    "mq": "0696 20 12 34",
    "mr": "22 12 34 56",
    "ms": "(664) 492-3456",
    "mt": "9696 1234",
    "mu": "5251 2345",
    "mv": "771-2345",
    "mw": "0991 23 45 67",
    "mx": "044 222 123 4567",
    "my": "012-345 6789",
    "mz": "82 123 4567",
    "na": "081 234 5678",
    "nc": "75.12.34",
    "ne": "93 12 34 56",
    "nf": "3 81234",
    "ng": "0802 123 4567",
    "ni": "8123 4567",
    "nl": "06 12345678",
    "no": "406 12 345",
    "np": "984-1234567",
    "nr": "555 1234",
    "nu": "888 4012",
    "nz": "021 123 4567",
    "om": "9212 3456",
    "pa": "6123-4567",
    "pe": "912 345 678",
    "pf": "87 12 34 56",
    "pg": "7012 3456",
    "ph": "0905 123 4567",
    "pk": "0301 2345678",
    "pl": "512 345 678",
    "pm": "055 12 34",
    "pn": "",
    "pr": "(787) 234-5678",
    "ps": "0599 123 456",
    "pt": "912 345 678",
    "pw": "620 1234",
    "py": "0961 456789",
    "qa": "3312 3456",
    "rs": "060 1234567",
    "re": "0692 12 34 56",
    "ro": "0712 034 567",
    "ru": "8 (912) 345-67-89",
    "rw": "0720 123 456",
    "sa": "051 234 5678",
    "sb": "74 21234",
    "sc": "2 510 123",
    "sd": "0912 345 678",
    "se": "070-123 45 67",
    "sg": "8123 4567",
    "sh": "51234",
    "si": "031 234 567",
    "sj": "412 34 567",
    "sk": "0912 123 456",
    "sl": "076 612345",
    "sm": "66 66 12 12",
    "sn": "70 123 45 67",
    "so": "7 1123456",
    "sr": "741-2345",
    "ss": "0977 123 456",
    "st": "981 2345",
    "sv": "7012 3456",
    "sx": "721 2345",
    "sy": "0944 567 890",
    "sz": "7612 3456",
    "ta": "8999",
    "tc": "(649) 231-1234",
    "td": "63 01 23 45",
    "tg": "90 11 23 45",
    "th": "081 234 5678",
    "tj": "(8) 917 12 3456",
    "tk": "7290",
    "tl": "7721 2345",
    "tm": "8 66 123456",
    "tn": "20 123 456",
    "to": "771 5123",
    "tr": "0501 234 56 78",
    "tt": "(868) 291-1234",
    "tv": "901234",
    "tw": "0912 345 678",
    "tz": "0621 234 567",
    "ua": "050 123 4567",
    "ug": "0712 345678",
    "us": "(555) 123-4567",
    "uy": "094 231 234",
    "uz": "8 91 234 56 78",
    "va": "06 698 12345",
    "vc": "(784) 430-1234",
    "ve": "0412-1234567",
    "vg": "(284) 300-1234",
    "vi": "(340) 642-1234",
    "vn": "0912 345 678",
    "vu": "591 2345",
    "wf": "82 12 34",
    "ws": "601234",
    "xk": "043 201 234",
    "ye": "0712 345 678",
    "yt": "0639 12 34 56",
    "za": "071 123 4567",
    "zm": "095 5123456",
    "zw": "071 2 345 678"
  };
  return placeholders[country.toLowerCase()] || "Enter phone number";
};
const cleanPhoneNumber = (value) => {
  return value ? value.toString().replace(/\D+/g, "") : "";
};
const formatPhoneNumber = (value, country = "us") => {
  const cleaned = cleanPhoneNumber(value);
  if (!cleaned) return "";
  try {
    if ((country === "us" || country === "ca") && cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    const phoneNumber = parsePhoneNumber(cleaned, country.toUpperCase());
    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.formatNational();
    }
    const expectedLength = getExpectedPhoneLength(country);
    if (cleaned.length >= expectedLength.min && cleaned.length <= expectedLength.max) {
      return applyBasicFormatting(cleaned, country);
    }
    return cleaned;
  } catch (error) {
    return cleaned;
  }
};
const applyBasicFormatting = (cleaned, country) => {
  switch (country.toLowerCase()) {
    case "us":
    case "ca":
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      break;
    case "gb":
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
      } else if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
      }
      break;
    case "au":
      if (cleaned.length === 9) {
        return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
      }
      break;
    case "de":
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
      } else if (cleaned.length >= 11) {
        return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      }
      break;
    case "fr":
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
      }
      break;
    case "in":
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
      }
      break;
    case "cn":
      if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
      }
      break;
    default:
      if (cleaned.length >= 7) {
        return cleaned.replace(/(\d{3})(\d{3})(\d+)/, "$1 $2 $3");
      }
      break;
  }
  return cleaned;
};
const getExpectedPhoneLength = (country) => {
  const lengthMap = {
    "us": { min: 10, max: 10 },
    // US: exactly 10 digits
    "ca": { min: 10, max: 10 },
    // Canada: exactly 10 digits
    "gb": { min: 10, max: 11 },
    // UK: 10-11 digits
    "au": { min: 9, max: 9 },
    // Australia: 9 digits
    "de": { min: 10, max: 12 },
    // Germany: 10-12 digits
    "fr": { min: 10, max: 10 },
    // France: 10 digits
    "it": { min: 9, max: 11 },
    // Italy: 9-11 digits
    "es": { min: 9, max: 9 },
    // Spain: 9 digits
    "jp": { min: 10, max: 11 },
    // Japan: 10-11 digits
    "kr": { min: 10, max: 11 },
    // South Korea: 10-11 digits
    "cn": { min: 11, max: 11 },
    // China: 11 digits
    "in": { min: 10, max: 10 },
    // India: 10 digits
    "br": { min: 10, max: 11 },
    // Brazil: 10-11 digits
    "mx": { min: 10, max: 10 },
    // Mexico: 10 digits
    "ru": { min: 10, max: 10 }
    // Russia: 10 digits
  };
  return lengthMap[country.toLowerCase()] || { min: 7, max: 15 };
};
const validatePhoneNumber = (value, country = "us", required = false) => {
  var _a, _b;
  if (!value || value.trim() === "") {
    if (required) {
      return { isValid: false, errorMessage: "Phone number is required" };
    }
    return { isValid: true };
  }
  const cleaned = cleanPhoneNumber(value);
  const expectedLength = getExpectedPhoneLength(country);
  if (cleaned.length === 0) {
    return { isValid: true };
  }
  try {
    const phoneNumber = parsePhoneNumber(cleaned, country.toUpperCase());
    if (phoneNumber && phoneNumber.isValid()) {
      return {
        isValid: true,
        formattedValue: phoneNumber.formatNational(),
        countryCode: (_a = phoneNumber.country) == null ? void 0 : _a.toLowerCase()
      };
    }
  } catch (error) {
  }
  if (cleaned.length >= expectedLength.min && cleaned.length <= expectedLength.max) {
    try {
      const phoneNumber = parsePhoneNumber("+" + cleaned);
      if (phoneNumber && phoneNumber.isValid()) {
        return {
          isValid: true,
          formattedValue: phoneNumber.formatNational(),
          countryCode: (_b = phoneNumber.country) == null ? void 0 : _b.toLowerCase()
        };
      }
    } catch (error) {
    }
    return {
      isValid: true,
      formattedValue: formatPhoneNumber(value, country),
      countryCode: country
    };
  }
  if (cleaned.length < expectedLength.min) {
    return { isValid: true };
  }
  if (cleaned.length > expectedLength.max) {
    return { isValid: false, errorMessage: "Invalid phone number format" };
  }
  return { isValid: false, errorMessage: "Invalid phone number format" };
};
const YardiControlPhoneNumber = forwardRef(
  ({
    value = "",
    onChange,
    onBlur,
    name,
    id,
    placeholder = "Enter phone number",
    required = false,
    readonly = false,
    disabled = false,
    isInvalid = false,
    errorMessage,
    className = "",
    inputClassName = "",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    defaultCountry = "us",
    preferredCountries = ["us", "ca", "gb", "au"],
    showCountryDropdown = false,
    textMode = false,
    textModeEmptyState = "--",
    autoComplete = "tel",
    maxLength,
    disableMaxLength = false
  }, ref) => {
    const normalizedDefaultCountry = normalizeCountryCode(defaultCountry);
    const [internalValue, setInternalValue] = useState(() => {
      return value ? formatPhoneNumber(value, normalizedDefaultCountry) : "";
    });
    const [selectedCountry, setSelectedCountry] = useState(normalizedDefaultCountry);
    const [validationState, setValidationState] = useState({ isValid: true });
    const inputRef = useRef(null);
    const itiRef = useRef(null);
    const memoizedPreferredCountries = useMemo(
      () => preferredCountries.map((country) => normalizeCountryCode(country)),
      [JSON.stringify(preferredCountries)]
    );
    const currentPlaceholder = getCountryPlaceholder(selectedCountry);
    useEffect(() => {
      if (inputRef.current && !itiRef.current) {
        itiRef.current = intlTelInput(inputRef.current, {
          // onlyCountries: memoizedPreferredCountries.length > 0 ? memoizedPreferredCountries : undefined, // Show all countries
          preferredCountries: memoizedPreferredCountries,
          initialCountry: normalizedDefaultCountry,
          allowDropdown: showCountryDropdown,
          separateDialCode: false,
          autoPlaceholder: "polite",
          // Let intl-tel-input handle placeholders automatically
          utilsScript: utilsScriptUrl
          // From phonecontrol folder
        });
        console.log("?? intl-tel-input initialized:", itiRef.current);
        const handleCountryChange = () => {
          console.log("??? Country change event triggered");
          if (itiRef.current) {
            const countryData = itiRef.current.getSelectedCountryData();
            console.log("??? Country data:", countryData);
            if (countryData && countryData.iso2) {
              console.log("??? Setting selected country to:", countryData.iso2);
              setSelectedCountry(countryData.iso2);
              if (inputRef.current) {
                inputRef.current.placeholder = getCountryPlaceholder(countryData.iso2);
                const currentValue = inputRef.current.value;
                if (currentValue) {
                  const formattedValue = formatPhoneNumber(currentValue, countryData.iso2);
                  inputRef.current.value = formattedValue;
                  setInternalValue(formattedValue);
                  const validationResult = validatePhoneNumber(formattedValue, countryData.iso2, required);
                  setValidationState(validationResult);
                  if (onChange) {
                    onChange(formattedValue);
                  }
                } else {
                  setValidationState({ isValid: true });
                }
              }
            }
          }
        };
        const handleInput = (e) => {
          e.stopPropagation();
          const target = e.target;
          const rawValue = target.value || "";
          const cursorPosition = target.selectionStart || 0;
          const formattedValue = formatPhoneNumber(rawValue, selectedCountry);
          target.value = formattedValue;
          setInternalValue(formattedValue);
          if (!validationState.isValid) {
            const result = validatePhoneNumber(formattedValue, selectedCountry, required);
            setValidationState(result);
          }
          const lengthDiff = formattedValue.length - rawValue.length;
          const newCursorPosition = Math.max(0, cursorPosition + lengthDiff);
          setTimeout(() => {
            target.setSelectionRange(newCursorPosition, newCursorPosition);
          }, 0);
          if (onChange) {
            onChange(formattedValue);
          }
        };
        const handleBlurEvent = (e) => {
          const target = e.target;
          const currentValue = target.value || "";
          const result = validatePhoneNumber(currentValue, selectedCountry, required);
          setValidationState(result);
          if (onBlur) {
            onBlur(currentValue);
          }
        };
        const setupEventListeners = () => {
          console.log("?? Setting up event listeners");
          if (!inputRef.current) {
            console.warn("?? Input ref not available for event listeners");
            return;
          }
          inputRef.current.addEventListener("input", handleInput);
          inputRef.current.addEventListener("blur", handleBlurEvent);
          inputRef.current.addEventListener("countrychange", handleCountryChange);
          console.log("? Basic event listeners attached");
          const setupCountryListeners = (retryCount = 0) => {
            var _a, _b;
            const maxRetries = 10;
            const retryDelay = 100;
            const countryContainer = (_b = (_a = inputRef.current) == null ? void 0 : _a.parentElement) == null ? void 0 : _b.querySelector(".iti__flag-container");
            console.log("?? Looking for country container, retry:", retryCount, "found:", !!countryContainer);
            if (countryContainer) {
              console.log("? Country container found, setting up listeners");
              countryContainer.addEventListener("click", () => {
                console.log("??? Country dropdown clicked");
                setTimeout(() => handleCountryChange(), 150);
              });
              const handleCountryListClick = (e) => {
                const target = e.target;
                if (target.closest(".iti__country")) {
                  console.log("??? Country list item clicked:", target.closest(".iti__country"));
                  setTimeout(() => handleCountryChange(), 150);
                }
              };
              document.addEventListener("click", handleCountryListClick);
              const cleanup = () => {
                document.removeEventListener("click", handleCountryListClick);
              };
              inputRef.current._countryListCleanup = cleanup;
            } else if (retryCount < maxRetries) {
              console.log(`? Country container not found, retrying in ${retryDelay}ms (${retryCount + 1}/${maxRetries})`);
              setTimeout(() => setupCountryListeners(retryCount + 1), retryDelay);
            } else {
              console.warn("? Failed to find country container after maximum retries");
            }
          };
          setupCountryListeners();
        };
        setTimeout(setupEventListeners, 50);
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              var _a, _b;
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                if (((_a = element.classList) == null ? void 0 : _a.contains("iti__country-list")) || ((_b = element.querySelector) == null ? void 0 : _b.call(element, ".iti__country-list"))) {
                  console.log("?? Dropdown detected by MutationObserver");
                  setTimeout(() => {
                    setupEventListeners();
                  }, 100);
                }
              }
            });
          });
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
        inputRef.current._dropdownObserver = observer;
        if (value) {
          const formattedValue = formatPhoneNumber(value, selectedCountry);
          inputRef.current.value = formattedValue;
          setInternalValue(formattedValue);
        }
        return () => {
          console.log("?? Cleaning up phone number component");
          if (inputRef.current) {
            inputRef.current.removeEventListener("input", handleInput);
            inputRef.current.removeEventListener("blur", handleBlurEvent);
            inputRef.current.removeEventListener("countrychange", handleCountryChange);
            if (inputRef.current._countryListCleanup) {
              inputRef.current._countryListCleanup();
            }
            if (inputRef.current._dropdownObserver) {
              inputRef.current._dropdownObserver.disconnect();
            }
          }
          if (itiRef.current) {
            itiRef.current.destroy();
            itiRef.current = null;
          }
        };
      }
    }, [normalizedDefaultCountry, showCountryDropdown, memoizedPreferredCountries]);
    useEffect(() => {
      if (inputRef.current) {
        const currentInputValue = inputRef.current.value;
        if (value !== void 0 && value !== currentInputValue) {
          if (document.activeElement !== inputRef.current) {
            const formattedValue = formatPhoneNumber(value || "", selectedCountry);
            inputRef.current.value = formattedValue;
            setInternalValue(formattedValue);
          }
        } else if (!value && currentInputValue) {
          inputRef.current.value = "";
          setInternalValue("");
        }
      }
    }, [value, selectedCountry]);
    const validate = useCallback(() => {
      const result = validatePhoneNumber(internalValue, selectedCountry, required);
      setValidationState(result);
      return result;
    }, [internalValue, selectedCountry, required]);
    useImperativeHandle(ref, () => ({
      validate,
      focus: () => {
        var _a;
        (_a = inputRef.current) == null ? void 0 : _a.focus();
      },
      blur: () => {
        var _a;
        (_a = inputRef.current) == null ? void 0 : _a.blur();
      },
      reset: () => {
        setInternalValue("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        if (onChange) {
          onChange("");
        }
      },
      getValue: () => internalValue,
      getCleanValue: () => cleanPhoneNumber(internalValue),
      setValue: (newValue) => {
        const formattedValue = formatPhoneNumber(newValue, selectedCountry);
        setInternalValue(formattedValue);
        if (inputRef.current) {
          inputRef.current.value = formattedValue;
        }
        if (onChange) {
          onChange(formattedValue);
        }
      },
      updateCountry: (country) => {
        if (itiRef.current) {
          itiRef.current.setCountry(country);
        }
      }
    }), [validate, internalValue, onChange]);
    if (textMode) {
      return /* @__PURE__ */ jsx("div", { className: `yardi-control ${className}`, children: /* @__PURE__ */ jsx("span", { className: `form-control ${inputClassName}`, children: internalValue || textModeEmptyState }) });
    }
    const showError = isInvalid || !validationState.isValid;
    const finalErrorMessage = errorMessage || validationState.errorMessage;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: `yardi-control ${className}`,
        "data-show-dropdown": showCountryDropdown ? "true" : "false",
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: inputRef,
              type: "tel",
              name,
              id,
              defaultValue: internalValue,
              placeholder: currentPlaceholder,
              disabled: disabled || readonly,
              readOnly: readonly,
              required,
              autoComplete,
              maxLength: disableMaxLength ? void 0 : maxLength,
              "aria-describedby": ariaDescribedby,
              "aria-label": ariaLabel,
              className: `form-control ${inputClassName} ${showError ? "is-invalid" : ""}`.trim()
            }
          ),
          showError && finalErrorMessage && /* @__PURE__ */ jsx("div", { className: "invalid-feedback d-block mt-1", children: /* @__PURE__ */ jsx("span", { className: "text-danger text-sm", children: finalErrorMessage }) })
        ]
      }
    );
  }
);
YardiControlPhoneNumber.displayName = "YardiControlPhoneNumber";
const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1e6;
let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}
const toastTimeouts = /* @__PURE__ */ new Map();
const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId
    });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};
const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };
    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      };
    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast2) => {
          addToRemoveQueue(toast2.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map(
          (t) => t.id === toastId || toastId === void 0 ? {
            ...t,
            open: false
          } : t
        )
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === void 0) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};
const listeners = [];
let memoryState = { toasts: [] };
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}
function toast({ ...props }) {
  const id = genId();
  const update = (props2) => dispatch({
    type: "UPDATE_TOAST",
    toast: { ...props2, id }
  });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      }
    }
  });
  return {
    id,
    dismiss,
    update
  };
}
const DEFAULT_MESSAGE = "Loading your application...";
const DEFAULT_SUB_MESSAGE = "Please wait while we process your request...";
const LoadingScreenVisual = ({
  displayMessage = DEFAULT_MESSAGE,
  subMessage = DEFAULT_SUB_MESSAGE
}) => {
  return /* @__PURE__ */ jsx("div", { className: "min-h-screen flex items-center justify-center bg-background", children: /* @__PURE__ */ jsxs("div", { className: "text-center", children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx("div", { className: "animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-primary mx-auto" }),
      /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "w-8 h-8 bg-primary rounded-full opacity-20 animate-pulse" }) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-6 space-y-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-lg font-medium text-foreground", children: displayMessage }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: subMessage })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex justify-center mt-4 space-x-1", children: [
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce" }),
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce", style: { animationDelay: "0.1s" } }),
      /* @__PURE__ */ jsx("div", { className: "w-2 h-2 bg-primary rounded-full animate-bounce", style: { animationDelay: "0.2s" } })
    ] })
  ] }) });
};
const ApplicationLoadingScreen = ({
  message,
  subMessage
}) => {
  const { t } = useTranslation();
  return /* @__PURE__ */ jsx(
    LoadingScreenVisual,
    {
      displayMessage: t(message || DEFAULT_MESSAGE),
      subMessage: t(subMessage || DEFAULT_SUB_MESSAGE)
    }
  );
};
const DateFormatUtils = {
  monthNames: {
    "jan": 1,
    "january": 1,
    "feb": 2,
    "february": 2,
    "mar": 3,
    "march": 3,
    "apr": 4,
    "april": 4,
    "may": 5,
    "jun": 6,
    "june": 6,
    "jul": 7,
    "july": 7,
    "aug": 8,
    "august": 8,
    "sep": 9,
    "september": 9,
    "oct": 10,
    "october": 10,
    "nov": 11,
    "november": 11,
    "dec": 12,
    "december": 12
  },
  monthNamesReverse: {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec"
  },
  isNumericKey: (keyCode) => {
    return keyCode >= 48 && keyCode <= 57 || keyCode >= 96 && keyCode <= 105;
  },
  isLetterKey: (keyCode) => {
    return keyCode >= 65 && keyCode <= 90;
  },
  isDeleteKey: (keyCode) => {
    return keyCode === 8 || keyCode === 46;
  },
  isSpaceKey: (keyCode) => {
    return keyCode === 32;
  },
  getKeyFromCode: (keyCode) => {
    if (keyCode >= 48 && keyCode <= 57) {
      return String.fromCharCode(keyCode);
    }
    if (keyCode >= 96 && keyCode <= 105) {
      return String.fromCharCode(keyCode - 48);
    }
    if (keyCode >= 65 && keyCode <= 90) {
      return String.fromCharCode(keyCode + 32);
    }
    if (keyCode === 32) {
      return " ";
    }
    return "";
  },
  insertCharAtPosition: (str, char, position) => {
    return str.slice(0, position) + char + str.slice(position);
  },
  parseFlexibleDate: (input, countryCode = "us") => {
    if (!input.trim()) return null;
    const cleanInput = input.trim().toLowerCase().replace(/[,]/g, "");
    const code = (countryCode || "us").toLowerCase();
    const mdyCountries = /* @__PURE__ */ new Set(["us", "ph", "pw", "fm", "mh", "gu", "as", "vi", "pr", "mp", "bz", "pa", "mx"]);
    const ymdCountries = /* @__PURE__ */ new Set(["jp", "cn", "kr", "kp", "tw", "hk", "mo"]);
    const dateOrder = mdyCountries.has(code) ? "MDY" : ymdCountries.has(code) ? "YMD" : "DMY";
    const patterns = [
      // MMM DD, YYYY (Aug 19, 1998) - Display format
      /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2}),?\s+(\d{4})$/,
      // DD MMM YYYY or D MMM YYYY (19 aug 1999, 5 jan 2020)
      /^(\d{1,2})\s+(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{4})$/,
      // MMM DD YYYY or MMM D YYYY (aug 19 1999, jan 5 2020)
      /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2})\s+(\d{4})$/,
      // YYYY MMM DD or YYYY MMM D (1999 aug 19, 2020 jan 5)
      /^(\d{4})\s+(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2})$/,
      // MM/DD/YYYY, MM-DD-YYYY, MM.DD.YYYY
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
      // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY (European format)
      /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/,
      // YYYY/MM/DD, YYYY-MM-DD (ISO format)
      /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/
    ];
    for (let i = 0; i < patterns.length; i++) {
      const match = cleanInput.match(patterns[i]);
      if (match) {
        let day, month, year;
        switch (i) {
          case 0:
            month = DateFormatUtils.monthNames[match[1]] || 0;
            day = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            break;
          case 1:
            day = parseInt(match[1], 10);
            month = DateFormatUtils.monthNames[match[2]] || 0;
            year = parseInt(match[3], 10);
            break;
          case 2:
            month = DateFormatUtils.monthNames[match[1]] || 0;
            day = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            break;
          case 3:
            year = parseInt(match[1], 10);
            month = DateFormatUtils.monthNames[match[2]] || 0;
            day = parseInt(match[3], 10);
            break;
          case 4:
          // /-/. separated dates (MM/DD/YYYY or DD/MM/YYYY depending on country)
          case 5: {
            const firstNum = parseInt(match[1], 10);
            const secondNum = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
            if (dateOrder === "DMY") {
              day = firstNum;
              month = secondNum;
            } else {
              month = firstNum;
              day = secondNum;
            }
            break;
          }
          case 6:
            year = parseInt(match[1], 10);
            month = parseInt(match[2], 10);
            day = parseInt(match[3], 10);
            break;
          default:
            continue;
        }
        if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
          continue;
        }
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
          return date;
        }
      }
    }
    return null;
  },
  formatDateForDisplay: (date) => {
    const month = DateFormatUtils.monthNamesReverse[date.getMonth() + 1];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  },
  formatMMDDYYYY: (numbers, separator) => {
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return numbers.slice(0, 2) + separator + numbers.slice(2);
    } else {
      return numbers.slice(0, 2) + separator + numbers.slice(2, 4) + separator + numbers.slice(4, 8);
    }
  },
  autoFormatDateInput: (input, cursorPos, separator = "/") => {
    if (/[a-zA-Z]/.test(input)) {
      const parsedDate = DateFormatUtils.parseFlexibleDate(input);
      if (parsedDate) {
        const formatted2 = DateFormatUtils.formatDateForDisplay(parsedDate);
        return { value: formatted2, cursorPosition: formatted2.length };
      }
      return { value: input, cursorPosition: cursorPos };
    }
    const numbersOnly = input.replace(/\D/g, "");
    if (!numbersOnly) {
      return { value: "", cursorPosition: 0 };
    }
    const formatted = DateFormatUtils.formatMMDDYYYY(numbersOnly, separator);
    const originalSeparators = (input.slice(0, cursorPos).match(/[^0-9]/g) || []).length;
    const formattedSeparators = (formatted.slice(0, cursorPos + (formatted.length - input.length)).match(/[^0-9]/g) || []).length;
    const newCursorPos = cursorPos + (formattedSeparators - originalSeparators);
    return { value: formatted, cursorPosition: newCursorPos };
  },
  isAlreadyFormatted: (value, separator = "/") => {
    if (!value) return false;
    if (/^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value)) {
      return true;
    }
    const hasLetters = /[a-zA-Z]/.test(value);
    const hasSeparators = value.includes(separator);
    return hasLetters || hasSeparators && DateFormatUtils.isValidDateFormat(value, separator);
  },
  isValidDateFormat: (value, separator = "/") => {
    if (/^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value)) {
      return true;
    }
    const parts = value.split(separator);
    return parts.length <= 3 && parts.every((part) => /^\d*$/.test(part));
  }
};
const RenderCustomTableControl = React__default.forwardRef(({
  Id,
  objectUUId,
  objectType,
  languageCode,
  data,
  showSaveButton = true,
  showPreviousButton = true,
  onAllSectionsCompleted,
  onSaveProgress
}, ref) => {
  var _a, _b;
  const phonePreferredCountries = useMemo(() => ["us", "ca", "gb", "au"], []);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { t: translate } = useTranslation();
  const [state, setState] = useState({
    warningMessage: "",
    bReadOnly: false,
    inProgress: false,
    errorMessage: "",
    hasDataLoaded: false,
    fields: {},
    customfields: { rowdata: [] },
    customdata: {},
    serverError: "",
    saving: false,
    editable: false,
    isPreview: false,
    HideEditSave: false,
    attachmentsOnly: false,
    pageCode: "",
    isTablePresent: false,
    tableColumns: [],
    tableItems: [],
    len: 2,
    hiddenFields: [],
    nonHiddenFields: [],
    AddressLine: false,
    isFirstSection: false,
    showSaveButton: true,
    showPreviousButton: true,
    isCustomTable: data.isCustomTable,
    sCustomTableName: data.sCustomTableName,
    ObjType: data.ObjType,
    ObjPointer: data.ObjPointer,
    foreigndb: data.foreigndb
  });
  const [initCountry, setInitCountry] = useState("us");
  const customFormService = useCustomFormService();
  const [pageData, setPageData] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [useFieldColumnMode, setUseFieldColumnMode] = useState(false);
  const [groupedFields, setGroupedFields] = useState({});
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    setError,
    clearErrors
  } = useForm();
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [sectionTitle, setSectionTitle] = useState("");
  useEffect(() => {
    const getSectionTitle = async () => {
      const sections = await customFormService.sections$.value;
      if (sections && sections.length > 0) {
        const currentSection = sections.find((s) => s.sCustomTableName === data.sCustomTableName);
        if (currentSection) {
          setSectionTitle(currentSection.title || currentSection.name || "");
        }
      }
    };
    getSectionTitle();
  }, [data.sCustomTableName, customFormService.sections$]);
  useEffect(() => {
    const checkFirstSection = async () => {
      const sections = await customFormService.sections$.value;
      if (sections && sections.length > 0) {
        const currentIndex = sections.findIndex((s) => s.sCustomTableName === data.sCustomTableName);
        const isFirst = currentIndex === 0;
        setState((prev) => ({ ...prev, isFirstSection: isFirst }));
      }
    };
    checkFirstSection();
  }, [data.sCustomTableName, customFormService.sections$]);
  const handlePrevious = async () => {
    const navigationResult = await customFormService.previousButtonClick(data.sectionName || "");
    if (navigationResult) {
      navigate(navigationResult.url, { replace: true, state: navigationResult.state });
    }
  };
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await loadPage();
        await checkMobileView();
        window.addEventListener("resize", handleResize);
        const sections = await customFormService.sections$.value;
        if (sections && sections.length > 0) {
          const firstCustomTableSection = sections.find((s) => s.isCustomTable);
          const isFirst = (firstCustomTableSection == null ? void 0 : firstCustomTableSection.sCustomTableName) === data.sCustomTableName;
          setState((prev) => ({ ...prev, isFirstSection: isFirst }));
        }
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        setLoading(false);
      }
    };
    init();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const checkMobileView = () => {
    setIsMobileView(window.innerWidth <= 768);
  };
  const handleResize = () => {
    checkMobileView();
    if (useFieldColumnMode) {
      prepareColumnLayout();
    }
  };
  const parseDropdownOptions = (fieldListString) => {
    if (!fieldListString) return [];
    return fieldListString.split("^").filter((item) => item && item.trim()).map((item) => {
      if (item.includes("|")) {
        const [id, name] = item.split("|");
        return { id: id.trim(), name: name.trim() };
      }
      if (item.includes(":")) {
        const [id, name] = item.split(":");
        return { id: id.trim(), name: name.trim() };
      }
      return { id: item.trim(), name: item.trim() };
    });
  };
  const prepareColumnLayout = (fields = state.nonHiddenFields) => {
    const grouped = isMobileView ? { 1: fields } : fields.reduce((acc, field) => {
      const col = field.Field_Column && field.Field_Column > 0 ? field.Field_Column : 1;
      if (!acc[col]) acc[col] = [];
      acc[col].push(field);
      return acc;
    }, {});
    setGroupedFields(grouped);
  };
  const initializeCustomData = (fields) => {
    const initialData = {};
    fields.forEach((field) => {
      if (!initialData[field.Field_Name]) {
        initialData[field.Field_Name] = field.Field_Default_Value || "";
      }
    });
    setState((prev) => ({
      ...prev,
      customdata: initialData
    }));
    return initialData;
  };
  const fetchCustomTableData = async (fieldsToUse) => {
    const fields = fieldsToUse ?? state.nonHiddenFields ?? [];
    try {
      const requestData = {
        table_customData: data.sCustomTableName,
        Id: Id || 0,
        sectionId: null,
        allSectionsData: null,
        appData: null,
        formData: null
      };
      const response = await configurableFormsApi.getCustomTableData(requestData);
      const dataObj = ((response == null ? void 0 : response.Data) && typeof response.Data === "object" ? response.Data : null) ?? ((response == null ? void 0 : response.data) && typeof response.data === "object" ? response.data : null) ?? (response && typeof response === "object" && !("Success" in response) ? response : null);
      if (dataObj && Object.keys(dataObj).length > 0) {
        fields.forEach((field) => {
          const fieldKey = field.Field_Name;
          const customValue = (fieldKey ? dataObj[fieldKey] : void 0) ?? (fieldKey ? dataObj[fieldKey.toUpperCase()] : void 0) ?? (fieldKey ? dataObj[fieldKey.toLowerCase()] : void 0);
          if (customValue !== void 0 && customValue !== null) {
            setValue(field.Field_Name, customValue);
          }
        });
        setState((prev) => ({
          ...prev,
          customdata: Object.fromEntries(
            Object.entries(dataObj).map(([k, v]) => [String(k).toUpperCase(), v])
          )
        }));
      } else {
        if (!dataObj) {
          initializeCustomData(fields);
        }
      }
      const customTablesData = {
        templateId: Id || 0,
        ObjectType: Number(data.ObjType) || 0,
        ObjectPointer: Number(data.ObjPointer) || 0
      };
      const customTablesResponse = await configurableFormsApi.getCustomTablesData(customTablesData);
      if (customTablesResponse) {
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast({
        title: translate("Error"),
        description: translate("Failed to fetch custom table data"),
        variant: "destructive"
      });
      initializeCustomData(fields);
    }
  };
  const validateForm = useCallback(() => {
    var _a2;
    let hasErrors = false;
    let errorFields = [];
    const formValues = getValues();
    (_a2 = state.nonHiddenFields) == null ? void 0 : _a2.forEach((field) => {
      var _a3, _b2, _c, _d;
      const fieldName = field.Field_Name;
      const value = formValues[fieldName] ?? state.customdata[fieldName] ?? (fieldName ? state.customdata[fieldName.toUpperCase()] : void 0);
      const isRequired = (field._bRequired || field._bAlwaysRequired) && !field._bIsReadOnly;
      const isEmpty = value === void 0 || value === null || value === "" || typeof value === "string" && value.trim() === "";
      if (isRequired && isEmpty) {
        hasErrors = true;
        errorFields.push(field.sFieldName);
        setError(fieldName, {
          type: "required",
          message: translate("This field is required")
        });
      } else if (isRequired && !isEmpty) {
        clearErrors(fieldName);
      }
      if (value && ((_a3 = field.Field_Type) == null ? void 0 : _a3.toLowerCase()) === "email") {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailRegex.test(value)) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Invalid email address")
          });
        }
      }
      if (value && (((_b2 = field.Field_Type) == null ? void 0 : _b2.toLowerCase()) === "phone" || ((_c = field.Field_Type) == null ? void 0 : _c.toLowerCase()) === "phone number")) {
        const phoneDigits = value.replace(/\D/g, "");
        if (phoneDigits.length < 10) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate("Please enter a valid phone number")
          });
        }
      }
      if (value && ((_d = field.Field_Type) == null ? void 0 : _d.toLowerCase()) === "date") {
        const parsedDate = DateFormatUtils.parseFlexibleDate(value);
        const isStandardDate = !isNaN(new Date(value).getTime());
        const isDisplayFormat = /^[a-zA-Z]{3}\s+\d{1,2},\s+\d{4}$/.test(value);
        if (!parsedDate && !isStandardDate && !isDisplayFormat) {
          hasErrors = true;
          errorFields.push(field.sFieldName);
          setError(fieldName, {
            type: "pattern",
            message: translate('Please enter a valid date (e.g., "Aug 19, 1999" or "08/19/1999")')
          });
        }
      }
    });
    if (hasErrors) {
      setState((prev) => ({
        ...prev,
        errorMessage: `${translate("Please fill out all required(*) fields:")} ${errorFields.join(", ")}`
      }));
      window.scrollTo(0, 0);
    } else {
      setState((prev) => ({
        ...prev,
        errorMessage: ""
      }));
    }
    return !hasErrors;
  }, [state.nonHiddenFields, state.customdata, setError, clearErrors, getValues, translate]);
  const checkPhone = useCallback((field, selectedValue) => {
    var _a2, _b2;
    if (((_a2 = field.sFieldName) == null ? void 0 : _a2.toLowerCase().includes("country")) && state.nonHiddenFields.some((f) => {
      var _a3;
      return ((_a3 = f.sControlType) == null ? void 0 : _a3.toLowerCase()) === "phone number";
    })) {
      const selectedCountry = (_b2 = field.dropdownlist) == null ? void 0 : _b2.find((country) => country.id === selectedValue);
      if (selectedCountry == null ? void 0 : selectedCountry.code) {
        const countryCode = selectedCountry.code.toString().toLowerCase();
        setInitCountry(countryCode);
      }
    }
  }, [state.nonHiddenFields]);
  const handleSave = async (formData) => {
    var _a2;
    if (!validateForm()) {
      return;
    }
    try {
      setState((prev) => ({ ...prev, saving: true }));
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: true, inProgressText: translate("Saving...") });
      const requestData = {
        sectionId: null,
        allSectionsData: null,
        appData: null,
        table_customData: data.sCustomTableName,
        Id: Id || 0,
        formData
      };
      const response = await configurableFormsApi.saveCustomTableData(requestData);
      const isSuccess = response === "Success" || response && (response.Success === true || response.success === true) || (response == null ? void 0 : response.Data) === "Success";
      if (isSuccess) {
        setState((prev) => ({ ...prev, errorMessage: "" }));
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast({
          title: translate("Success"),
          description: translate("Record saved successfully."),
          variant: "default",
          duration: 3e3
        });
        const stepID = parseInt(localStorage.getItem("CurrentStepID") || "0", 10);
        const sections = customFormService.sections$.value || [];
        const currentSection = sections.find((s) => s.sCustomTableName === data.sCustomTableName);
        const sectionIdForProgress = objectUUId || ((currentSection == null ? void 0 : currentSection.id) ?? (currentSection == null ? void 0 : currentSection.sectionId)) || data.sCustomTableName || "";
        try {
          const progressResult = await customFormService.saveSectionProgress(sectionIdForProgress, response, stepID);
          const incompleteList = ((_a2 = customFormService.incompleteSectionList$) == null ? void 0 : _a2.value) || [];
          const allSectionsNowCompleted = incompleteList.length === 0;
          const isReadOnly = ReadOnlyService.isReadOnly();
          const allowCompletedSections = isReadOnly || allSectionsNowCompleted;
          const navigationResult = await customFormService.nextButtonClick(data.sCustomTableName || "", objectUUId, allowCompletedSections);
          if (navigationResult) {
            setTimeout(() => {
              navigate(navigationResult.url, { replace: true, state: navigationResult.state });
            }, 1e3);
            return;
          }
          if (!navigationResult && ((progressResult == null ? void 0 : progressResult.allSectionsCompleted) || allSectionsNowCompleted) && onAllSectionsCompleted) {
            const hasFullData = progressResult && "completedSections" in progressResult;
            onAllSectionsCompleted({
              allSectionsCompleted: true,
              completedSections: hasFullData ? progressResult.completedSections : [],
              allSectionData: hasFullData ? progressResult.allSectionData : [],
              templateId: hasFullData ? progressResult.templateId : null,
              finalSectionData: response
            });
          }
        } catch (progressError) {
          console.warn("Section progress or navigation failed after save:", progressError);
        }
      } else {
        throw new Error("Failed to save data");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      setState((prev) => ({
        ...prev,
        errorMessage: translate("An error occurred while saving. Please try again.")
      }));
      toast({
        title: translate("Error"),
        description: translate("Failed to save form data. Please try again."),
        variant: "destructive",
        duration: 5e3
      });
    } finally {
      setState((prev) => ({ ...prev, saving: false }));
      setIsSaving(false);
      onSaveProgress == null ? void 0 : onSaveProgress({ inProgress: false });
    }
  };
  const setDisplayValue = useCallback((fieldName, value) => {
    setState((prev) => ({
      ...prev,
      customdata: {
        ...prev.customdata,
        [fieldName]: value
      }
    }));
    clearErrors(fieldName);
    if (Object.keys(errors).length === 0) {
      setState((prev) => ({
        ...prev,
        errorMessage: ""
      }));
    }
  }, [clearErrors, errors]);
  const loadPage = async () => {
    try {
      setState((prev) => ({ ...prev, inProgress: true }));
      if (!Id) {
        throw new Error("Id is required for custom table fields");
      }
      if (data.isCustomTable) {
        const propertyId = getConfigurableFormsConfigOrThrow().getPropertyId();
        const params = {
          Name: data.sCustomTableName,
          DId: data.foreigndb != null && data.foreigndb !== "" ? Number(data.foreigndb) : 0,
          pointer: propertyId ? Number(propertyId) : 0,
          prospectId: Id
        };
        console.log("Loading custom table fields with:", params);
        const response = await configurableFormsApi.getCustomTablesFieldsData(params);
        if (response) {
          const rawFields = Array.isArray(response) ? response : (response == null ? void 0 : response.Data) ?? (response == null ? void 0 : response.data) ?? [];
          console.log("Custom table fields response:", rawFields.length, "fields");
          const tableField = rawFields.find(
            (item) => {
              var _a2;
              return ((_a2 = item.Field_Type) == null ? void 0 : _a2.toUpperCase()) === "TABLE" && item.Field_Name === data.sCustomTableName;
            }
          );
          const fields = rawFields.filter((item) => {
            var _a2;
            return ((_a2 = item.Field_Type) == null ? void 0 : _a2.toUpperCase()) !== "TABLE";
          }).map((item) => ({
            ...item,
            sDefaultFieldName: item.Field_Desc,
            sFieldName: item.Field_Desc,
            _bVisible: true,
            _bDemographicInfo: false,
            _bRequired: item.Field_Mandatory === true || item.Field_Required === -1,
            _bIsReadOnly: item.Field_ReadOnly === -1 || item._bIsReadOnly === true,
            _bAlwaysRequired: item.Field_Mandatory === true || item.Field_Required === -1,
            Field_Name: item.Field_Name || item.Field_Desc,
            dropdownlist: item.Field_Type === "LIST" && item.Field_List ? parseDropdownOptions(item.Field_List) : [],
            isHeader: item.Field_iType === 2
          }));
          const groupedByHeader = fields.reduce((acc, field) => {
            if (field.isHeader) {
              acc.push({
                header: field,
                fields: []
              });
            } else if (acc.length > 0) {
              acc[acc.length - 1].fields.push(field);
            } else {
              if (!acc[0]) {
                acc[0] = { header: null, fields: [] };
              }
              acc[0].fields.push(field);
            }
            return acc;
          }, []);
          const nonHiddenFields = groupedByHeader.reduce((acc, group) => {
            if (group.fields.length > 0) {
              acc.push(...group.fields);
            }
            return acc;
          }, []);
          nonHiddenFields.forEach((field) => {
            if (!field.isHeader) {
              setValue(field.Field_Name, field.Field_Default_Value || "");
            }
          });
          const customdata = initializeCustomData(nonHiddenFields);
          setState((prev) => ({
            ...prev,
            fields,
            nonHiddenFields,
            groupedByHeader,
            hiddenFields: [],
            customdata,
            hasDataLoaded: true,
            tableField
          }));
          await fetchCustomTableData(nonHiddenFields);
        }
      }
    } catch (error) {
      console.error("Error loading page:", error);
      setState((prev) => ({
        ...prev,
        serverError: error instanceof Error ? error.message : translate("Failed to load page data"),
        hasDataLoaded: false
      }));
      toast({
        title: translate("Error"),
        description: error instanceof Error ? error.message : translate("Failed to load page data"),
        variant: "destructive"
      });
    } finally {
      setState((prev) => ({ ...prev, inProgress: false }));
    }
  };
  const renderField = (field) => {
    var _a2, _b2, _c;
    if (field.isHeader || ((_a2 = field.Field_Type) == null ? void 0 : _a2.toUpperCase()) === "TABLE") {
      return null;
    }
    const isRequired = field._bRequired || field._bAlwaysRequired;
    const isReadOnly = field._bIsReadOnly;
    const fieldName = field.Field_Name;
    const customValue = fieldName ? state.customdata[fieldName.toUpperCase()] : void 0;
    const value = customValue !== void 0 && customValue !== null ? customValue : watch(fieldName);
    const commonProps = {
      value: value ?? "",
      onChange: (args) => {
        const newValue = args == null ? void 0 : args.value;
        setValue(fieldName, newValue);
        if (newValue !== void 0 && newValue !== null && newValue !== "") {
          clearErrors(fieldName);
        }
      },
      placeholder: field.Field_Desc || "",
      readonly: isReadOnly,
      enabled: !isReadOnly,
      disabled: ReadOnlyService.isReadOnly(),
      overrideFwReadOnlyFlag: true,
      divClassName: "w-full",
      cssClass: `e-outline ${isReadOnly ? "e-input-readonly" : ""} ${errors[fieldName] ? "e-error" : ""}`,
      floatLabelType: "Never"
    };
    switch ((_b2 = field.Field_Type) == null ? void 0 : _b2.toUpperCase()) {
      case "TEXT":
        return /* @__PURE__ */ jsx(
          YardiControlTextBox,
          {
            ...commonProps,
            "aria-describedby": `error_${fieldName}`
          }
        );
      case "LIST":
        return /* @__PURE__ */ jsx(
          YardiControlDropdown,
          {
            ...commonProps,
            onChange: (args) => {
              const newValue = args == null ? void 0 : args.value;
              setValue(fieldName, newValue);
              if (newValue !== void 0 && newValue !== null && newValue !== "") {
                clearErrors(fieldName);
              }
              checkPhone(field, newValue);
            },
            dataSource: (_c = field.dropdownlist) == null ? void 0 : _c.map((option) => ({
              text: option.name,
              value: option.id
            })),
            fields: { text: "text", value: "value" },
            allowFiltering: true
          }
        );
      case "DATE":
        return /* @__PURE__ */ jsx(
          "div",
          {
            onKeyDown: (event) => {
              if (isReadOnly) return;
              const input = event.target;
              if (input.tagName !== "INPUT") return;
              const keyCode = event.keyCode || event.which;
              const allowedKeys = [
                ...Array.from({ length: 10 }, (_, i) => 48 + i),
                // 0-9
                ...Array.from({ length: 10 }, (_, i) => 96 + i),
                // Numpad 0-9
                ...Array.from({ length: 26 }, (_, i) => 65 + i),
                // A-Z
                32,
                // Space
                8,
                46,
                // Backspace, Delete
                9,
                13,
                27,
                // Tab, Enter, Escape
                37,
                38,
                39,
                40,
                // Arrow keys
                191,
                189,
                190
                // /, -, .
              ];
              if (!allowedKeys.includes(keyCode)) {
                event.preventDefault();
              }
            },
            onBlur: (event) => {
              if (isReadOnly) return;
              const input = event.target;
              if (input.tagName !== "INPUT") return;
              const currentValue = input.value || "";
              if (!currentValue.trim()) {
                clearErrors(fieldName);
                return;
              }
              const parsedDate = DateFormatUtils.parseFlexibleDate(currentValue, initCountry);
              if (parsedDate) {
                const formatted = DateFormatUtils.formatDateForDisplay(parsedDate);
                input.value = formatted;
                setValue(fieldName, formatted);
                setDisplayValue(fieldName, formatted);
                clearErrors(fieldName);
              } else {
                const numbersOnly = currentValue.replace(/\D/g, "");
                if (numbersOnly && /^\d+$/.test(numbersOnly) && numbersOnly.length >= 6) {
                  const formatResult = DateFormatUtils.autoFormatDateInput(currentValue, currentValue.length);
                  const testDate = DateFormatUtils.parseFlexibleDate(formatResult.value, initCountry);
                  if (testDate) {
                    input.value = formatResult.value;
                    setValue(fieldName, formatResult.value);
                    setDisplayValue(fieldName, formatResult.value);
                    clearErrors(fieldName);
                    return;
                  }
                }
                setError(fieldName, {
                  type: "pattern",
                  message: translate('Please enter a valid date (e.g., "Aug 19, 1999" or "08/19/1999")')
                });
              }
            },
            onInput: (event) => {
              if (isReadOnly) return;
              const input = event.target;
              if (input.tagName !== "INPUT") return;
              const currentValue = input.value || "";
              if (currentValue.trim() && !/^[\d\s\/\-\.a-zA-Z,]*$/.test(currentValue)) {
                setError(fieldName, {
                  type: "pattern",
                  message: translate('Please enter a valid date (e.g., "Aug 19, 1999" or "08/19/1999")')
                });
              } else if (currentValue.trim() && /^[a-zA-Z]{3,}$/.test(currentValue.trim()) && !Object.keys(DateFormatUtils.monthNames).includes(currentValue.trim().toLowerCase())) {
                setError(fieldName, {
                  type: "pattern",
                  message: translate('Please enter a valid date (e.g., "Aug 19, 1999" or "08/19/1999")')
                });
              } else if (currentValue.trim()) {
                const numbersOnly = currentValue.replace(/\D/g, "");
                if (numbersOnly.length === 8 && currentValue === numbersOnly) {
                  const code = (initCountry || "us").toLowerCase();
                  const mdyCountries = /* @__PURE__ */ new Set(["us", "ph", "pw", "fm", "mh", "gu", "as", "vi", "pr", "mp", "bz", "pa", "mx"]);
                  const ymdCountries = /* @__PURE__ */ new Set(["jp", "cn", "kr", "kp", "tw", "hk", "mo"]);
                  const dateOrder = mdyCountries.has(code) ? "MDY" : ymdCountries.has(code) ? "YMD" : "DMY";
                  let day, month, year;
                  if (dateOrder === "MDY") {
                    month = parseInt(numbersOnly.substr(0, 2), 10);
                    day = parseInt(numbersOnly.substr(2, 2), 10);
                    year = parseInt(numbersOnly.substr(4, 4), 10);
                  } else if (dateOrder === "YMD") {
                    year = parseInt(numbersOnly.substr(0, 4), 10);
                    month = parseInt(numbersOnly.substr(4, 2), 10);
                    day = parseInt(numbersOnly.substr(6, 2), 10);
                  } else {
                    day = parseInt(numbersOnly.substr(0, 2), 10);
                    month = parseInt(numbersOnly.substr(2, 2), 10);
                    year = parseInt(numbersOnly.substr(4, 4), 10);
                  }
                  if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
                    const parsedNumeric = new Date(year, month - 1, day);
                    if (parsedNumeric.getFullYear() === year && parsedNumeric.getMonth() === month - 1 && parsedNumeric.getDate() === day) {
                      const formatted = DateFormatUtils.formatDateForDisplay(parsedNumeric);
                      input.value = formatted;
                      setValue(fieldName, formatted);
                      setDisplayValue(fieldName, formatted);
                      clearErrors(fieldName);
                      return;
                    }
                  }
                }
                const parsedDate = DateFormatUtils.parseFlexibleDate(currentValue, initCountry);
                if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
                  const formatted = DateFormatUtils.formatDateForDisplay(parsedDate);
                  if (formatted && formatted !== currentValue) {
                    input.value = formatted;
                    setValue(fieldName, formatted);
                    setDisplayValue(fieldName, formatted);
                    clearErrors(fieldName);
                  }
                }
              }
            },
            children: /* @__PURE__ */ jsx(
              YardiControlDatePicker,
              {
                ...commonProps,
                strictMode: false,
                invalidFormatErrorMessage: "",
                divClassName: `w-full cf-hide-internal-date-error ${(errors == null ? void 0 : errors[fieldName]) ? "cf-external-date-error" : "cf-no-external-date-error"}`,
                onChange: (args) => {
                  const dateValue = args == null ? void 0 : args.value;
                  if (dateValue instanceof Date) {
                    const formatted = DateFormatUtils.formatDateForDisplay(dateValue);
                    setValue(fieldName, formatted);
                    setDisplayValue(fieldName, formatted);
                    clearErrors(fieldName);
                  } else {
                    const typedText = ((args == null ? void 0 : args.text) ?? "").toString().trim();
                    const parsed = typedText ? DateFormatUtils.parseFlexibleDate(typedText, initCountry) : null;
                    if (parsed instanceof Date && !isNaN(parsed.getTime())) {
                      const formatted = DateFormatUtils.formatDateForDisplay(parsed);
                      setValue(fieldName, formatted);
                      setDisplayValue(fieldName, formatted);
                      clearErrors(fieldName);
                      return;
                    }
                    const stringValue = dateValue ?? typedText ?? "";
                    setValue(fieldName, stringValue);
                    setDisplayValue(fieldName, stringValue);
                    if (!stringValue && isRequired) {
                      setError(fieldName, {
                        type: "required",
                        message: translate("This field is required")
                      });
                    } else {
                      clearErrors(fieldName);
                    }
                  }
                },
                placeholder: field.Field_Desc,
                format: "dd MMM yyyy"
              }
            )
          }
        );
      case "MONEY":
        return /* @__PURE__ */ jsx(
          YardiControlCurrency,
          {
            value: value ?? "",
            onChange: (e) => {
              const newValue = e == null ? void 0 : e.value;
              setValue(fieldName, newValue);
              if (newValue !== void 0 && newValue !== null && newValue !== "") {
                clearErrors(fieldName);
              }
            },
            placeholder: field.Field_Desc || "",
            readonly: isReadOnly,
            enabled: !isReadOnly,
            disabled: ReadOnlyService.isReadOnly(),
            overrideFwReadOnlyFlag: true,
            divClassName: "w-full",
            cssClass: `e-outline ${isReadOnly ? "e-input-readonly" : ""} ${errors[fieldName] ? "e-error form-field-error-glow" : ""}`,
            floatLabelType: "Never",
            "aria-describedby": `error_${fieldName}`,
            precision: 2,
            currency: "USD",
            locale: "en-US",
            format: "c2",
            showSpinButton: false
          }
        );
      case "INT":
      case "NUM":
        return /* @__PURE__ */ jsx(
          YardiControlTextBox,
          {
            value: value ?? "",
            onChange: (e) => {
              const newValue = (e == null ? void 0 : e.value) ?? "";
              setValue(fieldName, newValue);
              if (newValue !== "" && newValue !== void 0 && newValue !== null) {
                clearErrors(fieldName);
              }
            },
            placeholder: field.Field_Desc || "",
            readonly: isReadOnly,
            enabled: !isReadOnly,
            disabled: ReadOnlyService.isReadOnly(),
            overrideFwReadOnlyFlag: true,
            divClassName: "w-full",
            cssClass: `e-outline ${isReadOnly ? "e-input-readonly" : ""} ${errors[fieldName] ? "e-error" : ""}`,
            floatLabelType: "Never",
            type: "number",
            "aria-describedby": `error_${fieldName}`
          }
        );
      case "CHECKBOX":
        return /* @__PURE__ */ jsx(
          YardiCheckbox,
          {
            checked: value === true || value === "true" || value === -1,
            onChange: (args) => setValue(fieldName, args.checked),
            disabled: isReadOnly,
            label: field.Field_Desc || ""
          }
        );
      case "PHONE":
      case "PHONE NUMBER":
        return /* @__PURE__ */ jsx(
          YardiControlPhoneNumber,
          {
            value: value || "",
            onChange: (phoneValue) => {
              setValue(fieldName, phoneValue);
              setDisplayValue(fieldName, phoneValue);
            },
            onBlur: (phoneValue) => {
              setValue(fieldName, phoneValue);
              if (phoneValue) {
                const phoneDigits = phoneValue.replace(/\D/g, "");
                if (phoneDigits.length < 10) {
                  setError(fieldName, {
                    type: "pattern",
                    message: translate("Please enter a valid phone number")
                  });
                } else {
                  clearErrors(fieldName);
                }
              } else if (isRequired) {
                setError(fieldName, {
                  type: "required",
                  message: translate("This field is required")
                });
              }
            },
            placeholder: field.Field_Desc || translate("Enter phone number"),
            readonly: isReadOnly,
            disabled: isReadOnly,
            required: isRequired,
            name: fieldName,
            id: `phone_${fieldName}`,
            defaultCountry: initCountry,
            preferredCountries: phonePreferredCountries,
            showCountryDropdown: true,
            autoComplete: "tel",
            textMode: isReadOnly,
            textModeEmptyState: "--",
            maxLength: 20
          },
          `phone-${fieldName}-${initCountry}`
        );
      default:
        console.warn(`Unsupported field type: ${field.Field_Type}`);
        return /* @__PURE__ */ jsx(YardiControlTextBox, { ...commonProps });
    }
  };
  React__default.useImperativeHandle(ref, () => ({
    handlePrevious: () => handlePrevious(),
    submitForm: () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        );
      }
    }
  }));
  const currentSectionIdForBreadcrumb = data.sCustomTableName || objectUUId;
  return /* @__PURE__ */ jsx("div", { className: "configurable-form-container overflow-visible", children: loading || state.inProgress ? /* @__PURE__ */ jsx(ApplicationLoadingScreen, { message: translate("Loading your form...") }) : /* @__PURE__ */ jsxs("div", { className: "w-full pb-4", children: [
    /* @__PURE__ */ jsx("div", { className: "desktop-max-width", children: /* @__PURE__ */ jsx(
      ConfigurableFormBreadcrumb,
      {
        currentSectionId: currentSectionIdForBreadcrumb,
        sectionTitle: sectionTitle || ((_a = state.tableField) == null ? void 0 : _a.Field_Desc) || data.sCustomTableName || "Form",
        categoryLabel: (data == null ? void 0 : data.sectionType) ?? "Student Info",
        showRequired: !state.bReadOnly,
        isReadOnly: state.bReadOnly
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "desktop-max-width", children: /* @__PURE__ */ jsxs("form", { ref: formRef, onSubmit: handleSubmit(handleSave), children: [
      state.errorMessage && /* @__PURE__ */ jsx("div", { className: "bg-red-50 border border-red-200 rounded-lg px-4 py-1 mb-6", role: "alert", "aria-live": "polite", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-red-800", children: state.errorMessage }) }),
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "ml-4 text-red-400 hover:text-red-500 leading-none !h-auto",
            onClick: () => setState((prev) => ({ ...prev, errorMessage: "" })),
            children: [
              /* @__PURE__ */ jsx("span", { className: "sr-only", children: translate("Dismiss") }),
              /* @__PURE__ */ jsx("svg", { className: "h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })
            ]
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "space-y-4 overflow-visible", children: (_b = state.groupedByHeader) == null ? void 0 : _b.map((group, groupIndex) => /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
        group.header && /* @__PURE__ */ jsx("div", { className: "bg-purple-50 border-b border-purple-100 px-4 py-5", children: /* @__PURE__ */ jsx("h2", { className: "text-lg font-medium text-gray-900", children: group.header.Field_Desc }) }),
        group.fields.map((field, fieldIndex) => {
          var _a2, _b2;
          return /* @__PURE__ */ jsxs("div", { className: "space-y-2 w-full", children: [
            /* @__PURE__ */ jsxs("div", { className: "mb-1", children: [
              /* @__PURE__ */ jsxs(Label, { className: "block mb-1 !text-base w-full", children: [
                /* @__PURE__ */ jsx("span", { children: field.sFieldName || field.Field_Desc || "" }),
                (field._bRequired || field._bAlwaysRequired) && !field._bIsReadOnly && /* @__PURE__ */ jsx("span", { className: "required-asterisk ml-1", children: "*" })
              ] }),
              field.sCustomFieldNarrative && /* @__PURE__ */ jsxs("div", { className: "text-sm text-gray-500 mt-0.5", children: [
                /* @__PURE__ */ jsx(FontAwesomeIcon, { icon: faCircleInfo, className: "w-3 h-3 mr-1 inline-block align-text-bottom" }),
                /* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: field.sCustomFieldNarrative || "" } })
              ] })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "mt-1 w-full", children: renderField(field) }),
            errors[field.Field_Name || ""] && /* @__PURE__ */ jsx("p", { className: "text-sm text-red-500 mt-1", role: "alert", "aria-live": "polite", children: String(
              typeof ((_a2 = errors[field.Field_Name || ""]) == null ? void 0 : _a2.message) === "string" ? (_b2 = errors[field.Field_Name || ""]) == null ? void 0 : _b2.message : translate("This field is required")
            ) })
          ] }, fieldIndex);
        })
      ] }, groupIndex)) }),
      /* @__PURE__ */ jsx("div", { className: "fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200", children: /* @__PURE__ */ jsxs("div", { className: "max-w-md mx-auto px-4 py-4 flex justify-end gap-4", children: [
        !state.isFirstSection && showPreviousButton && /* @__PURE__ */ jsx("div", { style: { width: "100px" }, children: /* @__PURE__ */ jsx(
          YardiControlButton,
          {
            variant: "modal-secondary",
            size: "lg",
            onClick: handlePrevious,
            disabled: state.saving,
            children: translate("Back")
          }
        ) }),
        showSaveButton && /* @__PURE__ */ jsx("div", { style: { width: "140px" }, children: /* @__PURE__ */ jsx(
          YardiControlButton,
          {
            variant: "modal-primary",
            size: "lg",
            type: "submit",
            disabled: state.saving || isSaving,
            cssClass: state.saving || isSaving ? "opacity-50 cursor-not-allowed pointer-events-none" : "",
            onClick: () => {
              if (!isSaving) {
                setIsSaving(true);
              }
            },
            children: translate("Continue")
          }
        ) })
      ] }) })
    ] }) })
  ] }) });
});
const CustomForm = ({
  Id,
  objectUUId,
  objectType,
  languageCode,
  bReadOnly,
  customAppData,
  showSaveButton = true,
  showPreviousButton = true,
  onSaveData,
  onAllSectionsCompleted
}) => {
  var _a;
  const customFormService = useCustomFormService();
  const [state, setState] = useState({
    warningMessage: "",
    bReadOnly: false,
    inProgress: false,
    errorMessage: "",
    hasDataLoaded: false,
    fields: {},
    customfields: { rowdata: [] },
    customdata: {},
    serverError: "",
    saving: false,
    editable: false,
    isPreview: false,
    HideEditSave: false,
    attachmentsOnly: false,
    pageCode: "",
    isTablePresent: false,
    tableColumns: [],
    tableItems: [],
    len: 2,
    hiddenFields: [],
    nonHiddenFields: [],
    AddressLine: false,
    isFirstSection: false,
    showSaveButton,
    showPreviousButton,
    isCustomTable: false,
    sCustomTableName: "",
    ObjType: null,
    ObjPointer: null,
    foreigndb: null
  });
  const [pageData, setPageData] = useState(null);
  const [stateData, setStateData] = useState(null);
  const location = useLocation();
  useNavigate();
  useEffect(() => {
    loadPage();
  }, [objectUUId, customAppData]);
  const loadPage = async () => {
    var _a2, _b, _c;
    try {
      setState((prev) => ({ ...prev, inProgress: true }));
      const currentStateData = ((_a2 = location.state) == null ? void 0 : _a2.data) || ((_b = location.state) == null ? void 0 : _b.sectionData);
      setStateData(currentStateData);
      console.log("Router state data:", currentStateData);
      console.log("Full location.state:", location.state);
      const initialState = {
        ObjType: (currentStateData == null ? void 0 : currentStateData.ObjType) || customFormService.objType$.value,
        ObjPointer: (currentStateData == null ? void 0 : currentStateData.ObjPointer) || customFormService.objPointer$.value,
        sCustomTableName: (currentStateData == null ? void 0 : currentStateData.sCustomTableName) || "",
        foreigndb: (currentStateData == null ? void 0 : currentStateData.foreigndb) || customFormService.foreigndb$.value,
        isCustomTable: (currentStateData == null ? void 0 : currentStateData.isCustomTable) || false
      };
      console.log("Initial state:", initialState);
      setState((prev) => ({
        ...prev,
        ...initialState
      }));
      if (currentStateData == null ? void 0 : currentStateData.isCustomTable) {
        console.log("? Using custom table data from navigation state:", currentStateData);
        return;
      }
      const response = await configurableFormsApi.loadFieldsForEndUser(objectUUId || "", customAppData);
      if ((_c = response == null ? void 0 : response.Response) == null ? void 0 : _c[0]) {
        const section = response.Response[0];
        setPageData(section);
        console.log("Section data from API:", section);
        console.log("Section TableName:", section.TableName);
        console.log("Section sCustomTableName:", section.sCustomTableName);
        console.log("Section isCustomTable:", section.isCustomTable);
        console.log("Section ObjType:", section.ObjType);
        console.log("Section ObjPointer:", section.ObjPointer);
        const isCustomTable = !!(section.TableName || section.sCustomTableName || section.isCustomTable);
        const tableName = section.TableName || section.sCustomTableName;
        if (isCustomTable && tableName) {
          const customTableState = {
            TableName: tableName,
            ObjType: section.ObjType || customFormService.objType$.value || (currentStateData == null ? void 0 : currentStateData.ObjType),
            ObjPointer: section.ObjPointer || customFormService.objPointer$.value || (currentStateData == null ? void 0 : currentStateData.ObjPointer),
            foreigndb: section.foreigndb || customFormService.foreigndb$.value || (currentStateData == null ? void 0 : currentStateData.foreigndb)
          };
          console.log("Setting custom table state with:", customTableState);
          setState((prev) => ({
            ...prev,
            isCustomTable: true,
            sCustomTableName: tableName,
            ObjType: section.ObjType || customFormService.objType$.value || (currentStateData == null ? void 0 : currentStateData.ObjType),
            ObjPointer: section.ObjPointer || customFormService.objPointer$.value || (currentStateData == null ? void 0 : currentStateData.ObjPointer),
            foreigndb: section.foreigndb || customFormService.foreigndb$.value || (currentStateData == null ? void 0 : currentStateData.foreigndb)
          }));
          const customTablesData = await configurableFormsApi.getCustomTablesData({
            templateId: Id || 0,
            ObjectType: Number(section.ObjType || customFormService.objType$.value || (currentStateData == null ? void 0 : currentStateData.ObjType)) || 0,
            ObjectPointer: Number(section.ObjPointer || customFormService.objPointer$.value || (currentStateData == null ? void 0 : currentStateData.ObjPointer)) || 0
          });
          if (customTablesData) {
            setState((prev) => ({
              ...prev,
              customdata: customTablesData
            }));
          }
        } else {
          console.log("Not a custom table section");
        }
      }
    } catch (error) {
      console.error("Error loading page:", error);
      setState((prev) => ({ ...prev, serverError: "Failed to load page data" }));
    } finally {
      setState((prev) => ({ ...prev, inProgress: false }));
    }
  };
  console.log("Current state:", state);
  console.log("Is custom table?", state.isCustomTable);
  console.log("ObjType:", state == null ? void 0 : state.ObjType);
  console.log("ObjPointer:", state.ObjPointer);
  const handleSaveData = (data) => {
    if (onSaveData) {
      onSaveData(data);
    }
  };
  const handleAllSectionsCompleted = (data) => {
    console.log("?? CustomForm: All sections completed, propagating callback with data:", data);
    if (onAllSectionsCompleted) {
      onAllSectionsCompleted(data);
    }
  };
  if (state.inProgress) {
    return /* @__PURE__ */ jsx("div", { className: "p-3 text-center w-full" });
  }
  return /* @__PURE__ */ jsx("div", { className: "w-full md:max-w-[90%] mx-auto", children: /* @__PURE__ */ jsx("div", { className: "w-full mx-auto", children: /* @__PURE__ */ jsxs("div", { className: "card-body", children: [
    state.warningMessage && /* @__PURE__ */ jsx("div", { className: "alert alert-danger text-center", children: /* @__PURE__ */ jsxs("div", { className: "row", children: [
      /* @__PURE__ */ jsx("div", { className: "col-lg-10 text-left", children: /* @__PURE__ */ jsx("p", { className: "m-0", children: /* @__PURE__ */ jsx("span", { children: state.warningMessage }) }) }),
      /* @__PURE__ */ jsx("div", { className: "col-lg-2 text-right", children: /* @__PURE__ */ jsx(
        "button",
        {
          className: "btn btn-link pt-0 pb-0 text-danger",
          onClick: () => setState((prev) => ({ ...prev, warningMessage: "" })),
          "aria-label": "Close",
          children: /* @__PURE__ */ jsx("i", { className: "far fa-times pr-2", style: { color: "#721c24" } })
        }
      ) })
    ] }) }),
    (_a = pageData == null ? void 0 : pageData.Narratives) == null ? void 0 : _a.map((narr, i) => /* @__PURE__ */ jsxs("p", { className: "mb-4", children: [
      /* @__PURE__ */ jsx("i", { className: "far fa-info-circle mr-2 hidden md:inline" }),
      /* @__PURE__ */ jsx("div", { className: "w-full text-2xl font-semibold text-gray-900 mb-1 [&_img]:inline-block [&_img]:align-middle [&_img]:mr-1 [&_img]:h-5 [&_img]:w-5", children: /* @__PURE__ */ jsx(
        "span",
        {
          className: "block break-words",
          dangerouslySetInnerHTML: { __html: narr }
        }
      ) })
    ] }, i)),
    /* @__PURE__ */ jsx("div", { className: "w-full mx-auto space-y-6 pb-24", children: !state.isCustomTable ? /* @__PURE__ */ jsx(
      CustomFormControl,
      {
        Id,
        objectUUId,
        customAppData,
        onSaveData: handleSaveData,
        onAllSectionsCompleted: handleAllSectionsCompleted,
        showSaveButton,
        showPreviousButton
      }
    ) : /* @__PURE__ */ jsx(
      RenderCustomTableControl,
      {
        Id,
        objectUUId,
        objectType,
        languageCode,
        data: {
          ObjType: state.ObjType,
          ObjPointer: state.ObjPointer,
          isCustomTable: state.isCustomTable,
          sCustomTableName: state.sCustomTableName,
          sectionName: (stateData == null ? void 0 : stateData.sectionName) || (pageData == null ? void 0 : pageData.sectionName) || "",
          sectionType: "Custom Table",
          foreigndb: state.foreigndb
        }
      }
    ) })
  ] }) }) });
};
const DEFAULT_LABELS = {
  back: "Back",
  continue: "Continue",
  saving: "Saving...",
  loadingForm: "Loading your form...",
  error: "Error",
  noSections: "No sections available",
  initFailed: "Failed to initialize sections"
};
const CustomFormSections = ({
  getApplicantData,
  stepName = "",
  formId,
  sectionIds,
  stepID = 0,
  shouldFetchData = true,
  onBack = () => {
  },
  onContinue,
  onAllSectionsCompleted,
  renderMobileFooterLeft
}) => {
  var _a;
  const { t: translate } = useTranslation();
  const config2 = getConfigurableFormsConfigOrThrow();
  const labels = useMemo(() => {
    const merged = { ...DEFAULT_LABELS, ...config2.labels };
    return Object.fromEntries(
      Object.entries(merged).map(([k, v]) => [k, translate(String(v ?? ""))])
    );
  }, [config2.labels, translate]);
  const formRef = useRef(null);
  const customTableRef = useRef(null);
  const [configurableFormAppData] = useState(new BehaviorSubject(null));
  const [applicantIdState, setApplicantIdState] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ inProgress: false, inProgressText: "" });
  const [sectionLoadInProgress, setSectionLoadInProgress] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const customFormService = useCustomFormService();
  const currentSectionData = (_a = location.state) == null ? void 0 : _a.sectionData;
  useEffect(() => {
    if (stepID > 0) localStorage.setItem("CurrentStepID", stepID.toString());
  }, [stepID]);
  const setSectionUrls = useCallback(
    (sections2) => {
      var _a2;
      const baseUrl = (_a2 = config2.getSectionBaseUrl) == null ? void 0 : _a2.call(config2, stepName);
      if (baseUrl) sections2.forEach((s) => {
        s.url = baseUrl;
      });
    },
    [config2, stepName]
  );
  const handleBack = useCallback(() => {
    if ((currentSectionData == null ? void 0 : currentSectionData.isCustomTable) && customTableRef.current) customTableRef.current.handlePrevious();
    else if (formRef.current) formRef.current.handlePrevious();
  }, [currentSectionData == null ? void 0 : currentSectionData.isCustomTable]);
  const handleContinue = useCallback(() => {
    if (isSaving || saveProgress.inProgress || sectionLoadInProgress) return;
    setIsSaving(true);
    if ((currentSectionData == null ? void 0 : currentSectionData.isCustomTable) && customTableRef.current) customTableRef.current.submitForm();
    else if (formRef.current) formRef.current.submitForm();
  }, [isSaving, saveProgress.inProgress, sectionLoadInProgress, currentSectionData == null ? void 0 : currentSectionData.isCustomTable]);
  const handleSaveProgress = useCallback((params) => {
    setSaveProgress((prev) => ({ ...prev, inProgress: params.inProgress, inProgressText: params.inProgressText ?? "" }));
    if (!params.inProgress) setIsSaving(false);
  }, []);
  const loadDefaultSection = useCallback(
    (processedSections) => {
      if (hasNavigated) return;
      const currentSections = customFormService.sections$.value || [];
      const aid = applicantIdState || localStorage.getItem("LoginApplicantId") || "0";
      const doNav = (next2) => {
        setHasNavigated(true);
        navigate(next2.url, {
          state: {
            sectionData: {
              id: aid,
              sectionId: next2.sectionId,
              sectionName: next2.name,
              sectionNumber: next2.sectionNumber,
              isCustomTable: next2.isCustomTable,
              sCustomTableName: next2.sCustomTableName,
              ObjType: next2.ObjType,
              ObjPointer: next2.ObjPointer,
              foreigndb: customFormService.foreigndb$.value
            }
          }
        });
      };
      if (currentSections.length === 0) {
        const sub = customFormService.sections$.subscribe((sections2) => {
          if ((sections2 == null ? void 0 : sections2.length) > 0) {
            sub.unsubscribe();
            const next2 = customFormService.getNextIncompleteSection() || processedSections[0];
            if (next2) doNav(next2);
            else toast$1({ title: labels.error, description: labels.noSections, variant: "destructive" });
          }
        });
        return;
      }
      const next = customFormService.getNextIncompleteSection() || processedSections[0];
      if (next) doNav(next);
      else toast$1({ title: labels.error, description: labels.noSections, variant: "destructive" });
    },
    [hasNavigated, navigate, customFormService, applicantIdState, labels.error, labels.noSections]
  );
  const waitForAllOperationsToComplete = useCallback(
    (processedSections) => {
      customFormService.templateId$.pipe(
        filter((id) => id != null),
        switchMap(() => customFormService.sections$.pipe(filter((s) => (s == null ? void 0 : s.length) > 0), take(1)))
      ).subscribe({
        next: () => {
          setSectionUrls(processedSections);
          loadDefaultSection(processedSections);
        },
        error: () => {
          setSectionUrls(processedSections);
          loadDefaultSection(processedSections);
        }
      });
    },
    [customFormService, loadDefaultSection, setSectionUrls]
  );
  const initializeCustomFormSections = useCallback(async () => {
    try {
      configurableFormAppData.pipe(filter((data) => data !== null), take(1)).subscribe({
        next: async (appData) => {
          var _a2, _b, _c;
          const loginApplicantId = applicantIdState || localStorage.getItem("LoginApplicantId") || "0";
          let initResponse = null;
          if (formId && sectionIds) {
            try {
              initResponse = await customFormService.loadSectionsBySectionIds(
                formId,
                sectionIds,
                appData,
                ((_a2 = appData.CompanyId) == null ? void 0 : _a2.toString()) || "0",
                ((_b = appData.PropertyId) == null ? void 0 : _b.toString()) || "0",
                formId,
                appData.CafeType || 2,
                appData.Id || 0,
                loginApplicantId
              );
            } catch (e) {
              console.error(e);
            }
          }
          if ((initResponse == null ? void 0 : initResponse.Status) === "Success" && ((_c = initResponse.Sections) == null ? void 0 : _c.length)) {
            waitForAllOperationsToComplete(initResponse.Sections);
          } else {
            toast$1({ title: labels.error, description: (initResponse == null ? void 0 : initResponse.Message) || labels.initFailed, variant: "destructive" });
          }
        },
        error: () => toast$1({ title: labels.error, description: labels.initFailed, variant: "destructive" })
      });
    } catch {
      toast$1({ title: labels.error, description: labels.initFailed, variant: "destructive" });
    }
  }, [configurableFormAppData, customFormService, waitForAllOperationsToComplete, applicantIdState, formId, sectionIds, labels.error, labels.initFailed]);
  useEffect(() => {
    var _a2;
    (_a2 = customFormService.resetService) == null ? void 0 : _a2.call(customFormService);
    if (!shouldFetchData) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    getApplicantData().then((result) => {
      if (cancelled) return;
      if (result) {
        configurableFormAppData.next(result.appData);
        setApplicantIdState(result.applicantId);
        initializeCustomFormSections();
      }
      setIsLoading(false);
    });
    return () => {
      var _a3;
      cancelled = true;
      (_a3 = customFormService.resetService) == null ? void 0 : _a3.call(customFormService);
    };
  }, [stepName]);
  useEffect(() => {
    var _a2;
    if ((_a2 = location.state) == null ? void 0 : _a2.sectionData) {
      const sectionData = location.state.sectionData;
      if (sectionData == null ? void 0 : sectionData.sectionId) {
        customFormService.sections$.pipe(filter((s) => (s == null ? void 0 : s.length) > 0), take(1)).subscribe((sections2) => {
          const current = sections2.find((s) => s.sectionId === sectionData.sectionId);
          if (current) customFormService.updateCurrentSection(current);
        });
      }
    }
  }, [location.state, customFormService]);
  let sectionId = currentSectionData == null ? void 0 : currentSectionData.sectionId;
  if (!sectionId) {
    const sections2 = customFormService.sections$.value || [];
    const next = customFormService.getNextIncompleteSection();
    if (next) sectionId = next.sectionId;
    else if (sections2.length > 0) sectionId = sections2[0].sectionId;
  }
  const applicantId = (currentSectionData == null ? void 0 : currentSectionData.id) ?? applicantIdState ?? localStorage.getItem("LoginApplicantId") ?? "0";
  const sections = customFormService.sections$.value || [];
  const currentSectionIndex = sections.findIndex((s) => s.sectionId === sectionId);
  const isCurrentlyFirstSection = currentSectionIndex === 0;
  const appDataValue = configurableFormAppData.value;
  const disabled = isSaving || saveProgress.inProgress || sectionLoadInProgress;
  const savingLabel = saveProgress.inProgressText || labels.saving;
  const handleAllSectionsCompleted = useCallback(async (data) => {
    if (ReadOnlyService.isReadOnly()) {
      onContinue == null ? void 0 : onContinue();
      return;
    }
    await onAllSectionsCompleted(data);
    onContinue == null ? void 0 : onContinue();
  }, [onAllSectionsCompleted, onContinue]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    (isLoading || !appDataValue) && /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center h-full", children: /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: labels.loadingForm }) }),
    !isLoading && appDataValue && /* @__PURE__ */ jsxs("div", { className: "min-h-screen bg-white", children: [
      /* @__PURE__ */ jsx("div", { className: "flex-1 pb-24", children: sectionId ? /* @__PURE__ */ jsxs(Fragment, { children: [
        (currentSectionData == null ? void 0 : currentSectionData.isCustomTable) ? /* @__PURE__ */ jsx(
          RenderCustomTableControl,
          {
            ref: customTableRef,
            Id: applicantId,
            objectUUId: sectionId,
            objectType: 2,
            languageCode: "en",
            showSaveButton: false,
            showPreviousButton: false,
            onAllSectionsCompleted: handleAllSectionsCompleted,
            onSaveProgress: handleSaveProgress,
            data: {
              ObjType: currentSectionData == null ? void 0 : currentSectionData.ObjType,
              ObjPointer: currentSectionData == null ? void 0 : currentSectionData.ObjPointer,
              isCustomTable: true,
              sCustomTableName: (currentSectionData == null ? void 0 : currentSectionData.sCustomTableName) ?? "",
              sectionName: (currentSectionData == null ? void 0 : currentSectionData.sectionName) ?? "",
              sectionType: "Custom Table",
              foreigndb: currentSectionData == null ? void 0 : currentSectionData.foreigndb
            }
          }
        ) : /* @__PURE__ */ jsx(
          CustomFormControl,
          {
            ref: formRef,
            Id: applicantId,
            objectUUId: sectionId ?? "",
            customAppData: appDataValue,
            showSaveButton: false,
            showPreviousButton: false,
            onAllSectionsCompleted: handleAllSectionsCompleted,
            onSaveProgress: handleSaveProgress,
            onSectionLoadStateChange: (loading) => {
              setSectionLoadInProgress(loading);
              if (!loading) setIsSaving(false);
            },
            stepID
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "border-t border-gray-200 pt-6 mt-8 hidden xl:flex justify-end gap-2 w-full", children: [
          !isCurrentlyFirstSection && sections.length > 1 && /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-secondary", size: "lg", onClick: handleBack, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed px-6" : "px-6", overrideFwReadOnlyFlag: true, children: labels.back }),
          isCurrentlyFirstSection && sections.length > 0 && /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-secondary", size: "lg", onClick: onBack, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed px-6" : "px-6", overrideFwReadOnlyFlag: true, children: labels.back }),
          /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-primary", size: "lg", onClick: handleContinue, inProgress: saveProgress.inProgress, inProgressText: savingLabel, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed px-6" : "px-6", overrideFwReadOnlyFlag: true, children: labels.continue })
        ] })
      ] }) : /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center h-full", children: /* @__PURE__ */ jsx(ApplicationLoadingScreen$1, { message: labels.loadingForm }) }) }),
      sectionId && /* @__PURE__ */ jsxs("div", { className: "fixed bottom-0 left-0 right-0 z-10 bg-white shadow-elevation-subtle px-4 py-3 flex items-center gap-4 border-t border-gray-200 xl:hidden", children: [
        renderMobileFooterLeft == null ? void 0 : renderMobileFooterLeft(),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-2 flex-1", children: [
          !isCurrentlyFirstSection && sections.length > 1 && /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-secondary", size: "lg", onClick: handleBack, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed" : "", overrideFwReadOnlyFlag: true, children: labels.back }),
          isCurrentlyFirstSection && sections.length > 0 && /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-secondary", size: "lg", onClick: onBack, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed" : "", overrideFwReadOnlyFlag: true, children: labels.back }),
          /* @__PURE__ */ jsx(YardiControlButton, { variant: "modal-primary", size: "lg", onClick: handleContinue, inProgress: saveProgress.inProgress, inProgressText: savingLabel, disabled, cssClass: disabled ? "opacity-50 cursor-not-allowed" : "", overrideFwReadOnlyFlag: true, children: labels.continue })
        ] })
      ] })
    ] })
  ] });
};
const BreadcrumbProgress = ({
  steps,
  onStepClick,
  className = ""
}) => {
  const { t: translate } = useTranslation();
  const getPrimaryColor = () => {
    if (typeof window !== "undefined") {
      const root = getComputedStyle(document.documentElement);
      const primary = root.getPropertyValue("--primary").trim();
      return primary || "#7a3881";
    }
    return "#7a3881";
  };
  const primaryColor = getPrimaryColor();
  return /* @__PURE__ */ jsx(
    "nav",
    {
      className: `w-full flex items-center justify-center ${className}`,
      "aria-label": translate("Breadcrumb"),
      children: /* @__PURE__ */ jsx("ol", { className: "flex items-center flex-nowrap space-x-2", children: steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isActive = step.active || false;
        const isCompleted = step.completed || false;
        return /* @__PURE__ */ jsxs("li", { className: "flex items-center flex-shrink-0", children: [
          isActive ? (
            // Current section (completed or incomplete) - not clickable, bold black text
            /* @__PURE__ */ jsx(
              "span",
              {
                className: "text-sm font-bold whitespace-nowrap",
                style: { color: "#1f2937" },
                "aria-current": "page",
                children: step.label ? translate(step.label) : ""
              }
            )
          ) : isCompleted ? (
            // Completed section (not current) - clickable link, bold text with primary color and underline
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => onStepClick && onStepClick(step),
                type: "button",
                className: "text-sm font-bold underline hover:opacity-80 focus:outline-none transition-opacity whitespace-nowrap",
                style: {
                  color: primaryColor
                },
                children: step.label ? translate(step.label) : ""
              }
            )
          ) : (
            // Incomplete section (not yet visited) - not clickable, grayed out text
            /* @__PURE__ */ jsx(
              "span",
              {
                className: "text-sm font-bold whitespace-nowrap",
                style: { color: "#9ca3af" },
                children: step.label ? translate(step.label) : ""
              }
            )
          ),
          !isLast && /* @__PURE__ */ jsx(
            "span",
            {
              className: "mx-2 font-bold flex-shrink-0",
              style: { color: "#6b7280" },
              "aria-hidden": "true",
              children: ">"
            }
          )
        ] }, step.id);
      }) })
    }
  );
};
const SectionHeaderWithProgress = ({
  currentSectionId,
  sectionTitle = "",
  categoryLabel = "Student Info",
  showRequired = true,
  isReadOnly = false,
  className = ""
}) => {
  const navigate = useNavigate();
  const customFormService = useCustomFormService();
  const sections = customFormService.sections$.value || [];
  const completedSectionList = customFormService.completedSectionList || [];
  const [navigationPath, setNavigationPath] = useState(customFormService.navigationPath$.value || []);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  useEffect(() => {
    const subscription = customFormService.navigationPath$.subscribe((path) => {
      setNavigationPath(path || []);
    });
    return () => subscription.unsubscribe();
  }, [customFormService]);
  const visibleSections = useMemo(() => {
    return sections.filter((section) => {
      const isVisibleByQuery = section.isVisibleByQuery !== false;
      const isVisibleForCountry = section.isVisibleForCountry !== false;
      return isVisibleByQuery && isVisibleForCountry;
    });
  }, [sections]);
  const { steps, currentStepIndex, totalSteps, progressPercentage } = useMemo(() => {
    const currentSectionIdStr = String(currentSectionId || "");
    const sortedSections = [...visibleSections].sort((a, b) => {
      const orderA = a.iorder || a.iOrder || a.sectionOrder || a._Order || 0;
      const orderB = b.iorder || b.iOrder || b.sectionOrder || b._Order || 0;
      return orderA - orderB;
    });
    let currentIndex = -1;
    const progressSteps = sortedSections.map((section, index) => {
      const sectionIdStr = String(section.sectionId || section.id || "");
      const numericId = String(section.id || "");
      const isActive = sectionIdStr === currentSectionIdStr;
      const isCompleted = section.completed || completedSectionList.includes(numericId);
      if (isActive) {
        currentIndex = index;
      }
      return {
        id: sectionIdStr,
        label: section.name || section.sectionName || "",
        completed: isCompleted,
        active: isActive,
        clickable: isCompleted && !isActive
      };
    });
    const total = progressSteps.length;
    const progress = total > 0 ? (currentIndex + 1) / total * 100 : 0;
    return {
      steps: progressSteps,
      currentStepIndex: currentIndex,
      totalSteps: total,
      progressPercentage: Math.min(progress, 100)
    };
  }, [visibleSections, currentSectionId, completedSectionList]);
  const handleStepClick = useCallback(async (step) => {
    if (!step.clickable) return;
    setIsSheetOpen(false);
    const section = visibleSections.find(
      (s) => String(s.sectionId || s.id || "") === step.id
    );
    if (!section) return;
    const clickedIndex = navigationPath.findIndex((id) => id === step.id);
    if (clickedIndex >= 0) {
      const newPath = navigationPath.slice(0, clickedIndex + 1);
      customFormService.navigationPath$.next(newPath);
    } else {
      const newPath = [...navigationPath, step.id];
      customFormService.navigationPath$.next(newPath);
    }
    const navigationResult = await customFormService.navigateToSection(section.sectionId || section.id, true);
    if (navigationResult) {
      navigate(navigationResult.url, { state: navigationResult.state });
    }
  }, [visibleSections, navigationPath, customFormService, navigate]);
  const hasCompletedSections = completedSectionList.length > 0;
  const showProgress = visibleSections.length > 1 && (navigationPath.length > 0 || hasCompletedSections);
  const StepListItem = ({
    step,
    index,
    onSelect
  }) => {
    const isCompleted = step.completed && !step.active;
    const isCurrent = step.active;
    const isUpcoming = !step.completed && !step.active;
    return /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: onSelect,
        disabled: !step.clickable,
        className: cn$2(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
          step.clickable && "hover:bg-accent cursor-pointer",
          !step.clickable && "cursor-default",
          isCurrent && "bg-primary/5"
        ),
        "aria-current": isCurrent ? "step" : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn$2(
                "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                isCompleted && "bg-primary text-primary-foreground",
                isCurrent && "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2",
                isUpcoming && "bg-muted text-muted-foreground"
              ),
              children: isCompleted ? /* @__PURE__ */ jsx(Check, { className: "w-3.5 h-3.5", "aria-hidden": "true" }) : /* @__PURE__ */ jsx("span", { children: index + 1 })
            }
          ),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn$2(
                "flex-1 text-sm truncate",
                isCompleted && "text-foreground",
                isCurrent && "text-foreground font-semibold",
                isUpcoming && "text-muted-foreground"
              ),
              children: step.label
            }
          ),
          isCurrent && /* @__PURE__ */ jsx("span", { className: "text-xs text-primary font-medium", children: "Current" })
        ]
      }
    );
  };
  const StepTrigger = () => /* @__PURE__ */ jsxs("div", { className: "inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors", children: [
    /* @__PURE__ */ jsxs("span", { className: "text-sm", children: [
      "Step ",
      currentStepIndex + 1,
      " of ",
      totalSteps
    ] }),
    /* @__PURE__ */ jsx(
      ChevronDown,
      {
        className: cn$2(
          "w-4 h-4 transition-transform",
          isSheetOpen && "rotate-180"
        ),
        "aria-hidden": "true"
      }
    )
  ] });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$2("w-full mb-6", className),
      role: "banner",
      "aria-label": "Section header",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-1", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs uppercase tracking-wider font-medium text-muted-foreground", children: categoryLabel }),
          showRequired && !isReadOnly && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground uppercase", children: "*required" })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-3", children: [
          /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-foreground", children: sectionTitle }),
          showProgress && /* @__PURE__ */ jsx(Fragment, { children: isMobile ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setIsSheetOpen(true),
                className: cn$2(
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                ),
                "aria-expanded": isSheetOpen,
                "aria-haspopup": "dialog",
                children: /* @__PURE__ */ jsx(StepTrigger, {})
              }
            ),
            /* @__PURE__ */ jsx(Sheet, { open: isSheetOpen, onOpenChange: setIsSheetOpen, children: /* @__PURE__ */ jsxs(
              SheetContent,
              {
                side: "bottom",
                className: "max-h-[70vh] rounded-t-xl",
                children: [
                  /* @__PURE__ */ jsx(SheetHeader, { className: "px-4 pt-4 pb-2 border-b", children: /* @__PURE__ */ jsx(SheetTitle, { className: "text-base", children: "Application Progress" }) }),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "overflow-y-auto py-2",
                      role: "list",
                      "aria-label": "Form sections",
                      children: steps.map((step, index) => /* @__PURE__ */ jsx(
                        StepListItem,
                        {
                          step,
                          index,
                          onSelect: () => handleStepClick(step)
                        },
                        step.id
                      ))
                    }
                  )
                ]
              }
            ) })
          ] }) : /* @__PURE__ */ jsxs(DropdownMenu, { children: [
            /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: cn$2(
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                ),
                children: /* @__PURE__ */ jsx(StepTrigger, {})
              }
            ) }),
            /* @__PURE__ */ jsx(
              DropdownMenuContent,
              {
                align: "end",
                className: "w-[280px] max-h-[300px] overflow-y-auto p-0",
                sideOffset: 8,
                children: /* @__PURE__ */ jsx("div", { role: "list", "aria-label": "Form sections", children: steps.map((step, index) => {
                  const isCompleted = step.completed && !step.active;
                  const isCurrent = step.active;
                  const isUpcoming = !step.completed && !step.active;
                  return /* @__PURE__ */ jsxs(
                    DropdownMenuItem,
                    {
                      onClick: () => handleStepClick(step),
                      disabled: !step.clickable,
                      className: cn$2(
                        "flex items-center gap-3 px-3 py-2.5 cursor-default",
                        step.clickable && "cursor-pointer",
                        isCurrent && "bg-primary/5"
                      ),
                      children: [
                        /* @__PURE__ */ jsx(
                          "span",
                          {
                            className: cn$2(
                              "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium",
                              isCompleted && "bg-primary text-primary-foreground",
                              isCurrent && "bg-primary text-primary-foreground",
                              isUpcoming && "bg-muted text-muted-foreground"
                            ),
                            children: isCompleted ? /* @__PURE__ */ jsx(Check, { className: "w-3 h-3", "aria-hidden": "true" }) : /* @__PURE__ */ jsx("span", { children: index + 1 })
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "span",
                          {
                            className: cn$2(
                              "flex-1 truncate",
                              isCompleted && "text-foreground",
                              isCurrent && "text-foreground font-medium",
                              isUpcoming && "text-muted-foreground"
                            ),
                            children: step.label
                          }
                        )
                      ]
                    },
                    step.id
                  );
                }) })
              }
            )
          ] }) })
        ] }),
        showProgress && /* @__PURE__ */ jsx(
          Progress,
          {
            value: progressPercentage,
            className: "h-1.5 bg-gray-200",
            "aria-label": `Progress: ${currentStepIndex + 1} of ${totalSteps} steps`
          }
        )
      ]
    }
  );
};
export {
  BreadcrumbProgress,
  COUNTRY_DATE_FORMATS,
  ConfigurableFormBreadcrumb,
  CustomForm,
  CustomFormControl,
  CustomFormSections,
  ISO_TO_ZIP_KEY,
  RenderCustomTableControl,
  STATE_REGION_CODES,
  SectionHeaderWithProgress,
  TermsAndConditionsField,
  VALIDATION_PATTERNS,
  ZIP_CODE_PATTERNS,
  configurableFormsApi,
  getConfigurableFormsConfig,
  getConfigurableFormsConfigOrThrow,
  setConfigurableFormsConfig,
  useCustomFormService
};
//# sourceMappingURL=configurable-forms.esm.js.map
